// TraceLanesView — the lane reading of one trace: subjects × turns, as the
// panel's primary surface. The Context reading scrolls TIME vertically and
// states each operation's subject inline; this view inverts that projection —
// every subject the extractors recovered (each file, host, PR, query target,
// skill, unclaimed tool) holds one horizontal LANE, and the turns that touched
// it become marks on a shared left-to-right turn axis.
//
// The axis is TURN SEQUENCE, not wall clock (the store keeps no per-span
// timing), and EVERY turn is addressable: the axis runs 0..turnCount-1 at a
// uniform per-turn width, so any instant of the session can be selected even
// when no extractor claimed it — the dock falls back to the turn's raw blocks
// when there is no semantic reading to invert.
//
// The timeline is zoomable (⌘/ctrl-scroll, or the − + Fit 1:1 controls): at
// fit, the whole session's shape is one screen — where the work clustered,
// what each episode kept returning to — and it spends the WHOLE frame, going
// wider than 1:1 on a short trace rather than leaving the axis hugging its
// left edge under an empty panel. Zoomed in, each turn is a column. When
// a turn column gets narrower than a mark, adjacent activity merges into one
// counted mark (bucketMarks) rather than overdrawing — and a folded directory
// or domain group merges its lanes onto its header row the same way, so
// folding trades subject detail for density, never for coverage.
//
// Selecting a cell docks the subject AS OF that turn: a file lane replays
// reconstructFile over every op up to the playhead — the file as it was
// represented to the agent at that instant — with the same per-line provenance
// rail as the other X-ray readings (AnnotatedFileBody). Hovering a mark shows
// the same accumulated state in miniature plus the lineage of ops that led to
// it. Lanes whose activity sits outside the visible window grow edge pointers
// that say where (and how far) their nearest activity is, and clicking one
// navigates there. Everything resolves in place; the only exits are the
// explicit "open in Context" affordances.
import { Fragment, Suspense, createContext, lazy, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import {
  buildTraceLanes,
  reconstructLaneAsOf,
  cellKind,
  isConversationDomain,
  mergedCells,
  noteFor,
  noteWords,
  beatWords,
  opLine,
  callCellsAt,
  sameCallLanes,
} from "../lib/traceLanes.js";
import { buildTraceActions } from "../lib/traceActions.js";
import { markTurnRange, MARK_COLOR } from "../lib/traceLaneMarks.js";
import LanesCanvasPlane from "./LanesCanvasPlane.jsx";
import SessionCanvasView from "./SessionCanvasView.jsx";
import { WorkspaceAddSheet, useTraceWorkspaceHost } from "./TraceWorkspaceHost.jsx";
import { setTraceCanvasMode } from "../lib/traceCanvasMode.js";
import { keepTraceCanvas, saveTraceCanvas, traceCanvasId, useTraceCanvasOpen } from "../lib/traceCanvasStore.js";
import { EYEBROW_BASE, EYEBROW_CLASS } from "../lib/ui.js";
import { buildLaneScene, densityShown, foldAwareCellsById, insetAxisWidth, nearestTurns, pinnedBandInset, SCAN_H, STRIP_CTRL_W } from "../lib/laneScene.js";
import { stripMarkOf, stripMarkTargets } from "../lib/stripMarkGuide.js";
import {
  capLinks, TIER_STRENGTH,
} from "../lib/traceLaneLinks.js";
import { RAIL_W, RAIL_W_MIN, RAIL_W_MAX, clampRailW, ROW_H, RULER_H, DOCK_SHARE, SESSION_DOCK_SHARE, guideReveal, guideTargets } from "../lib/traceLaneLayout.js";
import { buildTraceInfluence, linksAt } from "../lib/traceInfluence.js";
import { fileBasis } from "../lib/traceFeedBasis.js";
import { shellOriginHints, commandDerivation, pathKey } from "../lib/traceShellOrigins.js";
import { shellCommandOf } from "../lib/traceForgeOps.js";
import { classifyLine, extractFileOps } from "../lib/traceFileTree.js";
import { rawTurn, readCall, readTurn, readToolCall, toolResultText } from "../lib/traceToolCall.js";
import { toolUseText } from "../lib/signalHighlight.js";
import { query } from "../lib/dataClient.js";
// Direct imports, not dataClient methods: these three thread the caller's
// AbortSignal into the trace-read gate, and dataClient's keyed in-flight
// dedupe cannot carry a per-caller signal.
import { fetchHostSessionForThread, fetchTraceResponse, searchTraceHandoffCandidates, fetchJoinMirrorWindow } from "../lib/apiClient.js";
import { deriveFileProbeSet, attachProbeMatches, linesFromReconstruction, PROBE_SESSION_CAP } from "../lib/traceElsewhereChannels.js";
import { deriveTraceNeedles, locateNeedleStrings, candidateDirectionLabel, rowContextBadges, joinKindRollup, buildJoinTurnMarks, buildJoinCellMarks, foldJoinSpans, assignJoinHues, mergeJoinRange, looksInjected } from "../lib/traceNeedleLens.js";
import { stageJoinFollow } from "../lib/joinFollow.js";
import { deriveAgentIdentity } from "../lib/agentIdentity.js";
import { appendCapturedResponse, responseTurnFromParts } from "../lib/traceCapturedResponse.js";
import { unwrapCodeModeTurns } from "../lib/traceCodeModeCells.js";
import {
  SYSTEM_STREAM_KINDS, systemSpan, systemKindOf, bucketWeight, sliceTurnCandidates, buildSliceTree,
} from "../lib/traceSystemStream.js";
import { buildTurnTimes, bucketEventsByTurn, turnSliceWindow } from "../lib/traceTurnTimes.js";
import { buildTurnStamps, rangeStamp } from "../lib/traceTurnStamp.js";
import { askDock, lendDock, takeDockBack, useDockBridge, useDockScope } from "../lib/dockBridge.js";
import { NO_LIGHT, createHandoffLight, litOn } from "../lib/handoffLight.js";
import { createBoardLanding } from "../lib/boardLanding.js";
import { StampCaptureContext, TraceStamp } from "./TraceStamp.jsx";
import { useSessionJourney } from "../lib/sessionJourney.js";
import { useTraceShellWork } from "../lib/useTraceShellWork.js";
import { readShellWork } from "../lib/traceShellWork.js";
import { handoffGrams, HANDOFF_STRING_CAP, sealedTaskRuns } from "../lib/fpJoin.js";
import {
  buildTopicAxis, rankTopics, topicBuckets, topicHues, topicRuns, TOPIC_OTHER_HUE,
} from "../lib/traceTopicBand.js";
import { fetchEndpointEvents, fetchEndpointEventDetails } from "../lib/traceTurnClockFetch.js";
import { eventLine } from "../lib/traceEndpointEvents.js";
import { buildTurnDensity } from "../lib/traceTurnDensity.js";
import { buildLaneWeights, mergeLaneWeights } from "../lib/traceLaneWeight.js";
import { chainTurnLineages, evictMisfiledCompactionRows, matchesSpawnPrompt } from "../lib/traceTurnLineage.js";
import { matchesTrimmedFrom, matchesCompactedFrom, conversationStableText, turnFingerprints } from "../lib/traceContentBlame.js";
import { getModelCost } from "../lib/modelCatalog.js";
import { tokenCostEnabled, applyTokenRates, sumCostBands } from "../lib/tokenCost.js";
import { parseServerTimestamp, fullTimestampFormatter } from "../lib/traceUtils.js";
import { landingMayReadEarlier, recordTurnBase } from "../lib/traceTurnFocus.js";
import { spawnWindowFromTurns, threadMissText } from "../lib/traceThreadsWiring.js";
import { getChatCreds } from "../lib/chatCreds.js";
import {
  defaultReviewModel,
  reviewMarks,
  incidentTone,
  SEVERITY_COLOR,
} from "../lib/traceReview.js";
import { getReviewProfile } from "../lib/reviewClient.js";
import {
  fetchTraceReviewFindings, findingsCovering, readFindingsCache, readTraceReviewFindings,
  buildFindingsReport, findingsSummaryLine, findingsByTurn, findingsInRange, findingCellPins,
  findIncidentById,
} from "../lib/traceReviewFindings.js";
import { incidentsOf, findingSimilarity, closestCitation } from "../lib/traceReviewIncidents.js";
import {
  buildTraceGraphOverlay, claimCellLinks, claimReplyText, clusterBadges, createSeenStore, graphAtTurn, graphBadgeAnchors,
  graphCellPins, graphOverlaySummary, graphStripMarks, graphStripSummary, toolUseLanes,
} from "../lib/traceGraphOverlay.js";
import { GraphCommentCard, GraphPinLayer } from "./LanesGraphComments.jsx";
import { graphVersion, notifyGraphChanged, readLiveGraph, subscribeGraph } from "../lib/investigationGraphRun.js";
import { graphReplies, nextReplyId } from "../lib/graphReplies.js";
import {
  fetchTraceReviewReports, readReportsCache, runPooled, writeReportsCache,
  buildReportsTimeline, reportsSummaryLine, reportSegments, reportsAt, reportLabel,
} from "../lib/traceReviewReports.js";
import {
  runHandoffScan,
  handoffCacheKey,
  readHandoffCache,
  writeHandoffCache,
  handoffMarks,
  handoffTrafficMarks,
  handoffSummaryLine,
} from "../lib/traceHandoffs.js";
import {
  runTraceReading,
  draftReadingCalls,
  readingCacheKey,
  readingClaimsHash,
  readingSessionOf,
  readReadingCache,
  subscribeReadingCache,
  writeReadingCache,
  buildReadLanes,
  readingMarks,
  readingHarvestByCall,
  readingDelta,
  writeReadingSnapshot,
  clearReadingCache,
  mergePromotedClaims,
  finalizeReading,
  readingRows,
  readingDockTotals,
  readingCoverage,
} from "../lib/traceReading.js";
import {
  emptyLiveReading,
  applyLiveEvents,
} from "../lib/traceReadingStream.js";
import {
  runReadingSummary,
  readSummaryCache,
  writeSummaryCache,
  clearSummaryCache,
} from "../lib/traceReadingSummary.js";
import ReadingDock from "./ReadingDock.jsx";
import TraceReviewFindingCard from "./TraceReviewFindingCard.jsx";
import TraceChat, { readerReplyKey } from "./TraceChat.jsx";
import { addTraceNotes } from "../lib/traceNoteStore.js";
import { createCaseReader } from "../lib/investigateReads.js";
import { askTrace, clearThreadNotes, readThreadNotes, subscribeThreadNotes } from "../lib/traceChatThreads.js";
import { addToChat, cellChatItem } from "../lib/chatContextStore.js";
import { followBoardPick } from "../lib/boardPickStore.js";
import { noteAnchors, noteReplyText, noteStripMarks, noteStripSummary, placeNotes } from "../lib/traceNotes.js";
import { formatCompact, formatUsd } from "../lib/renderers.js";
import { OverlayScrollbars } from "overlayscrollbars";
import { AnnotatedFileBody, DiffRows } from "./tracePayloads.jsx";
import { memberPayload } from "./TraceWorkFeed.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import ScrollArea from "./ScrollArea.jsx";
import { isWorkspaceWalk, shownWalkKey, useWalkthrough, walkthroughChatKey, workspaceWalkKey } from "../lib/traceWalkthrough.js";
import { claimOriginTool } from "../lib/walkClaims.js";
import { requestWorkspaceFocus } from "../lib/workspaceStore.js";
import { workspaceWalkSuffix, workspaceWalkTools } from "../lib/workspaceWalkChat.js";
import { boardCaseId } from "../lib/caseInbox.js";
import { toApiRange } from "../lib/shell.js";

// The workspace dock's Walkthrough tab, read only where a board is up.
const WalkthroughDock = lazy(() => import("./WalkthroughDock.jsx"));
// Its Investigate tab: the cases on the board, read once the tab is opened.
const CaseInbox = lazy(() => import("./CaseInbox.jsx"));

const META_CLASS = "text-[11px] leading-[1.3]";
// The dock's readings in one face and size, a turn's number and a row's label
// as much as what it holds: only colour tells their parts apart.
const DOCK_TEXT = "font-mono text-[12px] leading-[1.5]";
const FINDING_META = "text-[10px] leading-[1.4] text-[color:var(--muted)]";
// A docked turn can carry many claims; past this many paths the plane is a
// hairball, and the dock lists every claim anyway.
const GRAPH_LINKS_CAP = 24;
const NO_REPLIES = Object.freeze([]);
const NO_NOTES = Object.freeze([]);
const NO_CLAIMS = Object.freeze([]);
const NO_ANCHORS = new Map();
// What a folded scan mark counts, by strip. A scan strip's mark is one kind of
// thing, and only the review bands' things are findings: two captured requests
// folded into one mark on the Tokens strip were captioned "2 findings", which
// read as the findings having moved lanes (found live).
const SCAN_MARK_NOUN = {
  requests: "requests",
  findings: "findings",
  reports: "findings",
  review: "findings",
  handoff: "candidates",
  reading: "corrections",
  graph: "claims",
  notes: "notes",
};
export function scanMarkCountLine(strip, count) {
  if (!(count > 1)) return "";
  return ` \u00b7 ${count} ${SCAN_MARK_NOUN[strip] || "marks"}`;
}

// Grid geometry. The per-turn width is the ZOOM (cellW, px/turn, fractional at
// fit); everything else is fixed. Where per-turn marks stop being drawable and
// bucketing takes over is MARK_MIN_PX, in traceLaneMarks.js with the mark
// geometry that reads it.
// Lane weight lives in the RAIL, as a bar-in-cell behind each row's name: how
// much of this conversation's token mass that subject accounts for, filled left
// to right against the heaviest lane. The rail is where a reader already scans
// vertically to compare subjects, and it costs the timeline no ink at all.
//
// The share tint behind each row's sparkline, the tree's idiom in the rail's
// greyscale (the plot's soft ink at 65%). The plane draws it
// (LanesCanvasPlane); this is the legend swatch, and the two must agree.
const WEIGHT_FILL = "color-mix(in srgb, var(--plot-ink-soft) 65%, transparent)";
// Narrowest topic column worth drawing; below this zoom adjacent turns merge
// into one column carrying their whole mix (topicBuckets).
const TOPIC_SEG_MIN_PX = 3;
// The hand-off strip mirrors the review strip's geometry; its marks take the
// same 13px count badge, so the same separation floor applies.
// The content-joins annotation strip: thinner than the scan's — a bubble and
// an underline, no glyph vocabulary of its own.
// Two fold grains for the joins strip. A rendered chip is ~22px wide, so any
// fold narrower than that lets neighbours overlap into a smear (found live);
// the fine grain is the floor at which marks stay legible side by side, and
// the coarse grain is the rest state — spans, not chips, until the pointer
// arrives.
const JOIN_MARK_FOLD_PX = 24;
// The content-join accent: joins are cross-session edges — foreground
// reading, not chrome. Two treatments failed before this one: the pale
// info-blue wash disappeared into the plane, and a solid bright fill shouted
// over it. What makes a small mark read as an OBJECT on the page is a crisp
// solid ring around a quiet fill (the Notion-avatar move) — distinctness
// from the edge, not from luminance. The plane draws that edge (its join chip);
// the fill stays a muted tint so a busy strip doesn't become a string of beacons.
const JOIN_COLOR = "var(--join, #5db2ff)";
// Narrowest density bar worth drawing; below this zoom adjacent turns merge
// into one bucketed bar (the same detail-for-density trade as laneMarks).
const DENSITY_BAR_MIN_PX = 3;
const CELL_1X = 18;
const CELL_MAX = 26;
// The hand-zoom floor, shared with the spliced children so a child cannot be
// zoomed out past what the parent allows.
const CELL_MIN = 0.4;
// Below this the whole axis is cheap to build; above it the per-turn strips
// alone are tens of thousands of primitives and the scene takes a turn window.
// Turn labels are positions in the RECORD, not in the loaded page. The axis
// reads the record newest first, so the first turn on it is rarely the
// conversation's first turn, and a label that counted from zero renamed every
// turn each time a page landed or the session grew. The base is the count of
// messages before the loaded page, provided once by the view and added
// wherever a turn number is printed; it is zero when the whole record is on
// the axis. Messages and turns differ where a message unwraps into several
// turns, so the base is exact for the loaded page's order and approximate as
// an absolute count. Indices in state and handlers stay page-relative.
const TurnBaseContext = createContext(0);

const SCENE_WINDOW_MIN_TURNS = 4000;
// The dock slot's drag bounds: what the shortest useful dock needs, and what
// the plane above it keeps no matter how far the divider is dragged. The
// reserve is what stops a held-open chat from squeezing the lanes to nothing.
const DOCK_MIN_H = 140;
const DOCK_PLANE_RESERVE = 220;
// The reader's pinned dock height, shared across mounts (see the note at the
// `dockH` state). Null until a drag pins one, which is what lets the slot fall
// back to its 46% ceiling.
let sharedDockHeight = null;
// Drop the remembered height, so a test or a fresh surface starts from the
// ceiling rather than inheriting a drag from whatever ran before it.
export function resetSharedDockHeight() {
  sharedDockHeight = null;
}
// Why the judge cannot run on this trace, said in the reading panel's offer
// where the run button would be; null when it can run. A trace with calls to
// judge is only missing a chat model; one with none has nothing to judge.
// `asks`: the panel has the questions about the trace under it (a canvas's
// keeps them in its own chat instead).
export function readingOfferNote({ ready, hasCalls, asks = true }) {
  if (ready) return null;
  if (hasCalls) return "Configure a chat model to read this trace.";
  return asks
    ? "This trace has no tool calls for a judge to read. Questions below read the lanes and turns directly."
    : "This trace has no tool calls for a judge to read.";
}
// What tied one derivation step to the next, in the reader's words rather than
// the model's. "inferred" is the one link that is not a quotation of the command:
// a converter names its input and derives its output's name from it.
const DERIVE_LABEL = { wrote: "wrote it", prefix: "output prefix", mentions: "names it", stem: "inferred" };

// Geometry of the derivation tree. TREE_X is the turn gutter (30) plus its gap
// (6). The gutter right-aligns, so the distance from the turn to the tree is the
// same whether the trace ran to T9 or T426 - left-aligned it grew by whatever
// digits the number was short. TREE_ELBOW_Y is the middle of the statement line
// the tick points at.
const TREE_X = 36;
const TREE_TICK_W = 6;
const TREE_ELBOW_Y = 11;

// Accumulated-state tints for the hover card's excerpt — the same recipe as the
// X-ray gutter (color-mix alpha, no gradients), kept local because XRayBody's
// PROV table is private to that surface.
const CARD_TINT = {
  written: "color-mix(in srgb, var(--green) 10%, transparent)",
  inserted: "color-mix(in srgb, var(--green) 10%, transparent)",
  edited: "color-mix(in srgb, var(--green) 10%, transparent)",
  drift: "color-mix(in srgb, var(--amber,#c8922e) 13%, transparent)",
};

const clip = (s, n) => {
  const t = String(s || "");
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

function collectLanes(node) {
  const out = [...node.lanes];
  for (const d of node.dirs.values()) out.push(...collectLanes(d));
  return out;
}

// Merged weight for every foldable row, keyed by row id. A subtree's weight is
// a property of the SUBTREE, not of whether it happens to be folded right now,
// so this is built once per (model, weights) and never on a fold: flattenRows
// re-runs on every toggle, and hanging the merge off the rows it mints would
// re-merge every closed group each time one of them was clicked.
function subtreeWeights(model, weights) {
  const out = new Map();
  if (!weights) return out;
  const put = (id, lanes) => {
    const merged = mergeLaneWeights(weights, lanes.map((l) => l.id));
    if (merged) out.set(id, merged);
  };
  for (const group of model.groups) {
    put(group.id, group.lanes);
    if (group.kind !== "files" || !group.tree) continue;
    const walk = (node) => {
      for (const dir of node.dirs.values()) {
        put(`dir:${dir.path}`, collectLanes(dir));
        walk(dir);
      }
    };
    walk(group.tree);
  }
  return out;
}

// The rail tree flattened against the fold state: header rows for the files
// tree's directories and each domain group, lane rows beneath the open ones.
function defaultInsetFold(childModel) {
  return new Set((childModel?.groups || []).map((g) => g.id));
}

// The child conversations behind the spliced agent lanes. The fetch lives here
// rather than in the inset row because a canvas inset is a SUB-SCENE the parent
// builds: whoever builds the scene has to hold the turns.
// Why a spliced child has no timeline yet. Product copy, so it lives with the
// view rather than in the geometry: the plane only draws the sentence.
function insetNote(state) {
  if (state.status === "loading") return "Fetching the spawned thread's trace\u2026";
  if (state.status === "error") return "The spawned thread's trace could not be fetched.";
  return threadMissText(state.miss, { retry: "Collapse and re-expand once the timeline has settled." });
}

function useSplicedChildren(openAgents, model, threads, anchorOf) {
  const [byLane, setByLane] = useState(() => new Map());
  const loadingRef = useRef(new Set());
  // The OS clock refines while the panel settles. Re-fetching a loaded child on
  // every refinement would flicker it, so the anchor at expand time is the
  // join's input and the ref keeps it out of the dependency list.
  const anchorRef = useRef(anchorOf);
  anchorRef.current = anchorOf;
  // The open set, readable when a fetch lands: a result for a lane the reader
  // has since closed is dropped rather than re-added.
  const openRef = useRef(openAgents);
  openRef.current = openAgents;
  // Cancellation belongs to UNMOUNT, not to a re-run. A per-run flag cancelled
  // every fetch still in flight whenever another lane was opened -- so opening a
  // second thread stranded the first on "loading" forever, with its id still in
  // `loadingRef` and therefore never retried.
  const aliveRef = useRef(true);
  useEffect(() => {
    // Set on the way IN as well as cleared on the way out. StrictMode mounts,
    // unmounts and remounts in development, so a flag only ever cleared stays
    // cleared after the first cycle -- and every result lands on a component
    // that believes it is gone.
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);
  useEffect(() => {
    for (const id of [...loadingRef.current]) if (!openAgents.has(id)) loadingRef.current.delete(id);
    setByLane((prev) => {
      const next = new Map([...prev].filter(([id]) => openAgents.has(id)));
      return next.size === prev.size ? prev : next;
    });
    if (!threads?.loadLaneTurns) return undefined;
    for (const id of openAgents) {
      if (loadingRef.current.has(id)) continue;
      const lane = model.laneById?.get(id);
      if (!lane) continue;
      loadingRef.current.add(id);
      setByLane((p) => new Map(p).set(id, { status: "loading" }));
      const land = (state) => {
        if (!aliveRef.current || !openRef.current.has(id)) return;
        setByLane((p) => new Map(p).set(id, state));
      };
      const anchor = anchorRef.current?.(lane) ?? null;
      Promise.resolve(threads.loadLaneTurns(lane, {
        anchorMs: anchor?.anchorMs ?? null, anchorSpan: anchor?.span ?? null, sessionFromMs: anchor?.sessionFromMs ?? null,
      })).then(
        (sub) => {
          if (!sub?.turns) {
            land({ status: "empty", miss: sub?.miss || null });
            return;
          }
          // The child is read the way the parent is: code-mode cells unwrapped
          // into the calls they made, then its shell calls read for what they
          // consulted. The record hands back raw cells, and a child drawn from
          // those showed one "code cells" lane where its files and reads were.
          const ready = { ...sub, turns: unwrapCodeModeTurns(sub.turns) };
          land({ status: "ready", sub: ready });
          readShellWork(ready.turns).then((work) => {
            if (!aliveRef.current || !openRef.current.has(id) || !work?.items?.length) return;
            setByLane((p) => {
              const cur = p.get(id);
              return cur?.sub === ready ? new Map(p).set(id, { ...cur, shellItems: work.items }) : p;
            });
          }, () => {
            // A failed parse pass costs the child its shell reads, not its timeline.
          });
        },
        () => land({ status: "error" }),
      );
    }
  }, [openAgents, model, threads]);
  return byLane;
}

function flattenRows(model, closed) {
  const rows = [];
  for (const group of model.groups) {
    const gClosed = closed.has(group.id);
    rows.push({
      type: "head",
      id: group.id,
      label: group.label,
      count: group.laneCount,
      depth: 0,
      closed: gClosed,
      cells: gClosed ? mergedCells(group.lanes) : null,
      // Which lanes a folded row now STANDS FOR. Folding already merges their
      // marks onto this row; anything else that addresses a lane by row — the
      // influence overlay's endpoints — needs the same redirection, or a link
      // into a folded subtree draws at no row at all. NOT a handle for weight:
      // a fresh array per toggle, so keying a memo on it re-runs on every fold
      // (subtreeWeights is built off the model instead).
      laneIds: gClosed ? group.lanes.map((l) => l.id) : null,
    });
    if (gClosed) continue;
    if (group.kind === "files") {
      const walk = (node, depth) => {
        for (const dir of node.dirs.values()) {
          const id = `dir:${dir.path}`;
          const dClosed = closed.has(id);
          const all = collectLanes(dir);
          rows.push({
            type: "head",
            id,
            label: dir.name,
            count: all.length,
            depth,
            closed: dClosed,
            cells: dClosed ? mergedCells(all) : null,
            // Same redirection as the group row above: a closed DIRECTORY
            // stands for its whole subtree, not just the lanes directly in it.
            laneIds: dClosed ? all.map((l) => l.id) : null,
          });
          if (!dClosed) walk(dir, depth + 1);
        }
        for (const lane of node.lanes) rows.push({ type: "lane", lane, depth });
      };
      walk(group.tree, 1);
    } else {
      for (const lane of group.lanes) rows.push({ type: "lane", lane, depth: 1 });
    }
  }
  return rows;
}

// The mark vocabulary, kept to what a cell can say at 8px: hollow = consulted,
// filled = changed, red = a call here errored; a multi-op mark shows its count.
// Square on purpose — the ruler's beats are diamonds, and shape is what keeps
// "work on a subject" and "a beat of the conversation" apart at a glance.
function Mark({ kind, count = 1 }) {
  const color = MARK_COLOR[kind] || MARK_COLOR.read;
  if (count > 1) {
    return (
      <span
        className="grid h-[13px] min-w-[13px] place-items-center rounded-[2px] border px-[2px] font-mono text-[8px] leading-none"
        style={{
          color,
          borderColor: `color-mix(in srgb, ${color} 60%, var(--line))`,
          borderStyle: kind === "verified" ? "dashed" : "solid",
          background: kind === "read" || kind === "verified" || kind === "readCall" ? "transparent" : `color-mix(in srgb, ${color} 18%, transparent)`,
        }}
      >
        {count}
      </span>
    );
  }
  // Conversation marks are the ruler beats' own diamonds — shape is what keeps
  // "a beat of the conversation" and "work on a subject" apart at a glance. A
  // note is the same diamond a size down: it belongs to the conversation, but
  // it is said in passing rather than being an episode's boundary.
  if (kind === "note") {
    return (
      <span className="text-[7px] leading-none" style={{ color }}>
        ◆
      </span>
    );
  }
  if (kind === "prompt" || kind === "reply") {
    return (
      <span className="text-[10px] leading-none" style={{ color }}>
        {kind === "prompt" ? "◆" : "◇"}
      </span>
    );
  }
  return (
    <span
      className="h-[8px] w-[8px] rounded-[1px] border"
      style={{
        borderColor: color,
        borderStyle: kind === "verified" ? "dashed" : "solid",
        background: kind === "read" || kind === "verified" || kind === "readCall" ? "transparent" : color,
      }}
    />
  );
}

// Who is speaking in a conversation-lane row. "Agent" is reserved for the
// episode's conclusion; prose said mid-work is labelled by what it is, so a
// reader never mistakes a running commentary for the answer.
function proseLabelOf(item) {
  if (item?.domain === "prompt") return item.op?.kind === "notice" ? "Notice" : "User";
  if (item?.domain === "narration") return item.op?.kind === "thinking" ? "Thinking" : "Said";
  if (item?.domain === "harness") return "System";
  return "Agent";
}

function LegendMark({ kind, children }) {
  return (
    <span className="flex items-center gap-[5px] text-[10px] leading-none text-[color:var(--muted)]">
      <Mark kind={kind} />
      {children}
    </span>
  );
}

// The axis's account and the mark vocabulary, shown where the marks are:
// hovering the ruler's head. The counts used to sit on the toolbar as a row of
// facts and the marks' meanings under them as a permanent legend; both are
// chrome once learned, and the one who has not learned them can ask the axis.
// The rail head itself keeps the two figures worth a glance (lanes, turns).
function AxisLegend({ facts = [], weights = false, tokens = null }) {
  return (
    <span className="grid gap-[7px]" data-lanes-legend="">
      {facts.length ? (
        <span className="grid gap-[4px]">
          <span className="font-medium">This axis</span>
          {facts.map((f) => (
            <span key={f.key} className="grid gap-[1px]" data-axis-fact={f.key}>
              <span
                data-response-status={f.status || undefined}
                className={f.caveat ? "text-[color:var(--warning,#b8860b)]" : undefined}
              >
                {f.text}
              </span>
              {f.note ? <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">{f.note}</span> : null}
            </span>
          ))}
        </span>
      ) : null}
      <span className="font-medium">Reading the marks</span>
      <span className="grid grid-cols-2 gap-x-[14px] gap-y-[4px]">
        <LegendMark kind="read">consulted</LegendMark>
        <LegendMark kind="prompt">prompt</LegendMark>
        <LegendMark kind="write">changed</LegendMark>
        <LegendMark kind="reply">reply</LegendMark>
        <LegendMark kind="error">errored</LegendMark>
        <LegendMark kind="note">commentary</LegendMark>
        <LegendMark kind="verified">model reading</LegendMark>
        <LegendMark kind="readCall">call read by model</LegendMark>
      </span>
      <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">
        A number is several calls folded at this zoom; a bar trailing a mark is the stretch it stands for. Hover a
        mark for the call, click it to read the turn. Commentary is what the agent said while working, on the turn it
        said it.
      </span>
      <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">
        The sparkline behind each name is that row&apos;s activity over the whole trace, whatever the view shows of it,
        on one axis for every row. Hover the row for its counts.
      </span>
      {tokens ? <TokensLegend detail={tokens.detail} requests={tokens.requests} /> : null}
      {weights ? (
        <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">
          <span className="mr-[5px] inline-block h-[7px] w-[12px] rounded-[1px] align-middle" style={{ background: WEIGHT_FILL }} />
          token weight: the tint behind each row, as its share of this conversation&apos;s tokens. Hover a row for its
          figure.
        </span>
      ) : null}
    </span>
  );
}

// What the axis holds and how much of it was captured, as one line each.
const RESPONSE_TEXT = {
  captured: "response captured",
  missing: "response observed, not stored",
  none: "response not captured",
  unavailable: "responses n/a",
};
const RESPONSE_NOTE = {
  captured: "The response to this request was observed on the endpoint and is shown as the last turn.",
  missing: "A response was observed for this request, but its content has not landed in the store yet.",
  none: "No response was observed for this request. Anything the agent did after this request is not on this axis.",
  unavailable: "This deployment cannot read captured responses, so whether one exists is unknown.",
};

// What the token skyline on the axis band says: the bars, the line, and the
// request marks it carries when the axis is a session's. Part of the axis
// head's hover.
function TokensLegend({ detail, requests, weights = false }) {
  return (
    <span className="grid gap-[4px]" data-lanes-tokens-legend="">
      <span className="font-medium">Tokens{detail ? ` \u00b7 ${detail}` : ""}</span>
      <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">
        Bars are the tokens each turn put into the context; the line is the running total, and where it climbs
        steeply is where the trace got expensive fast.
      </span>
      {requests ? (
        <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">
          {requests}: one mark per captured request on the axis band, at the last message it shipped. Click a
          mark to open the request.
        </span>
      ) : null}
      {weights ? (
        <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">
          Token weight tints the ground of each row&apos;s sparkline in the rail, as its share of this conversation&apos;s tokens. Hover a row for its figure.
        </span>
      ) : null}
    </span>
  );
}

// The toolbar's separator between facts.
function Dot() {
  return <span className="text-[color:var(--line-strong)]">{"\u00b7"}</span>;
}

// One basis strip row, same vocabulary as the feed's PayloadFrame (which stays
// private to TraceWorkFeed): what kind of knowledge the dock is showing.
function StripRow({ label, detail, tone = null }) {
  return (
    <div
      className={`flex min-w-0 items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[4px] ${EYEBROW_BASE} ${
        tone === "warn" ? "text-[color:var(--red,#c0392b)]" : "text-[color:var(--eyebrow)]"
      }`}
    >
      <span className="shrink-0 whitespace-nowrap">{label}</span>
      {detail ? <span className={`${META_CLASS} min-w-0 flex-1 normal-case tracking-normal text-[color:var(--muted)]`}>{detail}</span> : null}
    </div>
  );
}

// LABEL │ value — the raw-turn dock's row, mirroring the feed's KvRow. Where
// the value came from (its turn, how it came back) heads the value rather
// than taking a column beside it, so the value keeps the row.
function LabeledRow({ label, text, failed = false, meta = null }) {
  return (
    <div className="flex min-w-0 items-baseline gap-[9px] px-[9px] py-[2px]">
      <span className={`${DOCK_TEXT} w-[70px] shrink-0 truncate text-[color:var(--muted)]`}>{label}</span>
      <span className="min-w-0 flex-1">
        {meta ? <span className={`${DOCK_TEXT} block text-[color:var(--muted-soft)]`} data-row-from="">{meta}</span> : null}
        <span className={`${DOCK_TEXT} block whitespace-pre-wrap break-words ${failed ? "text-[color:var(--red,#c0392b)]" : "text-[color:var(--ink)]"}`}>
          {text || "—"}
        </span>
      </span>
    </div>
  );
}

// The Raw tab's body: the turn as the record holds it (rawTurn), its message
// id and then each part, whole, with the turn holding a call's result or a
// result's call a click away (`onNavTurn`, which stays on this tab).
function RawTurnBody({ raw, onNavTurn }) {
  if (!raw) return <span className={`${DOCK_TEXT} block px-[9px] py-[4px] text-[color:var(--muted)]`}>out of range</span>;
  if (raw.oversized) {
    return (
      <span className={`${DOCK_TEXT} block px-[9px] py-[4px] text-[color:var(--muted)]`}>
        This message is larger than the record serves in one read, so its parts are not here to show.
      </span>
    );
  }
  return (
    <>
      {raw.messageId ? (
        <div data-raw-message="" className={`${DOCK_TEXT} border-b border-[color:var(--line-soft)] px-[9px] pb-[4px] pt-[2px] text-[color:var(--muted)]`}>
          message <span className="text-[color:var(--ink)]">{raw.messageId}</span>
        </div>
      ) : null}
      {raw.parts.length ? (
        raw.parts.map((part, i) => <RawPart key={i} n={i + 1} part={part} onNavTurn={onNavTurn} />)
      ) : (
        <span className={`${DOCK_TEXT} block px-[9px] py-[4px] text-[color:var(--muted)]`}>this turn carried no parts</span>
      )}
    </>
  );
}

const RAW_TEXT = `whitespace-pre-wrap break-words ${DOCK_TEXT}`;

function RawPart({ n, part, onNavTurn }) {
  const tb = useContext(TurnBaseContext);
  const tone = part.failed ? "text-[color:var(--red,#c0392b)]" : "text-[color:var(--ink)]";
  const words = (text) => text || <span className="text-[color:var(--muted)]">empty</span>;
  return (
    <div data-raw-part={part.type} className="min-w-0 border-b border-[color:var(--line-soft)] pb-[4px] pt-[2px] last:border-b-0">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-[8px] px-[9px] py-[2px]">
        <span className={`${DOCK_TEXT} shrink-0 text-[color:var(--muted)]`}>Part {n}</span>
        <span className={`${DOCK_TEXT} min-w-0 break-words text-[color:var(--muted)]`}>{[part.type, ...part.facts].join(" \u00b7 ")}</span>
        {part.pair ? (
          <button
            type="button"
            data-raw-pair={part.pair.turn}
            onClick={() => onNavTurn(part.pair.turn)}
            className={`${DOCK_TEXT} shrink-0 cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]`}
          >
            {part.pair.label} at T{tb + part.pair.turn}
          </button>
        ) : null}
      </div>
      {part.body != null ? <div className={`${RAW_TEXT} ${tone} px-[9px]`}>{words(part.body)}</div> : null}
      {part.items.map((item, j) => (
        <div key={j} className="min-w-0 px-[9px] pt-[3px]">
          <span className={`${DOCK_TEXT} block text-[color:var(--muted-soft)]`}>{item.label}</span>
          <div className={`${RAW_TEXT} ${tone}`}>{words(item.body)}</div>
        </div>
      ))}
    </div>
  );
}

// Where a call's result row came from: the turn it arrived in, whether a later
// wait poll returned it, and the cell host's own facts about it.
function resultMeta(b, base = 0) {
  if (b.missing) return "no result in the trace";
  return [`T${base + b.turnIndex}`, b.via, b.note].filter(Boolean).join(" \u00b7 ");
}

// How many ops the dock's lineage shows before it asks. A file read three times
// fits; one carrying 71 edits does not, and the newest are the ones that explain
// the state on screen.
const DOCK_LINEAGE_CAP = 12;
// How many other lanes the "Same call" row names before it counts the rest: a
// sweep over a whole directory is one call with dozens of subjects.
const SAME_CALL_CAP = 8;

// The ops behind a file reading, oldest to newest, each landing on its own turn.
//
// The hover card has shown this all along and the dock never did, which left the
// panel with nothing at all to read for a file whose content was never captured:
// the basis strip said why the body was empty and then stopped. It is also the
// only place the per-view sizes appear, and for a generated image those sizes are
// the sole evidence that the file was re-rendered between two views — there is no
// content hash to compare, so four views at four different sizes is the finding.
// How far either side of this trace to look for another session touching the
// same file. Measured on staging: this window costs ~0.45s scoped to one
// endpoint and ~1.3s across the tenant, while a 7-day window costs 7.4s and
// 18.2s. TIME is the expensive axis, so widening is offered on ENDPOINT scope
// only and never on the window.
const COUNTERPARTY_WINDOW_HOURS = 6;

function counterpartyWindow(traceTimestamp) {
  // The app passes `trace.timestamp`, which is epoch MILLISECONDS, not a string.
  // `Date.parse` on a number yields NaN, which silently rendered this whole
  // section as nothing — accept both forms rather than trust one.
  const ms = typeof traceTimestamp === "number"
    ? traceTimestamp
    : Date.parse(traceTimestamp ?? "");
  if (!Number.isFinite(ms)) return null;
  const pad = COUNTERPARTY_WINDOW_HOURS * 3600 * 1000;
  // Full RFC3339, the same shape `toApiRange` emits. The gateway parses these
  // into `google.protobuf.Timestamp` and rejects a space-separated
  // "YYYY-MM-DD HH:MM:SS" outright, even though ClickHouse itself accepts it.
  const iso = (t) => new Date(t).toISOString();
  return { startTime: iso(ms - pad), endTime: iso(ms + pad) };
}

// The joins lens's probe window: the SESSION's own span padded by the
// counterparty hours, not a point. The +-6h point window was measured for
// the per-file elsewhere panel; reused for the whole-trace lens it made a
// two-day session's early turns structurally invisible — the actual spawned
// sub-agent sat outside the window while the monitor echoing its scaffolding
// sat inside it and wore the pin (found live). Falls back to the point
// window when no session rows have arrived to span.
// TIME is this query class's expensive axis (see the measured note above),
// so the span is capped: a session longer than the ceiling keeps its NEWEST
// stretch. That trades away the earliest counterparts of a week-long session
// (p99 staging session is 5.4 days; p90 is 8 hours) for a bounded worst
// case — the uncapped alternative made one lens open cost whatever the
// session's age said it should.
const JOIN_WINDOW_MAX_MS = 4 * 86400 * 1000;

export function traceJoinWindow(sessionSpan, traceTimestamp) {
  if (sessionSpan?.from != null && sessionSpan?.to != null && sessionSpan.to >= sessionSpan.from) {
    const pad = COUNTERPARTY_WINDOW_HOURS * 3600 * 1000;
    const end = sessionSpan.to + pad;
    const start = Math.max(sessionSpan.from - pad, end - JOIN_WINDOW_MAX_MS);
    const iso = (t) => new Date(t).toISOString();
    return { startTime: iso(start), endTime: iso(end) };
  }
  return counterpartyWindow(traceTimestamp);
}

export function counterpartyLabel(row) {
  // `writes: 0` does NOT mean "did not author": an Anthropic agent writes
  // through its own tool and spawns no process, so its authorship leaves no
  // FileState write at all. Say what was observed, never infer authorship from
  // its absence.
  //
  // Each clause names its substrate. Three channels can now vouch for a row,
  // and "read 12× · named the file in 4 turns · 7 content mentions" is a
  // different strength of finding than any one clause alone — collapsing them
  // into one number would hide exactly the corroboration this search exists
  // to surface.
  const acts = [];
  if (row.writes) acts.push(`wrote ${row.writes}×`);
  if (row.reads) acts.push(`read ${row.reads}×`);
  if (row.commands) acts.push(`${row.commands} command${row.commands === 1 ? "" : "s"}`);
  const calls = row.channels?.calls;
  if (calls?.count) acts.push(`named the file in ${calls.count} turn${calls.count === 1 ? "" : "s"}`);
  const content = row.channels?.content;
  if (content?.total) {
    acts.push(`${content.total} content mention${content.total === 1 ? "" : "s"}${
      content.responses ? ` (${content.responses} in responses)` : ""}`);
  }
  if (row.probes?.of) {
    // The response-side count is the direction of the exchange: a probe hit in
    // this agent's responses means it PRODUCED the content, not just held it.
    acts.push(`matches ${row.probes.matched} of ${row.probes.of} content probes${
      row.probes.authored ? ` (${row.probes.authored} in responses)` : ""}`);
  }
  // "Unspecified" is the deprecated enum's ZERO VALUE, not a name: it survives
  // a truthiness filter, and rendering it named every Anthropic-path session.
  const enumType = row.agent_type && row.agent_type !== "Unspecified" ? row.agent_type : null;
  const identity = enumType || row.provider
    ? deriveAgentIdentity(enumType, "", "", row.provider || "")
    : null;
  const agentName = identity && identity.key !== "unknown" ? identity.name : (row.provider || null);
  const who = [agentName, row.model].filter(Boolean).join(" · ")
    || `session ${String(row.session_id).slice(0, 8)}`;
  return { who, what: acts.join(" · ") || "touched this file" };
}

function traceTabHref(traceId) {
  if (!traceId) return null;
  const p = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
  p.set("tab", `trace:${traceId}`);
  return `?${p.toString()}`;
}

// A sample's time shows its DATE when it differs from the focal trace's day.
// The content channel's padded window legitimately returns conversations
// weeks old (search rows are stamped with conversation ORIGIN time), and a
// bare clock read as "today" turned a 13-day-old mention into a fresh one.
function sampleStamp(at, refMs) {
  const s = String(at ?? "");
  const clock = s.slice(11, 19);
  if (!clock) return "—";
  const day = s.slice(0, 10);
  const refDay = Number.isFinite(refMs) ? new Date(refMs).toISOString().slice(0, 10) : null;
  return refDay && day && day !== refDay ? `${day.slice(5)} ${clock.slice(0, 5)}` : clock;
}

// One counterparty. The counts are a summary, not the reading — opening the row
// shows the events they came from, and the label opens that agent's own trace
// when the session resolved to a conversation.
function CounterpartyRow({ row, open, onToggle, onOpenTrace = null, refMs = null }) {
  const { who, what } = counterpartyLabel(row);
  const events = row.events || [];
  // Trace tabs are URL-backed, so a link is the whole navigation: no callback
  // has to be threaded down from the page for this to work.
  const href = traceTabHref(row.trace_id);
  return (
    <span data-file-elsewhere-row="" className="mt-[2px] grid min-w-0">
      <span className="flex min-w-0 items-baseline gap-[6px]">
        {href ? (
          // A real href so the affordance reads as a link and modifier-click /
          // open-in-new-tab still work, but a plain click is intercepted and
          // handed to the app's own tab opener: a full navigation would reload
          // the SPA and throw away the trace the reader is standing in.
          <a
            href={href}
            data-file-elsewhere-open=""
            onClick={(e) => {
              if (!onOpenTrace || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
              e.preventDefault();
              onOpenTrace(row.trace_id);
            }}
            className="min-w-0 truncate text-[10px] leading-[1.45] text-[color:var(--ink)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--accent,var(--ink))]"
          >
            {who}
          </a>
        ) : (
          <span className="min-w-0 truncate text-[10px] leading-[1.45] text-[color:var(--ink)]">{who}</span>
        )}
        <button
          type="button"
          data-file-elsewhere-expand=""
          onClick={onToggle}
          aria-expanded={open}
          className={`${META_CLASS} shrink-0 cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]`}
        >
          {what}
        </button>
      </span>
      {open ? (
        <span className="mt-[1px] grid min-w-0 gap-[1px] border-l border-[color:var(--line-soft)] pl-[7px]">
          {events.length ? events.map((e, i) => (
            <span key={i} data-file-elsewhere-event="" className="flex min-w-0 items-baseline gap-[6px]">
              <span className={`${META_CLASS} w-[62px] shrink-0 tabular-nums text-[color:var(--muted-soft)]`}>
                {sampleStamp(e.at, refMs)}
              </span>
              <span className={`${META_CLASS} w-[52px] shrink-0 text-[color:var(--muted)]`}>{e.kind}</span>
              <span className="min-w-0 flex-1 truncate font-mono text-[10px] leading-[1.45] text-[color:var(--muted)]">
                {e.detail || "—"}
              </span>
            </span>
          )) : null}
          {(row.channels?.calls?.turns || []).map((t, i) => (
            // Every prompt-side hit is its own openable turn — this is the
            // evidence the OS channel has to reconstruct through markers, and
            // the calls channel gets directly.
            <span key={`c${i}`} data-file-elsewhere-turn="" className="flex min-w-0 items-baseline gap-[6px]">
              <span className={`${META_CLASS} w-[62px] shrink-0 tabular-nums text-[color:var(--muted-soft)]`}>
                {sampleStamp(t.at, refMs)}
              </span>
              <span className={`${META_CLASS} w-[52px] shrink-0 text-[color:var(--muted)]`}>turn</span>
              <a
                href={traceTabHref(t.trace_id)}
                data-file-elsewhere-turnlink=""
                onClick={(e) => {
                  if (!onOpenTrace || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                  e.preventDefault();
                  onOpenTrace(t.trace_id);
                }}
                className={`${META_CLASS} min-w-0 flex-1 truncate text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]`}
              >
                named this file in a tool call
              </a>
            </span>
          ))}
          {(row.channels?.content?.samples || []).map((s, i) => (
            <span key={`s${i}`} data-file-elsewhere-content="" className="flex min-w-0 items-baseline gap-[6px]">
              <span className={`${META_CLASS} w-[62px] shrink-0 tabular-nums text-[color:var(--muted-soft)]`}>
                {sampleStamp(s.at, refMs)}
              </span>
              <span className={`${META_CLASS} w-[52px] shrink-0 text-[color:var(--muted)]`}>
                {s.role === "assistant" ? "response" : (s.kind || s.role || "content")}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-[10px] leading-[1.45] text-[color:var(--muted)]">
                {s.snippet || "—"}
              </span>
            </span>
          ))}
          {(row.probes?.samples || []).map((s, i) => (
            <span key={`p${i}`} data-file-elsewhere-probe-hit="" className="flex min-w-0 items-baseline gap-[6px]">
              <span className={`${META_CLASS} w-[62px] shrink-0 tabular-nums text-[color:var(--muted-soft)]`}>
                {sampleStamp(s.at, refMs)}
              </span>
              {/* A hit in the counterpart's own response is the authored side
                  of the exchange; every other hit is content it received. */}
              <span className={`${META_CLASS} w-[52px] shrink-0 text-[color:var(--muted)]`}>
                {s.role === "assistant" ? "response" : "overlap"}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-[10px] leading-[1.45] text-[color:var(--muted)]">
                {s.snippet || "—"}
              </span>
            </span>
          ))}
          {!events.length && !(row.channels?.calls?.turns || []).length
            && !(row.channels?.content?.samples || []).length
            && !(row.probes?.samples || []).length ? (
            <span className={`${META_CLASS} text-[color:var(--muted-soft)]`}>No event detail retained for this row.</span>
          ) : null}
          {row.host_session_id ? (
            // The file events are stamped with the HOST process, not the
            // conversation. Say so, or the resolved name looks like the thing
            // that was measured.
            <span className={`${META_CLASS} mt-[1px] text-[color:var(--muted-soft)]`}>
              File events are recorded against this agent&rsquo;s shared process
              ({String(row.host_session_id).slice(0, 8)}); the conversation was matched by turn timing.
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

// ── Content joins: the whole-trace needle lens ───────────────────────────────
//
// Runs once per trace when the lanes open: id-shaped needles from EVERY loaded
// turn — no focal turn, no file pivot — then one bounded index search per
// needle, tenant-wide. The result is every session carrying this trace's
// distinctive content, with direction where the sides prove it. Cached per
// trace so reopening the lanes never re-spends the fan-out.
const LENS_CACHE = new Map();
const LENS_CACHE_MAX = 20;

// Test seam: the cache is module-level (a re-opened trace must not re-spend
// the fan-out), which means suites sharing fixture ids would otherwise bleed
// results into each other.
export function clearTraceContentJoinsCache() {
  LENS_CACHE.clear();
}

// Harness seam (the hand-off strip's writeHandoffCache pattern): a fixture
// result stored under the exact key the hook derives renders the whole joins
// surface through its real cache-hit path, no backend. Rows keep whatever
// shape searchTraceHandoffCandidates returns; the caller owns realism.
export function seedTraceContentJoinsCache(cacheKey, rows, { of = rows.length, diffuse = 0, via = null, needles = null } = {}) {
  LENS_CACHE.set(cacheKey, { status: "done", rows, of, diffuse, via, needles, error: null });
}

// One shared batch per trace, whoever asks: the lanes view renders in mount
// PAIRS, and two instances firing the fan-out independently would double
// every open. Runs are refcounted — the batch aborts only when the LAST
// reader leaves, so a twin unmounting mid-flight cannot cancel the visible
// mount's answer. An entry whose signal is already aborted is never reused.
const LENS_INFLIGHT = new Map();

function acquireLensRun(cacheKey, start) {
  let run = LENS_INFLIGHT.get(cacheKey);
  if (!run || run.ctl.signal.aborted) {
    const ctl = new AbortController();
    const entry = { refs: 0, ctl, promise: null };
    entry.promise = start(ctl.signal).finally(() => {
      if (LENS_INFLIGHT.get(cacheKey) === entry) LENS_INFLIGHT.delete(cacheKey);
    });
    LENS_INFLIGHT.set(cacheKey, entry);
    run = entry;
  }
  run.refs += 1;
  return run;
}

// Per-block needle sources with LANE hints, so a needle inside a file write
// pins to that file's lane rather than just its turn. Lane spelling comes
// from the RENDERED MODEL when the caller has one: every lane item addresses
// its source block by (turnIndex, blockIndex), so mapping tool_use ids
// through the model guarantees a pin's lane id matches a row the lanes
// actually drew — including extractor-claimed tools (research, query) and
// the residual Bash/Grep lanes, which a hand-built id could only misspell.
// Without a model, file_path calls keep the files:<path> convention the
// lanes share (the spelling the tool used — normalizing would orphan every
// pin on a Windows trace).
//
// A tool RESULT belongs to the lane of the call that produced it: the read
// of a mailbox file is that file's content coming back, and crediting it to
// the conversation lane drowned the conversation rows in pins while the
// artifacts that actually carried the content showed none (found live). The
// result also pins to the CALL's turn (cellTurn): the lane charts a cell at
// the call, the result text is fed one turn later, and a pin at the feed
// turn failed the has-a-cell filter — so the joins strip flagged turns the
// lanes below could not corroborate (found live).
// A tool block's rendered text, once per tool call. The findings locator
// re-reads every block whenever the turns change, and a live session appends
// turns while the earlier calls stay put, so each call's rendering is paid
// once for the life of the page. A block is keyed by its call id when its
// content is fixed for that id: every real call (its history is re-sent
// verbatim), and a code-mode cell's virtual CALLS, whose inputs are carved
// from the cell's own code and so survive the fresh objects every unwrap
// fabricates. A virtual RESULT is the exception: a cell still running when
// first seen gets its finished output under the same id on a later poll, so
// it keys by object identity (today its content is a plain string and never
// reaches this cache at all; the rule stands for when it is not). Blocks
// without an id take the identity key too. A block whose text cannot be
// rendered contributes nothing and keeps its JSON form.
export const RAW_TEXT_ID_CAP = 8192;
const rawTextById = new Map();
const rawBlockText = new WeakMap();
export function _resetRawTextCacheForTests() {
  rawTextById.clear();
}
function rawTextOf(block, kind, render) {
  if (!block || typeof block !== "object") return "";
  const id = kind === "use" ? block.id : block.tool_use_id;
  const fixedForId = kind === "use" || !block.virtual;
  const key = fixedForId && typeof id === "string" && id ? `${kind}:${id}` : null;
  if (key) {
    if (rawTextById.has(key)) {
      const hit = rawTextById.get(key);
      rawTextById.delete(key);
      rawTextById.set(key, hit);
      return hit;
    }
  } else if (rawBlockText.has(block)) {
    return rawBlockText.get(block);
  }
  let text = "";
  try { text = render(block) || ""; } catch { text = ""; }
  if (key) {
    if (rawTextById.size >= RAW_TEXT_ID_CAP) rawTextById.delete(rawTextById.keys().next().value);
    rawTextById.set(key, text);
  } else {
    rawBlockText.set(block, text);
  }
  return text;
}

// `raw` appends each tool block's rendered text -- the call's field values
// and the result's text parts as written -- beside the JSON the census keys
// on. JSON escapes quotes and doubles backslashes, so a verbatim quote of a
// shell command or a Windows path never matches it; a reader locating quoted
// evidence needs the text as the agent saw it. The census leaves this off: its
// needles are derived from these strings and must not change shape.
export function turnsToJoinInputs(turns, lanesModel = null, { raw = false } = {}) {
  const laneByToolUse = new Map();
  // Blocks an agents lane addresses directly — the spawn call AND every
  // merge mark (the sync result or the in-band notification carrying the
  // thread's report). The report is the one content a spawned thread is
  // GUARANTEED to share verbatim with this session, so its needles must pin
  // to the thread's lane and compete for slots there; left on the
  // conversation lane they were starved by the needle cap, and the thread
  // stayed invisible to the census (found live — the child's task path only
  // ever appears in harness framing, which the search index rightly
  // excludes; its own words are the content-pure join).
  const laneByBlock = new Map();
  // A result whose call is not in the record is drawn on its own lane (an
  // unknown tool's), and a message sent as system on the system lane: what
  // they say pins where they are drawn, not on the conversation's row for
  // whoever sent them.
  const systemTurns = new Set();
  for (const g of lanesModel?.groups || []) {
    for (const lane of g?.lanes || []) {
      if (lane?.domain === "harness") for (const t of lane.turns || []) systemTurns.add(t);
      if (lane?.domain === "output") {
        for (const it of lane.items || []) {
          for (const bi of it?.op?.blocks || []) laneByBlock.set(`${it.turnIndex}:${bi}`, { lane: lane.id, turn: it.turnIndex });
        }
      }
    }
  }
  for (const g of lanesModel?.groups || []) {
    for (const lane of g?.lanes || []) {
      const isAgents = String(lane?.id || "").startsWith("agents:");
      for (const it of lane?.items || []) {
        if (it?.turnIndex == null || it?.blockIndex == null) continue;
        const b = turns?.[it.turnIndex]?.blocks?.[it.blockIndex];
        if (b?.type === "tool_use" && b.id && !laneByToolUse.has(b.id)) {
          laneByToolUse.set(b.id, { lane: lane.id, turn: it.turnIndex });
        }
        if (isAgents) {
          const key = `${it.turnIndex}:${it.blockIndex}`;
          if (!laneByBlock.has(key)) laneByBlock.set(key, { lane: lane.id, turn: it.turnIndex });
        }
      }
    }
  }
  (turns || []).forEach((t, i) => {
    for (const b of t?.blocks || []) {
      if (b?.type === "tool_use" && b.id && !laneByToolUse.has(b.id)
        && typeof b?.input?.file_path === "string") {
        laneByToolUse.set(b.id, { lane: `files:${b.input.file_path}`, turn: i });
      }
    }
  });
  return (turns || []).map((t, ti) => {
    const convLane = t?.role === "assistant" ? "reply:all" : systemTurns.has(ti) ? "harness:all" : "prompt:all";
    const segs = [];
    (t?.blocks || []).forEach((b, bi) => {
      const merge = laneByBlock.get(`${ti}:${bi}`) || null;
      if (b?.type === "text" && b.text) segs.push(merge ? { lane: merge.lane, text: b.text, cellTurn: merge.turn } : { lane: convLane, text: b.text });
      else if (b?.type === "thinking" && b.thinking) segs.push({ lane: convLane, text: b.thinking });
      else if (b?.type === "tool_use") {
        let text = "";
        try { text = JSON.stringify(b.input ?? ""); } catch { text = ""; }
        if (raw && b.input != null) {
          const rendered = rawTextOf(b, "use", (block) => toolUseText(block.input));
          if (rendered) text += `\n${rendered}`;
        }
        const home = (b.id && laneByToolUse.get(b.id)) || null;
        segs.push(home ? { lane: home.lane, text, cellTurn: home.turn } : { lane: convLane, text });
      } else if (b?.type === "tool_result") {
        let text = "";
        try { text = typeof b.content === "string" ? b.content : JSON.stringify(b.content ?? ""); } catch { text = ""; }
        if (raw && b.content != null && typeof b.content !== "string") {
          const rendered = rawTextOf(b, "result", (block) => toolResultText(block.content));
          if (rendered) text += `\n${rendered}`;
        }
        const home = merge || (b.tool_use_id && laneByToolUse.get(b.tool_use_id)) || null;
        segs.push(home ? { lane: home.lane, text, cellTurn: home.turn } : { lane: convLane, text });
      }
    });
    return { role: t?.role, messageId: t?.messageId ?? null, segments: segs };
  });
}

// The hand-off text of every sub-agent lane - its spawn and its merges, as
// the join inputs pin them - in the index's gram form, capped for one ask.
// Sealed task tokens lead: one per hand-off, and a Codex spawn's only text
// its child is sure to carry. The collectors lane carries no single
// thread's words.
export function handoffStringsOf(joinInputs) {
  const texts = [];
  for (const t of joinInputs || []) {
    for (const seg of t?.segments || []) {
      const lane = String(seg?.lane || "");
      if (lane.startsWith("agents:") && lane !== "agents:collectors") texts.push(seg.text);
    }
  }
  const out = new Set(texts.flatMap((text) => sealedTaskRuns(text)));
  for (const text of texts) {
    for (const gram of handoffGrams(text)) {
      if (out.size >= HANDOFF_STRING_CAP) return [...out].slice(0, HANDOFF_STRING_CAP);
      out.add(gram);
    }
  }
  return [...out].slice(0, HANDOFF_STRING_CAP);
}

// The lens state, owned by the lanes view itself: the summary section and
// the axis annotations read ONE fetch, so a candidate list and its marks can
// never disagree. `enabled: false` (spliced insets) keeps the hook inert.
//
// The fetch is a DIRECT apiClient call (fetchTraceResponse's pattern), not a
// dataClient query: query() de-duplicates in-flight calls by serialized
// params, an AbortSignal serializes to nothing, and a shared execution bound
// to a predecessor's signal rendered that predecessor's cancellation as this
// mount's answer — found live.
// The first turn a person spoke in, before which a trace's setup sits.
function firstUserTurnOf(turns) {
  const i = (turns || []).findIndex((t) => t?.role === "user");
  return i >= 0 ? i : 1;
}

// Context substrate: a string whose every located pin sits before the first
// user turn lives in the setup every sibling session shares (the system
// block, memory files, harness banners). Real sharing, but substrate rather
// than exchange -- so it leaves the axis (no pins, no giant head span) and
// the sessions it alone accounts for sink to the bottom of the inventory.
// Counts stay whole; nothing is gated.
function joinContextOf(contentJoins, firstUserTurn) {
  if (contentJoins.status !== "done") {
    return { needles: contentJoins.needles, contextStrings: 0, ctxIdx: null };
  }
  const ctxIdx = new Set();
  const needles = contentJoins.needles.map((n, i) => {
    const located = (n?.turns || []).length > 0;
    if (!located || !n.turns.every((t) => t < firstUserTurn)) return n;
    ctxIdx.add(i);
    return { ...n, turns: [], cells: [], contextOnly: true };
  });
  return { needles, contextStrings: ctxIdx.size, ctxIdx };
}

function joinRowsOf(contentJoins, joinContext) {
  const { ctxIdx } = joinContext;
  if (contentJoins.status !== "done" || !ctxIdx?.size) return contentJoins.rows;
  const rows = contentJoins.rows.map((r) => (
    (r.matchedNeedles || []).length && r.matchedNeedles.every((i) => ctxIdx.has(i))
      ? { ...r, contextSubstrate: true }
      : r
  ));
  return [...rows.filter((r) => !r.contextSubstrate), ...rows.filter((r) => r.contextSubstrate)];
}

// The dock's hand-off index: every turn that carries join evidence, from
// BOTH readings of the same pins -- the strip's occurrence turns and the
// cells' call turns -- so clicking either lands on a turn the dock can answer
// for. Entries dedupe by counterpart; count stays distinct sessions.
function joinsByTurnOf(status, joinMarks, joinCellMarks) {
  if (status !== "done") return null;
  const map = new Map();
  const fold = (mark) => {
    let slot = map.get(mark.turn);
    if (!slot) {
      slot = { sessions: new Set(), entries: [] };
      map.set(mark.turn, slot);
    }
    for (const s of mark.sessions || []) slot.sessions.add(s);
    for (const e of mark.entries || []) {
      if (slot.entries.length >= 8) break;
      if (!slot.entries.some((x) => x.session_id === e.session_id)) slot.entries.push(e);
    }
  };
  for (const m of joinMarks) fold(m);
  for (const m of joinCellMarks) fold(m);
  return map.size ? map : null;
}

function useTraceContentJoins({ trace, turns, traceTimestamp, model = null, searchWindow = null, focalSpan = null, enabled = true }) {
  const sessionId = trace?.sessionId ?? null;
  const focalEndpointId = trace?.endpointId ?? null;
  const focalSpanRef = useRef(focalSpan);
  focalSpanRef.current = focalSpan;
  const window_ = useMemo(
    () => searchWindow || counterpartyWindow(traceTimestamp),
    [searchWindow, traceTimestamp],
  );
  // The window is part of the cache identity: the session span lands after
  // the first fan-out on a cold open, and a widened window must re-ask
  // rather than serve the narrow answer forever.
  const cacheKey = (trace?.id || sessionId)
    ? `${trace?.id || sessionId}:${window_?.startTime || ""}-${window_?.endTime || ""}`
    : null;
  const joinInputs = useMemo(
    () => (enabled ? turnsToJoinInputs(turns, model) : []),
    [turns, model, enabled],
  );
  const needles = useMemo(() => deriveTraceNeedles(joinInputs), [joinInputs]);
  // The file lanes' own paths, for the index's deterministic lane-identity
  // strings — a file-mediated hand-off is matched by the file's name, so
  // its name must never be left to a surfacing lottery.
  const laneStrings = useMemo(() => {
    const lanes = new Set();
    for (const t of joinInputs) {
      for (const seg of t?.segments || []) {
        const lane = String(seg?.lane || "");
        if (lane.startsWith("files:")) lanes.add(lane.slice("files:".length));
      }
    }
    return [...lanes];
  }, [joinInputs]);
  const laneStringsRef = useRef(laneStrings);
  laneStringsRef.current = laneStrings;
  // Each sub-agent thread's hand-off - what its lane spawned with and was
  // answered with - as the index's own grams, fetched deterministically: the
  // spawned session is guaranteed to share that text, and it must not be
  // left to the surfaced-string cap.
  const handoffStrings = useMemo(() => handoffStringsOf(joinInputs), [joinInputs]);
  const handoffStringsRef = useRef(handoffStrings);
  handoffStringsRef.current = handoffStrings;
  const joinInputsRef = useRef(joinInputs);
  joinInputsRef.current = joinInputs;
  // Keyed by CONTENT: the turns array is rebuilt as async hydration lands
  // (the appended response, redeemed bodies), and every identity change was
  // aborting a 20-second batch to start an identical one.
  const needlesKey = useMemo(() => needles.map((n) => n.needle).join("\n"), [needles]);
  const needlesRef = useRef(needles);
  needlesRef.current = needles;
  const [state, setState] = useState({ status: "idle", rows: [], of: 0, diffuse: 0, error: null });
  useEffect(() => {
    if (!enabled || !cacheKey || !window_ || !needlesKey) return undefined;
    const cached = LENS_CACHE.get(cacheKey);
    if (cached) {
      setState(cached);
      return undefined;
    }
    let dead = false;
    setState({ status: "loading", rows: [], of: 0, diffuse: 0, error: null });
    const run = acquireLensRun(cacheKey, (signal) =>
      searchTraceHandoffCandidates({
        needles: needlesRef.current, sessionId, signal,
        laneStrings: laneStringsRef.current,
        handoffStrings: handoffStringsRef.current,
        focalSpan: focalSpanRef.current, focalEndpointId, ...window_,
      }));
    run.promise.then((res) => {
      if (dead) return;
      // Index-provided strings arrive without positions (the index knows
      // WHO shares them, not WHERE they sit in this transcript); the
      // locator stamps turn/cell pins by finding each string's verbatim
      // text in the loaded turns. Marks and the fold's matchedNeedles
      // indices then speak the same list.
      const locate = (list) => {
        const inputs = joinInputsRef.current;
        const missing = (list || []).filter((n) => n && !(n.turns || []).length);
        if (!missing.length || !inputs.length) return list;
        const found = new Map(
          locateNeedleStrings(inputs, missing.map((n) => n.needle)).map((l) => [l.needle, l]),
        );
        return list.map((n) => {
          const l = found.get(n?.needle);
          return l ? { ...n, turns: l.turns, cells: l.cells } : n;
        });
      };
      const next = res.ok
        ? {
          status: "done",
          rows: (res.rows || []).filter((r) => r.session_id !== sessionId),
          of: res.of || 0, diffuse: res.diffuse || 0, via: res.via || null,
          needles: res.needles ? locate(res.needles) : null, error: null,
        }
        : { status: "error", rows: [], of: 0, diffuse: 0, error: res.error || null };
      if (res.ok) {
        if (LENS_CACHE.size >= LENS_CACHE_MAX) {
          const oldest = LENS_CACHE.keys().next().value;
          if (oldest !== undefined) LENS_CACHE.delete(oldest);
        }
        LENS_CACHE.set(cacheKey, next);
      }
      setState(next);
    });
    // Abandoning the trace abandons the question — but only when the LAST
    // reader leaves: stop the unstarted probes instead of letting the batch
    // keep draining server-side.
    return () => {
      dead = true;
      run.refs -= 1;
      if (run.refs <= 0) run.ctl.abort();
    };
  }, [enabled, cacheKey, needlesKey, sessionId, focalEndpointId, window_]);
  // Marks and matchedNeedles indices must speak the same list: the
  // index-backed search may have augmented the derived needles with shared
  // strings it found beyond the sample.
  return { ...state, needles: state.needles || needles };
}

// The axis annotations for the content joins — the strip is the feature's
// whole surface now: rail summary for status, rail tooltip for the session
// inventory, counted bubbles pinned to annotated turns. Adjacent annotated
// turns fold into one bubble with an underline spanning the run at the
// current zoom (the hand-off traffic idiom), and a folded span counts
// DISTINCT sessions across its turns.
// What the joins strip says about itself, in every state it can be in. The
// canvas plane draws this string rather than deriving one from the marks: a
// pending search has no marks yet, and "nothing found" and "still looking" are
// not the same claim.
export function joinsSummaryLine(lens) {
  if (!lens) return "";
  // The index search is not needle-driven, so a needle count would claim a
  // unit the search no longer has; the honest pending line names the goal.
  if (lens.status === "loading") return "searching for shared content\u2026";
  if (lens.status === "error") return `content search failed${lens.error ? `: ${lens.error}` : ""}`;
  const observers = lens.rows.filter((r) => r.observer).length;
  if (!lens.rows.length) {
    return `no other conversation carries this trace's distinctive content${
      lens.diffuse ? ` \u00b7 ${lens.diffuse} string${lens.diffuse === 1 ? "" : "s"} too common to count` : ""}`;
  }
  // The strong tier: sessions whose evidence is more than one weak string or
  // shared setup. The summary claims both numbers so the big one is never
  // read as the meaningful one.
  const strong = lens.rows.filter((r) => !r.weak && !r.observer && !r.contextSubstrate).length;
  return `${lens.rows.length} session${lens.rows.length === 1 ? "" : "s"} share content${
    strong && strong < lens.rows.length - observers ? ` \u00b7 ${strong} strong` : ""}${
    observers ? ` \u00b7 ${observers} observer${observers === 1 ? "" : "s"}` : ""} \u00b7 ${
    lens.via === "fingerprints"
      ? `${lens.of} shared string${lens.of === 1 ? "" : "s"}`
      : `${lens.of} string${lens.of === 1 ? "" : "s"} checked`}${
    lens.diffuse ? ` \u00b7 ${lens.diffuse} too common to count` : ""}${
    lens.via === "fingerprints" ? " \u00b7 via content index" : ""}`;
}

// The joins strip's rail tooltip: the session inventory behind the summary
// line. The canvas draws the strip's header; this is the reading a hover on
// it resolves to \u2014 who shares content, which way it moved, and how much of
// the surfaced list each counterpart carries.
export function JoinsInventoryTooltip({ lens, hues = null }) {
  if (!lens) return null;
  return (
    <span className="grid gap-[4px]">
      <span className="font-medium">Content joins</span>
      <span className="text-[color:var(--muted)]">
        Sessions carrying this trace&apos;s distinctive strings, matched verbatim in captured
        conversations. Bubbles mark the turns and cells whose content some other session also
        holds; select one to read the turn.
      </span>
      {lens.rows.length ? (
        <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">{joinKindRollup(lens.rows)}</span>
      ) : null}
      {lens.rows.length ? (
        <span className="grid gap-[2px]">
          {lens.rows.slice(0, 8).map((r) => (
            <span key={r.session_id} className="text-[10px] leading-[1.4]">
              <span style={{ color: hues?.get(r.session_id) || JOIN_COLOR }}>{joinWho(r)}</span>
              <span className="text-[color:var(--muted-soft)]">
                {" \u00b7 "}{candidateDirectionLabel(r)} {" \u00b7 "} matches {r.matched} of {r.of}
                {r.contextSubstrate ? " \u00b7 shared setup only" : r.weak ? " \u00b7 weak overlap" : ""}
              </span>
            </span>
          ))}
          {lens.rows.length > 8 ? (
            <span className="text-[10px] text-[color:var(--muted-soft)]">+{lens.rows.length - 8} more</span>
          ) : null}
        </span>
      ) : null}
      {lens.contextStrings ? (
        <span className="text-[color:var(--muted-soft)]">
          {lens.contextStrings} string{lens.contextStrings === 1 ? " lives" : "s live"} only in this
          {" "}session&apos;s setup (the system block and context loading) - shared substrate, kept out of
          {" "}the axis pins. Sessions matched only there sort last.
        </span>
      ) : null}
      {lens.diffuse ? (
        <span className="text-[color:var(--muted-soft)]">
          {lens.diffuse} string{lens.diffuse === 1 ? "" : "s"} matched too many sessions to count -
          {" "}content that common is shared environment, not an exchange between two agents.
        </span>
      ) : null}
    </span>
  );
}

// The needle strings a hand-off card stands for -- the clicked pin's needle
// first, the row's sampled strings behind it -- as one canonical key, so the
// locate toggle and the "is this card's highlight active" check can never
// disagree on what a card means.
const joinFollowNeedleKey = (entry) => [
  ...new Set([entry?.needle, ...(entry?.row?.samples || []).map((s) => s?.needle)].filter(Boolean)),
].slice(0, 8).join("\n");

// What a review mark is kin to, in the hover: how often its conduct recurs
// and over which turns, a miniature of the axis with a tick per occurrence
// shaded by how closely each resembles the finding under the pointer, then the
// other occurrences themselves: each one's turn and the words it was cited
// for, so the reader sees the conduct repeat rather than trusting a count.
// Related conduct is a hollow tick and a marked row. The arcs on the plane
// reach the same turns; this is the same fact where the pointer already is.
const KIN_W = 220;
const KIN_ROWS = 8;
const KIN_QUOTE = 96;
function kinRows(kin) {
    const here = new Set(kin.here);
    const rows = [];
    // Each row is scored against the hovered finding of ITS OWN incident: two
    // habits can share a mark, and a row matched against the other habit's
    // finding would show the wrong quote with the wrong shade.
    const add = (member, related, incident) => {
        if (here.has(member)) return;
        const anchor = kin.here.find((h) => incident.members.includes(h)) || kin.here[0];
        const best = closestCitation(anchor, member);
        if (!best) return;
        const turn = best.turn ?? Math.min(...member.turns) - 1;
        if (turn >= kin.from && turn <= kin.through) return;
        rows.push({ id: member.id, turn, quote: best.quote, score: best.score, related });
    };
    for (const i of kin.incidents) if (i.count > 1) for (const m of i.members) add(m, false, i);
    for (const i of kin.related) for (const m of i.members) add(m, true, i);
    return rows.sort((a, b) => a.turn - b.turn);
}
// The hovered finding's own citation at the hovered turn: the words every
// other row was matched against, so the list opens with what "this" is.
function anchorRow(kin) {
    for (const f of kin.here) {
        const c = (f.citations || []).find((x) => x.verified !== "missing" && x.quote && x.locatedTurn != null && x.locatedTurn - 1 >= kin.from && x.locatedTurn - 1 <= kin.through)
            || (f.citations || []).find((x) => x.verified !== "missing" && x.quote);
        if (c) return { id: f.id, turn: c.locatedTurn != null ? c.locatedTurn - 1 : kin.from, quote: c.quote };
    }
    return null;
}
function IncidentKin({ kin, turnCount, base = 0 }) {
  const tb = base;
  const habits = kin.incidents.filter((i) => i.count > 1);
  const occurrences = habits.reduce((n, i) => n + i.count, 0);
  if (!occurrences && !kin.related.length) return null;
  const color = SEVERITY_COLOR[kin.tone] || "var(--muted)";
  const from = Math.min(...[...kin.incidents, ...kin.related].map((i) => i.from));
  const through = Math.max(...[...kin.incidents, ...kin.related].map((i) => i.through));
  const x = (turn) => (turnCount > 1 ? (turn / (turnCount - 1)) * (KIN_W - 2) + 1 : 1);
  const heat = (member) => Math.max(0, ...kin.here.map((h) => findingSimilarity(h, member)));
  const line = occurrences
    ? `${occurrences} occurrences \u00b7 T${tb + from}\u2013T${tb + through}${kin.related.length ? ` \u00b7 related conduct at ${kin.related.reduce((n, i) => n + i.count, 0)} more` : ""} \u00b7 arcs reach the others`
    : `recurs nowhere \u00b7 related conduct at ${kin.related.reduce((n, i) => n + i.count, 0)} turn${kin.related.reduce((n, i) => n + i.count, 0) === 1 ? "" : "s"}, dashed`;
  const rows = kinRows(kin);
  const shown = rows.slice(0, KIN_ROWS);
  const anchor = anchorRow(kin);
  const clip = (q) => (q.length > KIN_QUOTE ? `${q.slice(0, KIN_QUOTE - 1)}\u2026` : q);
  return (
    <span className="grid max-w-[480px] gap-[3px]" data-incident-kin="">
      <span className="text-[10px] text-[color:var(--muted)]">{line}</span>
      <svg width={KIN_W} height={10} viewBox={`0 0 ${KIN_W} 10`} aria-hidden="true" style={{ color, display: "block" }}>
        <line x1={0} x2={KIN_W} y1={9.5} y2={9.5} stroke="var(--line)" strokeWidth={1} />
        {kin.related.flatMap((i) => i.members.map((m) => m.turns.map((t) => t - 1)).flat()).map((t) => (
          <rect key={`r${t}`} x={x(t) - 1} y={2} width={2} height={7} fill="none" stroke="var(--muted)" strokeWidth={1} strokeDasharray="1.5 1" />
        ))}
        {habits.flatMap((i) => i.members).map((m) => m.turns.map((t) => t - 1).map((t) => (
          <rect key={`${m.id}:${t}`} x={x(t) - 1} y={t >= kin.from && t <= kin.through ? 0 : 2} width={t >= kin.from && t <= kin.through ? 3 : 2} height={t >= kin.from && t <= kin.through ? 9 : 7} fill="currentColor" opacity={t >= kin.from && t <= kin.through ? 1 : 0.3 + 0.7 * heat(m)} />
        )))}
      </svg>
      {shown.length ? (
        <span className="grid gap-[2px] pt-[2px]" data-incident-kin-rows="">
          {anchor ? (
            <span key="anchor" className="flex min-w-0 items-baseline gap-[6px] font-mono text-[10px] leading-[1.35]" data-kin-row="here">
              <span className="shrink-0 tabular-nums" style={{ color }}>T{tb + anchor.turn}</span>
              <span className="shrink-0 text-[color:var(--muted-soft)]">here</span>
              <span className="min-w-0 truncate text-[color:var(--ink)]">{clip(anchor.quote)}</span>
            </span>
          ) : null}
          {shown.map((r) => (
            <span key={r.id} className="flex min-w-0 items-baseline gap-[6px] font-mono text-[10px] leading-[1.35]" data-kin-row={r.related ? "related" : "own"}>
              <span className="shrink-0 tabular-nums" style={{ color, opacity: r.related ? 0.55 : 0.5 + 0.5 * r.score }}>T{tb + r.turn}</span>
              {r.related ? <span className="shrink-0 text-[color:var(--muted-soft)]">related</span> : null}
              <span className="min-w-0 truncate text-[color:var(--muted)]">{clip(r.quote)}</span>
            </span>
          ))}
          {rows.length > shown.length ? <span className="font-mono text-[10px] text-[color:var(--muted-soft)]">+{rows.length - shown.length} more</span> : null}
        </span>
      ) : null}
    </span>
  );
}

function DockFindings({ at, turn, through = turn, profiles = null, incidentOf = null, turns = [], sessionId = null, onScopeNavigate = null, onNavTurn = null }) {
  const tb = useContext(TurnBaseContext);
  // The docked conduct that recurs, if any: the door to the same habit in
  // other sessions. It opens the Reviews lens at this session's scope with
  // that conduct's chip on, by the title the lens groups under.
  const recurring = at.map((inc) => incidentOf?.get(inc.id)).find((i) => i && i.count > 1) || null;
  const elsewhere = recurring && sessionId && onScopeNavigate
    ? () => onScopeNavigate("sessions", sessionId, { view: "scope.reviews", params: { conduct: recurring.title } })
    : null;
  return (
    <div data-dock-findings="" className="grid min-w-0 gap-[9px] px-[9px] py-[2px]">
      <span className="flex min-w-0 items-baseline gap-[6px]">
        <span className={EYEBROW_CLASS} style={{ color: SEVERITY_COLOR.warn }}>Findings</span>
        <span className={`${META_CLASS} min-w-0 truncate text-[color:var(--muted)]`}>
          {at.length} finding{at.length === 1 ? "" : "s"} cite {through > turn ? `turns ${tb + turn}-${tb + through}` : `turn ${tb + turn}`} {"\u00b7"} trace review
          {elsewhere ? (
            <>
              {" \u00b7 "}
              <button type="button" data-conduct-elsewhere="" onClick={elsewhere} className="cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]">
                this conduct in other sessions
              </button>
            </>
          ) : null}
        </span>
      </span>
      {at.map((inc, i) => (
        <TraceReviewFindingCard
          key={inc.id || i}
          inc={inc}
          profile={profiles?.byReportId?.[inc.reportId] || profiles?.tenant || null}
          incident={incidentOf?.get(inc.id) || null}
          turns={turns}
          onNavTurn={onNavTurn}
          turnLabelBase={tb}
        />
      ))}
    </div>
  );
}

// The chat's knowledge graph at the docked turn. What the investigation
// concluded comes first -- the claims resting on this turn -- and under each
// claim the anchors it cites: how each was placed (or why it could not be),
// and what it relates to off the axis, the endpoint a process ran on, the
// account it ran as, the host it reached. Then the claims about this trace
// whose evidence sits on other turns or on none, and the claims about the
// sessions this one is related to, each a door into that session's lanes
// with the same graph over it.
const GRAPH_COLOR = "var(--graph, #3d5afe)";

function GraphTurnChips({ turns = [], onPick }) {
  const tb = useContext(TurnBaseContext);
  if (!turns.length) return null;
  return turns.slice(0, 6).map((t) => (
    <button
      key={t}
      type="button"
      data-graph-turn={t}
      onClick={() => onPick?.(t)}
      className="cursor-pointer p-0 font-mono underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]"
    >
      T{tb + t}
    </button>
  ));
}

function GraphAnchorLine({ anchor, onPickTurn }) {
  const rel = anchor.relations.slice(0, 4).map((r) => {
    const via = r.via.replace(/_/g, " ");
    return r.reverse ? `${r.label || r.id} ${via}` : `${via} ${r.label || r.id}`;
  });
  return (
    <span data-graph-anchor={anchor.key} className={`grid min-w-0 gap-[1px] ${FINDING_META}`}>
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-[6px]">
        <span className="text-[color:var(--muted-soft)]">{anchor.kind}</span>
        <span className="min-w-0 font-mono text-[color:var(--ink)] [overflow-wrap:anywhere]">{anchor.display || anchor.id}</span>
        {anchor.marks ? <span className="font-mono text-[color:var(--muted-soft)]">{anchor.marks}</span> : null}
      </span>
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-[6px]">
        {anchor.placement ? (
          <>
            <GraphTurnChips turns={anchor.placement.turns} onPick={onPickTurn} />
            <span>{anchor.placement.detail}</span>
          </>
        ) : (
          <span>not on the axis: {anchor.unplaced}</span>
        )}
      </span>
      {rel.length ? <span className="min-w-0 [overflow-wrap:anywhere]">{rel.join(" \u00b7 ")}</span> : null}
    </span>
  );
}

function GraphClaimCard({ claim, anchors, active = false, onPickTurn, onHover, onOpenTrace = null }) {
  const cited = claim.anchors.map((k) => anchors.get(k)).filter(Boolean);
  return (
    <div
      data-graph-claim={claim.id}
      data-graph-claim-active={active ? "" : undefined}
      onMouseEnter={() => onHover?.(claim.id)}
      onMouseLeave={() => onHover?.(null)}
      className={`grid min-w-0 gap-[4px] rounded-[2px] px-[8px] py-[6px] ${active ? "bg-[color:var(--control-bg)]" : ""}`}
    >
      <span className="text-[11px] leading-[1.45] text-[color:var(--ink)] [overflow-wrap:anywhere]">{claim.statement}</span>
      <span className={`flex min-w-0 flex-wrap items-baseline gap-x-[8px] ${FINDING_META}`}>
        <span>{claim.kind}</span>
        <span style={claim.status === "supported" ? { color: GRAPH_COLOR } : { color: SEVERITY_COLOR.warn }}>{claim.status}</span>
        {claim.bearing === "context" ? <span>context</span> : null}
        <GraphTurnChips turns={claim.turns} onPick={(t) => onPickTurn?.(t, claim.id)} />
        {!claim.turns.length && claim.here ? <span>none of its evidence is on the axis</span> : null}
        {onOpenTrace ? claim.otherSessions.slice(0, 3).map((id) => (
          <button
            key={id}
            type="button"
            data-graph-open-trace={id}
            onClick={() => onOpenTrace(id)}
            className="cursor-pointer p-0 font-mono underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]"
          >
            open session {id.slice(0, 8)}
          </button>
        )) : null}
      </span>
      {claim.status !== "supported" && claim.reason ? <span className={`${FINDING_META} [overflow-wrap:anywhere]`}>{claim.reason}</span> : null}
      {cited.length ? (
        <span className="grid min-w-0 gap-[4px] pl-[10px]">
          {cited.map((a) => <GraphAnchorLine key={a.key} anchor={a} onPickTurn={(t) => onPickTurn?.(t, claim.id)} />)}
        </span>
      ) : null}
    </div>
  );
}

function DockGraph({ graph, turn, through = turn, claimId = null, onPickClaim = null, onHoverClaim = null, onOpenTrace = null, onOpenChat = null }) {
  const tb = useContext(TurnBaseContext);
  const at = useMemo(() => graphAtTurn(graph, turn, through), [graph, turn, through]);
  const anchors = useMemo(() => new Map((graph?.anchors || []).map((a) => [a.key, a])), [graph]);
  const hereIds = new Set(at.claims.map((c) => c.id));
  const rest = (graph?.claims || []).filter((c) => c.here && !hereIds.has(c.id));
  const related = (graph?.claims || []).filter((c) => !c.here);
  const loose = at.anchors.filter((a) => !a.claims.length);
  const span = through > turn ? `turns ${tb + turn}-${tb + through}` : `turn ${tb + turn}`;
  const pick = (t, id = null) => onPickClaim?.(t, id);
  const card = (c) => (
    <GraphClaimCard key={c.id} claim={c} anchors={anchors} active={c.id === claimId} onPickTurn={pick} onHover={onHoverClaim} onOpenTrace={onOpenTrace} />
  );
  const section = (label, items) => (items.length ? (
    <div className="grid min-w-0 gap-[2px]">
      <span className={`${EYEBROW_BASE} px-[8px] text-[color:var(--muted)]`}>{label}</span>
      {items}
    </div>
  ) : null);
  return (
    <div data-dock-graph="" className="grid min-w-0 gap-[9px] px-[9px] py-[2px]">
      <span className="flex min-w-0 items-baseline gap-[6px]">
        <span className={EYEBROW_CLASS} style={{ color: GRAPH_COLOR }}>Graph</span>
        <span className={`${META_CLASS} min-w-0 truncate text-[color:var(--muted)]`}>
          {at.claims.length
            ? `${at.claims.length} claim${at.claims.length === 1 ? " rests" : "s rest"} on ${span}`
            : at.anchors.length ? `${at.anchors.length} anchor${at.anchors.length === 1 ? "" : "s"} on ${span}, no claim` : `nothing the graph holds sits on ${span}`}
          {" \u00b7 the chat's knowledge graph"}
          {onOpenChat ? (
            <>
              {" \u00b7 "}
              <button type="button" data-graph-open-chat="" onClick={onOpenChat} className="cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]">
                open the chat
              </button>
            </>
          ) : null}
        </span>
      </span>
      {at.claims.length ? <div className="grid min-w-0 gap-[2px]">{at.claims.map(card)}</div> : null}
      {section("Also placed here", loose.map((a) => (
        <span key={a.key} className="px-[8px]"><GraphAnchorLine anchor={a} onPickTurn={(t) => pick(t)} /></span>
      )))}
      {section("Other claims about this trace", rest.map(card))}
      {section("Related sessions", related.map(card))}
    </div>
  );
}

// The pipeline's reports as the story of the session: one row per reviewed
// window, in window order, each carrying the judge's own account of what the
// agent did there. The judge may not spend its summary on the incidents, so on
// a clean window the summary is pure narrative -- which is why the list reads
// as chapters rather than as verdicts. The active chapter (the one clicked, or
// the one holding the docked turn) is outlined in ink; a placed chapter's turn
// range is a link to its first turn, and an unplaced one says so in place of it.
function DockReports({ timeline, activeIndex = null, onNavTurn = null }) {
  const tb = useContext(TurnBaseContext);
  const reports = timeline?.reports || [];
  return (
    <div data-dock-reports="" className="grid min-w-0 gap-[9px] px-[9px] py-[2px]">
      <span className="flex min-w-0 items-baseline gap-[6px]">
        <span className={EYEBROW_CLASS}>Reports</span>
        <span className={`${META_CLASS} min-w-0 truncate text-[color:var(--muted)]`}>
          {reportsSummaryLine(timeline)} {"·"} trace review
        </span>
      </span>
      {reports.map((r) => {
        const active = r.index === activeIndex;
        const loud = r.tone === "warn" || r.tone === "danger";
        const color = loud ? SEVERITY_COLOR[r.tone] : "var(--line-strong)";
        const rungs = [r.worstBehaviour, r.worstContent].filter(Boolean).join(" · ");
        const verdict = !loud && (r.clean || !(r.findingCount > 0))
          ? "clean"
          : `${r.findingCount} finding${r.findingCount === 1 ? "" : "s"}${rungs ? ` · ${rungs}` : ""}`;
        const span = r.placed ? (r.through > r.from ? `T${tb + r.from}-T${tb + r.through}` : `T${tb + r.from}`) : null;
        const when = r.completedAt != null ? fullTimestampFormatter.format(new Date(r.completedAt)) : null;
        return (
          <div
            key={r.id || r.index}
            data-dock-report={r.index}
            data-dock-report-active={active ? "" : undefined}
            className="grid min-w-0 gap-[3px] border-l-2 pl-[8px]"
            style={{ borderColor: active ? "var(--ink)" : `color-mix(in srgb, ${color} 55%, transparent)` }}
          >
            <span className={`flex min-w-0 flex-wrap items-baseline gap-x-[8px] ${FINDING_META}`}>
              <span className="font-mono text-[color:var(--ink)]">{r.index}</span>
              {span && onNavTurn ? (
                <button
                  type="button"
                  data-report-turns=""
                  className="cursor-pointer p-0 font-mono underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]"
                  onClick={() => onNavTurn(r.from)}
                >
                  {span}
                </button>
              ) : (
                <span className="font-mono">{span || "not placed on the axis"}</span>
              )}
              {when ? <span>reviewed {when}</span> : null}
              <span style={loud ? { color } : undefined}>{verdict}</span>
              {r.partial ? <span>partial window</span> : null}
            </span>
            <span className={`text-[11px] leading-[1.45] [overflow-wrap:anywhere] ${r.summary ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>
              {reportLabel(r)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// The hand-off reading behind a join mark: the dock's answer to "who else
// holds this turn's content". One row per counterpart — who, which way the
// content moved, their footprint, the mirrored excerpt — and the same
// open-trace affordance the sub-agent and elsewhere rows carry, so the
// drill-down continues in the counterpart's own session instead of ending
// at a tooltip.
function DockJoins({ at, turn, hues = null, searchWindow = null, refMs = null, onOpenTrace = null, onOpenJoin = null, onLayJoin = null, onLocateJoin = null, followKey = null, flush = false }) {
  const more = at.count - at.entries.length;
  return (
    <div
      data-dock-joins=""
      className={flush
        ? "grid min-w-0 gap-[7px] px-[9px] py-[2px]"
        : "mx-[9px] mb-[4px] mt-[2px] grid min-w-0 gap-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[6px]"}
    >
      <span className="flex min-w-0 items-baseline gap-[6px]">
        <span className={EYEBROW_CLASS} style={{ color: JOIN_COLOR }}>Hand-offs</span>
        <span className={`${META_CLASS} min-w-0 truncate text-[color:var(--muted)]`}>
          {at.through > at.turn ? `turns ${at.turn}-${at.through} \u00b7 content` : "this turn's content"} is shared with {at.count} session{at.count === 1 ? "" : "s"}
        </span>
      </span>
      <JoinFlowLine entries={at.entries} />
      {at.entries.map((e, i) => (
        <span
          key={`${e.session_id}:${i}`}
          className="grid min-w-0 gap-[3px] border-l-2 pl-[8px]"
          style={{ borderColor: `color-mix(in srgb, ${hues?.get(e.session_id) || JOIN_COLOR} 45%, transparent)` }}
        >
          <span className="flex min-w-0 items-baseline gap-[7px] text-[11px] leading-[1.4] text-[color:var(--ink)]">
            <span className="min-w-0 truncate" style={{ color: hues?.get(e.session_id) || "var(--ink)" }}>{joinWho({ ...e.row, session_id: e.session_id })}</span>
            <span className="shrink-0 text-[color:var(--muted)]">{candidateDirectionLabel(e.row || {})}</span>
            {rowContextBadges(e.row) ? (
              <span className="shrink-0 text-[color:var(--muted-soft)]">{rowContextBadges(e.row)}</span>
            ) : null}
            {onLayJoin && e.row?.session_id ? (
              // On a workspace the other session opens on the board, under
              // the card this cell is on.
              <button
                type="button"
                data-join-lay=""
                onClick={() => onLayJoin(e)}
                className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[5px] py-[1px] text-[10px] leading-[1.4] text-[color:var(--muted)] hover:text-[color:var(--ink)]"
              >
                open on this workspace
              </button>
            ) : onOpenTrace && e.row?.trace_id ? (
              <button
                type="button"
                data-join-open=""
                onClick={() => (onOpenJoin ? onOpenJoin(e) : onOpenTrace(e.row.trace_id))}
                className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[5px] py-[1px] text-[10px] leading-[1.4] text-[color:var(--muted)] hover:text-[color:var(--ink)]"
              >
                open session ↗
              </button>
            ) : null}
            {onLocateJoin ? (
              <button
                type="button"
                data-join-locate=""
                aria-pressed={followKey != null && joinFollowNeedleKey(e) === followKey}
                onClick={() => onLocateJoin(e)}
                className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[5px] py-[1px] text-[10px] leading-[1.4] text-[color:var(--muted)] hover:text-[color:var(--ink)]"
              >
                {followKey != null && joinFollowNeedleKey(e) === followKey ? "hide in this session" : "show in this session"}
              </button>
            ) : null}
          </span>
          <MirrorExcerpt
            entry={e}
            searchWindow={searchWindow}
            refMs={refMs}
            sideFallback={needleSideSummary(e.hits) || theirSideSummary(e.row)}
          />
        </span>
      ))}
      {more > 0 ? (
        <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
          +{more} more session{more === 1 ? "" : "s"} share this turn's content — hover the join mark for the full list.
        </span>
      ) : null}
    </div>
  );
}

// The flow line: a mark's counterparts ordered around this session by
// wall-clock first carriage — who held the content before this session
// existed, who picked it up after it ended. Concurrent carriers stay in the
// cards below; the line claims only what time can prove, which is why a
// mark whose company is all concurrent renders no line at all.
export function JoinFlowLine({ entries }) {
  const stamp = (e) => parseServerTimestamp(e?.row?.first_seen);
  const before = (entries || []).filter((e) => e?.row?.precededFocal)
    .sort((a, b) => stamp(a) - stamp(b));
  const after = (entries || []).filter((e) => e?.row?.followedFocal)
    .sort((a, b) => stamp(a) - stamp(b));
  if (!before.length && !after.length) return null;
  // Dates unconditionally: a flow line exists precisely because carriage
  // crossed this session's boundaries, which usually means days.
  const fmt = (at) => {
    const t = String(at ?? "");
    return `${t.slice(5, 10)} ${t.slice(11, 16)}`.trim() || "\u2014";
  };
  const seg = (list) => list.slice(0, 2)
    .map((e) => `${joinWho({ ...e.row, session_id: e.session_id })} (${fmt(e.row.first_seen)})`)
    .join(" \u2192 ") + (list.length > 2 ? ` +${list.length - 2}` : "");
  return (
    <span data-join-flow="" className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
      {before.length ? `${seg(before)} \u2192 ` : ""}this session{after.length ? ` \u2192 ${seg(after)}` : ""}
    </span>
  );
}

// The counterpart's display name, with the enum's "Unspecified" filtered —
// it reads as a name and means nothing. Falls back to the session prefix.
function joinWho(x) {
  const agent = x?.agent_type && x.agent_type !== "Unspecified" ? x.agent_type : null;
  return [agent, x?.model].filter(Boolean).join(" \u00b7 ")
    || `session ${String(x?.session_id || "").slice(0, 8)}`;
}

// The counterpart's footprint by role: "2 in prompt text \u00b7 1 in responses"
// reads the whole overlap, where a single hit's timestamp read one moment.
function theirSideSummary(row) {
  const parts = [];
  if (row?.theirPrompts) parts.push(`${row.theirPrompts} in prompt text`);
  if (row?.theirResponses) parts.push(`${row.theirResponses} in response${row.theirResponses === 1 ? "" : "s"}`);
  if (row?.theirTools) parts.push(`${row.theirTools} in tool results`);
  return parts.join(" \u00b7 ");
}

// The pin-local footprint: THIS string's hits on their side, not the whole
// relationship's — a session sharing twenty-five files repeated its session
// total on every pin, which read as the same fact twenty-five times.
function needleSideSummary(hits) {
  if (!hits) return "";
  return theirSideSummary({ theirPrompts: hits.prompts, theirResponses: hits.responses, theirTools: hits.tools });
}

// The excerpt, centered on the shared string with the match split out so the
// renderer can highlight it — a chunk-start fragment made every excerpt read
// as noise, when the readable part is the text AROUND the match.
// The search rows carry ~512 chars of the counterpart's text; show most of
// it. The slice still centers on the match so the window is the shared
// string IN USE, not whatever the chunk boundary led with.
function centerExcerpt(snippet, needle) {
  const text = String(snippet || "");
  const idx = text.toLowerCase().indexOf(String(needle || "").toLowerCase());
  if (idx < 0) return { before: text.slice(0, 480), match: "", after: "" };
  const start = Math.max(0, idx - 160);
  const end = Math.min(text.length, idx + needle.length + 340);
  return {
    before: (start > 0 ? "\u2026" : "") + text.slice(start, idx),
    match: text.slice(idx, idx + needle.length),
    after: text.slice(idx + needle.length, end) + (end < text.length ? "\u2026" : ""),
  };
}

// One preview's hydration: when the stored snippet does not contain the
// match (it is the chunk's HEAD, and the match can sit anywhere in a 32KB
// chunk), fetch that one counterpart chunk's full text and re-center on the
// real occurrence. Cached per (session, needle); the tooltip label only
// mounts when shown, so the fetch is hover-lazy by construction. A refused
// or failed fetch keeps the snippet head — context beats nothing.
// The cache holds PROMISES, not results: two pins hovering the same
// (session, needle) — or a re-render racing a pending fetch — share one
// in-flight request instead of firing twice. Reads bump the entry so the
// eviction is least-recently-USED, not first-inserted: a pin the reader
// keeps returning to must not age out on a one-hover schedule.
const MIRROR_CACHE = new Map();
const MIRROR_CACHE_MAX = 200;

export function clearJoinMirrorCache() {
  MIRROR_CACHE.clear();
}

function acquireMirrorWindow(cacheKey, start) {
  let p = MIRROR_CACHE.get(cacheKey);
  if (p) {
    MIRROR_CACHE.delete(cacheKey);
    MIRROR_CACHE.set(cacheKey, p);
    return p;
  }
  p = start();
  if (MIRROR_CACHE.size >= MIRROR_CACHE_MAX) {
    const oldest = MIRROR_CACHE.keys().next().value;
    if (oldest !== undefined) MIRROR_CACHE.delete(oldest);
  }
  MIRROR_CACHE.set(cacheKey, p);
  return p;
}

function MirrorExcerpt({ entry, searchWindow = null, refMs = null, sideFallback = "" }) {
  const { needle, mirror } = entry;
  // An index-provided snippet IS the shared string — containment proves
  // nothing about context, so those rows always hydrate to the counterpart's
  // real surrounding content; a probe snippet that already carries its match
  // is an excerpt and displays as-is.
  const snippetHasMatch = !mirror?.indexed && (mirror?.snippet
    ? mirror.snippet.toLowerCase().includes(String(needle || "").toLowerCase())
    : false);
  const cacheKey = `${entry.session_id}@${needle}`;
  const [hydrated, setHydrated] = useState(null);
  // Primitive deps on purpose: the window object is rebuilt per render, and
  // keying the effect on its identity re-fired pending fetches.
  const startTime = searchWindow?.startTime ?? null;
  const endTime = searchWindow?.endTime ?? null;
  useEffect(() => {
    if (snippetHasMatch || !startTime || !endTime) return undefined;
    let dead = false;
    acquireMirrorWindow(cacheKey, () =>
      fetchJoinMirrorWindow({ sessionId: entry.session_id, needle, startTime, endTime }))
      .then((res) => {
        if (!dead) setHydrated(res?.ok ? res : null);
      });
    return () => { dead = true; };
  }, [cacheKey, snippetHasMatch, startTime, endTime, entry.session_id, needle]);
  const source = hydrated?.text || mirror?.snippet || "";
  const sideLine = needleSideSummary(hydrated?.counts) || sideFallback;
  if (!source) {
    return sideLine ? (
      <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
        on their side: {sideLine}
      </span>
    ) : null;
  }
  const excerpt = centerExcerpt(source, needle);
  return (
    <>
      {sideLine ? (
        <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
          on their side: {sideLine}
        </span>
      ) : null}
      <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
        excerpt from: {(hydrated?.role || mirror?.role) === "assistant"
          ? "a response"
          : (hydrated?.kind || mirror?.kind || hydrated?.role || mirror?.role || "content")}
        {" \u00b7 "}{sampleStamp(hydrated?.at ?? mirror?.at, refMs)}
        {looksInjected(source) ? <>{" \u00b7 "}inside injected context</> : null}
      </span>
      <span
        className="block whitespace-pre-wrap rounded-[3px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[6px] py-[4px] text-[10px] leading-[1.5] text-[color:var(--muted)]"
        style={{ display: "-webkit-box", WebkitLineClamp: 9, WebkitBoxOrient: "vertical", overflow: "hidden" }}
      >
        {excerpt.before}
        {excerpt.match ? (
          <span className="font-mono font-medium" style={{ color: JOIN_COLOR }}>{excerpt.match}</span>
        ) : null}
        {excerpt.after}
      </span>
    </>
  );
}

export function JoinMarkTooltip({ title, entries, structure = null, hues = null, refMs = null, searchWindow = null }) {
  const tb = useContext(TurnBaseContext);
  return (
    <span className="grid gap-[7px]">
      <span className="font-medium">{title}</span>
      <JoinFlowLine entries={entries} />
      {structure?.length ? (
        // The span's internal stretches, in words: counts are DISTINCT
        // sessions per stretch and one session can span several, so these
        // deliberately do not sum to the title's total — the tooltip is the
        // one surface with room to say so instead of making the axis imply
        // an arithmetic that cannot hold.
        <span className="grid gap-[2px]">
          {structure.slice(0, 6).map((m) => (
            <span key={m.turn} className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
              {m.through > m.turn ? `turns ${tb + m.turn}-${tb + m.through}` : `turn ${tb + m.turn}`}
              {" \u00b7 "}{m.count} session{m.count === 1 ? "" : "s"}
            </span>
          ))}
          {structure.length > 6 ? (
            <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">+{structure.length - 6} more stretches</span>
          ) : null}
        </span>
      ) : null}
      {entries.map((e, i) => {
        const summary = needleSideSummary(e.hits) || theirSideSummary(e.row);
        return (
          // One card per counterpart: name in ink, direction beside it, how
          // the join matched, their footprint by role, and an excerpt
          // centered on the shared string.
          <span
            key={i}
            className="grid gap-[3px] border-l-2 pl-[8px]"
            style={{ borderColor: `color-mix(in srgb, ${hues?.get(e.session_id) || JOIN_COLOR} 45%, transparent)` }}
          >
            <span className="text-[11px] leading-[1.4] text-[color:var(--ink)]">
              {joinWho({ ...e.row, session_id: e.session_id })}
              <span className="text-[color:var(--muted)]">{" \u00b7 "}{candidateDirectionLabel(e.row || {})}</span>
              {rowContextBadges(e.row) ? (
                <span className="text-[color:var(--muted-soft)]">{" \u00b7 "}{rowContextBadges(e.row)}</span>
              ) : null}
            </span>
            {e.identity ? (
              // The needle IS this artifact's own name — say so instead of
              // restating the label the reader is already hovering.
              <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">matched by this file's name</span>
            ) : (
              <span
                className="max-w-full justify-self-start truncate rounded-[3px] px-[4px] py-[1px] font-mono text-[10px] leading-[1.5]"
                style={{ color: JOIN_COLOR, background: `color-mix(in srgb, ${JOIN_COLOR} 14%, transparent)` }}
              >
                {e.needle}
              </span>
            )}
            {e.mirror ? (
              <MirrorExcerpt
                entry={e}
                searchWindow={searchWindow}
                refMs={refMs}
                sideFallback={summary || "matched"}
              />
            ) : (
              <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                on their side: {summary || "matched"}
              </span>
            )}
          </span>
        );
      })}
      <span className="text-[color:var(--muted-soft)]">Select to read the turn under the pointer.</span>
    </span>
  );
}

// Which cell marks actually get pins. Conversation lanes carry none (the
// strip's turn marks cover them, and per-mention bubbles drowned those
// rows); an OPEN lane must have a cell at the pin's turn (a needle can occur
// at a turn the lane does not chart, and a floating bubble reads as
// misplaced); a lane absent from the map is folded into its group header,
// whose row aggregates — the pin stays.
export function filterJoinCellMarks(marks = [], laneCellsById = new Map()) {
  return marks.filter((m) => {
    if (m.lane === "prompt:all" || m.lane === "reply:all" || m.lane === "commentary") return false;
    const cells = laneCellsById.get(m.lane);
    return cells ? cells.has(m.turn) : true;
  });
}

// Other sessions that touched this same file, from three substrates at once:
// raw OS telemetry (the only place a process that never names the file is
// visible), the prompt rows whose resource_paths name it (each hit an openable
// turn), and the conversation model's content search (which sees tool results
// and captured responses). Runs only when asked — see the cost note above.
//
// `probeLines` is the file's reconstructed content; when present, a second
// on-demand search checks which sessions carried the same CONTENT, not just
// the same name. That search relates two agents even when no path joins them,
// which is the direction this panel is growing in.
function FileElsewhere({ path, endpointId, traceTimestamp, sessionId, onOpenTrace = null, probeLines = null }) {
  const [state, setState] = useState({
    status: "idle", rows: [], focal: null, channels: null, wide: false, openRow: null,
    probeStatus: "idle", probeCount: 0, probeError: null,
  });
  const window_ = counterpartyWindow(traceTimestamp);
  if (!path || !window_) return null;

  const search = async (allEndpoints) => {
    setState((s) => ({ ...s, status: "loading", wide: allEndpoints }));
    const res = await query("getFileCounterparties", {
      path, endpointId, allEndpoints, sessionId, ...window_,
    });
    setState((s) => ({
      ...s,
      status: res.ok ? "done" : "error",
      // The execute splits the focal session out already; the filter stays as
      // the local guarantee, because this component's claim is "OTHER sessions"
      // and it should hold regardless of who built the rows.
      rows: (res.rows || []).filter((r) => r.session_id !== sessionId),
      focal: res.focal || null,
      channels: res.channels || null,
      wide: allEndpoints,
      truncated: Boolean(res.truncated),
      error: res.error || null,
      probeStatus: "idle",
    }));
  };

  const probes = deriveFileProbeSet(probeLines || []);

  // Stage the shared strings like the joins dock does, or the hop lands
  // unmarked. Path first: NEEDLE_CAP truncates, and a counterpart that only
  // NAMED the file never held its bytes for a content probe to match.
  const openCounterpart = onOpenTrace
    ? (traceId) => {
      stageJoinFollow(traceId, { needles: [path, ...probes], fromTraceId: null });
      onOpenTrace(traceId);
    }
    : null;

  const probe = async () => {
    setState((s) => ({ ...s, probeStatus: "loading" }));
    const res = await query("getFileContentProbes", {
      probes, endpointId, sessionId, allEndpoints: state.wide, ...window_,
    });
    setState((s) => {
      if (!res.ok) return { ...s, probeStatus: "error", probeError: res.error || null };
      const fold = res.rows || [];
      const focalProbe = fold.find((p) => p.session_id === sessionId) || null;
      const rest = fold.filter((p) => p.session_id !== sessionId);
      return {
        ...s,
        probeStatus: "done",
        // `of` excludes diffuse probes, and zero counted probes is a real
        // answer ("everything this file contains is everywhere") — do not
        // paper over it with the asked count.
        probeCount: Number.isFinite(res.of) ? res.of : probes.length,
        probeDiffuse: res.diffuse || 0,
        probeMatched: rest.length,
        probeVia: res.via || "conversations",
        probeScope: res.scope || "endpoint",
        rows: attachProbeMatches(s.rows, rest),
        focal: s.focal && focalProbe ? { ...s.focal, probes: focalProbe } : s.focal,
      };
    });
  };

  const channelNote = (name, ch) => (ch && !ch.ok ? (
    <span data-file-elsewhere-channel-down="" className={`${META_CLASS} mt-[2px] block text-[color:var(--muted-soft)]`}>
      The {name} search could not run here; these results are from the other channels only.
    </span>
  ) : null);

  return (
    <div className="min-w-0 border-t border-[color:var(--line-soft)] px-[9px] py-[5px]" aria-busy={state.status === "loading" || state.probeStatus === "loading"}>
      <span className={EYEBROW_CLASS}>Elsewhere</span>
      {state.status === "idle" ? (
        <button
          type="button"
          data-file-elsewhere-search=""
          onClick={() => search(false)}
          className={`${META_CLASS} ml-[8px] cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]`}
        >
          find other sessions that touched this file
        </button>
      ) : null}
      {state.status === "loading" ? (
        <span className={`${META_CLASS} ml-[8px] text-[color:var(--muted-soft)]`}>searching…</span>
      ) : null}
      {state.status === "error" ? (
        <span className={`${META_CLASS} mt-[2px] block text-[color:var(--muted)]`}>
          Could not search for other sessions. {state.error || ""}
        </span>
      ) : null}
      {state.status === "done" && state.focal ? (
        // The focal session's own evidence, in the same vocabulary as the
        // counterparties — the reader is comparing both sides of an exchange,
        // and one side rendered differently stops being comparable.
        <span data-file-elsewhere-focal="" className={`${META_CLASS} mt-[2px] block text-[color:var(--muted)]`}>
          This session: {counterpartyLabel(state.focal).what}
        </span>
      ) : null}
      {state.status === "done" && !state.rows.length ? (
        <span className={`${META_CLASS} mt-[2px] block text-[color:var(--muted)]`}>
          No other session touched this file {state.wide ? "on any endpoint" : "on this endpoint"} within
          {` ${COUNTERPARTY_WINDOW_HOURS}h`} of this trace.
        </span>
      ) : null}
      {state.rows.map((r) => (
        <CounterpartyRow
          key={r.session_id}
          row={r}
          refMs={typeof traceTimestamp === "number" ? traceTimestamp : Date.parse(traceTimestamp ?? "")}
          onOpenTrace={openCounterpart}
          open={state.openRow === r.session_id}
          onToggle={() => setState((s) => ({ ...s, openRow: s.openRow === r.session_id ? null : r.session_id }))}
        />
      ))}
      {state.status === "done" ? channelNote("tool-call", state.channels?.calls) : null}
      {state.status === "done" ? channelNote("conversation-content", state.channels?.content) : null}
      {state.truncated ? (
        // Rows come back oldest-first, so the cap drops the LATEST touches.
        // Saying so is the difference between a partial answer and a wrong one.
        <span data-file-elsewhere-truncated="" className={`${META_CLASS} mt-[2px] block text-[color:var(--muted)]`}>
          Too many matches to show them all. The most recent activity on this file may be missing.
        </span>
      ) : null}
      {state.status === "done" && probes.length && state.probeStatus === "idle" && state.channels?.content?.ok ? (
        <button
          type="button"
          data-file-elsewhere-probe=""
          onClick={probe}
          className={`${META_CLASS} mt-[2px] block cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]`}
        >
          search for this file&rsquo;s content in other conversations
        </button>
      ) : null}
      {state.probeStatus === "loading" ? (
        <span className={`${META_CLASS} mt-[2px] block text-[color:var(--muted-soft)]`}>matching content…</span>
      ) : null}
      {state.probeStatus === "error" ? (
        <span className={`${META_CLASS} mt-[2px] block text-[color:var(--muted)]`}>
          Could not run the content match. {state.probeError || ""}
        </span>
      ) : null}
      {state.probeStatus === "done" && state.probeCount ? (
        // "Ran and found nothing" and "ran and matched" are different answers;
        // the old copy said the second either way, which read as broken when
        // nothing matched.
        <span data-file-elsewhere-probed="" className={`${META_CLASS} mt-[2px] block text-[color:var(--muted-soft)]`}>
          {state.probeMatched
            ? `Content match ran ${state.probeCount} probe${state.probeCount === 1 ? "" : "s"} from this file's ids and distinctive lines; rows above show what matched.${
                state.probeVia === "raw-bodies" ? " Matched in raw request bodies - this content is not in the conversation index yet." : ""}${
                state.probeScope === "all-endpoints" && !state.wide ? " Found beyond this endpoint - the reader lives under another endpoint id." : ""}`
            : `Content match ran ${state.probeCount} probe${state.probeCount === 1 ? "" : "s"} from this file's ids and distinctive lines; no other conversation carried this content on any endpoint in the window.`}
        </span>
      ) : null}
      {state.probeStatus === "done" && state.probeDiffuse ? (
        // A diffuse probe is an answer about the file, not a failure: the
        // content it tested is common to too many sessions to tie two agents
        // together.
        <span data-file-elsewhere-probe-diffuse="" className={`${META_CLASS} mt-[2px] block text-[color:var(--muted-soft)]`}>
          {state.probeDiffuse} probe{state.probeDiffuse === 1 ? "" : "s"} matched more than {PROBE_SESSION_CAP} sessions
          {" "}and {state.probeDiffuse === 1 ? "was" : "were"} not counted - content that common is shared
          {" "}environment, not an exchange between two agents.
        </span>
      ) : null}
      {state.status === "done" && !state.wide ? (
        <button
          type="button"
          data-file-elsewhere-widen=""
          onClick={() => search(true)}
          className={`${META_CLASS} mt-[2px] block cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]`}
        >
          search all endpoints
        </button>
      ) : null}
    </div>
  );
}

// --- System band -------------------------------------------------------------
//
// The session's OS telemetry ON THE TURN AXIS, as bottom rows of the lanes
// plane. Turn times come from the conversation model's per-reply timestamps
// (traceTurnTimes) — approximate by construction, and the band's label says
// how much of the axis is measured. A session that cannot be dated gets no
// bars rather than a fabricated alignment.
//
// A hosted agent (a Codex app-server) files its OS telemetry under the HOST
// process's session id, so the conversation-scoped read comes back empty for
// exactly the sessions a cross-agent reader most wants to see. When that
// happens and the trace's body names its thread, the band resolves the host
// and reads the telemetry recorded against it — saying so, because the host
// multiplexes other conversations and the attribution is coarser.
const SYSTEM_SPAN_LIMIT = 20000;
const SYSTEM_DETAIL_LIMIT = 200;
const SYSTEM_ALL_TYPES = SYSTEM_STREAM_KINDS.flatMap((k) => k.types);

// One event as a line for the band's detail. eventLine covers the kinds that
// can name a file or a command; the network kinds it declines are still worth
// a line here — this band is about what the machine did, not matching names.
export function systemEventLine(event) {
  const read = eventLine(event);
  if (read) return `${read.verb} ${read.text}`;
  const data = event?.data;
  if (event?.kind === "DnsQuery") return data?.query_name ? `resolved ${data.query_name}` : null;
  if (event?.kind === "NetworkConnection") {
    return data?.remote_address ? `connected ${data.remote_address}${data.remote_port ? `:${data.remote_port}` : ""}` : null;
  }
  return null;
}

function useSystemTelemetry({ trace, turns }) {
  const sessionId = trace?.sessionId ?? null;
  const endpointId = trace?.endpointId ?? null;
  const tsMs = typeof trace?.timestamp === "number" ? trace.timestamp : Date.parse(trace?.timestamp ?? "");
  const identityKey = JSON.stringify((turns ?? []).map((t) => [t.messageId, t.messageAt]));
  const firstMessageAt = (turns ?? []).find((t) => t.messageId && Number.isFinite(t.messageAt))?.messageAt ?? null;
  const [state, setState] = useState({ status: "idle" });
  // Cache telemetry by trace and the first observed message time. Appending
  // a captured response or changing message identities only rebuilds buckets.
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  const materialsRef = useRef(null);
  const finish = useCallback((mat) => {
    const clock = buildTurnTimes(turnsRef.current ?? []);
    if (!clock) {
      setState({ status: "undatable" });
      return;
    }
    // The span reaches back before the first turn (systemSpan's lead) and
    // past the last (its tail); the same bounds give the first and last
    // dated turns that lead-in and tail here and in the dock's slice window.
    const buckets = bucketEventsByTurn(mat.events, clock.times, systemKindOf, {
      floorTs: mat.span.fromTs,
      tailTs: mat.span.toTs,
    });
    // Which kind rows exist is decided HERE, once: the render and the
    // plane's height math must agree on it or the band overdraws.
    const kindsPresent = SYSTEM_STREAM_KINDS
      .filter((k) => [...buckets.perTurn.values()].some((b) => b[k.key]))
      .map((k) => k.key);
    setState({
      status: "ready",
      perTurn: buckets.perTurn,
      before: buckets.before,
      kindsPresent,
      clock,
      span: mat.span,
      source: mat.source,
      hosted: mat.hosted,
      readSessionId: mat.readSessionId,
      empty: mat.events.length === 0,
      truncated: mat.truncated,
    });
  }, []);
  useEffect(() => {
    materialsRef.current = null;
    if (!sessionId || !endpointId || !Number.isFinite(tsMs) || !turnsRef.current?.length) {
      setState({ status: "idle" });
      return undefined;
    }
    let dead = false;
    const ctl = new AbortController();
    const iso = (t) => new Date(t).toISOString();
    (async () => {
      setState({ status: "loading" });
      try {
        const source = "messages";
        const clock = buildTurnTimes(turnsRef.current ?? []);
        if (!clock) {
          if (!dead) setState({ status: "undatable" });
          return;
        }
        const floorTs = clock.times.find(Number.isFinite);
        const read = (sid, fromTs, toTs, limit) => fetchEndpointEvents({
          sessionId: sid, endpointId, fromTs, toTs,
          eventTypes: SYSTEM_ALL_TYPES, limit, signal: ctl.signal,
        });
        // One read covers the span. The read is oldest-first, so when it
        // fills SYSTEM_SPAN_LIMIT and the span leads in before the first
        // turn, a busy lead-in has crowded out the conversation's own rows:
        // read the turns' window first, then give the lead-in what the cap
        // leaves. The lead-in stays oldest-first: its earliest rows are the
        // session's own process starts, which is what it is read for.
        const readSpan = async (sid, s) => {
          const all = await read(sid, s.fromTs, s.toTs, SYSTEM_SPAN_LIMIT);
          if (all.length < SYSTEM_SPAN_LIMIT || !(floorTs > s.fromTs)) {
            return { events: all, truncated: all.length >= SYSTEM_SPAN_LIMIT };
          }
          const own = await read(sid, floorTs, s.toTs, SYSTEM_SPAN_LIMIT);
          const budget = SYSTEM_SPAN_LIMIT - own.length;
          const leadRead = budget > 0 ? await read(sid, s.fromTs, floorTs, budget) : [];
          // Both ranges are inclusive at the first turn's instant.
          const ownIds = new Set(own.map((e) => e.id));
          const lead = leadRead.filter((e) => !ownIds.has(e.id));
          return { events: [...lead, ...own], truncated: budget <= 0 || leadRead.length >= budget };
        };
        let span = systemSpan({ focalTs: tsMs, floorTs });
        let readSessionId = sessionId;
        let hosted = false;
        let { events, truncated } = await readSpan(sessionId, span);
        if (!events.length) {
          const threadId = trace?.conversationThreadId || null;
          if (threadId) {
            // The shared host serves other conversations too, so its read gets
            // no lead: before the first turn, the host's rows are someone
            // else's work, and up to a day of them would land on turn 1 and
            // crowd this conversation's own rows out of SYSTEM_SPAN_LIMIT.
            const hostSpan = systemSpan({ focalTs: tsMs, floorTs, leadMs: 0 });
            const host = await fetchHostSessionForThread({
              threadId, endpointId, startTime: iso(hostSpan.fromTs), endTime: iso(hostSpan.toTs), signal: ctl.signal,
            });
            if (host?.hostSessionId && host.hostSessionId !== sessionId) {
              readSessionId = host.hostSessionId;
              hosted = true;
              span = hostSpan;
              ({ events, truncated } = await readSpan(readSessionId, span));
            }
          }
        }
        if (dead) return;
        const mat = { source, events, truncated, hosted, readSessionId, span };
        materialsRef.current = mat;
        finish(mat);
      } catch (err) {
        if (!dead) {
          // The band renders nothing for a failed read — same as a session with
          // no telemetry — so the console line is the only trace that the read
          // FAILED rather than found nothing.
          console.warn("System telemetry read failed for this trace:", err);
          setState({ status: "error", error: String(err?.message || err) });
        }
      }
    })();
    return () => {
      dead = true;
      ctl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- turns ride turnsRef; see above
  }, [trace?.id, trace?.conversationThreadId, sessionId, endpointId, tsMs, firstMessageAt, finish]);
  // The appended turn re-dates the axis; everything fetched is still good.
  useEffect(() => {
    if (materialsRef.current) finish(materialsRef.current);
  }, [identityKey, finish]);
  return state;
}

// The literal events behind one band bucket, read per-slice, rendered as the
// dock's subject body — the band is one more group of lanes, so its reading
// opens in the same dock as everything else.
function SystemDockBody({ system, turns = [], onNavTurn, bodyScroll }) {
  const tb = useContext(TurnBaseContext);
  const [detail, setDetail] = useState({ status: "loading" });
  const { kind, start, end } = system;
  // What this trace's tool calls named — a name match upgrades an event
  // from "time-coincident" to "this IS the call's process/file", and links it
  // back to the turn that made the call. buildSliceTree limits this catalog
  // using each event's own turn-clock position, independent of visual buckets.
  const candidates = useMemo(
    () => sliceTurnCandidates(turns, 0, turns.length - 1),
    [turns],
  );
  // The slice is exactly what the band counted for these turns: the window
  // is bucketEventsByTurn's own boundary rule, inclusive at both ends. An
  // event can share a turn's instant, which a half-open window would read as
  // empty (the first turn's window used to be [first, first]).
  const sliceWindow = useMemo(
    () => turnSliceWindow(system.clockTimes, start, end, { floorTs: system.spanFromTs, tailTs: system.spanToTs }),
    [system.clockTimes, start, end, system.spanFromTs, system.spanToTs],
  );
  const fromTs = sliceWindow?.fromTs ?? null;
  const toTs = sliceWindow?.toTs ?? null;
  useEffect(() => {
    let dead = false;
    (async () => {
      setDetail({ status: "loading" });
      try {
        const own = SYSTEM_STREAM_KINDS.find((k) => k.key === kind)?.types ?? [];
        // A file or network slice fetches ProcessStart too — not to display
        // bare, but as the tree's scaffolding: the rows nest under the
        // process that did them, and the process needs its name and parent.
        const types = kind === "process" ? own : [...own, "ProcessStart"];
        const limit = kind === "process" ? SYSTEM_DETAIL_LIMIT : SYSTEM_DETAIL_LIMIT * 2;
        // The read's range is matched inclusively at whole milliseconds, so
        // at a turn anchor it reaches one past the window and the parsed
        // instants are cut back to it: a sub-millisecond row at the anchor
        // stays in. The tail ceiling is not an anchor and is read as is.
        const fetched = Number.isFinite(fromTs) && Number.isFinite(toTs)
          ? await fetchEndpointEventDetails({
            sessionId: system.readSessionId, endpointId: system.endpointId, fromTs,
            toTs: sliceWindow.tail ? toTs : toTs + 1,
            eventTypes: types, limit,
          })
          : [];
        const events = fetched.filter((e) => e.ts >= fromTs && e.ts <= toTs);
        // Rows come back oldest-first, so a full read whose last row already
        // lies past the window read everything inside it: only the reach
        // past the anchor was cut.
        const last = fetched[fetched.length - 1];
        const capped = fetched.length >= limit && last.ts <= toTs;
        if (!dead) setDetail({ status: "ready", events, capped });
      } catch (err) {
        if (!dead) setDetail({ status: "error", error: String(err?.message || err) });
      }
    })();
    return () => {
      dead = true;
    };
  }, [kind, start, end, system.readSessionId, system.endpointId, fromTs, toTs, sliceWindow?.tail]);

  // Time of day, with the date when the slice crosses a UTC day (the lead
  // reaches back up to a day, so a bare clock would read backwards).
  const crossesDay = Number.isFinite(fromTs) && Number.isFinite(toTs)
    && new Date(fromTs).toISOString().slice(0, 10) !== new Date(toTs).toISOString().slice(0, 10);
  const fmtClock = (t) => (Number.isFinite(t)
    ? new Date(t).toISOString().slice(crossesDay ? 5 : 11, 19).replace("T", " ")
    : "?");
  // The slice as a process tree: pipeline stages under their wrapper, file
  // and network rows under the process that did them, and match chips
  // propagated down the lineage — a descendant of a matched shell belongs to
  // that tool call structurally, no name comparison needed.
  const treeRows = useMemo(
    () => (detail.status === "ready"
      ? buildSliceTree(detail.events || [], candidates, {
        prune: kind !== "process",
        clockTimes: system.clockTimes,
      })
      : []),
    [detail, candidates, kind, system.clockTimes],
  );
  const matchedTurns = useMemo(
    () => [...new Set(treeRows.filter((r) => r.match).map((r) => r.match.turn))].sort((a, b) => a - b),
    [treeRows],
  );
  const matchedCount = useMemo(() => treeRows.filter((r) => r.match).length, [treeRows]);
  return (
    <div data-lanes-scroll="" data-system-dock="" className={bodyScroll} aria-busy={detail.status === "loading"}>
      <div className="grid min-w-0 gap-[1px] px-[9px] py-[5px]">
        <span className={`${META_CLASS} text-[color:var(--muted-soft)]`}>
          ~{fmtClock(fromTs)}–{fmtClock(toTs)} UTC
          {system.hosted ? " · recorded against this agent's shared host process, which serves other conversations too" : ""}
        </span>
        {detail.status === "ready" && treeRows.length ? (
          <span data-system-match-summary="" className={`${META_CLASS} flex min-w-0 flex-wrap items-baseline gap-[6px] text-[color:var(--muted)]`}>
            {matchedTurns.length ? (
              <>
                <span>
                  {matchedCount} of {treeRows.length} rows tie to a tool call in this slice, by name or by process lineage:
                </span>
                {matchedTurns.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-system-match-turn={t}
                    onClick={() => onNavTurn && onNavTurn(t)}
                    className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[3px] py-0 font-mono text-[9px] leading-[1.4] text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                  >
                    T{tb + t}
                  </button>
                ))}
              </>
            ) : (
              <span className="text-[color:var(--muted-soft)]">
                No event here is named by this slice&rsquo;s tool calls; the pairing is by time alone.
              </span>
            )}
          </span>
        ) : null}
        {detail.status === "loading" ? (
          <span className={`${META_CLASS} text-[color:var(--muted-soft)]`}>reading events…</span>
        ) : null}
        {detail.status === "error" ? (
          <span className={`${META_CLASS} text-[color:var(--muted)]`}>Could not read these events. {detail.error || ""}</span>
        ) : null}
        {detail.status === "ready" ? (
          <>
            {treeRows.map((r, i) => {
              const e = r.event;
              const line = systemEventLine(e) || e.kind;
              const match = r.match;
              const isScaffold = r.type === "process" && kind !== "process";
              return (
                <HoverTooltip
                  key={e.id || i}
                  wide
                  placement="top"
                  maxWidth={560}
                  label={
                    <span className="grid min-w-0 gap-[3px]">
                      <span className="min-w-0 font-mono text-[10px] leading-[1.5] [overflow-wrap:anywhere] text-[color:var(--ink)]">{line}</span>
                      {match ? (
                        <span className="text-[10px] text-[color:var(--muted)]">
                          {match.inherited
                            ? `Done by a process descended from the tool call at T${tb + match.turn}.`
                            : `Matches the tool call at T${tb + match.turn} (${match.basis}).`}
                        </span>
                      ) : null}
                    </span>
                  }
                >
                  <span
                    data-system-dock-row=""
                    data-system-dock-depth={r.depth}
                    data-system-dock-match={match ? match.turn : undefined}
                    className="flex min-w-0 items-baseline gap-[6px]"
                    style={r.depth ? { paddingLeft: r.depth * 12 } : undefined}
                  >
                    <span className={`${META_CLASS} ${crossesDay ? "w-[100px]" : "w-[62px]"} shrink-0 tabular-nums text-[color:var(--muted-soft)]`}>{fmtClock(e.ts)}</span>
                    {r.depth ? <span aria-hidden className="shrink-0 text-[10px] leading-none text-[color:var(--line-strong)]">└</span> : null}
                    {match ? (
                      // The chip opens the tool call the row ties to. A solid
                      // chip is a NAME match; a lineage chip inherits from a
                      // matched ancestor process — structural, said in the
                      // hover card.
                      <button
                        type="button"
                        data-system-match-turn={match.turn}
                        data-system-match-inherited={match.inherited ? "" : undefined}
                        onClick={() => onNavTurn && onNavTurn(match.turn)}
                        className={`shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[3px] py-0 font-mono text-[9px] leading-[1.4] ${match.inherited ? "text-[color:var(--muted-soft)]" : "text-[color:var(--muted)]"} hover:text-[color:var(--ink)]`}
                      >
                        T{tb + match.turn}
                      </button>
                    ) : null}
                    <span className={`min-w-0 flex-1 truncate font-mono text-[10px] leading-[1.45] ${match && !match.inherited ? "text-[color:var(--ink)]" : isScaffold ? "text-[color:var(--muted-soft)]" : "text-[color:var(--muted)]"}`}>
                      {line}
                    </span>
                    {r.type === "process" && r.attached ? (
                      <span className={`${META_CLASS} shrink-0 text-[color:var(--muted-soft)]`}>
                        {r.attached} event{r.attached === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </span>
                </HoverTooltip>
              );
            })}
            {detail.capped ? (
              <span className={`${META_CLASS} text-[color:var(--muted-soft)]`}>The read stopped at its cap; later events in this slice are not shown.</span>
            ) : null}
            {!treeRows.length ? (
              <span className={`${META_CLASS} text-[color:var(--muted-soft)]`}>No payload detail for this slice.</span>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function FileLineage({ items, shownTurn, onPick, turns = null, autoOpenTurn = null }) {
  const tb = useContext(TurnBaseContext);
  const [showAll, setShowAll] = useState(false);
  // What the reader chose for a row. Without a choice a row opens where the
  // subject above showed nothing at the shown turn and stays closed elsewhere;
  // a choice outlasts that default, so a row they closed stays closed when the
  // playhead moves to another turn whose subject is empty too.
  const [chosen, setChosen] = useState(() => new Map());
  const rows = items || [];
  if (!rows.length) return null;
  const visible = showAll ? rows : rows.slice(-DOCK_LINEAGE_CAP);
  const hidden = rows.length - visible.length;
  return (
    <div className="min-w-0 border-t border-[color:var(--line-soft)] px-[9px] py-[5px]">
      <span className={EYEBROW_CLASS}>Lineage</span>
      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className={`${META_CLASS} ml-[8px] cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]`}
        >
          +{hidden} earlier
        </button>
      ) : null}
      {visible.map((it, i) => {
        const key = `${it.turnIndex}:${it.blockIndex}`;
        const canOpen = Boolean(turns) && it.turnIndex != null && it.blockIndex != null;
        const byDefault = autoOpenTurn != null && it.turnIndex === autoOpenTurn;
        const open = canOpen && (chosen.has(key) ? chosen.get(key) : byDefault);
        return (
          <div key={i} className="mt-[2px] min-w-0">
            <div className="flex min-w-0 items-baseline gap-[4px]">
              <button
                type="button"
                data-file-lineage-toggle=""
                disabled={!canOpen}
                aria-expanded={open}
                aria-label={open ? "Hide the call and what came back" : "Show the call and what came back"}
                onClick={() => setChosen((cur) => new Map(cur).set(key, !open))}
                className={`grid h-[14px] w-[14px] shrink-0 place-items-center self-center p-0 text-[color:var(--muted)] ${
                  canOpen ? "cursor-pointer hover:text-[color:var(--ink)]" : "cursor-default opacity-0"
                }`}
              >
                <LaneChevron open={open} />
              </button>
              <button
                type="button"
                data-file-lineage-op=""
                disabled={it.turnIndex == null}
                onClick={() => it.turnIndex != null && onPick(it.turnIndex)}
                className={`flex min-w-0 flex-1 items-baseline gap-[8px] p-0 text-left ${
                  it.turnIndex == null ? "cursor-default" : "cursor-pointer"
                }`}
              >
                <span
                  className={`${DOCK_TEXT} w-[44px] shrink-0 text-right tabular-nums ${
                    it.turnIndex === shownTurn ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"
                  }`}
                >
                  T{it.turnIndex == null ? "\u2014" : tb + it.turnIndex}
                </span>
                <Mark kind={cellKind([it])} />
                <span
                  className={`min-w-0 flex-1 truncate ${DOCK_TEXT} ${
                    it.turnIndex === shownTurn ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"
                  }`}
                >
                  {opLine(it)}
                </span>
              </button>
            </div>
            {open ? <CallReading turns={turns} turnIndex={it.turnIndex} blockIndex={it.blockIndex} /> : null}
          </div>
        );
      })}
    </div>
  );
}

// The call behind one lineage op and what came back for it, as captured and
// whole: the evidence for whatever the subject above does or does not show.
function CallReading({ turns, turnIndex, blockIndex }) {
  const tb = useContext(TurnBaseContext);
  const read = useMemo(() => readCall(turns, turnIndex, blockIndex, Infinity), [turns, turnIndex, blockIndex]);
  if (!read) return null;
  return (
    <div data-file-lineage-call="" className="mb-[3px] ml-[18px] mt-[2px] min-w-0 rounded-[2px] bg-[color:var(--control-bg)] py-[2px]">
      <LabeledRow label={read.label} text={read.text} />
      {read.results.map((r, j) => (
        <LabeledRow key={j} label={r.label} text={r.text} failed={r.failed} meta={resultMeta(r, tb)} />
      ))}
    </div>
  );
}

// A window of the reconstruction around what the hovered turn touched, for the
// hover card: accumulated text with provenance tints, no interactivity.
function excerptRows(rec, turn) {
  const rows = rec.rows.filter((r) => r.no != null);
  if (!rows.length) return null;
  let focus = rows.findIndex((r) => (r.entry?.touches || []).some((op) => op && op.promptId === turn));
  if (focus < 0) focus = 0;
  const start = Math.max(0, Math.min(focus - 4, rows.length - 12));
  const slice = rows.slice(start, start + 12);
  return { slice, before: start, after: Math.max(0, rows.length - start - slice.length) };
}

// The influence half of the hover card: what fed this mark and what it fed,
// each with the EVIDENCE that made the claim — the line on the plane says
// where the other end is, this says why the two are linked at all.
const INFLUENCE_CARD_CAP = 3;

// Each tier is a DIFFERENT comparison, and the evidence only makes sense next
// to the one that produced it: an identity that appeared in the other's text, a
// verbatim run of prose, rare tokens both hold, or — for a consultation, where
// nothing transferred at all — what the source op went looking for.
const EVIDENCE_VERB = { cited: "names", quoted: "quotes", entity: "shares", adjacent: "looked for" };

// Hardest claim first. Anything that KEEPS only some of a cell's links — the
// card's three rows, the overlay's draw cap — orders by this, never by the
// order the model happens to hand them over in.

function evidenceLine(link) {
  const verb = EVIDENCE_VERB[link.tier] || "matched";
  const body = link.tier === "quoted"
    ? `"${clip(link.evidence[0], 64)}"`
    : link.evidence.map((e) => clip(e, 40)).join(", ");
  return `${verb} ${body}`;
}

function InfluenceRows({ label, links, endOf, laneById }) {
  const tb = useContext(TurnBaseContext);
  if (!links.length) return null;
  // STRONGEST first, and sorted here rather than relying on the order they
  // arrive in: the model sorts links weakest-first so the hardest claims paint
  // on top on the plane, which is the exact opposite of what a list truncated
  // to three should keep. Reading the draw order as a display order hid the one
  // cited or quoted link behind "+1" while showing two consultations.
  const shown = [...links]
    .sort((a, b) => (TIER_STRENGTH[a.tier] ?? 9) - (TIER_STRENGTH[b.tier] ?? 9))
    .slice(0, INFLUENCE_CARD_CAP);
  return (
    <span className="grid gap-[1px]">
      <span className={EYEBROW_CLASS}>
        {label}
        {links.length > shown.length ? ` +${links.length - shown.length}` : ""}
      </span>
      {shown.map((link, i) => {
        const end = endOf(link);
        const other = laneById?.get(end.laneId);
        return (
          <span key={i} className="grid min-w-0 gap-[1px]">
            <span className="flex min-w-0 items-baseline gap-[6px] text-[10px] leading-[1.45] text-[color:var(--muted)]">
              <span className="w-[34px] shrink-0 font-mono tabular-nums">T{tb + end.turn}</span>
              <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">{other?.label || end.laneId}</span>
              <span className="shrink-0" style={{ color: TIER_COLOR[link.tier] }}>
                {TIER_LABEL[link.tier] || link.tier}
              </span>
            </span>
            {/* The evidence sits under the row it belongs to, led by the VERB
                of the rule that found it — a tier name alone ("related") says
                nothing about what was actually compared, and these four rules
                are not variations on one claim. A consultation has no transfer
                to show at all, so what it carries is the source's own stated
                intent, which is why it reads as a search rather than a match. */}
            {link.evidence.length ? (
              <span className="min-w-0 truncate pl-[40px] font-mono text-[9px] leading-[1.5] text-[color:var(--muted-soft)]">
                {evidenceLine(link)}
              </span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

// The hover reading of one mark: the subject's ACCUMULATED state as of the
// hovered turn (a tinted excerpt of the reconstruction for file lanes), the
// lineage of operations that led to it, and what fed it or it fed. Mounted
// lazily — HoverTooltip only renders its label when shown, so the replay runs
// on hover, not per cell.
function LaneCellCard({ lane, turn, firstTurn = null, turns = null, influence = null, laneById = null, narration = null, stamps = null }) {
  const tb = useContext(TurnBaseContext);
  const isFile = lane.domain === "files";
  const rec = useMemo(() => (isFile ? reconstructLaneAsOf(lane, turn) : null), [isFile, lane, turn]);
  const lineage = useMemo(
    () => lane.items.filter((it) => it.turnIndex == null || it.turnIndex <= turn),
    [lane, turn],
  );
  // A non-file mark's op line can be as thin as the tool's name (residual
  // lanes carry nothing else) — recover the actual call from the raw blocks
  // so the hover always shows what ran, not just what it was called.
  const isProseLane = isConversationDomain(lane.domain);
  const calls = useMemo(() => {
    if (isFile || isProseLane || !turns) return [];
    return (lane.cells.get(turn) || [])
      .filter((it) => it.turnIndex != null)
      .slice(0, 2)
      .map((it) => readToolCall(turns, it.turnIndex, it.blockIndex))
      .filter(Boolean);
  }, [isFile, isProseLane, turns, lane, turn]);
  const beatText = isProseLane ? (lane.cells.get(turn) || [])[0]?.op?.text : null;
  // What the agent said it was doing when this mark was made. A work mark alone
  // says a call happened at a turn; the note is where the reader learns why it
  // ran — the reading the narration lane makes scannable, delivered at the mark
  // being inspected instead of one row away.
  const note = useMemo(() => {
    // No early return on empty narration: a call that carries its own
    // description has a note even in a trace where the agent never spoke.
    if (isProseLane) return null;
    const first = (lane.cells.get(turn) || [])[0];
    return first ? noteFor(turns, narration, first.turnIndex, first.blockIndex) : null;
  }, [isProseLane, turns, narration, lane, turn]);
  const influenceHere = useMemo(
    () => (influence ? linksAt(influence, { laneId: lane.id, fromTurn: firstTurn ?? turn, toTurn: turn }) : null),
    [influence, lane.id, firstTurn, turn],
  );
  const ex = rec && rec.rows.length ? excerptRows(rec, turn) : null;
  const shown = lineage.slice(-7);
  const spanLabel = firstTurn != null && firstTurn !== turn ? `T${tb + firstTurn}–T${tb + turn}` : `as of T${tb + turn}`;
  // The mark spans turns, so the reading it carries is the span's, and a span
  // whose ends were recorded differently says so instead of taking one end's.
  const stamp = stamps ? rangeStamp(stamps, firstTurn ?? turn, turn) : undefined;
  return (
    <span className="grid min-w-0 gap-[5px] py-[2px]">
      <span className="flex min-w-0 items-baseline gap-[6px]">
        <span className="min-w-0 truncate font-mono text-[11px] text-[color:var(--ink)]">{lane.path || lane.label}</span>
        <span className="shrink-0 text-[10px] text-[color:var(--muted)]">{spanLabel}</span>
        <TraceStamp stamp={stamp} className="ml-auto" />
      </span>
      {isFile && rec ? (
        <span className="text-[10px] leading-[1.3] text-[color:var(--muted)]">
          {rec.known
            ? `${rec.known} lines known`
            : (lane.cells.get(turn) || []).some((it) => it.op?.observedEmpty)
              ? "the command returned nothing for this file"
              : "no content observed yet"}
          {rec.blindEdits ? ` · ${rec.blindEdits} unplaced hunk${rec.blindEdits === 1 ? "" : "s"}` : ""}
        </span>
      ) : null}
      {beatText ? (
        <span className="block min-w-0 whitespace-pre-wrap rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[7px] py-[4px] text-[10px] leading-[1.5] text-[color:var(--ink)]">
          {clip(beatText, 300)}
        </span>
      ) : null}
      {note ? (
        <span className="grid min-w-0 gap-[1px]">
          <span className={EYEBROW_CLASS}>{note.thinking ? "Thinking" : note.described ? "Described" : "Said"}{note.turnIndex !== turn ? ` · T${tb + note.turnIndex}` : ""}</span>
          <span className="block min-w-0 whitespace-pre-wrap rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[7px] py-[4px] text-[10px] leading-[1.5] text-[color:var(--muted)]">
            {clip(note.text, 260)}
          </span>
        </span>
      ) : null}
      {calls.length ? (
        <span className="grid min-w-0 gap-[3px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[7px] py-[4px]">
          {calls.map((c, i) => (
            <span key={i} className="grid min-w-0 gap-[1px]">
              {/* [overflow-wrap:anywhere], not break-words: these are JSON
                  payloads with hundred-char unbroken tokens — break-word
                  neither breaks them reliably nor shrinks the min-content
                  that lets the card's own width clamp hold. */}
              <span className={`min-w-0 font-mono text-[10px] leading-[1.5] [overflow-wrap:anywhere] ${c.failed ? "text-[color:var(--red,#c0392b)]" : "text-[color:var(--ink)]"}`}>
                {clip(c.call || c.name, 220)}
              </span>
              {c.result ? <span className="min-w-0 font-mono text-[10px] leading-[1.5] [overflow-wrap:anywhere] text-[color:var(--muted)]">{clip(c.result, 160)}</span> : null}
            </span>
          ))}
        </span>
      ) : null}
      {ex ? (
        <span className="grid min-w-0 rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] py-[2px]">
          {ex.before > 0 ? (
            <span className="px-[7px] font-mono text-[9px] leading-[1.6] text-[color:var(--muted-soft)]">… {ex.before} earlier lines</span>
          ) : null}
          {ex.slice.map((r) => {
            const kind = classifyLine(r.entry);
            return (
              <span
                key={r.no}
                className="flex min-w-0 gap-[7px] px-[7px] font-mono text-[10px] leading-[1.55]"
                style={CARD_TINT[kind] ? { background: CARD_TINT[kind] } : undefined}
              >
                <span className="w-[26px] shrink-0 text-right tabular-nums text-[color:var(--muted-soft)]">{r.no}</span>
                <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">{r.text === "" ? " " : clip(r.text, 110)}</span>
              </span>
            );
          })}
          {ex.after > 0 ? (
            <span className="px-[7px] font-mono text-[9px] leading-[1.6] text-[color:var(--muted-soft)]">… {ex.after} more lines</span>
          ) : null}
        </span>
      ) : null}
      <span className="grid gap-[1px]">
        <span className={EYEBROW_CLASS}>Lineage</span>
        {lineage.length > shown.length ? (
          <span className="text-[9px] leading-[1.4] text-[color:var(--muted-soft)]">+{lineage.length - shown.length} earlier</span>
        ) : null}
        {shown.map((it, i) => (
          <span
            key={i}
            className={`flex min-w-0 items-baseline gap-[6px] text-[10px] leading-[1.45] ${
              it.turnIndex != null && it.turnIndex >= (firstTurn ?? turn) ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"
            }`}
          >
            <span className="w-[34px] shrink-0 font-mono tabular-nums">T{it.turnIndex == null ? "\u2014" : tb + it.turnIndex}</span>
            <span className="min-w-0 flex-1 truncate">{opLine(it)}</span>
          </span>
        ))}
      </span>
      {influenceHere?.incoming.length ? (
        <InfluenceRows label="Fed by" links={influenceHere.incoming} endOf={(l) => l.from} laneById={laneById} />
      ) : null}
      {influenceHere?.outgoing.length ? (
        <InfluenceRows label="Fed" links={influenceHere.outgoing} endOf={(l) => l.to} laneById={laneById} />
      ) : null}
      <span className="text-[9px] leading-[1.4] text-[color:var(--muted-soft)]">click the mark to open this state in the dock</span>
    </span>
  );
}

// The dock is ONE tabbed panel — the timeline's second reading surface, never
// a navigation. SUBJECT reads the selected lane as of the playhead (the as-of
// reconstruction, or the op stack); TURN reads the playhead turn itself, raw
// blocks as captured. "See this turn" is therefore a TAB, not a pivot to the
// Context view: the reading loads inline and the timeline keeps its zoom,
// One fetch shape for every thread surface: the run's sibling traces via the
// caller-supplied resolver, with load/error state the sections render from.
// Accepts both resolver shapes - a bare rows array, or { rows, truncated }
// (getTurnTraces caps its projections and says when the cap was hit).
function useThreadRows(threads) {
  const [state, setState] = useState({ status: "idle", rows: null, truncated: false });
  useEffect(() => {
    if (!threads?.resolve) return undefined;
    let on = true;
    setState({ status: "loading", rows: null, truncated: false });
    threads.resolve().then(
      (res) => {
        if (!on) return;
        const rows = Array.isArray(res) ? res : Array.isArray(res?.rows) ? res.rows : [];
        const truncated = Boolean(!Array.isArray(res) && res?.truncated);
        setState({ status: "ready", rows, truncated });
      },
      () => on && setState({ status: "error", rows: null, truncated: false }),
    );
    return () => {
      on = false;
    };
  }, [threads]);
  return state;
}

const threadTimeOf = (t) => {
  try {
    return new Date(t).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "";
  }
};

// One sibling-trace row: time, summary, click to hop; the trace being read
// is marked and inert.
function ThreadTraceRow({ row, current, onOpenTrace, indent = false }) {
  const isCurrent = current && row.traceId === current;
  return (
    <button
      type="button"
      data-turn-thread-trace={row.traceId}
      data-current={isCurrent || undefined}
      disabled={isCurrent}
      onClick={() => !isCurrent && onOpenTrace?.(row.traceId)}
      className={`${META_CLASS} flex min-w-0 items-baseline gap-[8px] p-0 text-left ${indent ? "pl-[18px]" : ""} ${
        isCurrent ? "cursor-default text-[color:var(--ink)]" : "cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)]"
      }`}
    >
      <span className="shrink-0 font-mono text-[10px] tabular-nums">{threadTimeOf(row.time)}</span>
      <span className="min-w-0 flex-1 truncate">{row.summary || "(not yet summarized)"}</span>
      {isCurrent ? <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">this trace</span> : null}
    </button>
  );
}

const LINEAGE_KIND_LABEL = {
  root: "first chain",
  fork: "forked",
  continuation: "chain break",
  // Display-only kinds resolved by opening verification (never produced by
  // the chain model itself).
  "verified-fork": "forked",
  "context-trim": "context trim",
  "trim-of-this": "trim of this trace",
  compaction: "compaction restart",
  "compaction-of-this": "compacted from this trace",
};
// What each lineage kind MEANS, shown on the kind chip - the panel's labels
// are inferences and must define themselves where they're read. Measured
// honesty bound (real 420-call fleet run): fresh-start FORKS are reliable
// (the system-prompt-sized cache signature is unambiguous), but SEGMENT
// IDENTITY is not - cache arithmetic is inexact in the wild, so no tolerance
// cleanly separates threads (loose merges parent/worker calls into one
// group, tight shatters one thread into dozens). Claim spawn events, never
// proven thread membership; exact threading arrives with the canonical
// store's chain tree, which is not yet queryable from the browser.
const LINEAGE_KIND_DEF = {
  root: "The run's first cache chain, usually where the user's prompt landed. A chain SEGMENT, not a proven conversation: calls from parallel look-alike threads can land in the same segment.",
  fork: "A fresh cache start mid-run. This matches both a sub-agent's first request and a thread re-sent after context editing. Its first stored request has not been read yet, so which one is unverified.",
  continuation: "A chain that appears mid-run already carrying a large context that matches no open thread. Usually a thread resuming after compaction, cache expiry, capture loss, or out-of-order upload. Not counted as a new agent.",
  "verified-fork": "A real spawned conversation: its first stored request opens with a user prompt (shown as this thread's label). Membership of later calls is still approximate.",
  "context-trim": "Not a new agent. The harness re-sent the same thread after dropping earlier turns or tool results to save context, so the cache restarted fresh. Verified from this chain's first stored request, which begins mid-conversation.",
  "trim-of-this": "This chain is the conversation you are reading, re-sent after context editing: its retained opening turns match this trace's turns verbatim. Its calls belong to this thread.",
  compaction: "Not a new agent. The conversation was compacted: a model-written summary of the earlier turns opens this fresh context window. The original turns live in the pre-compaction conversation.",
  "compaction-of-this": "This chain opens with a compaction summary that quotes this trace's content verbatim. It is this conversation, continued after compaction.",
};
const LINEAGE_ROWS_CAP = 20;

// The whole turn's threads, reachable from ANY of its traces — the hop a
// WORKER trace needs. A sub-agent's own conversation spawned nothing, so it
// has no agent lane to dock; this panel (opened from the API-calls chip)
// shows the turn's traces grouped into LINEAGES — the distinct conversations
// reconstructed from each call's cache-chain arithmetic (traceTurnLineage.js):
// the main thread first, then each fork in spawn order. "forked" is a
// confident fresh start; "chain break" is a large-context chain that matched
// nothing (cache expiry / compaction) and is NOT claimed as a spawn. Falls
// back to the flat time-ordered list when the token bands aren't there to
// chain on.
function TurnThreadsPanel({ threads, rates = null, currentTurnKeys = null, currentText = "", height = 240, onResizeStart = null }) {
  const state = useThreadRows(threads);
  const rows = state.rows || [];
  const current = String(threads?.currentTraceId || "");
  const currentTime = useMemo(() => {
    const r = rows.find((x) => String(x?.traceId) === current);
    return r ? Number(r.time) || 0 : 0;
  }, [rows, current]);
  const model = useMemo(() => chainTurnLineages(rows), [rows]);
  // Verify each segment's opening from its first call's stored request: a
  // user prompt proves a real spawned conversation (and becomes the thread's
  // label); a mid-conversation start proves a context-edited re-send. LAST
  // calls are read too: at a compaction seam the summarizer's token-only
  // capture misjoins its bands onto the new window's first call, which
  // arithmetic then files as an old chain's last row - its opening is the
  // evidence that re-homes it. One bounded id-scoped fetch, after the groups
  // have already rendered.
  const [openings, setOpenings] = useState(null);
  useEffect(() => {
    if (!model || !threads?.resolveOpenings) return undefined;
    let on = true;
    const ids = [];
    for (const L of model.lineages) {
      if (L.rows[0]?.traceId) ids.push(L.rows[0].traceId);
      if (L.rows.length > 1 && L.rows[L.rows.length - 1]?.traceId) ids.push(L.rows[L.rows.length - 1].traceId);
    }
    const unique = [...new Set(ids)].slice(0, 48);
    if (!unique.length) return undefined;
    threads.resolveOpenings(unique).then(
      (map) => on && setOpenings(map || {}),
      () => on && setOpenings({}),
    );
    return () => {
      on = false;
    };
  }, [model, threads]);
  // Membership correction from content: banner-opening rows filed into a
  // foreign chain move to the chain that shares their summary. Derived from
  // the ORIGINAL model's openings so a correction cannot trigger a refetch.
  const displayModel = useMemo(
    () => (model && openings ? evictMisfiledCompactionRows(model, openings) : model),
    [model, openings],
  );
  // Expansion state keys on lineage index; the lineage being read opens
  // itself so "where am I" never needs a click.
  const [expanded, setExpanded] = useState(null);
  const currentLineage = useMemo(
    () => (displayModel ? displayModel.lineages.findIndex((L) => L.rows.some((r) => r.traceId === current)) : -1),
    [displayModel, current],
  );
  const isExpanded = (i) => (expanded ? expanded.has(i) : i === currentLineage);
  const toggle = (i) => {
    setExpanded((cur) => {
      const next = new Set(cur ?? (currentLineage >= 0 ? [currentLineage] : []));
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };
  return (
    <div data-turn-threads="" className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[9px] py-[5px]">
      <div className="flex items-baseline gap-[8px]">
        <span className={EYEBROW_CLASS}>{displayModel ? "Threads in this run" : "Traces in this run"}</span>
        <span className="min-w-0 truncate text-[10px] leading-none text-[color:var(--muted-soft)]">
          {displayModel
            ? "chain segments inferred from cache arithmetic. Fork starts are reliable; call membership is approximate"
            : "every API call is its own trace, including sub-agent calls"}
        </span>
      </div>
      {state.truncated ? (
        <span data-threads-truncated="" className={`${META_CLASS} block pt-[2px] text-[color:var(--amber,#c8922e)]`}>
          This run exceeds the 1,000-call fetch limit. Later calls are missing, so threads and counts are incomplete.
        </span>
      ) : null}
      {state.status === "loading" ? (
        <span className={`${META_CLASS} block pt-[4px] text-[color:var(--muted)]`} aria-busy="true">
          Finding this run's traces…
        </span>
      ) : state.status === "error" ? (
        <span className={`${META_CLASS} block pt-[4px] text-[color:var(--muted)]`}>Sibling-trace lookup failed.</span>
      ) : displayModel ? (
        <ScrollArea maxHeight={`${height}px`} className="mt-[3px]">
          <div className="grid gap-[2px]">
            {displayModel.lineages.map((L, i) => {
              const holdsCurrent = i === currentLineage;
              const open = isExpanded(i);
              const shown = open ? L.rows.slice(0, LINEAGE_ROWS_CAP) : [];
              // The hop target is the thread's LAST trace: each call's stored
              // conversation is a prefix of the next, so the last one is the
              // whole thread.
              const fullest = L.rows[L.rows.length - 1];
              // Opening verification upgrades the display kind and the label.
              const opening = openings?.[L.rows[0]?.traceId] || null;
              let kind = L.kind === "fork" && opening
                ? (opening.kind === "prompt" ? "verified-fork" : opening.kind === "mid" ? "context-trim" : "fork")
                : L.kind;
              // A compaction restart looks like a fresh start (fork) or a
              // large unmatched chain (continuation) to cache arithmetic;
              // the banner in its opening overrides both.
              if (opening?.kind === "compaction" && (L.kind === "fork" || L.kind === "continuation")) {
                kind = "compaction";
              }
              if (kind === "context-trim" && currentTurnKeys?.length
                  && matchesTrimmedFrom(opening?.turnKeys, currentTurnKeys).matched) {
                kind = "trim-of-this";
              }
              // Direction guard: summaries quote user messages, and each
              // window carries those quotes forward into the next summary, so
              // an ANCESTOR chain (or the chain holding this very trace)
              // matches too. Only a window opened after this call can have
              // been compacted from the conversation being read.
              if (kind === "compaction" && currentText && currentTime > 0 && L.start > currentTime
                  && matchesCompactedFrom(opening?.compactionFragments, currentText).matched) {
                kind = "compaction-of-this";
              }
              const label = (opening?.kind === "prompt" && opening.text) || L.rows[0].summary || "(not yet summarized)";
              const spawnedHere = kind === "verified-fork" && matchesSpawnPrompt(opening?.text, threads?.spawnPrompts);
              // Exact spend for the calls in this segment - real per-call
              // bands priced at catalog rates, no calibration involved.
              const cost = rates
                ? L.rows.reduce((a, r) => a
                    + (Number(r.input) || 0) * rates.input
                    + (Number(r.output) || 0) * rates.output
                    + (Number(r.cacheRead) || 0) * rates.cacheRead
                    + (Number(r.cacheWrite) || 0) * rates.cacheWrite, 0) / 1_000_000
                : null;
              return (
                <div key={i} data-lineage-group={i} data-lineage-kind={kind} className="min-w-0">
                  <div
                    className={`flex min-w-0 items-baseline gap-[8px] rounded-[2px] px-[4px] py-[2px] ${L.kind === "root" ? "" : "ml-[14px]"}`}
                    style={{ background: "color-mix(in srgb, var(--control-bg) 72%, transparent)" }}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(i)}
                      aria-expanded={open}
                      aria-label={`${open ? "Collapse" : "Expand"} thread: ${LINEAGE_KIND_LABEL[kind]}, ${L.calls} calls`}
                      className={`${META_CLASS} flex min-w-0 flex-1 cursor-pointer items-baseline gap-[8px] p-0 text-left`}
                    >
                      <span className="w-[10px] shrink-0 text-[color:var(--muted)]"><LaneChevron open={open} /></span>
                      <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                        {threadTimeOf(L.start)}
                      </span>
                      <HoverTooltip wide placement="bottom" label={LINEAGE_KIND_DEF[kind] || ""}>
                        <span
                          tabIndex={0}
                          className={`shrink-0 cursor-help rounded-[2px] border px-[4px] text-[10px] leading-[1.4] ${
                            kind === "verified-fork" || kind === "trim-of-this" || kind === "compaction-of-this"
                              ? "border-[color:var(--green)] text-[color:var(--green)]"
                              : kind === "fork"
                                ? "border-[color:var(--eyebrow)] text-[color:var(--eyebrow)]"
                                : kind === "root"
                                  ? "border-[color:var(--line-strong)] text-[color:var(--ink)]"
                                  : "border-[color:var(--line)] text-[color:var(--muted-soft)]"
                          }`}
                        >
                          {LINEAGE_KIND_LABEL[kind] || kind}
                        </span>
                      </HoverTooltip>
                      {spawnedHere ? (
                        <HoverTooltip wide placement="bottom" label="This thread's opening prompt matches an Agent call issued by the conversation you are reading. It was spawned from here.">
                          <span tabIndex={0} data-spawned-here="" className="shrink-0 cursor-help rounded-[2px] border border-[color:var(--green)] px-[4px] text-[10px] leading-[1.4] text-[color:var(--green)]">
                            spawned by this trace
                          </span>
                        </HoverTooltip>
                      ) : null}
                      <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{`${L.calls} call${L.calls === 1 ? "" : "s"}`}</span>
                      {cost != null && cost > 0 ? (
                        <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{`~${formatUsd(cost)}`}</span>
                      ) : null}
                      <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">{label}</span>
                      {holdsCurrent ? (
                        <HoverTooltip
                          wide
                          placement="bottom"
                          label="The API call this trace records sits in this segment. The call count is the segment's, not the conversation's. A long conversation fragments across segments whenever context editing restarts its cache chain."
                        >
                          <span tabIndex={0} className="shrink-0 cursor-help text-[10px] text-[color:var(--muted-soft)]">this trace</span>
                        </HoverTooltip>
                      ) : null}
                    </button>
                    {fullest?.traceId && fullest.traceId !== current ? (
                      <button
                        type="button"
                        data-lineage-open={i}
                        onClick={() => threads.onOpenTrace?.(fullest.traceId)}
                        aria-label={`Open this thread's trace`}
                        className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[5px] text-[10px] leading-[1.5] text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                      >
                        open
                      </button>
                    ) : null}
                  </div>
                  {open ? (
                    <div
                      className={`grid gap-[1px] border-l border-[color:var(--line)] py-[2px] pl-[10px] ${L.kind === "root" ? "ml-[9px]" : "ml-[23px]"}`}
                    >
                      {shown.map((r) => (
                        <ThreadTraceRow key={r.traceId} row={r} current={current} onOpenTrace={threads.onOpenTrace} />
                      ))}
                      {L.rows.length > shown.length ? (
                        <span className="text-[10px] leading-[1.3] text-[color:var(--muted-soft)]">{`+${L.rows.length - shown.length} more calls in this thread. "open" reads the whole thread as one trace`}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {displayModel.unchained.length ? (
              <span className="pt-[2px] text-[10px] leading-[1.3] text-[color:var(--muted-soft)]">
                {`${displayModel.unchained.length} call${displayModel.unchained.length === 1 ? "" : "s"} had no token observation to chain on`}
              </span>
            ) : null}
          </div>
        </ScrollArea>
      ) : rows.length ? (
        <ScrollArea maxHeight={`${height}px`} className="mt-[3px]">
          <div className="grid gap-[1px]">
            {rows.map((r) => (
              <ThreadTraceRow key={r.traceId} row={r} current={current} onOpenTrace={threads.onOpenTrace} />
            ))}
          </div>
        </ScrollArea>
      ) : state.status === "ready" ? (
        <span className={`${META_CLASS} block pt-[4px] text-[color:var(--muted)]`}>No sibling traces captured for this run.</span>
      ) : null}
      {onResizeStart ? (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize threads panel"
          data-threads-divider=""
          onPointerDown={onResizeStart}
          onMouseDown={(e) => e.preventDefault()}
          className="group -mb-[3px] flex h-[10px] cursor-row-resize select-none items-center justify-center"
          style={{ touchAction: "none" }}
        >
          <span className="h-[3px] w-[48px] rounded-full bg-[color:var(--line)] transition-colors group-hover:bg-[color:var(--line-strong)]" />
        </div>
      ) : null}
    </div>
  );
}

// scroll and selection. A deliberate tab strip (not two buttons) so further
// readings can join it without changing the dock's shape — and because a TURN
// reading exists for every point on the axis, any selection has content even
// where no extractor claimed anything.
function LanesDock({
  turns,
  turnStamps = [],
  turnCount,
  beats,
  narration = [],
  readingHarvest = null,
  lane,
  system = null,
  onScrubSystem = null,
  turn,
  tab,
  traceTimestamp,
  endpointId = null,
  sessionId = null,
  onOpenTrace = null,
  onScopeNavigate = null,
  threads = null,
  joins = null,
  joinThrough = null,
  findings = null,
  findingIncidentOf = null,
  findingThrough = null,
  graph = null,
  graphThrough = null,
  graphClaim = null,
  onPickGraph = null,
  onHoverClaim = null,
  onOpenGraphChat = null,
  reportsTimeline = null,
  reviewProfile = null,
  reportIndex = null,
  joinHues = null,
  joinSearchWindow = null,
  onOpenJoin = null,
  onLayJoin = null,
  onLocateJoin = null,
  followKey = null,
  onScrubLane,
  onNavTurn,
  onSetTab,
  onSelectLane,
  // (laneId, turn) -> the other lanes the calls behind that cell touched.
  sameCall = null,
  onSelectCell = null,
  onHighlight,
  hasLane = () => false,
  siblingLanes = [],
  onClose,
  // Keeps this cell's reading on a canvas's board, under its card.
  keepAction = null,
  layAction = null,
  // What the trace this reads is called, for what it puts into a chat.
  sessionTitle = null,
  fill = false,
  height = null,
  // A tab of the permanent dock: the dock owns the frame, the height and the
  // close, so this renders only its own header and body.
  bare = false,
}) {
  const tb = useContext(TurnBaseContext);
  const joinsAt = useMemo(
    () => mergeJoinRange(joins, turn, Math.max(turn, joinThrough ?? turn)),
    [joins, turn, joinThrough],
  );
  const findingsAt = useMemo(
    () => findingsInRange(findings, turn, Math.max(turn, findingThrough ?? turn)),
    [findings, turn, findingThrough],
  );
  const hasReports = !!reportsTimeline?.reports?.length;
  // The chapter the dock is open on: the one clicked, else the one whose
  // window holds the docked turn, else none outlined.
  const activeReport = useMemo(
    () => reportIndex ?? (reportsAt(reportsTimeline, turn)[0]?.index ?? null),
    [reportsTimeline, reportIndex, turn],
  );
  // The graph tab is there whenever a chat's graph is on the trace: besides the
  // docked turn it lists every claim about the trace, placed or not.
  const hasGraph = !!graph;
  const activeTab = (lane || system) && tab === "subject"
    ? "subject"
    : tab === "reports" && hasReports ? "reports"
      : tab === "findings" && findingsAt.length ? "findings"
        : tab === "graph" && hasGraph ? "graph"
          : tab === "joins" && joinsAt ? "joins"
            : tab === "raw" ? "raw" : "turn";
  const isFile = lane?.domain === "files";
  const passed = lane ? lane.turns.filter((t) => t <= turn) : [];
  const prevTurn = passed.length > 1 ? passed[passed.length - 2] : null;
  const laterTurns = lane ? lane.turns.filter((t) => t > turn) : [];
  const nextTurn = laterTurns.length ? laterTurns[0] : null;
  const shownTurn = passed.length ? passed[passed.length - 1] : null;

  const itemsUpTo = useMemo(
    () => (lane ? lane.items.filter((it) => it.turnIndex == null || it.turnIndex <= turn) : []),
    [lane, turn],
  );
  const rec = useMemo(() => (lane && isFile ? reconstructLaneAsOf(lane, turn) : null), [lane, isFile, turn]);
  // The content-probe source is the file at its FULLEST reconstruction (the
  // lane's last occupied turn), not the scrub position: the probes ask "did
  // this file's content travel elsewhere", and the answer should not change
  // because the reader happens to be parked mid-history.
  const probeLines = useMemo(() => {
    if (!lane || !isFile || !lane.turns?.length) return null;
    return linesFromReconstruction(reconstructLaneAsOf(lane, lane.turns[lane.turns.length - 1]));
  }, [lane, isFile]);
  const strips = useMemo(() => (isFile ? fileBasis(rec, itemsUpTo) : []), [isFile, rec, itemsUpTo]);
  // Where to look when a file has no captured body. Directory activity, never
  // this file's provenance: a script that names its own outputs internally
  // leaves nothing on the command line to attribute them with.
  // How the op at the playhead produced this file, when a shell command wrote it.
  // The op line names only the statement that mentioned the path; the steps that
  // made the bytes are elsewhere in the same captured command.
  // The same path spellings extractShellWriteOps resolved this write against.
  // Without them a target that only resolved through the basename fallback is
  // invisible to the derivation's own search for the writing statement, and the
  // panel silently disappears for exactly the writes that needed the fallback to
  // be attributed at all.
  const knownPaths = useMemo(() => {
    const m = new Map();
    for (const op of extractFileOps(turns || [])) {
      if (op.path) m.set(pathKey(op.path), op.path);
    }
    return m;
  }, [turns]);
  // How this file was produced, and by which spelling of it.
  //
  // A Cowork trace records the write under the CONTAINER path and the reads
  // under the HOST one, so the row a reader opens is usually the one with no
  // write on it at all. The rows stay separate - that is the whole point of the
  // relation - but the derivation belongs to the file, not to the spelling, so a
  // lane with no write of its own falls back to its twin's and says whose it is.
  const derivation = useMemo(() => {
    const none = { chain: [], turn: null, via: null, at: null };
    if (!isFile || !lane?.path) return none;
    const shellOpBefore = (l, upto) => {
      let best = null;
      for (const it of l?.items || []) {
        if (!it.op?.shell || it.turnIndex == null || it.turnIndex > upto) continue;
        if (!best || it.turnIndex > best.turnIndex) best = it;
      }
      return best;
    };
    const candidates = [{ lane, own: true }, ...siblingLanes.map((l) => ({ lane: l, own: false }))];
    for (const { lane: src, own } of candidates) {
      const op = shellOpBefore(src, turn);
      if (!op) continue;
      const command = shellCommandOf(turns?.[op.turnIndex]?.blocks?.[op.blockIndex]);
      if (!command) continue;
      const chain = commandDerivation(command, src.path, knownPaths);
      // `at` is the lane the command's own call sits on, which is not always the
      // lane the reader opened: a Cowork write is recorded under the container
      // path, so a chain reached through the twin runs in the TWIN's row. Every
      // step of the chain shares that one cell, and it is the only mark a step
      // can always make.
      if (chain.length > 1) return { chain, turn: op.turnIndex, via: own ? null : src.path, at: src.path };
    }
    return none;
  }, [isFile, lane, siblingLanes, turn, turns, knownPaths]);
  const originHints = useMemo(() => {
    if (!isFile || !lane?.path || rec?.rows?.length) return [];
    const claimed = itemsUpTo.map((it) => it.op?.shell?.statement).filter(Boolean);
    return shellOriginHints(turns, lane.path, { beforeTurn: turn, exclude: claimed });
  }, [isFile, lane, rec, turns, turn, itemsUpTo]);
  const traceOf = useMemo(() => {
    const m = new Map();
    for (const it of itemsUpTo) {
      if (it.turnIndex != null) {
        m.set(it.turnIndex, { summary: `Turn ${tb + it.turnIndex} — ${it.op?.kind || it.domain}`, timestamp: it.op?.ts ?? traceTimestamp ?? undefined });
      }
    }
    return m;
  }, [itemsUpTo, traceTimestamp]);
  // A turn opened in the dock reads whole, and so does what a chat is handed
  // of it: nothing here is cut short.
  const read = useMemo(() => readTurn(turns, turn, Infinity, { results: true }), [turns, turn]);
  // The same turn as the record holds it, for the Raw tab.
  const raw = useMemo(() => (activeTab === "raw" ? rawTurn(turns, turn) : null), [activeTab, turns, turn]);
  // Stepping turns from the Raw tab stays on it; every other way to a turn
  // opens its reading.
  const navTurn = (t) => {
    onNavTurn(t);
    if (activeTab === "raw") onSetTab("raw");
  };
  const beat = beats.get(turn);

  const cellItems = lane && !isFile ? (shownTurn != null ? lane.cells.get(shownTurn) || [] : []) : null;
  // A file lane's note is anchored on the op AT the playhead, so it moves as the
  // reader scrubs — the same pairing the op rows below get, sited where a file
  // reading can carry it.
  const fileNote = useMemo(() => {
    if (!isFile || shownTurn == null) return null;
    const first = (lane?.cells.get(shownTurn) || [])[0];
    return first ? noteFor(turns, narration, first.turnIndex, first.blockIndex) : null;
  }, [isFile, turns, lane, shownTurn, narration]);
  // What else the calls behind the shown cell touched: the other files of a
  // batched read, the subjects of a code cell's other inner calls. Header
  // level, like "Same file as", so a reader never scrolls past a body to find
  // the rest of the action.
  const sameCallList = useMemo(
    () => (lane && shownTurn != null && sameCall ? sameCall(lane.id, shownTurn) : []),
    [lane, shownTurn, sameCall],
  );
  const sameCallRow = sameCallList.length ? (
    <div className={`flex min-w-0 items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[4px] ${EYEBROW_BASE} text-[color:var(--eyebrow)]`}>
      <span className="shrink-0 whitespace-nowrap">Same call</span>
      {/* Inline and wrapping: a call that touched eight subjects is two lines
          here, not eight stacked above the body it is about. */}
      <span className="flex min-w-0 flex-1 flex-wrap gap-x-[12px] gap-y-[1px]">
        {sameCallList.slice(0, SAME_CALL_CAP).map((s) => (
          <button
            key={s.laneId}
            type="button"
            data-same-call={s.laneId}
            onClick={() => onSelectCell?.(s.laneId, s.turn)}
            className={`${META_CLASS} min-w-0 max-w-full cursor-pointer truncate p-0 text-left font-mono normal-case tracking-normal text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline`}
          >
            <span className="text-[color:var(--muted-soft)]">{s.verb} </span>
            {s.label}
            {s.model ? <span className="text-[color:var(--muted-soft)]"> {"\u00b7"} model</span> : null}
          </button>
        ))}
        {sameCallList.length > SAME_CALL_CAP ? (
          <span className={`${META_CLASS} normal-case tracking-normal text-[color:var(--muted-soft)]`}>
            +{sameCallList.length - SAME_CALL_CAP} more
          </span>
        ) : null}
      </span>
    </div>
  ) : null;
  const navBtn = "grid h-[18px] w-[18px] place-items-center rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[11px] leading-none text-[color:var(--muted)] cursor-pointer hover:text-[color:var(--ink)] disabled:cursor-default disabled:opacity-40";
  const tabBtn = (id, label) => (
    <button
      key={id}
      type="button"
      data-dock-tab-btn={id}
      onClick={() => onSetTab(id)}
      className={`${EYEBROW_BASE} ${
        activeTab === id ? "font-medium text-[color:var(--eyebrow)]" : "cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)]"
      }`}
    >
      {label}
    </button>
  );

  const bodyScroll = `min-w-0 overflow-y-auto ${fill ? "min-h-0 flex-1" : "max-h-[42vh]"}`;

  return (
    <div
      data-lanes-dock=""
      data-dock-tab={activeTab}
      className={bare
        ? "flex min-h-0 min-w-0 flex-1 flex-col"
        : "flex min-h-0 min-w-0 flex-none flex-col rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]"}
      style={fill && !bare ? (height != null ? { height } : { maxHeight: "46%" }) : undefined}
    >
      <div className="flex min-w-0 flex-none flex-wrap items-center gap-x-[10px] gap-y-[4px] border-b border-[color:var(--line-soft)] px-[9px] py-[5px]">
        <span className="flex shrink-0 items-center gap-[6px]">
          {lane || system ? tabBtn("subject", "Subject") : null}
          {lane || system ? <span className="text-[10px] leading-none text-[color:var(--line-strong)]">|</span> : null}
          {hasReports ? tabBtn("reports", "Reports") : null}
          {hasReports ? <span className="text-[10px] leading-none text-[color:var(--line-strong)]">|</span> : null}
          {findingsAt.length ? tabBtn("findings", "Findings") : null}
          {findingsAt.length ? <span className="text-[10px] leading-none text-[color:var(--line-strong)]">|</span> : null}
          {hasGraph ? tabBtn("graph", "Graph") : null}
          {hasGraph ? <span className="text-[10px] leading-none text-[color:var(--line-strong)]">|</span> : null}
          {joinsAt ? tabBtn("joins", "Hand-offs") : null}
          {joinsAt ? <span className="text-[10px] leading-none text-[color:var(--line-strong)]">|</span> : null}
          {tabBtn("turn", `Turn ${tb + turn}`)}
          <span className="text-[10px] leading-none text-[color:var(--line-strong)]">|</span>
          {tabBtn("raw", "Raw")}
        </span>
        {activeTab === "subject" ? (
          system ? (
            <>
              <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[color:var(--ink)]">{system.label}</span>
              <span className={`${META_CLASS} shrink-0 text-[color:var(--muted)]`}>
                {formatCompact(system.total)} event{system.total === 1 ? "" : "s"}
              </span>
              <span className="flex shrink-0 items-center gap-[6px]">
                <button type="button" className={navBtn} disabled={system.prevTurn == null} onClick={() => system.prevTurn != null && onScrubSystem(system.prevTurn)} aria-label="Previous slice">
                  ‹
                </button>
                <span data-system-slice="" className="font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                  {system.start === system.end ? `T${tb + system.start}` : `T${tb + system.start}–T${tb + system.end}`}
                </span>
                <TraceStamp stamp={rangeStamp(turnStamps, system.start, system.end)} />
                <button type="button" className={navBtn} disabled={system.nextTurn == null} onClick={() => system.nextTurn != null && onScrubSystem(system.nextTurn)} aria-label="Next slice">
                  ›
                </button>
              </span>
            </>
          ) : (
          <>
            <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[color:var(--ink)]">{lane.path || lane.label}</span>
            <span className={`${META_CLASS} shrink-0 text-[color:var(--muted)]`}>{lane.digest}</span>
            <span className="flex shrink-0 items-center gap-[6px]">
              <button type="button" className={navBtn} disabled={prevTurn == null} onClick={() => prevTurn != null && onScrubLane(prevTurn)} aria-label="Previous op">
                ‹
              </button>
              <span className="font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                {passed.length}/{lane.turns.length} · as of T{tb + turn}
              </span>
              <TraceStamp stamp={turnStamps[turn]} />
              <button type="button" className={navBtn} disabled={nextTurn == null} onClick={() => nextTurn != null && onScrubLane(nextTurn)} aria-label="Next op">
                ›
              </button>
            </span>
          </>
          )
        ) : (
          <>
            <span className={`${META_CLASS} min-w-0 flex-1 truncate text-[color:var(--muted)]`}>
              {raw ? (
                raw.oversized
                  ? `${raw.role} \u00b7 too large to read here`
                  : `${raw.role} \u00b7 ${raw.parts.length} part${raw.parts.length === 1 ? "" : "s"} as recorded`
              ) : read
                ? turns[turn]?.captured
                  // Not an echo from a later request body — this turn was
                  // observed as a response on the endpoint, and its provenance
                  // is different in kind from every other turn on the axis.
                  ? `${read.role} · ${read.own} block${read.own === 1 ? "" : "s"} · observed response${turns[turn]?.captureCompleteness === "partial" ? " (partial: text only)" : ""}`
                  : `${read.role} · ${read.own} block${read.own === 1 ? "" : "s"} as captured${beat?.prompt ? " · episode start" : beat?.reply ? " · episode outcome" : ""}`
                : "out of range"}
            </span>
            {read ? <TraceStamp stamp={turnStamps[turn]} /> : null}
            <span className="flex shrink-0 items-center gap-[6px]">
              <button type="button" className={navBtn} disabled={turn <= 0} onClick={() => navTurn(turn - 1)} aria-label="Previous turn">
                ‹
              </button>
              <span data-turn-of="" className="font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                {tb + turn + 1} of {tb + turnCount} message{tb + turnCount === 1 ? "" : "s"}
              </span>
              <button type="button" className={navBtn} disabled={turn >= turnCount - 1} onClick={() => navTurn(turn + 1)} aria-label="Next turn">
                ›
              </button>
            </span>
          </>
        )}
        {keepAction ? (
          <HoverTooltip placement="top" label="Keep this cell's reading under its card on the board">
            <button
              type="button"
              data-dock-keep=""
              onClick={keepAction}
              className="inline-flex h-[20px] shrink-0 cursor-pointer items-center gap-[5px] rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[7px] text-[11px] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
            >
              <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4.5 1.5h3l-.5 3 2 1.5H3l2-1.5zM6 6v4.5" /></svg>
              Keep on the board
            </button>
          </HoverTooltip>
        ) : null}
        {sessionId != null && read ? (
          <HoverTooltip placement="top" label="Puts this message into the chat in front, to ask about it there">
            <button
              type="button"
              data-dock-add-to-chat=""
              onClick={() => addToChat(cellChatItem({ sessionId, sessionTitle, turns, turn, base: tb, lane, read }))}
              className="inline-flex h-[20px] shrink-0 cursor-pointer items-center rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[7px] text-[11px] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
            >
              Add to chat
            </button>
          </HoverTooltip>
        ) : null}
        {layAction ? (
          <HoverTooltip placement="top" label="Lay this agent's own session on the board, beside the one that started it">
            <button
              type="button"
              data-dock-lay=""
              onClick={layAction}
              className="inline-flex h-[20px] shrink-0 cursor-pointer items-center rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[7px] text-[11px] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
            >
              Lay its session on the board
            </button>
          </HoverTooltip>
        ) : null}
        {bare ? null : (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close detail dock"
            className="shrink-0 cursor-pointer text-[12px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]"
          >
            ×
          </button>
        )}
      </div>
      {activeTab === "subject" ? (
        system ? (
          <SystemDockBody
            key={`${system.kind}-${system.start}-${system.end}`}
            system={system}
            turns={turns}
            onNavTurn={onNavTurn}
            bodyScroll={bodyScroll}
          />
        ) : isFile ? (
          passed.length === 0 ? (
            <span className={`${META_CLASS} block px-[9px] py-[6px] text-[color:var(--muted)]`}>
              This file is first touched at T{tb + lane.turns[0]}. Open the Turn tab to read that point in the conversation.
            </span>
          ) : (
            <>
              {/* The same file through the other mount. Its row stays its own -
                  this is the way across, and selecting it highlights the pair. */}
              {lane.sameFileAs?.length ? (
                <div className={`flex min-w-0 items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[4px] ${EYEBROW_BASE} text-[color:var(--eyebrow)]`}>
                  <span className="shrink-0 whitespace-nowrap">Same file as</span>
                  <span className="grid min-w-0 flex-1 gap-[1px]">
                    {lane.sameFileAs.map((p) => (
                      <button
                        key={p}
                        type="button"
                        data-same-file-as={p}
                        onClick={() => onSelectLane(`files:${p}`)}
                        className={`${META_CLASS} min-w-0 cursor-pointer truncate p-0 text-left font-mono normal-case tracking-normal text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline`}
                      >
                        {p}
                      </button>
                    ))}
                  </span>
                </div>
              ) : null}
              {sameCallRow}
              {/* Header level, beside "Same file as" and ABOVE the scrolling
                  body: both answer "where else does this file exist", and a
                  reader should not have to scroll past a whole file to ask.
                  Keyed on the lane because the dock does not remount when the
                  selection moves, so results would otherwise persist onto the
                  next file. */}
              <FileElsewhere
                key={`${lane.id}-elsewhere`}
                path={lane.path}
                endpointId={endpointId}
                sessionId={sessionId}
                traceTimestamp={traceTimestamp}
                onOpenTrace={onOpenTrace}
                probeLines={probeLines}
              />
              {strips.map((s, i) => (
                <StripRow key={i} label={s.label} detail={s.detail} tone={s.tone} />
              ))}
              {/* Why this file was touched at the playhead. A file lane's dock
                  is the reconstruction, not an op list, so the note has to be
                  stated here or it reaches every lane except the commonest. */}
              {fileNote ? (
                <StripRow
                  label={fileNote.thinking ? "Thinking" : fileNote.described ? "Described" : "Said"}
                  detail={`${fileNote.turnIndex !== shownTurn ? `T${tb + fileNote.turnIndex} · ` : ""}${clip(fileNote.text, 300)}`}
                />
              ) : null}
              <div data-lanes-scroll="" className={bodyScroll}>
                {rec.rows.length ? (
                  <AnnotatedFileBody
                    rec={rec}
                    traceOf={traceOf}
                    onSelectTrace={(t) => {
                      if (typeof t === "number") onScrubLane(t);
                    }}
                    cap={0}
                    filePath={lane.path}
                  />
                ) : null}
                {rec.blind?.length ? <DiffRows blind={rec.blind} /> : null}
                {/* Only when nothing above has already said WHY the body is
                    empty. A not-captured strip states the reason, and saying
                    it twice reads as two different problems. */}
                {!rec.rows.length && !rec.blind?.length && !strips.some((s) => s.notCaptured) ? (
                  <span className={`${META_CLASS} block px-[9px] py-[6px] text-[color:var(--muted)]`}>
                    {lane.items?.length && lane.items.every((it) => it.op?.observedEmpty)
                      ? "The commands on this lane ran and returned nothing - the empty output is itself the observation."
                      : "The trace records these ops but captured no content for them."}
                  </span>
                ) : null}
                {/* What produced the state above, per turn. Reads after the body
                    for a file that has one, and IS the reading for a file that
                    does not. */}
                {/* Keyed on the lane so `showAll` belongs to the FILE, not to
                    the dock: the dock does not remount when the selection moves
                    to another file, so an expansion made on one lineage was
                    carrying over and the next file opened fully expanded. The
                    key also keeps the expansion while scrubbing the SAME file,
                    which is the reader's choice to preserve. */}
                <FileLineage
                  key={lane.id}
                  items={itemsUpTo}
                  shownTurn={shownTurn}
                  onPick={onScrubLane}
                  turns={turns}
                  autoOpenTurn={rec.rows.length ? null : shownTurn}
                />
                {derivation.chain.length > 1 ? (
                  <div className="min-w-0 border-t border-[color:var(--line-soft)] px-[9px] py-[5px]">
                    <span className={EYEBROW_CLASS}>Derivation</span>
                    <span className={`${META_CLASS} mt-[1px] block text-[color:var(--muted-soft)]`}>
                      The steps of the command that wrote this file, in order. Each row names what it handed to the next
                      one and how the command tied them together.
                      {derivation.via ? ` The write is recorded under ${derivation.via}, the other name for this file.` : ""}
                    </span>
                    {derivation.chain.map((d, i) => {
                    // Every step produced something, and everything it produced
                    // eventually became a file the rail DOES carry - the chain
                    // ends at the write this lane exists for. So a step whose own
                    // artifact never became a row points at the first one
                    // downstream that did, and several steps collapsing onto one
                    // row is the honest shape rather than a dead hover.
                    // What the call came FROM: the script it ran, the source it
                    // copied, the input it converted - the paths the statement
                    // names, kept in the order it names them. This is the answer
                    // the reader wants first and the only one that distinguishes
                    // one step of a chain from the next, because every other mark
                    // here is shared by the whole command.
                    const from = (d.names || []).filter(hasLane);
                    // Where the step RAN. One `&&` run is one call in one cell,
                    // and that cell sits on the row the call was recorded under -
                    // for a Cowork write, the container mount rather than the
                    // host row the reader opened.
                    const ran = derivation.at;
                    // What the step's artifact BECAME, when the rail carries it.
                    // The weaker fact of the two: most of a build chain produces
                    // /tmp files nothing else in the trace touches, so this is
                    // frequently absent, and when present it is a name match
                    // rather than a record of the call.
                    const own = hasLane(d.produced);
                    const made = own
                      ? d.produced
                      : derivation.chain.slice(i + 1).map((n) => n.produced).find(hasLane) || null;
                    // What this step CONSUMED is the previous step's artifact -
                    // that is what "handed to the next one" means - and it is
                    // named right there in the command text. `node build_deck.js`
                    // says build_deck.js, which is usually a row, and pointing
                    // only at what a step produced left that unanswered.
                    const input = i > 0 ? derivation.chain[i - 1].produced : null;
                    // Call row first. Reaching it through the artifact left the
                    // middle of a chain dark - `python3 ... --convert-to pdf` and
                    // `pdftoppm` produce /tmp files nothing else in the trace
                    // touches, so they resolved to no row of their own and landed
                    // on the call's cell only by accident of the collapse above.
                    const marks = [...new Set(
                      [...from, ran, made, hasLane(input) ? input : null].filter(Boolean),
                    )];
                    return (
                      <div
                        key={i}
                        data-derivation-step={d.produced}
                        data-derivation-pointable={marks.length ? "" : undefined}
                        data-derivation-target={from[0] || ran || made || undefined}
                        // JSON, not a joined list: a host mount path is
                        // `.../Application Support/...`, so any separator that
                        // reads as ordinary punctuation splits one path into two.
                        data-derivation-marks={marks.length ? JSON.stringify(marks) : undefined}
                        // Pointing at a step points at the artifact it produced:
                        // the rail row for that path, and the mark on the turn
                        // this command ran. Several steps of one chain can be
                        // rows in the rail, and that correspondence is invisible
                        // until something draws it.
                        onMouseEnter={() => {
                          onHighlight?.({ paths: marks, turn: derivation.turn, commandTurn: derivation.turn });
                        }}
                        onMouseLeave={() => {
                          onHighlight?.(null);
                        }}
                        // Padding, not margin: the spine below is drawn
                        // inside this box and has to reach the box above
                        // it. A margin here is exactly the gap the typed
                        // glyphs used to leave.
                        className="relative grid min-w-0 gap-[1px] pt-[3px]"
                      >
                        {/* The tree, drawn rather than typed. A per-row
                            `├` breaks wherever the row box does, so the
                            column read as five detached marks instead of
                            one chain. The vertical spans the whole box so
                            consecutive steps meet with nothing between them,
                            and stops at the elbow at BOTH ends of the chain: a
                            trunk running past the first elbow has nothing above
                            to reach, and climbs into the paragraph above. */}
                        <span
                          aria-hidden
                          className="pointer-events-none absolute w-px bg-[color:var(--muted-soft)]"
                          style={{
                            left: TREE_X,
                            top: i === 0 ? TREE_ELBOW_Y : 0,
                            height: i === 0
                              ? `calc(100% - ${TREE_ELBOW_Y}px)`
                              : (i === derivation.chain.length - 1 ? TREE_ELBOW_Y : "100%"),
                          }}
                        />
                        <span
                          aria-hidden
                          className="pointer-events-none absolute h-px bg-[color:var(--muted-soft)]"
                          style={{ left: TREE_X, top: TREE_ELBOW_Y, width: TREE_TICK_W }}
                        />
                        <span className="flex min-w-0 items-baseline gap-[6px]">
                          {/* Which turn this line was read out of. Same gutter,
                              width and vocabulary as the lineage above, because
                              it answers the same question and the two lists are
                              read against each other. Every step of a chain
                              carries it rather than the section carrying it once:
                              a panel that names the turn only in its header
                              reads as if the steps came from somewhere else. */}
                          <span className="w-[30px] shrink-0 text-right font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                            T{tb + derivation.turn}
                          </span>
                          {/* Holds the drawn tree's column open. */}
                          <span aria-hidden className="w-[6px] shrink-0" />
                          <span className="min-w-0 flex-1 truncate font-mono text-[11px] leading-[1.5] text-[color:var(--ink)]">
                            {d.line}
                          </span>
                          <span className={`${META_CLASS} shrink-0 text-[color:var(--muted-soft)]`}>{DERIVE_LABEL[d.how] || d.how}</span>
                        </span>
                        {/* Indented past the turn gutter so the artifact line
                            sits under the statement it came from, not under the
                            turn that statement was read out of. */}
                        <span className="flex min-w-0 items-baseline gap-[6px] pl-[48px]">
                          <span
                            className={`min-w-0 truncate font-mono text-[10px] ${
                              made
                                ? "text-[color:var(--ink)] underline decoration-dotted underline-offset-2"
                                : "text-[color:var(--muted)]"
                            }`}
                          >
                            → {d.produced}
                          </span>
                          {made && !own ? (
                            <span className={`${META_CLASS} shrink-0 whitespace-nowrap text-[color:var(--muted-soft)]`}>
                              becomes {made.replace(/\\/g, "/").split("/").pop()}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    );
                    })}
                  </div>
                ) : null}
                {originHints.length ? (
                  <div className="min-w-0 border-t border-[color:var(--line-soft)] px-[9px] py-[5px]">
                    <span className={EYEBROW_CLASS}>Directory activity</span>
                    <span className={`${META_CLASS} mt-[1px] block text-[color:var(--muted-soft)]`}>
                      Shell statements that ran in this file's directory, newest first. These are where to look for
                      what produced this file, not claims that any of them wrote it.
                    </span>
                    {originHints.map((h, i) => (
                      <button
                        key={i}
                        type="button"
                        data-origin-hint=""
                        onClick={() => {
                          onNavTurn(h.turnIndex);
                          onSetTab("turn");
                        }}
                        className="mt-[3px] flex w-full min-w-0 cursor-pointer items-baseline gap-[8px] p-0 text-left"
                      >
                        <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">T{tb + h.turnIndex}</span>
                        <span className="min-w-0 flex-1 truncate font-mono text-[11px] leading-[1.5] text-[color:var(--ink)] hover:underline">
                          {h.statement}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )
        ) : (
          <>
          {sameCallRow}
          <div data-lanes-scroll="" className={`${bodyScroll} py-[2px]`}>
            {cellItems.length ? (
              cellItems.map((it, i) => {
                // The feed op is a CLAIM, not the content — a residual op in
                // particular carries only the tool's name. The call itself is
                // in the raw blocks, so recover it here: the command as
                // authored and the result as returned, never a bare label.
                // A conversation lane carries its text on the op instead.
                const isBeat = isConversationDomain(it.domain);
                // The same payload renderer the Context tab routes this item
                // through — the diff, the page, the query with its result
                // table — with the raw call/result rows as the fallback for
                // whatever it doesn't model. One trace, one item shape, one
                // reading in both tabs. `onNavTurn` is the real requestTurn:
                // a payload's jump-to-turn affordances open that turn in THIS
                // dock — a null here still built truthy onSelectTrace
                // closures downstream, so the buttons rendered live but
                // silently no-opped.
                // A verified reading's evidence IS its quote — the exact bytes
                // the judge anchored the addition to — not a reconstructed
                // payload it never drafted.
                const verifiedOp = !isBeat && it.op?.verified ? it.op : null;
                const payload = !isBeat && !verifiedOp ? memberPayload([it], onNavTurn, traceTimestamp) : null;
                // A notification merge carries the agent's returned report on
                // the mark itself - read THAT, not the spawn's tool_result
                // (which for an async spawn is only the launch ack). Sync
                // merge and ack marks sit on the result BLOCK, which
                // readToolCall cannot read directly - their readable
                // call/result pair lives at the spawn item.
                const notifReport = lane?.domain === "agents" && it.op?.kind === "merge" && it.op?.report != null
                  ? it.op
                  : null;
                const anchor = lane?.domain === "agents" && (it.op?.kind === "merge" || it.op?.kind === "ack") && !notifReport
                  ? lane.items[0]
                  : it;
                // Output whose call the record lacks: what came back, whole,
                // and nothing to recover the call from.
                const unpaired = it.domain === "output" && it.turnIndex != null
                  ? (it.op?.blocks || []).map((bi) => turns?.[it.turnIndex]?.blocks?.[bi]).filter(Boolean)
                  : null;
                const recovered = !isBeat && !payload && !notifReport && !unpaired && anchor.turnIndex != null ? readToolCall(turns, anchor.turnIndex, anchor.blockIndex, { whole: true }) : null;
                // The agent's stated reason for this call, from the narration
                // lane's own notes. Last, under whatever the call itself shows:
                // it is context for the payload, not a substitute for it.
                const note = !isBeat ? noteFor(turns, narration, it.turnIndex, it.blockIndex) : null;
                // The residual half of the static↔dynamic link: an
                // unclassified call the reading harvested names the structured
                // readings that came out of it, so the two presentations of
                // one call point at each other.
                const harvested = !isBeat && it.op?.readByModel && readingHarvest && it.id?.startsWith("other:")
                  ? readingHarvest.get(it.id.slice("other:".length).split("#")[0]) || null
                  : null;
                return (
                  <div key={i} className="min-w-0 border-b border-[color:var(--line-soft)] pb-[3px] last:border-b-0">
                    <div className="flex min-w-0 items-baseline gap-[8px] px-[9px] py-[2px]">
                      <span className={`${DOCK_TEXT} shrink-0 tabular-nums text-[color:var(--muted)]`}>T{it.turnIndex == null ? "\u2014" : tb + it.turnIndex}</span>
                      <Mark kind={cellKind([it])} />
                      {/* Who spoke heads a message; its words follow whole,
                          so the head does not start them over. */}
                      <span className={`${DOCK_TEXT} min-w-0 flex-1 break-words ${isBeat ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>{isBeat ? proseLabelOf(it) : opLine(it)}</span>
                    </div>
                    {isBeat ? (
                      <div data-beat-words="" className={`${DOCK_TEXT} whitespace-pre-wrap break-words px-[9px] py-[2px] text-[color:var(--ink)]`}>
                        {beatWords(turns, it) || "\u2014"}
                      </div>
                    ) : verifiedOp ? (
                      <LabeledRow
                        label={`Model reading · ${verifiedOp.kind || "op"}`}
                        text={[
                          verifiedOp.subject || verifiedOp.path || verifiedOp.url || verifiedOp.query || verifiedOp.endpoint || "",
                          `quote: ${verifiedOp.quote || "(none recorded)"}`,
                          // How this reading came to exist: which call it was
                          // read out of, and which judge produced it.
                          `read out of the ${verifiedOp.fromCall?.name || "tool"} call at T${tb + (verifiedOp.fromCall?.turn ?? it.turnIndex)}${verifiedOp.judgeModel ? ` by ${verifiedOp.judgeModel}` : ""} · quote-verified`,
                        ].filter(Boolean).join("\n")}
                      />
                    ) : payload ? (
                      <div className="min-w-0 px-[4px] pb-[2px]">{payload}</div>
                    ) : notifReport ? (
                      <LabeledRow
                        label="Report"
                        text={notifReport.report || notifReport.summary || "The notification carried no report body."}
                      />
                    ) : unpaired ? (
                      unpaired.map((b, j) => (
                        <LabeledRow
                          key={j}
                          label="Result"
                          meta="the call that returned this is not in the record"
                          text={toolResultText(b.content)}
                          failed={Boolean(b.isError ?? b.is_error)}
                        />
                      ))
                    ) : recovered ? (
                      <>
                        <LabeledRow label={recovered.name} text={recovered.call} />
                        <LabeledRow
                          label={recovered.resultNote ? `Result · ${recovered.resultNote}` : "Result"}
                          text={recovered.result}
                          failed={recovered.failed}
                        />
                      </>
                    ) : null}
                    {harvested?.labels?.length ? (
                      <LabeledRow
                        label="Read by model"
                        text={`This call's work was read into ${harvested.total} verified lane${harvested.total === 1 ? "" : "s"}:\n${harvested.labels.join("\n")}${
                          harvested.total > harvested.labels.length ? `\n+${harvested.total - harvested.labels.length} more` : ""
                        }`}
                      />
                    ) : null}
                    {note ? (
                      <LabeledRow
                        label={note.thinking ? "Thinking" : note.described ? "Described" : "Said"}
                        text={`${note.turnIndex !== it.turnIndex ? `T${tb + note.turnIndex} · ` : ""}${noteWords(turns, note)}`}
                      />
                    ) : null}
                  </div>
                );
              })
            ) : (
              <span className={`${META_CLASS} block px-[9px] py-[4px] text-[color:var(--muted)]`}>
                This lane is first touched at T{tb + lane.turns[0]}. Open the Turn tab to read that point in the conversation.
              </span>
            )}
          </div>
          </>
        )
      ) : activeTab === "reports" ? (
        <div data-lanes-scroll="" className={`${bodyScroll} py-[4px]`}>
          <DockReports timeline={reportsTimeline} activeIndex={activeReport} onNavTurn={onNavTurn} />
        </div>
      ) : activeTab === "findings" ? (
        <div data-lanes-scroll="" className={`${bodyScroll} py-[4px]`}>
          <DockFindings
            at={findingsAt}
            turn={turn}
            through={Math.max(turn, findingThrough ?? turn)}
            profiles={reviewProfile}
            incidentOf={findingIncidentOf}
            turns={turns}
            sessionId={sessionId}
            onScopeNavigate={onScopeNavigate}
            onNavTurn={onNavTurn}
          />
        </div>
      ) : activeTab === "graph" ? (
        <div data-lanes-scroll="" className={`${bodyScroll} py-[4px]`}>
          <DockGraph
            graph={graph}
            turn={turn}
            through={Math.max(turn, graphThrough ?? turn)}
            claimId={graphClaim}
            onPickClaim={onPickGraph}
            onHoverClaim={onHoverClaim}
            onOpenTrace={onOpenTrace}
            onOpenChat={onOpenGraphChat}
          />
        </div>
      ) : activeTab === "joins" && joinsAt ? (
        <div data-lanes-scroll="" className={`${bodyScroll} py-[4px]`}>
          <DockJoins at={joinsAt} turn={turn} hues={joinHues} searchWindow={joinSearchWindow} refMs={traceTimestamp ?? null} onOpenTrace={onOpenTrace} onOpenJoin={onOpenJoin} onLayJoin={onLayJoin} onLocateJoin={onLocateJoin} followKey={followKey} flush />
        </div>
      ) : activeTab === "raw" ? (
        <div data-lanes-scroll="" data-raw-turn="" className={`${bodyScroll} py-[2px]`}>
          <RawTurnBody raw={raw} onNavTurn={navTurn} />
        </div>
      ) : (
        <div data-lanes-scroll="" className={`${bodyScroll} py-[2px]`}>
          {read?.blocks.length ? (
            read.blocks.map((b, i) => <LabeledRow key={i} label={b.label} text={b.text} failed={b.failed} meta={b.result ? resultMeta(b, tb) : null} />)
          ) : (
            <span className={`${META_CLASS} block px-[9px] py-[4px] text-[color:var(--muted)]`}>this turn carried no blocks</span>
          )}
        </div>
      )}
    </div>
  );
}

// What a cell's dock reads of one trace, whichever card the trace is on and
// whichever dock reads it: its lanes (with what its reading read, as its own
// lanes view builds them), its reports, its findings, the chat's claims on
// it and its hand-offs, each from the hooks and folds its own lanes view
// uses. `trace` is { id, sessionId, endpointId }; `turns` and `model` its
// record as read, `shellItems` its shell door's ops. `given` holds what a
// host already has of it (a lanes view of its own trace: all of it; a board:
// the claims it placed), taken as it is rather than read again. Nothing is
// read with `enabled` false.
const NOT_GIVEN = {};
export function useTraceDockData({ trace = null, turns = null, model = null, shellItems = null, traceTimestamp = null, focalSpan = null, graphKey = null, given = NOT_GIVEN, enabled = true }) {
  const on = enabled && !!trace && !!turns?.length && !!model;
  const ownReading = useLanesReading(on && !given.reading ? trace : null, turns, { defer: true });
  const reading = given.reading || ownReading;
  const done = reading.status === "done";
  // Its lanes once its reading is in, built as its own view builds them: what
  // the reading read joins the lanes it read.
  const harvested = useMemo(
    () => (on && done && !given.model ? buildReadLanes(turns, { report: reading.report, shellItems }) : null),
    [on, done, given.model, reading.report, shellItems, turns],
  );
  const lanes = given.model || harvested || model;
  const readingHarvest = useMemo(() => (done ? readingHarvestByCall(reading.report) : null), [done, reading.report]);
  const ownReports = useLanesReports({ trace, turns, enabled: on && !given.reports });
  const reports = given.reports || ownReports;
  const ownFindings = useLanesFindings({ trace, turns, model: lanes, traceTimestamp, enabled: on && given.findingsReport === undefined });
  const findingsReport = given.findingsReport !== undefined ? given.findingsReport : ownFindings.report;
  const findingsOpen = !!findingsReport && findingsReport.incidents.length > 0;
  const ownProfile = useLanesReviewProfile(on && given.reviewProfile === undefined && findingsOpen, reports.rows);
  const reviewProfile = given.reviewProfile !== undefined ? given.reviewProfile : ownProfile;
  const ownGraph = useLanesGraph({ graphKey, trace, turns, model: lanes, enabled: on && given.graphOverlay === undefined });
  const graphOverlay = given.graphOverlay !== undefined ? given.graphOverlay : ownGraph.overlay;
  const joinSearchWindow = useMemo(() => traceJoinWindow(focalSpan, traceTimestamp), [focalSpan, traceTimestamp]);
  const ownJoins = useTraceContentJoins({ trace, turns, traceTimestamp, model: lanes, searchWindow: joinSearchWindow, focalSpan, enabled: on && !given.contentJoins });
  const contentJoins = given.contentJoins || ownJoins;
  const firstUserTurn = useMemo(() => firstUserTurnOf(turns), [turns]);
  const joinContext = useMemo(() => joinContextOf(contentJoins, firstUserTurn), [contentJoins.status, contentJoins.needles, firstUserTurn]); // eslint-disable-line react-hooks/exhaustive-deps
  const joinRows = useMemo(() => joinRowsOf(contentJoins, joinContext), [contentJoins.status, contentJoins.rows, joinContext]); // eslint-disable-line react-hooks/exhaustive-deps
  // Over every lane's cells, not only the ones a plane has unfolded: what the
  // dock can answer for is the trace's, not a view's.
  const joinsByTurn = useMemo(() => {
    if (contentJoins.status !== "done") return null;
    const cells = new Map();
    for (const l of lanes?.laneById?.values() || []) if (l.cells) cells.set(l.id, l.cells);
    return joinsByTurnOf(contentJoins.status, buildJoinTurnMarks(joinContext.needles, joinRows), filterJoinCellMarks(buildJoinCellMarks(joinContext.needles, joinRows), cells));
  }, [contentJoins.status, joinContext, joinRows, lanes]);
  const joinHues = useMemo(() => (contentJoins.status === "done" ? assignJoinHues(joinRows) : null), [contentJoins.status, joinRows]);
  const findingsAtTurn = useMemo(() => (findingsOpen ? findingsByTurn(findingsReport) : null), [findingsOpen, findingsReport]);
  const findingIncidentOf = useMemo(() => (findingsOpen ? incidentsOf(findingsReport)?.incidentOf ?? null : null), [findingsOpen, findingsReport]);
  const stamps = useMemo(() => (turns ? buildTurnStamps(turns) : []), [turns]);
  return useMemo(() => ({
    trace, turns, stamps, model: lanes, readingHarvest, reportsTimeline: reports.timeline || null, reviewProfile,
    findingsAtTurn, findingIncidentOf, graphOverlay, joinsByTurn, joinHues, joinSearchWindow, traceTimestamp,
  }), [trace, turns, stamps, lanes, readingHarvest, reports.timeline, reviewProfile, findingsAtTurn, findingIncidentOf, graphOverlay, joinsByTurn, joinHues, joinSearchWindow, traceTimestamp]);
}

// A spliced child's dock data: its record alone (its turns and lanes).
function recordDockData(turns, model) {
  return {
    trace: null, turns, stamps: buildTurnStamps(turns), model, readingHarvest: null, reportsTimeline: null, reviewProfile: null,
    findingsAtTurn: null, findingIncidentOf: null, graphOverlay: null, joinsByTurn: null, joinHues: null, joinSearchWindow: null, traceTimestamp: null,
  };
}

// A pick's span on the clock, for the joins' search window and time flags.
const spanOfRecord = (rec) => (Number.isFinite(rec?.firstMs)
  ? { from: rec.firstMs, to: Number.isFinite(rec?.lastMs) ? rec.lastMs : rec.firstMs }
  : null);

// Opening a counterpart from a hand-off card stages the shared needle strings
// under ITS trace id first, so the counterpart's detail opens on the shared
// content instead of at its own top (joinFollow.js). The clicked pin's needle
// leads; the row's sampled strings ride along so the landing can mark every
// shared span the counterpart renders.
function openJoinFrom(fromTraceId, onOpenTrace) {
  return (entry) => {
    if (!entry?.row?.trace_id) return;
    stageJoinFollow(entry.row.trace_id, {
      needles: [entry.needle, ...(entry.row.samples || []).map((s) => s?.needle)].filter(Boolean),
      fromTraceId,
    });
    onOpenTrace(entry.row.trace_id);
  };
}

// The cells of a trace that carry a hand-off's strings (`follow`, one a line),
// for its card on a board, where the lane drew a cell. Unlike a lanes view,
// a card has no strip of turn marks to stand for its conversation, so the
// conversation's own cells ring too.
function handoffCellsOf(turns, model, follow) {
  if (!follow || !turns?.length || !model) return [];
  const seen = new Map();
  for (const h of locateNeedleStrings(turnsToJoinInputs(turns, model), follow.split("\n"))) {
    for (const c of h.cells || []) {
      if (model.laneById?.get(c.lane)?.cells?.has(c.turn)) seen.set(`${c.lane}@${c.turn}`, { lane: c.lane, turn: c.turn });
    }
  }
  return [...seen.values()];
}

// A dock's hand-off cards laying their other session on the board the cell
// is on (`land`: the board's, bound to the cell), its shared text lit on its
// card once its record is in.
function handoffLayOf(land) {
  if (!land) return null;
  return (entry) => {
    const row = entry?.row;
    if (!row?.session_id) return;
    const follow = joinFollowNeedleKey(entry);
    const startMs = Date.parse(row.first_seen || "");
    land({
      sid: row.session_id,
      title: [row.agent_type, row.model].filter(Boolean).join(" \u00b7 "),
      startMs: Number.isFinite(startMs) ? startMs : null,
      follow: follow || null,
      cellsOf: follow ? (turns, model) => handoffCellsOf(turns, model, follow) : null,
    });
  };
}

// A dock's hand-off cards lighting their shared text on the card they read
// (`key`, null for a board's own trace), from that card's trace (`data`):
// the same card again puts it out.
function useLitHandoff(light, key, data) {
  const lit = useSyncExternalStore(light.subscribe, light.get, light.get);
  const onLocateJoin = useCallback((entry) => {
    const follow = joinFollowNeedleKey(entry);
    if (follow) light.toggle(key, follow, handoffCellsOf(data?.turns, data?.model, follow), (turns, model) => handoffCellsOf(turns, model, follow));
  }, [light, key, data?.turns, data?.model]);
  return useMemo(() => ({ onLocateJoin, followKey: litOn(lit, key) }), [onLocateJoin, lit, key]);
}

// One cell's dock, read from its trace's dock data (useTraceDockData) at `at`
// ({ laneId, turn, tab, joinThrough, findingThrough, graphThrough,
// graphClaim, reportIndex }), whatever card the cell is on and whatever dock
// it is read in. `onAt` moves it as a state setter does, from what it is
// now (two moves in one click compose). A host that moves something of its
// own as the dock does (a lanes view's plane) hands its own movers in
// `moves`; what only a lanes view of its own trace has (its system band, its
// threads, its located follow) rides in `extra`.
const noop = () => {};
export function CellDock({ data, at, onAt, base = 0, onClose, keepAction = null, layAction = null, sessionTitle = null, onOpenTrace = null, onScopeNavigate = null, onOpenGraphChat = null, onHoverClaim = null, moves = null, extra = null, fill = false, height = null, bare = false }) {
  const model = data?.model || null;
  const lane = at?.laneId ? model?.laneById?.get(at.laneId) || null : null;
  // A Cowork file's twin rows: the same file under the container's path and
  // the host's.
  const siblingLanes = useMemo(() => (lane?.sameFileAs || []).map((p) => model.laneById.get(`files:${p}`)).filter(Boolean), [lane, model]);
  const fromTraceId = data?.trace?.id ?? null;
  const openJoin = useMemo(() => (onOpenTrace ? openJoinFrom(fromTraceId, onOpenTrace) : null), [onOpenTrace, fromTraceId]);
  if (!model || !data.turns || !at) return null;
  return (
    <TurnBaseContext.Provider value={base}>
      <LanesDock
        turns={data.turns}
        turnStamps={data.stamps}
        turnCount={data.turns.length}
        beats={model.beats}
        narration={model.narration}
        readingHarvest={data.readingHarvest}
        lane={lane}
        system={extra?.system ?? null}
        onScrubSystem={extra?.onScrubSystem ?? null}
        turn={at.turn}
        tab={at.tab}
        traceTimestamp={data.traceTimestamp}
        endpointId={data.trace?.endpointId ?? null}
        sessionId={data.trace?.sessionId ?? null}
        onOpenTrace={onOpenTrace}
        onScopeNavigate={onScopeNavigate}
        threads={extra?.threads ?? null}
        joins={data.joinsByTurn}
        joinThrough={at.joinThrough ?? at.turn}
        findings={data.findingsAtTurn}
        findingIncidentOf={data.findingIncidentOf}
        findingThrough={at.findingThrough ?? at.turn}
        graph={data.graphOverlay}
        graphThrough={at.graphThrough ?? at.turn}
        graphClaim={at.graphClaim ?? null}
        onPickGraph={moves?.onPickGraph || ((t, claimId) => onAt((cur) => ({ ...cur, turn: t, tab: "graph", graphThrough: t, graphClaim: claimId ?? null })))}
        onHoverClaim={onHoverClaim}
        onOpenGraphChat={onOpenGraphChat}
        reportsTimeline={data.reportsTimeline}
        reviewProfile={data.reviewProfile}
        reportIndex={at.reportIndex ?? null}
        joinHues={data.joinHues}
        joinSearchWindow={data.joinSearchWindow}
        onOpenJoin={openJoin}
        onLayJoin={extra?.onLayJoin ?? null}
        onLocateJoin={extra?.onLocateJoin ?? null}
        followKey={extra?.followKey ?? null}
        onScrubLane={moves?.onScrubLane || ((t) => onAt((cur) => ({ ...cur, turn: t })))}
        onSelectLane={moves?.onSelectLane || ((laneId) => onAt((cur) => ({ ...cur, laneId, tab: "subject" })))}
        sameCall={(laneId, t) => sameCallLanes(model, laneId, t)}
        onSelectCell={moves?.onSelectCell || ((laneId, t) => onAt(() => ({ laneId, turn: t, tab: "subject" })))}
        onHighlight={extra?.onHighlight || noop}
        hasLane={(path) => Boolean(path && model.laneById?.get(`files:${path}`))}
        siblingLanes={siblingLanes}
        onNavTurn={moves?.onNavTurn || ((t) => onAt((cur) => ({ ...cur, turn: t, tab: "turn" })))}
        onSetTab={(t) => onAt((cur) => ({ ...cur, tab: t }))}
        onClose={onClose}
        keepAction={keepAction}
        layAction={layAction}
        sessionTitle={sessionTitle}
        fill={fill}
        height={height}
        bare={bare}
      />
    </TurnBaseContext.Provider>
  );
}

// What a lanes view has of its own trace (useTraceDockData), for a dock
// reading a cell of its board's anchor card beside the card.
const AnchorDockContext = createContext(null);

// A cell picked on a board's card, read in a dock of its own (the workspace
// home's, or beside the card on a board with no dock): from that card's
// trace, as its own lanes view would read it (`pick`: boardPickStore.js), its
// turn and its lane, stepped through in place. The anchor's card (`key`
// null) is its lanes view's trace, read from what that view has of it.
// Opened on what the cell is about (a file's reconstruction, a call's
// reading), as a lanes view opens a cell picked on its lanes, and again on
// each new click.
export function BoardCellDock({ pick, onClose, onOpenTrace = null, onScopeNavigate = null, fill = true, bare = true }) {
  const opened = () => ({ laneId: pick.laneId, turn: pick.turn, tab: pick.tab || (pick.laneId ? "subject" : null) });
  const [at, setAt] = useState(opened);
  useEffect(() => { setAt(opened()); }, [pick.session, pick.laneId, pick.turn, pick.click]); // eslint-disable-line react-hooks/exhaustive-deps
  // Wherever it steps, the board lights that cell and its tab names it.
  const scope = useDockScope();
  useEffect(() => {
    pick.follow?.({ laneId: at.laneId ?? null, turn: at.turn });
    if (pick.ask != null) followBoardPick(pick.ask, { laneId: at.laneId ?? null, turn: at.turn }, scope);
  }, [at.laneId, at.turn]); // eslint-disable-line react-hooks/exhaustive-deps
  const anchor = useContext(AnchorDockContext);
  const hosted = pick.key == null ? anchor : null;
  const rec = pick.record || null;
  const trace = useMemo(() => (pick.session ? { id: pick.session, sessionId: pick.session, endpointId: rec?.endpointId || null } : null), [pick.session, rec?.endpointId]);
  const focalSpan = useMemo(() => spanOfRecord(rec), [rec?.firstMs, rec?.lastMs]); // eslint-disable-line react-hooks/exhaustive-deps
  const given = useMemo(() => (pick.overlay !== undefined ? { graphOverlay: pick.overlay } : NOT_GIVEN), [pick.overlay]);
  const own = useTraceDockData({ trace, turns: rec?.turns || null, model: rec?.model || null, shellItems: rec?.shellItems || null, traceTimestamp: rec?.firstMs ?? null, focalSpan, given, enabled: !hosted });
  const data = hosted || own;
  // Its hand-offs light their shared text on the card it reads, on the board
  // the cell was picked on.
  const handoff = useLitHandoff(pick.light || NO_LIGHT, pick.key ?? null, data);
  const extra = useMemo(() => (pick.light ? { ...handoff, onLayJoin: handoffLayOf(pick.land) } : null), [pick.light, pick.land, handoff]);
  return (
    <CellDock
      data={data}
      extra={extra}
      at={at}
      onAt={setAt}
      base={pick.base || 0}
      onClose={onClose}
      keepAction={pick.keep || null}
      layAction={pick.lay || null}
      sessionTitle={pick.title || null}
      onOpenTrace={onOpenTrace}
      onScopeNavigate={onScopeNavigate}
      fill={fill}
      bare={bare}
    />
  );
}

// A chat with no trace model of its own: it reads every trace on a
// workspace with its own tools.
const NO_TRACE_MODEL = () => null;

// Where a dock tab's questions pin the notes their answers point at on a
// board: its pins on `session`'s card (none without one), each kept by the
// conversation that pinned it (`key`) and named by the question it answers.
// Ids carry a count as well as the time, so two pins in one millisecond do
// not collide.
let boardNoteSeq = 0;
export const boardNoteSink = (key, session) => (notes) => (session ? addTraceNotes((notes || []).map((n, i) => ({
  id: `${key}:${Date.now().toString(36)}${(boardNoteSeq += 1).toString(36)}:${i}`,
  session,
  messageId: n.messageId,
  printed: n.printed,
  lane: n.lane,
  text: n.text,
  chat: { key, title: n.question || "" },
  at: n.at || Date.now(),
}))) : []);

// The trace selected on a board, read in the workspace home's dock as a
// trace's own dock reads its trace (`card`: { session, trace, turns },
// boardPickStore.js): the judges' reading of its calls, run from here, and
// the questions about it under it, as a trace's own dock has them. `shown`:
// whether the dock has it in front. `onBusy` hears whether a run is under
// way.
export function BoardReadingDock({ card, shown = true, onBusy = null }) {
  const reading = useLanesReading(card.trace, card.turns, { defer: true });
  const busy = reading.status === "running";
  useEffect(() => { onBusy?.(busy); }, [busy, onBusy]);
  useEffect(() => () => onBusy?.(false), [onBusy]);
  // What the questions read, as a trace's own dock hands its chat: the turns,
  // their lanes with the reading's verdicts, and where the record's page
  // starts.
  const lanes = useMemo(
    () => buildReadLanes(card.turns || [], { report: reading.status === "done" ? reading.report : null }),
    [card.turns, reading.status, reading.report],
  );
  const sourceRef = useRef(null);
  sourceRef.current = { turns: card.turns || [], lanes, reading, turnBase: recordTurnBase(card.trace?.record || null) };
  const getModel = useCallback(() => sourceRef.current, []);
  const shape = useMemo(
    () => ({ turnCount: lanes.turnCount, laneCount: lanes.lanes.length, hasReading: !!reading.report }),
    [lanes.turnCount, lanes.lanes.length, reading.report],
  );
  const session = String(card.session || card.trace?.sessionId || card.trace?.id || "").toLowerCase() || null;
  const notes = useMemo(() => boardNoteSink(`reading:${session}`, session), [session]);
  // A reading run from a workspace's board makes that workspace the reader's.
  const read = useMemo(() => readOnBoard(reading.ready, reading.run, card.board), [reading.ready, reading.run, card.board]);
  const chat = card.trace?.id ? (
    <TraceChat
      key={card.trace.id}
      traceId={card.trace.id}
      chatTab="card"
      getModel={getModel}
      shape={shape}
      enabled={reading.chatStatus === "ready"}
      unavailable={reading.chatStatus === "none" ? "Configure a chat model to ask about this trace." : null}
      shown={shown}
      onNotes={notes}
      workspace={card.board || null}
    />
  ) : null;
  return (
    <ReadingDock
      rows={busy ? readingRows(reading.liveReport, (reading.live?.judged || []).map((j) => j.id)) : readingRows(reading.report)}
      callsById={reading.callsById}
      summary={reading.summary}
      totals={busy ? reading.live?.totals : (reading.report ? readingDockTotals(reading.report) : null)}
      batches={reading.live?.batches || []}
      busy={busy}
      progress={reading.progress}
      onStop={reading.stop}
      onRead={read}
      unreadable={readingOfferNote({ ready: reading.ready, hasCalls: reading.hasCalls, asks: !!chat })}
      fill
      bare
      chat={chat}
    />
  );
}

// The weight figure in words, for the rail tooltip. One unit, always: estimated
// tokens of THIS conversation. The strip above can be in dollars, because its
// level is a measured total; a lane's share of a measured total is only as
// sound as that total's grain, and this reading does not need it.
function weightLine(weight, weights) {
  if (!weight || !weights) return null;
  const pct = weights.whole > 0 ? (weight.total / weights.whole) * 100 : 0;
  const share = pct >= 1 ? `${Math.round(pct)}%` : pct > 0 ? "<1%" : "0%";
  return `~${formatCompact(Math.round(weight.total))} tokens · ${share} of this conversation`;
}

// The dock's fold affordance: the scope tree's Chevron (SidebarNavRuntime),
// at the threads panel's row size, so the dock and the rail turn the same glyph.
function LaneChevron({ open = false }) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={["h-[10px] w-[10px] shrink-0 transition-transform duration-150", open ? "rotate-90" : ""].join(" ")}
    >
      <path d="m6 3 5 5-5 5" />
    </svg>
  );
}

// Per-turn token density under the ruler: one bar per turn (bucketed at low
// zoom), bottom-aligned on the same axis the lanes put work on. Height is the
// turn's share of the conversation's token mass -- or of the measured spend
// when the level memo priced one (see traceTurnDensity.js), which
// position-weights mass by how many later calls re-read it. This is the
// "where did it get expensive" reading of the timeline; clicking a bar docks
// the heaviest turn under it.

// What the strip's dollars measure, spelled out for the label + tooltip.
// `level` names the spend actually attributed (see the level memo in
// TraceLanesView): the session's runs through this one, the whole session, or
// — fallback — the viewed prompt's own rollup, which understates a
// multi-run conversation and must say so.
const LEVEL_SHORT = {
  "session-to-date": "session so far",
  session: "session",
  run: "this run only",
  prompt: "this prompt only",
};

// What the token strip calls itself. Mass-only from the turns alone; when the
// caller's basis prices the trace, it names the measured spend it attributes
// and at which level. The canvas plane draws this string, so it has to be the
// same one -- a hardcoded mass label reported an estimate for a priced trace.
export function densityLabelLine(density, level = null) {
  if (!density) return "";
  return density.costed
    ? `est. share of ${formatUsd(density.totalCost)}${level?.kind && LEVEL_SHORT[level.kind] ? ` \u00b7 ${LEVEL_SHORT[level.kind]}` : ""}`
    : `~${formatCompact(density.totalEst)} est.`;
}

// ── Topics ──────────────────────────────────────────────────────────────────
// The session's journey through cluster space, ON THIS AXIS. The hero band the
// panel's other readings open with runs on an index of the session's own
// prompts, which is a different count from this conversation's turns; here the
// same rows are dated onto the turn axis (lib/traceTopicBand) so a stretch of
// lanes can be read as "this is what the session was working on while that work
// happened".
//
// It rides the bottom edge of the RULER, five pixels of colour under the turn
// numbers, because it is context for the axis rather than another subject on
// it. One column per turn while a turn column fits, merged columns stacked by
// share when the axis is compressed; a column that does not fill its height is
// a bucket the join could only place in part, and blank is a real state rather
// than a missing paint. Columns left of the last measured anchor
// (`estimatedBefore`) are drawn fainter: their topics are right, their POSITIONS
// are an even spacing over an unmeasured stretch.
//
const topicNameOf = (axis, id) => axis.names.get(id) || "unlabeled topic";

// What the ribbon means, where every other encoding of this view explains
// itself. The swatch is the palette, so the legend shows the encoding rather
// than naming it.
function TopicLegendBody({ axis, ranked, hues, turnCount, dated, truncated, estimatedBefore }) {
  const nameOf = (id) => topicNameOf(axis, id);
  const datedLine = dated === "messages"
    ? "Turns are dated from identified conversation messages; undated context has no time assignment"
    : dated === "replies"
    ? "Turns are dated from this conversation's reply timestamps"
    : dated === "captures"
      ? "Turns are dated from the session's request captures"
      : "No reply timestamps were available, so turns are spaced evenly across the session's span";
  const estimatedLine = estimatedBefore >= turnCount
    ? " Every column is an estimate."
    : estimatedBefore > 0
      ? ` The first ${estimatedBefore} turns are before the earliest reply the read returned: they are spaced evenly from the session's start.`
      : "";
  return (
    <span className="grid gap-[4px]">
      <span className="font-medium">Topics</span>
      <span className="text-[color:var(--muted)]">
        Every request this session made is grouped with similar requests from across the tenant. The token
        bars on the axis take the hue of the group the session was working in as the turns went by; where
        the axis is compressed, a bar takes the group most of its turns sat in. Hover the axis to name a
        stretch and mark its extent across the lanes.
      </span>
      <span className="text-[color:var(--muted)]">
        {`${datedLine}, so a topic's edges are approximate. `}
        {`${axis.turnsWithTopic} of ${turnCount} turns are covered`}
        {axis.undatedHead > 1 ? `; the first ${axis.undatedHead} are undated and carry none` : ""}
        {"."}
        {axis.before
          ? ` ${axis.before} earlier request${axis.before === 1 ? " falls" : "s fall"} before the axis starts.`
          : ""}
        {estimatedLine}
        {truncated ? " The session read hit its row cap, so its most recent requests are missing." : ""}
      </span>
      {ranked.length ? (
        <span className="grid gap-[2px]">
          {ranked.slice(0, 6).map((t) => (
            <span key={t.id} className="flex items-baseline gap-[5px]">
              <span aria-hidden="true" className="mt-[3px] h-[7px] w-[7px] shrink-0" style={{ background: hues.get(t.id) || TOPIC_OTHER_HUE }} />
              <span className="min-w-0 flex-1 truncate">{nameOf(t.id)}</span>
              <span className="shrink-0 text-[color:var(--muted-soft)]">{`${t.turns} turns`}</span>
            </span>
          ))}
          {ranked.length > 6 ? (
            <span className="text-[color:var(--muted-soft)]">{`+${ranked.length - 6} more`}</span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

// The toolbar's chip: the palette as a swatch and the count, with the legend
// on hover. The Topics band's rail head hovers the same body.
function TopicLegend(props) {
  const { ranked, hues } = props;
  return (
    <HoverTooltip wide placement="bottom" maxWidth={380} label={<TopicLegendBody {...props} />}>
      <span
        tabIndex={0}
        data-topic-legend=""
        className="flex cursor-help items-center gap-[5px] text-[10px] leading-none text-[color:var(--muted)]"
      >
        <span aria-hidden="true" className="flex h-[7px] w-[12px]">
          {ranked.slice(0, 3).map((t, i) => (
            <span key={t.id} className="h-full flex-1" style={{ background: hues.get(t.id) || TOPIC_OTHER_HUE, opacity: 1 - i * 0.2 }} />
          ))}
        </span>
        {`${ranked.length} topic${ranked.length === 1 ? "" : "s"}`}
      </span>
    </HoverTooltip>
  );
}

// ── Influence overlay ───────────────────────────────────────────────────────
// The provenance links (traceInfluence.js) drawn between the cells they
// connect. Every link runs earlier → later, so it always reads left to right:
// what enters the focused mark from the left FED it, what leaves to the right
// is what it fed.
//
// Focus-driven on purpose. The whole graph over a plane of dozens of lanes is
// the hairball the arc timeline already declines to draw at this density; one
// cell's neighbourhood is the question a reader standing on a mark actually
// has.
//
// Only half of that neighbourhood is bounded for us. Provenance caps edges per
// TARGET (MAX_EDGES_PER_TARGET, every class, not just consultations), so what
// fed a cell is at most twelve links. What a cell FED has no such ceiling — one
// widely-quoted source can feed every later production, each of which kept it
// under its own cap — so the draw side takes the strongest INFLUENCE_DRAW_CAP
// and says how many it set aside rather than stacking dozens of curves on one
// mark.

// Tier drives colour, same vocabulary and hues as every other provenance
// surface (see LineageViews.TIER_COLOR) — `cited` is the only warm hue because
// noticing whether the hardest claim appears at all is half the point.
const TIER_COLOR = {
  cited: "var(--amber, #b8860b)",
  quoted: "var(--info, #7fa3c9)",
  entity: "var(--green)",
  adjacent: "var(--muted-soft)",
};
const TIER_LABEL = { cited: "cited", quoted: "quoted", entity: "related", adjacent: "consulted" };

// Where the reader's rail width is kept, per browser: the column a reader
// widened for long paths stays wide on the next trace.
const RAIL_W_KEY = "lanesRailW";
// Whether the reader keeps the permanent dock minimized, per browser.
const DOCK_MIN_KEY = "lanesDockMinimized";
// Whether a canvas's dock stands beside its board rather than under it.

// Fill mode: the view owns the rest of its SCROLLPORT — the nearest scrollable
// ancestor (the detail panel's viewport), not the window. Sizing against the
// window when the panel is shorter leaves the takeover taller than the panel,
// and the panel then scrolls around a view that scrolls inside it: two nested
// vertical scrollbars for one reading. Falls back to the window when nothing
// above scrolls (the harness, tests).
function useViewportFill(ref, enabled) {
  const [h, setH] = useState(null);
  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    // EVERY ancestor up to body, snapshotted before anything is overridden.
    // `clipped` (overflow-y auto/scroll/hidden — all of them clip, and all are
    // scroll containers) bounds the sizing math; `scrollports` is the subset
    // whose size the ResizeObserver watches.
    const ancestors = [];
    for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) ancestors.push(n);
    const overflowOf = new Map(ancestors.map((n) => [n, getComputedStyle(n).overflowY]));
    const scrollports = ancestors.filter((n) => ["auto", "scroll"].includes(overflowOf.get(n)));
    const clipped = new Set(ancestors.filter((n) => ["auto", "scroll", "hidden"].includes(overflowOf.get(n))));
    // While the takeover owns the panel, the chain must not scroll AROUND it.
    // Sizing alone cannot guarantee that: fractional line heights sum to
    // sub-pixel remainders (a 720.2px column in a 720px viewport), and one
    // leftover pixel is a whole second scrollbar. Clipping with `hidden`
    // cannot guarantee it either — an overflow-hidden box is STILL a scroll
    // container, so focus management and scrollIntoView can displace it with
    // no scrollbar to drag it back, and OverlayScrollbars rewrites its
    // viewport's inline style on update, dropping the override. So the chain
    // is PINNED for the takeover's lifetime: scroll containers get
    // `overflow-y: clip` (which forbids programmatic scrolling outright,
    // unlike hidden), every ancestor snaps back to 0 on any scroll event, a
    // style guard re-asserts the clip when something rewrites it — and all of
    // it is restored on the way out. This hook only runs in fill mode, so
    // every other reading keeps its scrolling untouched.
    const CLIP =
      typeof CSS !== "undefined" && CSS.supports?.("overflow-y", "clip") ? "clip" : "hidden";
    // An OverlayScrollbars ancestor measures overflow for ITSELF, and the
    // inline clip hides scrolling from the browser, not from the library —
    // its content is still taller than its viewport, so it keeps rendering
    // its overlay bar: a phantom panel scrollbar overlapping the grid's own.
    // Silence it at the source, through the library's options, and restore on
    // the way out. (The takeover's own grid is a descendant, never touched.)
    // Idempotent, and re-run from the settle ticks below: layout effects run
    // child-first, so an ancestor ScrollArea mounting in the same commit has
    // no instance yet when this hook first walks the chain.
    const osInstances = [];
    const silenceOsAncestors = () => {
      if (typeof OverlayScrollbars !== "function") return;
      for (const n of ancestors) {
        const inst = OverlayScrollbars(n);
        if (inst && !osInstances.some(([known]) => known === inst)) {
          const prev = inst.options().overflow;
          osInstances.push([inst, { x: prev.x, y: prev.y }]);
          inst.options({ overflow: { y: "hidden" } });
        }
      }
    };
    silenceOsAncestors();
    const restores = [...clipped].map((sp) => [sp, sp.style.overflowY]);
    const pin = (n) => {
      if (clipped.has(n) && n.style.overflowY !== CLIP) n.style.overflowY = CLIP;
      if (n.scrollTop !== 0) n.scrollTop = 0;
    };
    for (const n of ancestors) pin(n);
    const styleGuard =
      typeof MutationObserver === "function"
        ? new MutationObserver((muts) => {
            for (const m of muts) pin(m.target);
          })
        : null;
    for (const sp of clipped) styleGuard?.observe(sp, { attributes: true, attributeFilter: ["style"] });
    // ONE deterministic formula, applied once per settled layout: the height
    // is the distance from our top edge to the nearest clipping bottom edge,
    // minus every bottom inset BETWEEN us and that edge — each clipping
    // ancestor's own bottom padding/border plus those of every wrapper in
    // between. The in-between insets are the load-bearing part: the panel's
    // designed bottom padding lives on a content wrapper that is not itself
    // a scroll container, and honoring only scrollport paddings squished it
    // to nothing. Exact math, no fudge pixels — the chain is clipped and
    // pinned, so a sub-pixel remainder can't mint a scrollbar; floor() just
    // rounds it down. No corrective second pass either: the multi-pass
    // shrink is what made the bottom edge land in a different place on every
    // entry and visibly bounce on mode switches.
    const measure = () => {
      const node = ref.current;
      if (!node) return;
      const top = node.getBoundingClientRect().top;
      let bottom = window.innerHeight || 800;
      let inset = 0;
      for (const a of ancestors) {
        const cs = getComputedStyle(a);
        const pb = parseFloat(cs.paddingBottom) || 0;
        const bb = parseFloat(cs.borderBottomWidth) || 0;
        if (clipped.has(a)) {
          bottom = Math.min(bottom, a.getBoundingClientRect().bottom - pb - bb - inset);
        }
        inset += pb + bb;
      }
      setH(Math.max(420, Math.floor(bottom - top)));
    };
    measure();
    // Resizes settle before they re-measure: a FLIP panel expansion fires the
    // observer every animation frame, and re-sizing the takeover per frame
    // relays out the whole grid per frame. Identical re-measures after settle
    // are free — same integer, React bails.
    let settleTimer = 0;
    const onResizeSettled = () => {
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        settleTimer = 0;
        silenceOsAncestors();
        measure();
      }, 180);
    };
    // One late pass for content above us that lays out asynchronously (the
    // summary line) and for ancestor ScrollArea instances that initialize
    // after this child effect.
    const lateSettle = window.setTimeout(() => {
      silenceOsAncestors();
      measure();
    }, 250);
    window.addEventListener("resize", onResizeSettled);
    // Displacement snapping stays IMMEDIATE — it is a correctness guard, not
    // a layout read; only the measure behind it settles.
    const onAncestorScroll = (e) => {
      pin(e.currentTarget);
      onResizeSettled();
    };
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(onResizeSettled) : null;
    for (const n of ancestors) n.addEventListener("scroll", onAncestorScroll, { passive: true });
    for (const sp of clipped) ro?.observe(sp);
    return () => {
      if (settleTimer) window.clearTimeout(settleTimer);
      window.clearTimeout(lateSettle);
      window.removeEventListener("resize", onResizeSettled);
      for (const n of ancestors) n.removeEventListener("scroll", onAncestorScroll);
      // Disconnect before restoring, or the guard would re-pin the restore.
      styleGuard?.disconnect();
      for (const [sp, prev] of restores) sp.style.overflowY = prev;
      for (const [inst, prev] of osInstances) {
        try {
          inst.options({ overflow: prev });
        } catch {
          /* the instance was destroyed with its panel — nothing to restore */
        }
      }
      ro?.disconnect();
    };
  }, [ref, enabled]);
  return enabled ? h : null;
}

// The time of the oldest message a record's turns carry (null when none
// carries one).
export function oldestTurnMs(turns) {
  let min = Infinity;
  for (const t of turns || []) if (Number.isFinite(t?.messageAt) && t.messageAt < min) min = t.messageAt;
  return Number.isFinite(min) ? min : null;
}

// The trace-review pipeline's findings for this conversation, on the axis
// WITHOUT being asked: the review already happened server-side, so the only
// cost of showing it is a bounded read of the analytics projection, read back
// as far as the axis holds messages and on again when an earlier page comes
// in. Inert in a spliced inset (a nested view must not fan out per expansion)
// and when the trace carries no session id, which is the key the projection
// is written under.
function useLanesFindings({ trace, turns, model, traceTimestamp, enabled = true }) {
  const sessionId = enabled ? trace?.sessionId || null : null;
  const [state, setState] = useState({ status: "idle", rows: null, truncated: false, error: null });
  // The oldest message on the axis, which the findings are read back to. The
  // trace's timestamp only anchors a record whose turns carry no times, read
  // through a ref so it never asks again.
  const sinceMs = useMemo(() => oldestTurnMs(turns), [turns]);
  const stampRef = useRef(traceTimestamp);
  stampRef.current = traceTimestamp;
  useEffect(() => {
    if (!sessionId) {
      setState({ status: "idle", rows: null, truncated: false, error: null });
      return undefined;
    }
    const have = findingsCovering(sessionId, sinceMs);
    if (have) {
      setState({ status: "done", rows: have.rows, truncated: have.truncated, error: null });
      return undefined;
    }
    // What an earlier read holds stands while the read goes on below it.
    const partial = readFindingsCache(sessionId);
    setState(partial
      ? { status: "done", rows: partial.rows, truncated: partial.truncated, error: null }
      : { status: "loading", rows: null, truncated: false, error: null });
    // Cancelled when the trace, or how far back its axis reaches, changes: the
    // next read goes on from what this one kept.
    const controller = new AbortController();
    readTraceReviewFindings({ sessionId, sinceMs, traceTimestamp: stampRef.current, signal: controller.signal, fetch: fetchTraceReviewFindings }).then((res) => {
      if (!controller.signal.aborted) setState({ status: "done", rows: res.rows, truncated: res.truncated, error: null });
    }, (error) => {
      // The first read failed (one past it that fails keeps what came before
      // it, cut short): denied, unreachable, or an analytics surface without
      // the entity. The strip is additive, so a failed read adds no chrome.
      // The reason goes to the console, because an absent strip is otherwise
      // indistinguishable from a conversation the pipeline never flagged.
      if (controller.signal.aborted) return;
      console.warn("[lanes] trace review findings unavailable:", error?.message || error);
      setState({ status: "error", rows: null, truncated: false, error });
    });
    return () => controller.abort();
  }, [sessionId, sinceMs]);
  const report = useMemo(() => {
    if (!state.rows?.length) return null;
    return buildFindingsReport(state.rows, turnsToJoinInputs(turns, model, { raw: true }), { truncated: state.truncated });
  }, [state.rows, state.truncated, turns, model]);
  return { ...state, report };
}

// A chat's knowledge graph on this trace, when the tab was opened from the
// chat (the `kg` param names the chat). The graph lives in the chat's own
// registry while the chat is open in this tab and in the browser store
// otherwise, so the overlay follows the investigation live: every write the
// chat's agent persists re-walks it. Nothing is fetched.
function useLanesGraph({ graphKey, trace, turns, model, enabled = true }) {
  const key = enabled && graphKey ? graphKey : null;
  const subscribe = useCallback((cb) => (key ? subscribeGraph(key, cb) : () => {}), [key]);
  const version = useSyncExternalStore(subscribe, () => (key ? graphVersion(key) : 0), () => 0);
  const graph = useMemo(() => (key ? readLiveGraph(key) : null), [key, version]); // eslint-disable-line react-hooks/exhaustive-deps
  // Right after a reload the store's key waits on the signed-in identity, so
  // an empty read is asked again a few times before it is believed.
  const retries = useRef(0);
  useEffect(() => { retries.current = 0; }, [key]);
  useEffect(() => {
    if (!key || graph?.nodes?.size || retries.current >= 3) return undefined;
    const t = setTimeout(() => { retries.current += 1; notifyGraphChanged(key); }, 1500);
    return () => clearTimeout(t);
  }, [key, graph, version]);
  // What the walk reads off the trace changes with the turns, not with the
  // graph, which a running chat writes after every call.
  const turnInputs = useMemo(() => (key ? turnsToJoinInputs(turns, model) : null), [key, turns, model]);
  const laneOfToolUse = useMemo(() => (key ? toolUseLanes(model, turns) : null), [key, turns, model]);
  const overlay = useMemo(() => {
    if (!graph?.nodes?.size) return null;
    return buildTraceGraphOverlay(graph, { trace, turns, turnInputs, laneOfToolUse });
    // The graph object is mutated in place; the version is what says it moved.
  }, [graph, version, trace?.sessionId, trace?.id, turns, turnInputs, laneOfToolUse]); // eslint-disable-line react-hooks/exhaustive-deps
  return { on: !!key, empty: !!key && !graph?.nodes?.size, overlay, graph, version };
}

// The trace-review pipeline's REPORTS for this conversation, on the axis
// without being asked -- the findings strip's posture, one hop further into the
// review API, because the summaries live only in the Postgres report documents
// and not in the analytics projection. Inert in a spliced inset and for a trace
// with no session id. The read is a bounded fan-out (one document per reviewed
// window, newest REPORTS_LIMIT) cached per conversation; a denied or
// unreachable read adds no chrome and says why on the console, since an absent
// strip is otherwise indistinguishable from a conversation never reviewed.
function useLanesReports({ trace, turns, enabled = true }) {
  const sessionId = enabled ? trace?.sessionId || null : null;
  const [state, setState] = useState({ status: "idle", rows: null, truncated: false, error: null });
  useEffect(() => {
    if (!sessionId) {
      setState({ status: "idle", rows: null, truncated: false, error: null });
      return undefined;
    }
    const cached = readReportsCache(sessionId);
    if (cached) {
      setState({ status: "done", rows: cached.rows, truncated: cached.truncated, error: null });
      return undefined;
    }
    const controller = new AbortController();
    setState({ status: "loading", rows: null, truncated: false, error: null });
    fetchTraceReviewReports({ sessionId, signal: controller.signal }).then((res) => {
      if (controller.signal.aborted) return;
      writeReportsCache(sessionId, res);
      setState({ status: "done", rows: res.rows, truncated: res.truncated, error: null });
    }, (error) => {
      if (controller.signal.aborted) return;
      console.warn("[lanes] trace review reports unavailable:", error?.message || error);
      setState({ status: "error", rows: null, truncated: false, error });
    });
    return () => controller.abort();
  }, [sessionId]);
  const timeline = useMemo(() => {
    if (!state.rows?.length) return null;
    return buildReportsTimeline(state.rows, turns, { truncated: state.truncated });
  }, [state.rows, state.truncated, turns]);
  return { ...state, timeline };
}

const PROFILE_SCOPE_LIMIT = 16;
const PROFILE_FETCH_CONCURRENCY = 4;
const profileScopeKey = (scope) => scope?.key ? `${scope.kind}:${scope.key}` : "tenant";
const mergeReviewProfile = (base, scoped) => {
  if (!base) return scoped || null;
  if (!scoped) return base;
  return {
    ...base,
    policyText: scoped.policyText || base.policyText,
    norms: scoped.norms?.length ? scoped.norms : base.norms,
    intents: scoped.intents?.length ? scoped.intents : base.intents,
    discoverySummary: scoped.discoverySummary || base.discoverySummary,
  };
};

function useLanesReviewProfile(enabled, reports = null) {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    const controller = new AbortController();
    const scopes = [];
    const seen = new Set(["tenant"]);
    const reportRows = reports || [];
    for (const report of reportRows) {
      const scope = report.scope?.key ? report.scope : null;
      const key = profileScopeKey(scope);
      if (!seen.has(key) && scopes.length < PROFILE_SCOPE_LIMIT) {
        seen.add(key);
        scopes.push({ key, scope });
      }
    }
    const request = (scope) => getReviewProfile({ scope, signal: controller.signal })
      .then((value) => ({ status: "fulfilled", value }), (reason) => ({ status: "rejected", reason }));
    Promise.all([
      request(null),
      runPooled(scopes.map(({ scope }) => () => request(scope)), PROFILE_FETCH_CONCURRENCY),
    ]).then(
      ([tenantResult, scoped]) => {
        if (!active) return;
        const tenant = tenantResult.status === "fulfilled" ? tenantResult.value : null;
        const byScope = Object.fromEntries(scopes.flatMap(({ key }, index) => (
          scoped[index].status === "fulfilled" ? [[key, scoped[index].value]] : []
        )));
        const byReportId = Object.fromEntries(reportRows.map((report) => [
          report.reportId || report.id,
          mergeReviewProfile(tenant, byScope[profileScopeKey(report.scope)]),
        ]));
        setProfile({ tenant, byReportId });
      },
      () => { if (active) setProfile(null); },
    );
    return () => { active = false; controller.abort(); };
  }, [enabled, reports]);
  return profile;
}

//
// One-click hand-off scan from the timeline: the same launch pattern as the
// A one-click scan aimed at a different question: where might this session be talking
// to ANOTHER agent? The review judges a newest-first window; the scan exists
// to seed a content-based join with the counterpart's session, and a hand-off
// missed in an unread stretch is a broken join, so runHandoffScan chunks the
// WHOLE turn axis and runs one pass per chunk. Cached per trace+model in the
// lib, so re-opening the trace shows the finished report instead of
// re-spending the fan-out.
function useLanesHandoffs(trace) {
  // Same gating as the review hook: no trace, no creds spend. The two hooks
  // both call getChatCreds, which coalesces concurrent GETs, so mounting both
  // costs one request.
  const scannable = !!trace?.turns?.length;
  const [creds, setCreds] = useState(null);
  useEffect(() => {
    if (!scannable) return undefined;
    let on = true;
    getChatCreds().then((c) => { if (on) setCreds(c); }).catch(() => { /* unauth/offline */ });
    return () => { on = false; };
  }, [scannable]);

  // The scan detects rather than judges, but the review's model-default
  // reasoning carries over: a weaker scanner silently returns fewer
  // candidates, and recall is this feature's whole point.
  const model = defaultReviewModel(creds);
  const key = scannable ? handoffCacheKey({ trace, model }) : null;

  const [state, setState] = useState({ status: "idle", report: null, error: null, progress: null });
  const runIdRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    runIdRef.current++;
    abortRef.current?.abort();
    const cached = key ? readHandoffCache(key) : null;
    setState(cached
      ? { status: "done", report: cached, error: null, progress: null }
      : { status: "idle", report: null, error: null, progress: null });
    return () => abortRef.current?.abort();
  }, [key]);

  const run = useCallback(() => {
    if (!key) return;
    const runId = ++runIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ status: "running", report: null, error: null, progress: null });
    runHandoffScan({
      trace,
      creds,
      model,
      signal: controller.signal,
      // Pass progress so a long trace's multi-window scan reads as advancing
      // rather than as one opaque spinner.
      onProgress: (progress) => {
        if (runId !== runIdRef.current) return;
        setState((s) => (s.status === "running" ? { ...s, progress } : s));
      },
    }).then((r) => {
      if (runId !== runIdRef.current) return;
      if (r.status === "done") {
        writeHandoffCache(key, r.report);
        setState({ status: "done", report: r.report, error: null, progress: null });
      } else if (r.status === "aborted") {
        setState({ status: "idle", report: null, error: null, progress: null });
      } else {
        setState({ status: "error", report: null, error: r.error, progress: null });
      }
    });
  }, [key, trace, creds, model]);

  const stop = useCallback(() => abortRef.current?.abort(), []);
  return { ...state, run, stop, ready: !!creds?.provider && !!key };
}

// One-click referee over this trace's drafted reading: the semantic feed's
// claims (every extractor, runtime lenses and the code fallback included) are
// judged per call against the raw bytes, and work the drafters missed comes
// back as quote-anchored additions. Verdicts persist in the localStorage
// ledger keyed by call content, so the next request of the same session —
// which re-carries most of the same calls — reuses them instead of
// re-spending; the same ledger is the local gap-mining corpus the rail
// tooltip summarizes.
// A reading's run as a dock offers it, null while it cannot run: run from a
// workspace's board (`board`, its id), it makes that workspace the reader's.
function readOnBoard(ready, run, board) {
  if (!ready) return null;
  if (!board) return run;
  return (opts) => {
    saveTraceCanvas(String(board));
    return run(opts);
  };
}

export function useLanesReading(trace, turns, { defer = false } = {}) {
  const readable = !!trace?.id && !!turns?.length;
  const [creds, setCreds] = useState(null);
  // "loading" until the settings answer, then whether a chat model is there.
  // A failed read (unauth, offline) is "none": the chat cannot start either way.
  const [credsStatus, setCredsStatus] = useState("loading");
  useEffect(() => {
    if (!readable) { setCredsStatus("none"); return undefined; }
    let on = true;
    getChatCreds().then((c) => {
      if (!on) return;
      setCreds(c);
      setCredsStatus(c?.provider ? "ready" : "none");
    }).catch(() => { if (on) setCredsStatus("none"); });
    return () => { on = false; };
  }, [readable]);

  const model = defaultReviewModel(creds);
  // Drafting runs up front because the cache key needs the claims
  // fingerprint: a drafter upgrade changes the claims, and reusing a verdict
  // on claims that no longer exist would judge the wrong reading.
  // On a canvas (the workspace) the draft waits for an idle moment after the
  // board has drawn (a second at most): the board's opening has no use for
  // it, and it was among the costliest things that opening waited on.
  const now = useMemo(() => (readable && !defer ? draftReadingCalls(turns) : null), [readable, defer, turns]);
  const [later, setLater] = useState(null);
  useEffect(() => {
    setLater(null);
    if (!readable || !defer) return undefined;
    let live = true;
    const run = () => { if (live) setLater({ turns, drafted: draftReadingCalls(turns) }); };
    const idle = typeof requestIdleCallback === "function";
    const handle = idle ? requestIdleCallback(run, { timeout: 1000 }) : setTimeout(run, 0);
    return () => { live = false; if (idle) cancelIdleCallback(handle); else clearTimeout(handle); };
  }, [readable, defer, turns]);
  const drafted = defer ? (later?.turns === turns ? later.drafted : null) : now;
  const claimsHash = useMemo(() => readingClaimsHash(drafted), [drafted]);
  const key = readable && drafted?.calls?.length ? readingCacheKey({ trace, model, claimsHash }) : null;

  const [state, setState] = useState({ status: "idle", report: null, error: null, progress: null, delta: null });
  const runIdRef = useRef(0);
  const abortRef = useRef(null);
  // The run as it happens, kept apart from the settled report: the judge writes
  // its answer call by call, and a reader who can watch that is reading a
  // judgment being made rather than waiting on a spinner. The state is folded
  // one FRAME at a time — the sub-agent hands events through per token, and
  // this hook sits above the whole lane model.
  //
  // Live state re-renders the view, which rebuilds the plane's scene. Measured
  // on the lanes-harness fixture (622 turns, 62 lanes): buildLaneScene is
  // 0.6ms, so a run's ~25 folds a second cost about as much as a second of
  // scrolling. The 29ms builder is buildTraceLanes, and it stays OUT of this
  // path on purpose — see the liveReport note below.
  const [live, setLive] = useState(null);
  // The dock is the reader's to close. It opens on the run and STAYS after the
  // run lands — the reading is the answer, and a dock that vanished when it
  // finished took the list of what was found with it.
  const [dockOpen, setDockOpen] = useState(false);
  // The account of the run, in a sentence. It follows a finished reading
  // automatically -- the reading is what cost something, and the account over
  // its already-compressed operations is small beside it -- and caches beside
  // the report so reopening a trace never buys a second one.
  const [summary, setSummary] = useState(null);
  const liveQueue = useRef([]);
  const liveFrame = useRef(0);
  // The calls this run is judging, for turning streamed verdicts into a
  // report-shaped partial. A `fresh` run judges a DIFFERENT set (the previous
  // read promoted in), so the draft is captured at launch, not read off props.
  const liveCallsRef = useRef(null);

  const stopLive = useCallback(() => {
    if (liveFrame.current) cancelAnimationFrame(liveFrame.current);
    liveFrame.current = 0;
    liveQueue.current = [];
  }, []);

  useEffect(() => {
    runIdRef.current++;
    abortRef.current?.abort();
    stopLive();
    setLive(null);
    setDockOpen(false);
    const cached = key ? readReadingCache(key) : null;
    const cachedSummary = cached && key ? readSummaryCache(key) : null;
    setSummary(cachedSummary ? { status: "done", summary: cachedSummary } : null);
    setState(cached
      ? { status: "done", report: cached, error: null, progress: null, delta: null, fresh: false }
      : { status: "idle", report: null, error: null, progress: null, delta: null, fresh: false });
    return () => abortRef.current?.abort();
  }, [key, stopLive]);
  // A run of the same reading that lands elsewhere (the trace's lanes view,
  // the workspace home's Reading, a dock reading one of its cells) lands here
  // too, unless one is under way here.
  useEffect(() => subscribeReadingCache(key, (report) => {
    setState((s) => (s.status === "running" || s.report === report ? s : { status: "done", report, error: null, progress: null, delta: null, fresh: false }));
  }), [key]);
  useEffect(() => stopLive, [stopLive]);

  const run = useCallback((opts) => {
    if (!key) return;
    // `fresh` is the explicit re-run: ignore banked verdicts and judge every
    // call in a clean context, with the previous read's additions promoted
    // into the draft — the prior read acting as one more drafter.
    const fresh = opts?.fresh === true;
    const draftedForRun = fresh && opts?.prior ? mergePromotedClaims(drafted, opts.prior) : drafted;
    const runId = ++runIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    stopLive();
    liveCallsRef.current = draftedForRun?.calls || null;
    setLive(emptyLiveReading());
    setDockOpen(true);
    setSummary(null);
    if (fresh) clearSummaryCache(key);
    setState({ status: "running", report: null, error: null, progress: null, delta: null, fresh });
    runTraceReading({
      turns,
      drafted: draftedForRun,
      creds,
      model,
      signal: controller.signal,
      ignoreLedger: fresh,
      onProgress: (progress) => {
        if (runId !== runIdRef.current) return;
        setState((s) => (s.status === "running" ? { ...s, progress } : s));
      },
      // One fold per frame, whatever the token rate: the queue is the throttle.
      onLive: (ev) => {
        if (runId !== runIdRef.current) return;
        liveQueue.current.push(ev);
        if (liveFrame.current) return;
        liveFrame.current = requestAnimationFrame(() => {
          liveFrame.current = 0;
          const batch = liveQueue.current;
          liveQueue.current = [];
          if (runId !== runIdRef.current) return;
          setLive((cur) => applyLiveEvents(cur || emptyLiveReading(), batch));
        });
      },
    }).then((r) => {
      if (runId !== runIdRef.current) return;
      stopLive();
      // The live state SURVIVES a finished run: its batches are what the dock's
      // "Judges" section reports on. Only a run that produced no reading takes
      // the dock down with it — the strip carries that outcome instead.
      if (r.status !== "done") {
        setLive(null);
        setDockOpen(false);
      }
      if (r.status === "done") {
        // The delta against the LAST completed run of this trace — computed
        // before this run snapshots — is the longitudinal benchmark: a re-run
        // with better drafters shows additions migrating into claims.
        const delta = readingDelta(trace?.id, r.report);
        writeReadingSnapshot(trace?.id, { claimsHash, model, report: r.report });
        writeReadingCache(key, r.report, readingSessionOf(trace));
        setState({ status: "done", report: r.report, error: null, progress: null, delta, fresh });
        // The account follows the reading it describes. It reads the CORRECTED
        // reading only -- accepted claims and quote-anchored additions -- so it
        // inherits the gates rather than narrating raw turns.
        setSummary({ status: "running", summary: null, error: null });
        runReadingSummary({ report: r.report, turns, creds, model, signal: controller.signal })
          .then((sr) => {
            if (runId !== runIdRef.current) return;
            if (sr.status === "done") {
              writeSummaryCache(key, sr.summary);
              setSummary({ status: "done", summary: sr.summary, error: null });
            } else {
              setSummary({ status: "error", summary: null, error: sr.error });
            }
          });
      } else if (r.status === "aborted") {
        setState({ status: "idle", report: null, error: null, progress: null, delta: null, fresh: false });
      } else {
        setState({ status: "error", report: null, error: r.error, progress: null, delta: null, fresh: false });
      }
    });
  }, [key, turns, drafted, creds, model, trace?.id, claimsHash, stopLive]);

  // The second pass: a clean-context re-read of the WHOLE trace, with the
  // last read's verified additions promoted into the draft. Full cost by
  // design — a benchmark run, not a cache walk.
  const rerun = useCallback(() => {
    if (!key) return;
    clearReadingCache(key);
    run({ fresh: true, prior: state.report });
  }, [key, run, state.report]);

  // The partial reading, in the settled report's own shape, so the axis marks
  // and the summary line come from the SAME code that renders a finished run.
  // Nothing downstream needs a second notion of "a reading" to draw a live one.
  //
  // Deliberately NOT fed to the lane model: materializing verified additions
  // re-walks every block of the trace and can mint new subject rows, so a live
  // feed would reflow the plane under the reader's cursor mid-run. Marks land
  // live; lanes land when the run settles.
  const liveReport = useMemo(() => {
    const calls = liveCallsRef.current;
    if (!live?.judged?.length || !calls?.length) return null;
    return finalizeReading(calls, live.judged);
  }, [live]);

  const stop = useCallback(() => abortRef.current?.abort(), []);
  const closeDock = useCallback(() => setDockOpen(false), []);
  // Reopening costs nothing: the report is already in the session cache, and
  // only the reader's decision to close it was lost.
  const openDock = useCallback(() => setDockOpen(true), []);
  // The calls as drafted, by id. The dock shows what the judge was handed for
  // a call, and that is this -- not the report, which holds only its answers.
  const callsById = useMemo(
    () => new Map((drafted?.calls || []).map((c) => [c.id, c])),
    [drafted],
  );
  return {
    ...state, live, liveReport, summary, dockOpen, closeDock, openDock, callsById, run, rerun, stop,
    ready: !!creds?.provider && !!key,
    // Asking needs only a chat model. A trace with no tool calls has nothing
    // for the judge to read, but its conversation is still worth asking about,
    // and a panel gated on the judge was unreachable on every chat trace.
    canAsk: !!creds?.provider,
    chatStatus: credsStatus,
    // Whether there is anything for the judge to read at all.
    hasCalls: !!drafted?.calls?.length,
  };
}

// What the reading strip says about itself, in every state. Extracted so the
// canvas strip states exactly what the DOM strip stated: the run's OUTCOME
// leads, because appended at the tail it renders inside the rail's clipped
// region and the run looks like it did nothing.
export function readingSummaryLine({ status, report, error, progress, delta, fresh, live = null }) {
  if (status === "running") {
    // Once verdicts start landing they ARE the progress: how much has been read
    // and what it found says more than how many batches are outstanding, and it
    // leads the line because the rail clips the tail.
    const t = live?.totals;
    if (t?.calls) {
      return `read ${t.calls} call${t.calls === 1 ? "" : "s"} \u00b7 ${t.accepted} accepted, ${t.rejected} disputed, ${t.additions} addition${t.additions === 1 ? "" : "s"}${
        t.reused ? ` \u00b7 ${t.reused} reused` : ""
      }${progress?.total > 1 ? ` \u00b7 batch ${Math.min(progress.done + 1, progress.total)} of ${progress.total}` : ""}`;
    }
    return progress?.total > 1
      ? `judging\u2026 ${progress.done} of ${progress.total} batches${progress.reused ? ` \u00b7 ${progress.reused} calls reused` : ""}`
      : `judging every call\u2026${progress?.reused ? ` (${progress.reused} reused)` : ""}`;
  }
  if (status === "error") return error?.message || "the reading returned no report";
  const t = report?.totals;
  if (!t) return "";
  const rerunNoop = !delta && !fresh && report?.reuse && report.reuse.judged === 0 && report.reuse.hits > 0;
  const failures = report?.judgeFailures || null;
  const promoted = t?.promoted || null;
  return `${
    failures ? `${failures.calls} call${failures.calls === 1 ? "" : "s"} unjudged (judge error) \u00b7 ` : ""
  }${
    fresh && promoted ? `prior readings: ${promoted.confirmed} confirmed, ${promoted.refuted} refuted \u00b7 ` : ""
  }${
    delta
      ? `vs last read: claims ${delta.prior.claims}\u2192${t.claims}, additions ${delta.prior.verified}\u2192${t.verified}, disputed ${delta.prior.rejected}\u2192${t.rejected} \u00b7 `
      : fresh
        ? `re-read ${report?.reuse?.judged ?? 0} calls \u2014 unchanged vs last read \u00b7 `
        : rerunNoop ? `nothing re-judged (${report.reuse.hits} reused) \u00b7 ` : ""
  }${t.accepted}/${t.claims} claims accepted \u00b7 ${t.verified} verified addition${t.verified === 1 ? "" : "s"} \u00b7 ${t.rejected} disputed${t.discarded ? ` \u00b7 ${t.discarded} unanchored discarded` : ""}`;
}

// The default export appends the CAPTURED RESPONSE to the turns before the
// view builds anything. A trace is a request body: every reply it shows was
// recovered from a later request's echo, so the reply to the request being
// viewed — the final beat of the session, and the message that seeds whatever
// another agent does next — has never been on this axis. The conversation
// model stores observed responses now; when one exists for this trace it
// becomes the last turn, and every consumer (lanes, beats, density, the dock)
// picks it up positionally with no special cases.
export default function TraceLanesView({ trace = null, turns, ...rest }) {
  const traceId = trace?.sessionTranscript ? null : trace?.id ?? null;
  const sessionId = trace?.sessionId ?? null;
  const timestamp = trace?.timestamp ?? null;
  const [response, setResponse] = useState(null);
  useEffect(() => {
    setResponse(null);
    if (!traceId || !sessionId) return undefined;
    let dead = false;
    const ctl = new AbortController();
    fetchTraceResponse({ traceId, sessionId, timestamp, signal: ctl.signal }).then((res) => {
      if (!dead && res) setResponse(res);
    }).catch((err) => {
      // A failed read is the "unavailable" state the API already models — a
      // silent null here would render no legend chip at all, indistinguishable
      // from a deployment that was never asked.
      if (!dead) setResponse({ status: "unavailable", reason: String(err?.message || err) });
    });
    return () => {
      dead = true;
      ctl.abort();
    };
  }, [traceId, sessionId, timestamp]);
  const effTurns = useMemo(() => {
    if (response?.status !== "captured") return turns;
    const turn = responseTurnFromParts(response.parts, {
      messageId: response.messageId, sourceEventId: response.sourceEventId,
      at: response.at, capture: response.capture, completeness: response.completeness,
    });
    const [unwrappedTurn] = turn ? unwrapCodeModeTurns([turn]) : [turn];
    return appendCapturedResponse(turns, unwrappedTurn);
  }, [turns, response]);
  return <TraceLanesViewInner trace={trace} turns={effTurns} responseStatus={response?.status ?? null} {...rest} />;
}

const NO_SHELL_ITEMS = [];

function TraceLanesViewInner({ trace = null, turns, onOpenTrace = null, onScopeNavigate = null, traceTimestamp = undefined, densityBasis = null, threads = null, fill = false, responseStatus = null, collapseGroups = false, followNeedles = null, axisNotes = null, record = null, onLoadEarlier = null, alertTurns = null, turnFocus = null, turnFocusArrival = 0, onTurnFocus = null, findingFocus = null, graphKey = null, graphClaimFocus = null, onGraphClaimLanded = null, onGraphClose = null, onOpenGraphChat = null, onGraphReply = null, canvas = null }) {
  // The reading hook lives ABOVE the model on purpose: a finished reading's
  // verified additions materialize as lane ops, so the model rebuilds with
  // them the moment the report lands (or restores from cache on mount).
  const reading = useLanesReading(trace, turns, { defer: !!canvas });
  const readingOpen = reading.status !== "idle";
  const readHere = useMemo(() => readOnBoard(reading.ready, reading.run, canvas?.workspaceId), [reading.ready, reading.run, canvas?.workspaceId]);
  // One stamp per turn, shared by the dock header and the hover cards so both
  // readings of the same turn agree on when it happened.
  const turnStamps = useMemo(() => buildTurnStamps(turns ?? []), [turns]);
  // The shell door's own ops. They join the model through `extraItems`, so a
  // file read spelled `cat x.js` lands on that file's lane instead of in a lane
  // whose identity is the string "Bash", and each op carries how far the
  // command behind it could reach. There is no strip for either: what a reading
  // pass placed or cleared is a fact about the pass, and the lanes are where a
  // reader looks for what the agent did.
  // One table of the calls the agent made, read by the shell door and by the
  // model, so both pair a call with its result the same way and every packet
  // names the call it came from.
  const actions = useMemo(() => buildTraceActions(turns ?? []), [turns]);
  const shellWork = useTraceShellWork(turns, actions);
  // The plane's pick handler is memoized on purpose -- the plane re-renders on
  // its identity, and `reading` is a fresh object every render, so listing it
  // as a dependency would rebuild the handler per frame of a live run. The
  // reading rides a ref assigned during render instead, so a pointer event
  // always reads the current one. Without this the handler captured the
  // reading from the render that built it, and a strip-row click asked a
  // report that was still null.
  const readingRef = useRef(reading);
  readingRef.current = reading;
  const readingHarvest = useMemo(
    () => (reading.status === "done" ? readingHarvestByCall(reading.report) : null),
    [reading.status, reading.report],
  );
  // Two sources of extra items, and they are different tiers: the reading's are
  // model-verified, the shell door's are grammar-derived. Both sit outside the
  // feed's claim accounting for the same reason.
  // On a canvas (the workspace) the anchor takes its shell reads once they are
  // all in: each partial pass redrew the card's rows while the board laid
  // itself out around it. The lanes page fills them in as they come.
  const shellItems = canvas && shellWork.status === "running" ? NO_SHELL_ITEMS : shellWork.items;
  const model = useMemo(
    () => buildReadLanes(turns, { report: reading.status === "done" ? reading.report : null, shellItems, actions }),
    [turns, reading.status, reading.report, shellItems, actions],
  );
  const { turnCount } = model;
  // Token density per turn. Mass-only from the turns alone; when the caller's
  // basis prices (level memo below), the bars carry the calibrated cost
  // attribution instead. The kill switch that suppresses every other cost
  // surface suppresses the $ here too. Keyed on the basis' primitive fields
  // (plus the stable sessionRows array), not the basis' identity -- callers
  // pass an inline object, and rebuilding the model per parent render would
  // re-walk every block of a large conversation for nothing.
  const hasBasis = densityBasis != null;
  const dbInput = densityBasis?.totals?.input ?? null;
  const dbOutput = densityBasis?.totals?.output ?? null;
  const dbCacheRead = densityBasis?.totals?.cacheRead ?? null;
  const dbCacheWrite = densityBasis?.totals?.cacheWrite ?? null;
  const dbInclusiveCacheRead = densityBasis?.totals?.inclusiveCacheRead ?? null;
  const dbProvider = densityBasis?.provider ?? "";
  const dbModel = densityBasis?.model ?? "";
  const callsTotal = densityBasis?.callsTotal ?? null;
  const dbGrain = densityBasis?.grain ?? "";
  // Stable array identity from getTrace — safe as a memo key even though the
  // enclosing densityBasis object is rebuilt per parent render.
  const dbSessionRows = densityBasis?.sessionRows ?? null;
  const dbSubmissionId = densityBasis?.currentSubmissionId ?? "";
  // Which measured spend the density strip attributes, best level first:
  //   session  — the session's spend rows, cut to the runs up through this one
  //              when the viewed capture's run is identifiable ("through this
  //              run"); otherwise the whole session. The conversation on the
  //              axis is session history, so this is the level the shape in
  //              traceTurnDensity.js models.
  //   fallback — the prompt's own grain-aware rollup (no session rows, or a
  //              session row's model missing from the catalog): honest but
  //              partial, named by the grain that answered. A user_day rollup
  //              is shared beyond this session — attributing it here would
  //              overstate, so that grain stays mass-only.
  const level = useMemo(() => {
    if (!hasBasis || !tokenCostEnabled()) return null;
    const rows = Array.isArray(dbSessionRows) ? dbSessionRows : [];
    if (rows.length) {
      let used = rows;
      let toDate = false;
      if (dbSubmissionId) {
        const cutoffs = rows
          .filter((r) => r.submissionId === dbSubmissionId)
          .map((r) => parseServerTimestamp(r.lastObservedAt))
          .filter((t) => Number.isFinite(t));
        if (cutoffs.length) {
          const cutoff = Math.max(...cutoffs);
          used = rows.filter((r) => {
            if (r.submissionId === dbSubmissionId) return true;
            const t = parseServerTimestamp(r.lastObservedAt);
            return Number.isFinite(t) && t <= cutoff;
          });
          toDate = true;
        }
      }
      const bands = sumCostBands(used);
      if (bands.priced) {
        const runs = new Set(used.map((r) => r.submissionId).filter(Boolean)).size;
        // The viewed run's own spend, for the tooltip's "this run alone"
        // line — only meaningful when the prompt rollup IS run-grain.
        const runUsd = dbGrain === "submission"
          ? applyTokenRates(
            { input: dbInput, output: dbOutput, cacheRead: dbCacheRead, cacheCreation: dbCacheWrite, inclusiveCacheRead: dbInclusiveCacheRead },
            getModelCost(dbProvider, dbModel),
          )?.total ?? null
          : null;
        return {
          kind: toDate ? "session-to-date" : "session",
          bandUsd: bands,
          usd: bands.total,
          calls: used.reduce((a, r) => a + (r.callsTotal || 0), 0),
          runs: runs > 0 ? runs : null,
          runUsd,
          runCalls: runUsd != null ? callsTotal : null,
        };
      }
    }
    if (dbGrain === "user_day") return null;
    const bands = applyTokenRates(
      { input: dbInput, output: dbOutput, cacheRead: dbCacheRead, cacheCreation: dbCacheWrite, inclusiveCacheRead: dbInclusiveCacheRead },
      getModelCost(dbProvider, dbModel),
    );
    if (!bands || !(bands.total > 0)) return null;
    const kind = dbGrain === "turn" ? "prompt" : dbGrain === "session" ? "session" : "run";
    return { kind, bandUsd: bands, usd: bands.total, calls: callsTotal, runs: null, runUsd: null, runCalls: null };
  }, [hasBasis, dbSessionRows, dbSubmissionId, dbGrain, dbInput, dbOutput, dbCacheRead, dbCacheWrite, dbInclusiveCacheRead, dbProvider, dbModel, callsTotal]);
  const density = useMemo(
    () => buildTurnDensity(turns, { bandUsd: level?.bandUsd ?? null }),
    [turns, level],
  );
  // The same mass the strip bars per turn, re-split per SUBJECT: the strip says
  // WHEN the context filled up, the rail fills say WHAT filled it. Derived from
  // the turns alone, so it neither needs nor inherits the measured basis.
  const weights = useMemo(() => buildLaneWeights(turns, model), [turns, model]);
  // Deliberately NOT keyed on the fold state: folding changes which subtree is
  // shown as one row, never what that subtree weighs.
  const rowWeights = useMemo(() => subtreeWeights(model, weights), [model, weights]);
  // Rates again for the threads panel's per-segment spend (exact per-call
  // bands, not the calibrated attribution the strip uses).
  const threadRates = useMemo(
    () => (hasBasis && tokenCostEnabled() ? getModelCost(dbProvider, dbModel) : null),
    [hasBasis, dbProvider, dbModel],
  );
  // This conversation's turn fingerprints - what a context-trim shard's
  // retained turns are matched against to blame it onto this thread.
  // UNCAPPED: a shard's retained window can sit anywhere in a long
  // conversation (trims drop the head too), so the match target must cover
  // every turn, not just the first forty the candidate side is capped to.
  const currentTurnKeys = useMemo(() => turnFingerprints(turns, Infinity), [turns]);
  // The containment target for compacted-from anchors: a later chain's
  // summary quotes this conversation's content when it was compacted from it.
  const currentStableText = useMemo(() => conversationStableText(turns), [turns]);
  // The hand-off strip exists only once asked.
  const handoffs = useLanesHandoffs(trace);
  const handoffOpen = handoffs.status !== "idle";

  // The content-joins lens: one fetch feeds the summary section and the axis
  // annotation strip. Inert inside spliced insets — a nested lanes view must
  // never fire its own fan-out per expansion.
  //
  // Exposure bounds, stated because they are easy to misread as absent: this
  // is NOT a default-on fleet surface. The Lanes view itself ships behind the
  // launched dashboard surface, the
  // per-trace cache absorbs reopens, the refcounted flight collapses the
  // mount pair, and switching traces aborts unstarted probes — so at most one
  // bounded batch runs per open lanes view, for opted-in readers only. The
  // per-open cost is stated in the PR record; the multi-term search operator
  // is the planned collapse to one query per open.
  // Wall-clock span of the whole session, from the DB rows the density basis
  // already loads. Needed BEFORE the joins lens fires: its probe window must
  // cover the session, not a point (see traceJoinWindow).
  const dbSessionRowsEarly = densityBasis?.sessionRows ?? null;
  const sessionSpan = useMemo(() => {
    const times = (Array.isArray(dbSessionRowsEarly) ? dbSessionRowsEarly : [])
      .map((r) => parseServerTimestamp(r.lastObservedAt))
      .filter((t) => Number.isFinite(t));
    return times.length >= 2 ? { from: Math.min(...times), to: Math.max(...times) } : null;
  }, [dbSessionRowsEarly]);
  // The joins' focal span accepts a SINGLE run row where the spliced-lane
  // anchor cannot: interpolation needs two points, but the time flags only
  // need honest bounds. One row carries the run's LAST-observed time, which
  // proves an AFTER (a counterpart first seen later than everything observed
  // genuinely followed) and can never prove a BEFORE — a point-span at the
  // end would stamp "predates this session" on concurrent partners. So the
  // single-run span is half-open: to without from (found live: a single-run
  // mailbox trace's downstream carriers drew no flow line at all).
  const joinFocalSpan = useMemo(() => {
    const times = (Array.isArray(dbSessionRowsEarly) ? dbSessionRowsEarly : [])
      .map((r) => parseServerTimestamp(r.lastObservedAt))
      .filter((t) => Number.isFinite(t));
    if (times.length >= 2) return { from: Math.min(...times), to: Math.max(...times) };
    if (times.length === 1) return { from: null, to: times[0] };
    return null;
  }, [dbSessionRowsEarly]);
  const joinSearchWindow = useMemo(() => traceJoinWindow(joinFocalSpan, traceTimestamp), [joinFocalSpan, traceTimestamp]);
  const pipelineReports = useLanesReports({ trace, turns, enabled: !collapseGroups });
  const reportsTimeline = pipelineReports.timeline;
  // The reports strip opens itself whenever the pipeline has reviewed this
  // conversation at all: the chapters are the context every other mark sits
  // in, and a band saying "not reviewed" on every unreviewed trace would tax
  // the common case for nothing.
  const reportsOpen = !!reportsTimeline && reportsTimeline.reports.length > 0;
  const findings = useLanesFindings({ trace, turns, model, traceTimestamp, enabled: !collapseGroups });
  const findingsReport = findings.report;
  // The findings strip opens itself whenever the pipeline has findings for this
  // conversation, located or not: a finding the loaded turns cannot place is
  // still a finding, and hiding it would make "nothing flagged" and "flagged,
  // could not place" look the same. A band saying "no findings" on every trace
  // would tax the timeline for the common case, and "never reviewed" versus
  // "reviewed clean" is the report table's distinction, not this strip's.
  const findingsOpen = !!findingsReport && findingsReport.incidents.length > 0;
  const reviewProfile = useLanesReviewProfile(findingsOpen, pipelineReports.rows);
  const findingsAtTurn = useMemo(() => (findingsOpen ? findingsByTurn(findingsReport) : null), [findingsOpen, findingsReport]);
  const findingPins = useMemo(() => (findingsOpen ? findingCellPins(findingsReport, incidentTone) : []), [findingsOpen, findingsReport]);
  // Recurrence over the findings: the same conduct raised on later turns,
  // folded into incidents by what they cite (traceReviewIncidents.js). A view
  // over the report, so the strip can draw a habit's reach and the dock can
  // say "this one again" without a row changing.
  const findingIncidents = useMemo(() => (findingsOpen && findingsReport ? incidentsOf(findingsReport) : null), [findingsOpen, findingsReport]);
  // The chat's knowledge graph, when this tab was opened from a chat. It is the
  // reader's own investigation, so its strip shows whenever the link carried
  // one, and says so when the graph names nothing in this trace.
  const chatGraph = useLanesGraph({ graphKey, trace, turns, model, enabled: !collapseGroups });
  const graphOverlay = chatGraph.overlay;
  const graphPins = useMemo(() => (graphOverlay ? graphCellPins(graphOverlay) : []), [graphOverlay]);
  // The graph's claims as comments: where each is pinned, which pin's card is
  // open (named by one of the anchors it gathers, since zoom re-clusters), and
  // which claims the reader has seen, per chat.
  const badgeAnchors = useMemo(() => (graphOverlay ? graphBadgeAnchors(graphOverlay) : []), [graphOverlay]);
  // The strip marks the turns claims rest on, except where a comment pin
  // stands on the strip itself: the pin is that turn's mark.
  const graphMarks = useMemo(() => {
    if (!graphOverlay) return [];
    const pinned = new Set(badgeAnchors.filter((a) => a.lane == null).map((a) => a.turn));
    return graphStripMarks(graphOverlay, { skipTurns: pinned });
  }, [graphOverlay, badgeAnchors]);
  const [openPin, setOpenPin] = useState(null); // { id, claims }
  const openPinRef = useRef(null);
  openPinRef.current = openPin;
  const seenStore = useMemo(() => createSeenStore(), []);
  const [seenClaims, setSeenClaims] = useState(null);
  const graphReady = !!graphOverlay;
  useEffect(() => {
    if (!graphKey || !graphReady) { setSeenClaims(null); return; }
    setSeenClaims(seenStore.baseline(graphKey, graphOverlay.claims.map((c) => c.id)));
    // Once per chat: claims that arrive afterwards are the new ones.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphKey, graphReady, seenStore]);
  const isUnseen = useCallback((id) => !!seenClaims && !seenClaims.has(id), [seenClaims]);
  useEffect(() => { setOpenPin(null); }, [trace?.id]);
  // Closing a card is what makes its claims seen; so is moving to another.
  const markPinSeen = useCallback((pin) => {
    if (pin && graphKey) setSeenClaims(seenStore.markSeen(graphKey, pin.claims));
  }, [graphKey, seenStore]);
  const closePin = useCallback(() => {
    markPinSeen(openPinRef.current);
    setOpenPin(null);
  }, [markPinSeen]);
  const contentJoins = useTraceContentJoins({ trace, turns, traceTimestamp, model, searchWindow: joinSearchWindow, focalSpan: joinFocalSpan, enabled: !collapseGroups });
  // Its setup's strings set aside as substrate (joinContextOf).
  const firstUserTurn = useMemo(() => firstUserTurnOf(turns), [turns]);
  const joinContext = useMemo(() => joinContextOf(contentJoins, firstUserTurn), [contentJoins.status, contentJoins.needles, firstUserTurn]); // eslint-disable-line react-hooks/exhaustive-deps
  const joinRows = useMemo(() => joinRowsOf(contentJoins, joinContext), [contentJoins.status, contentJoins.rows, joinContext]); // eslint-disable-line react-hooks/exhaustive-deps
  // The one lens object every join surface reads — the strip's summary line,
  // its rail tooltip, and the marks all speak the demoted rows and the
  // substrate count, so no surface can claim a number another one denies.
  const joinLens = useMemo(
    () => ({ ...contentJoins, rows: joinRows, contextStrings: joinContext.contextStrings }),
    [contentJoins, joinRows, joinContext],
  );
  const joinMarks = useMemo(
    () => (contentJoins.status === "done" ? buildJoinTurnMarks(joinContext.needles, joinRows) : []),
    [contentJoins.status, joinContext, joinRows],
  );
  // The strip is the feature's whole surface (there is no panel), so it shows
  // from the moment the lens starts: status in the rail, marks when they land.
  const joinsOpen = contentJoins.status !== "idle" && contentJoins.needles.length > 0;
  // One identity hue per counterpart session, match-ranked, shared by every
  // join surface (strip, pins, tooltips, dock) so a colour READS as a who.
  const joinHues = useMemo(
    () => (contentJoins.status === "done" ? assignJoinHues(joinRows) : null),
    [contentJoins.status, joinRows],
  );
  // Which counterparts are observer-shaped, for the marks: a mark whose
  // every session is an observer renders demoted (watch glyph, muted chrome)
  // — it says "seen by a watcher", not "exchanged with a peer".
  const joinObserverIds = useMemo(
    () => (contentJoins.status === "done"
      ? new Set(joinRows.filter((r) => r.observer).map((r) => r.session_id))
      : null),
    [contentJoins.status, joinRows],
  );
  // One stable window object per timestamp: rebuilding it per render re-keyed
  // every mirror-fetch effect by identity.

  // collapseGroups (the spliced-inset reading) opens with every group folded:
  // the inset is an overview of the child conversation, and unfolding is one
  // click. Initializer-only on purpose - after mount the fold state is the
  // reader's.
  const [closed, setClosed] = useState(() => (collapseGroups ? new Set(model.groups.map((g) => g.id)) : new Set()));
  // { laneId: string|null, turn: number, tab: "subject"|"turn" } — one
  // selection drives the playhead, the lane highlight, AND which dock reading
  // is open. tab is a preference: with no lane selected, "turn" is all there is.
  const [sel, setSel] = useState(null);
  // A head carries its subtree's weight whether it is open or folded, the way
  // its sparkline carries the subtree's activity: a fill that moved from the
  // children to the parent on fold read as the data changing (found live).
  const baseRows = useMemo(() => flattenRows(model, closed).map((r) => (
    r.type === "head" ? { ...r, weight: rowWeights.get(r.id) || null } : r
  )), [model, closed, rowWeights]);
  // Which agent lanes have their sub-agent's own timeline spliced in. The
  // child is a different conversation with a different clock, so the inset
  // row it injects hosts a nested lanes view rather than marks on this axis.
  // Reset when the trace changes - lane ids are per-model.
  const [openAgents, setOpenAgents] = useState(() => new Set());
  useEffect(() => setOpenAgents(new Set()), [model]);
  const toggleAgent = useCallback((id) => {
    setOpenAgents((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const canSplice = Boolean(threads?.loadLaneTurns);
  const rows = useMemo(() => {
    if (!openAgents.size) return baseRows;
    const out = [];
    for (const r of baseRows) {
      const open = r.type === "lane" && r.lane.domain === "agents" && openAgents.has(r.lane.id);
      out.push(open ? { ...r, expanded: true } : r);
      if (open) out.push({ type: "inset", lane: r.lane });
    }
    return out;
  }, [baseRows, openAgents]);

  // A spliced child is a plane: its groups fold, it zooms and it pans on its
  // own. One shared set of state would fold or zoom every open thread at once,
  // so each is keyed by the agents lane it hangs under.
  const [insetClosed, setInsetClosed] = useState(() => new Map());
  const [insetZoom, setInsetZoom] = useState(() => new Map());
  const [insetPan, setInsetPan] = useState(() => new Map());

  // The system band: the session's OS telemetry on the turn axis, plus the
  // one open bucket's detail reading. The pick is cleared whenever the trace
  // identity changes — a slice of one session's telemetry must not stay open
  // over another's lanes.
  const sys = useSystemTelemetry({ trace, turns });
  // Where in wall-clock time a spliced lane's child opened, best evidence
  // first. The record dates every turn by the capture that shipped it, and
  // the captures around the spawn bound the child's origin exactly
  // (spawnWindowFromTurns) - the span. The clock estimate stands in when the
  // record cannot date the spawn: the OS-telemetry clock once it has settled,
  // else an interpolation over the session's own run times - approximate,
  // and the join widens its search window to match.
  const spawnAnchorOf = useCallback((lane) => {
    const turn = lane.turns[0];
    if (turn == null) return null;
    const span = spawnWindowFromTurns(turns, lane);
    const clock = sys.status === "ready" ? sys.clock?.times?.[turn] : null;
    let anchorMs = Number.isFinite(clock) ? clock : null;
    if (anchorMs == null && sessionSpan && turnCount > 1) {
      anchorMs = sessionSpan.from + (turn / (turnCount - 1)) * (sessionSpan.to - sessionSpan.from);
    }
    return span || anchorMs != null ? { anchorMs, span, sessionFromMs: sessionSpan?.from ?? null } : null;
  }, [turns, sys, sessionSpan, turnCount]);
  // The child conversations behind the open agent lanes, held here because the
  // parent is what builds their sub-scenes.
  const children = useSplicedChildren(openAgents, model, threads, spawnAnchorOf);
  // The focal request's capture — the ceiling of the turn axis.
  const focalMs = useMemo(() => {
    const t = trace?.timestamp;
    if (t instanceof Date) return t.getTime();
    const ms = parseServerTimestamp(t);
    return Number.isFinite(ms) ? ms : null;
  }, [trace?.timestamp]);
  // ── Topics ────────────────────────────────────────────────────────────────
  // The session's cluster journey, dated onto this axis: one bounded
  // per-session query (lib/sessionJourney.js), read through the shared result
  // cache. A topic keeps one hue wherever this axis' own ranking allows
  // (topicHues).
  const journeyState = useSessionJourney(trace);
  const journey = journeyState.status === "ready" ? journeyState.journey : null;
  // Preserve identified observation times, including undated turns. When no
  // message clock exists, the topic strip labels its session-span estimate.
  const topicClock = useMemo(() => {
    const rows = journey?.rows;
    const floorMs = rows?.length ? rows[0].tsMs : null;
    if (sys.status === "ready" && sys.clock?.times?.length === turnCount) {
      return { times: sys.clock.times, dated: sys.source, estimatedBefore: 0 };
    }
    if (!rows?.length || turnCount < 2 || !Number.isFinite(focalMs) || !(focalMs > floorMs)) return null;
    const times = new Array(turnCount);
    for (let i = 0; i < turnCount; i += 1) times[i] = floorMs + ((focalMs - floorMs) * i) / (turnCount - 1);
    return { times, dated: "span", estimatedBefore: turnCount };
  }, [sys, journey, turnCount, focalMs]);
  const topicAxis = useMemo(
    () => (journey && topicClock ? buildTopicAxis(journey.rows, topicClock.times) : null),
    [journey, topicClock],
  );
  // One ranking and one run list, shared by the strip's labels and legend, the
  // hue assignment, and the plane's guides.
  const topicRanked = useMemo(() => (topicAxis ? rankTopics(topicAxis.topicOf) : []), [topicAxis]);
  const topicHueMap = useMemo(() => topicHues(journey?.topClusters || [], topicRanked), [journey, topicRanked]);
  const topicRunList = useMemo(() => (topicAxis ? topicRuns(topicAxis.topicOf) : []), [topicAxis]);
  const [sysOpen, setSysOpen] = useState(true);
  // The pin survives across traces and sessions: a reader who wants the
  // telemetry footer wants it every time, not per trace.
  const [sysPinned, setSysPinned] = useState(() => {
    try {
      return localStorage.getItem("lanesSystemPinned") === "1";
    } catch {
      return false;
    }
  });
  const toggleSysPin = useCallback(() => {
    setSysPinned((v) => {
      try {
        localStorage.setItem("lanesSystemPinned", v ? "0" : "1");
      } catch {
        // Preference storage is a nicety; the toggle still works for the session.
      }
      return !v;
    });
  }, []);
  const [sysPick, setSysPick] = useState(null);
  const traceKey = trace?.id ?? null;
  // Both picks name a turn of the trace they were made in, and the
  // open-counterpart hop swaps the trace WITHOUT remounting this component.
  useEffect(() => {
    setSysPick(null);
    setSel(null);
  }, [traceKey]);
  // A bucket pick is a SELECTION: it opens the same dock every other reading
  // opens, keyed as a synthetic system lane, and moves the playhead to the
  // slice. Re-picking the open bucket closes it.
  const pickSystem = useCallback((range) => {
    if (!range) {
      setSysPick(null);
      setSel(null);
      return;
    }
    setSysPick(range);
    setSel({ laneId: `system:${range.kind}`, turn: range.end, tab: "subject" });
  }, []);
  const systemSel = sel?.laneId?.startsWith("system:") ? sysPick : null;
  const systemSubject = useMemo(() => {
    if (!systemSel || sys.status !== "ready") return null;
    const def = SYSTEM_STREAM_KINDS.find((k) => k.key === systemSel.kind);
    if (!def) return null;
    const occupied = [...sys.perTurn.entries()]
      .filter(([, b]) => b[systemSel.kind])
      .map(([t]) => t)
      .sort((a, b) => a - b);
    const total = occupied.reduce((a, t) => a + (sys.perTurn.get(t)[systemSel.kind] || 0), 0);
    const prevTurn = [...occupied].reverse().find((t) => t < systemSel.start) ?? null;
    const nextTurn = occupied.find((t) => t > systemSel.end) ?? null;
    return {
      ...systemSel,
      label: def.label.toLowerCase(),
      total,
      prevTurn,
      nextTurn,
      hosted: sys.hosted,
      readSessionId: sys.readSessionId,
      endpointId: trace?.endpointId ?? null,
      clockTimes: sys.clock.times,
      spanFromTs: sys.span.fromTs,
      spanToTs: sys.span.toTs,
    };
  }, [systemSel, sys, trace]);

  // Zoom: px per turn. Starts at 1:1 and snaps to fit once the scroller is
  // measurable, unless the reader has already zoomed by hand.
  const [cellW, setCellW] = useState(CELL_1X);
  const userZoomedRef = useRef(false);
  const rootRef = useRef(null);
  const pendingScrollRef = useRef(null);
  // The grid scrolls inside the house ScrollArea (OverlayScrollbars) so its
  // bars match every other surface of the panel; the SCROLLING element is the
  // instance's viewport, not the host, and everything that reads or drives
  // scroll position binds to it.
  const [scrollEl, setScrollEl] = useState(null);
  const scrollElRef = useRef(null);
  scrollElRef.current = scrollEl;
  const [view, setView] = useState({ left: 0, width: 0, top: 0 });
  // Fit FILLS the frame: the turns divide the available width and nothing
  // caps the result, so a fourteen-turn trace spreads its columns across the
  // panel instead of stopping at 1:1 with four fifths of the frame empty. The
  // zoom range then stretches to reach it — out to fit, or to 1:1 when fit is
  // the wider of the two, and in to fit past the nominal cell cap. (Same rule
  // as the scout's method lanes, which fit far fewer columns than a trace.)
  // The rail's width, the reader's to drag at the seam between the names and
  // the axis. Every x on the axis is offset by it, so it is an input to the
  // scene rather than a constant the plane reads.
  const [railW, setRailW] = useState(() => {
    try { return clampRailW(localStorage.getItem(RAIL_W_KEY) ?? RAIL_W); } catch { return RAIL_W; }
  });
  const railWRef = useRef(railW);
  railWRef.current = railW;
  const startRailDrag = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startW = railWRef.current;
    const move = (ev) => {
      const next = clampRailW(startW + (ev.clientX - startX));
      railWRef.current = next;
      setRailW(next);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      try { localStorage.setItem(RAIL_W_KEY, String(railWRef.current)); } catch { /* private mode: the width lasts the session */ }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }, []);

  const fitRef = useRef(CELL_1X);
  const minZoomRef = useRef(CELL_MIN);
  const maxZoomRef = useRef(CELL_MAX);
  if (view.width > railW + 60 && turnCount > 0) {
    fitRef.current = Math.max(0.4, (view.width - railW - 2) / turnCount);
    minZoomRef.current = Math.max(CELL_MIN, Math.min(fitRef.current, CELL_1X));
    maxZoomRef.current = Math.max(CELL_MAX, fitRef.current);
  }
  const clampZoom = useCallback((v) => Math.max(minZoomRef.current, Math.min(maxZoomRef.current, v)), []);

  const fillH = useViewportFill(rootRef, fill);

  useEffect(() => {
    const el = scrollEl;
    if (!el) return undefined;
    let raf = 0;
    let timer = 0;
    const read = () => {
      if (raf) window.cancelAnimationFrame(raf);
      if (timer) window.clearTimeout(timer);
      raf = 0;
      timer = 0;
      // Width comes from the ROOT, not the scroll viewport: the viewport hugs
      // its own content (w-fit), so a fit computed from it chases the width
      // that fit just changed — every settled read walked the axis another
      // couple of pixels narrower, forever. The root is the width-stable
      // panel column; content can't move it. (Viewport fallback for mounts
      // where the root hasn't laid out — and it slightly over-reads the
      // visible window when the grid hugs short, where nothing is off-screen
      // for the pointers to miss anyway.)
      const width = rootRef.current?.clientWidth || el.clientWidth;
      const left = el.scrollLeft;
      // `height` is the VIEWPORT's, and it drives the plane's floor in fill
      // mode so the rail edge and episode gridlines reach the frame's bottom
      // instead of stopping under the last lane.
      const height = el.clientHeight;
      // `top` is the vertical companion to `left`: the related-row pointers need
      // to know which rows are scrolled out of the band, the same way the turn
      // pointers need to know which marks are scrolled out of the window.
      const top = el.scrollTop;
      setView((cur) => (
        cur.left === left && cur.width === width && cur.height === height && cur.top === top
          ? cur
          : { left, width, height, top }
      ));
    };
    read();
    // rAF for frame-aligned reads, with a timeout backstop: rAF is starved
    // in hidden or display-throttled documents, and a one-shot latch with no
    // second path froze the view at its mount-time geometry until the next
    // event AFTER the document became visible again.
    const onScroll = () => {
      if (raf || timer) return;
      raf = window.requestAnimationFrame(read);
      timer = window.setTimeout(read, 64);
    };
    el.addEventListener("scroll", onScroll);
    // Resizes SETTLE before they read: a FLIP panel expansion fires the
    // observer every animation frame, and a fit-mode read per frame re-lays
    // out the entire grid per frame. One read after the animation completes
    // is the whole requirement. The observer catches container-driven
    // resizes (the detail panel expanding); the window listener catches
    // window-driven ones even while the document is hidden, where observer
    // delivery is suspended.
    let settle = 0;
    const onResize = () => {
      if (settle) window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        settle = 0;
        read();
      }, 180);
    };
    window.addEventListener("resize", onResize);
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(onResize) : null;
    // Observe the ROOT (the panel expanding is a root resize; the hugging
    // viewport only follows content, which is downstream of the refit this
    // observation exists to trigger). The viewport is still observed for the
    // fallback case above; its content-driven fires read back the same root
    // width and bail.
    if (rootRef.current) ro?.observe(rootRef.current);
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
      if (timer) window.clearTimeout(timer);
      if (settle) window.clearTimeout(settle);
    };
  }, [scrollEl]);

  // Fit is a MODE, not a one-shot: until the reader zooms by hand, the axis
  // keeps refitting as its container resizes — the detail panel expands from
  // side column to full width (and back), and a fit computed at one width is
  // just a stale zoom at every other. The first useful measure is simply the
  // first refit.
  useEffect(() => {
    if (userZoomedRef.current || view.width <= railW + 60 || !turnCount) return;
    setCellW((cur) => (cur === fitRef.current ? cur : fitRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.width, turnCount, railW]);

  const zoomAt = useCallback((factor, viewportOffset = null) => {
    userZoomedRef.current = true;
    const el = scrollElRef.current;
    setCellW((cur) => {
      const next = clampZoom(cur * factor);
      if (el && next !== cur) {
        const railW = railWRef.current;
        const off = viewportOffset ?? railW + (el.clientWidth - railW) / 2;
        const t = (el.scrollLeft + off - railW) / cur;
        pendingScrollRef.current = Math.max(0, railW + t * next - off);
      }
      return next;
    });
  }, [clampZoom]);
  const zoomTo = useCallback((value) => {
    userZoomedRef.current = true;
    setCellW(clampZoom(value));
  }, [clampZoom]);
  const width = Math.max(1, Math.ceil(turnCount * cellW));

  // A page prepended to the axis (a session reading's "earlier", or the
  // conversation record landing in front of a capture) shifts every turn index
  // by the page's length. The reader's place is a MESSAGE, not an index, so the
  // selection, the system pick and the viewport move with it: the same turns
  // stay under the pointer and the same cells stay selected. Detected by the
  // previous first message's new position; a different conversation (the old
  // first message is gone) shifts nothing, and the trace-key reset handles it.
  // A pick inside a spliced child, or on another session's card on the board,
  // is on that conversation's turns, which the page did not move.
  const prependRef = useRef({ first: null });
  useLayoutEffect(() => {
    const prev = prependRef.current.first;
    const first = turns?.[0]?.messageId ?? null;
    prependRef.current = { first };
    if (!prev || !first || prev === first) return;
    const k = turns.findIndex((t) => t?.messageId === prev);
    if (k <= 0) return;
    const el = scrollElRef.current;
    if (el) pendingScrollRef.current = el.scrollLeft + k * cellW;
    setSel((cur) => (cur && !cur.insetOf ? {
      ...cur,
      turn: cur.turn + k,
      ...(cur.joinThrough != null ? { joinThrough: cur.joinThrough + k } : null),
      ...(cur.findingThrough != null ? { findingThrough: cur.findingThrough + k } : null),
    } : cur));
    setSysPick((cur) => (cur ? { ...cur, start: cur.start + k, end: cur.end + k } : cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cellW is read for the shift, not watched
  }, [turns]);
  // The zoom-anchored scroll correction lands in the same layout pass as the
  // width change, so the axis never paints a frame at the old offset.
  useLayoutEffect(() => {
    const el = scrollElRef.current;
    if (el && pendingScrollRef.current != null) {
      el.scrollLeft = pendingScrollRef.current;
      pendingScrollRef.current = null;
    }
  }, [width, cellW]);
  // ⌘/ctrl-scroll (and trackpad pinch, which arrives as ctrl+wheel) zooms at
  // the cursor. Native listener: React's synthetic wheel can't preventDefault.
  useEffect(() => {
    const el = scrollEl;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0022), e.clientX - rect.left);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [scrollEl, zoomAt]);

  const centerOn = useCallback(
    (turn) => {
      const el = scrollElRef.current;
      if (!el || el.clientWidth <= railW) return;
      const left = Math.max(0, turn * cellW + cellW / 2 - (el.clientWidth - railW) / 2);
      if (typeof el.scrollTo === "function") el.scrollTo({ left, behavior: "smooth" });
      else el.scrollLeft = left;
    },
    [cellW, railW],
  );
  // Cell/plane picks select in place; `travel` (rail names, pointers, dock nav)
  // also brings the mark into view. Picking a mark opens the SUBJECT reading;
  // the ruler keeps the current reading while a subject scrub is underway and
  // otherwise opens the TURN reading; a beat is always a TURN reading — it IS
  // conversation, not work on a subject.
  const pick = useCallback((laneId, turn, travel = false, insetOf = null, tab = "subject") => {
    // `insetOf` names the spliced child a pick was made inside, because the
    // lane it names lives in THAT conversation's model, not this one's. `tab`
    // is the reading it opens on: what the cell is about, or the message
    // itself ("turn", as a board's annotation opens its cell).
    setSel({ laneId, turn, tab: tab || "subject", insetOf });
    if (travel) centerOn(turn);
  }, [centerOn]);
  const goTo = useCallback((laneId, turn) => pick(laneId, turn, true), [pick]);
  const pickTurn = useCallback(
    (turn, travel = false) => {
      setSel((cur) =>
        cur?.laneId && cur.tab === "subject"
          ? { ...cur, turn }
          : { laneId: cur?.laneId ?? null, turn, tab: "turn" },
      );
      if (travel) centerOn(turn);
    },
    [centerOn],
  );
  // A join annotation opens the dock ON the hand-off: the reader clicked a
  // statement about another session, so the dock leads with the counterpart
  // reading, with the turn's own content one tab away.
  const pickJoin = useCallback(
    (turn, travel = false, through = turn) => {
      setSel({ laneId: null, turn, joinThrough: through, tab: "joins" });
      if (travel) centerOn(turn);
    },
    [centerOn],
  );
  // A findings mark opens the dock ON the finding: the reader clicked a
  // judgment about the turn, so the dock leads with what was found, the
  // turn's own content one tab away. `through` is the folded mark's reach.
  const pickFinding = useCallback(
    (turn, travel = false, through = turn) => {
      setSel({ laneId: null, turn, findingThrough: through, tab: "findings" });
      if (travel) centerOn(turn);
    },
    [centerOn],
  );
  // A graph mark opens the dock on what the chat established there: the claims
  // resting on the turn and the anchors the walk placed on it. `claimId` names
  // the claim to lead with, whose evidence the plane then draws as a path.
  const pickGraph = useCallback(
    (turn, travel = false, through = turn, claimId = null) => {
      setSel({ laneId: null, turn, graphThrough: through, graphClaim: claimId, tab: "graph" });
      if (travel) centerOn(turn);
    },
    [centerOn],
  );
  // A chapter opens the dock ON the report: the reader clicked the judge's
  // account of a stretch of the session, so the dock leads with the story, the
  // aimed turn's own content one tab away.
  const pickReport = useCallback(
    (turn, index, travel = false) => {
      setSel({ laneId: null, turn, reportIndex: index, tab: "reports" });
      if (travel) centerOn(turn);
    },
    [centerOn],
  );
  const pickBeat = useCallback((turn) => {
    setSel((cur) => ({ laneId: cur?.laneId ?? null, turn, tab: "turn" }));
  }, []);
  // The focal half of a hand-off's traversal (openJoinFrom opens the other
  // half): light the cells that carry the card's needle, right here in the
  // lanes -- same landing treatment a followed hand-off gets, no view change.
  // A repeat click on the same card TOGGLES the highlight off (a highlight
  // the reader cannot turn off is a stain, found live); a different card
  // replaces it; the legend chip's clear works for follows that arrived with
  // the trace and have no card.
  const [localFollow, setLocalFollow] = useState(null);
  // A locate belongs to the trace it was set on. The panel re-points to a
  // new trace WITHOUT remounting, and a lingering locate -- or a lingering
  // clear -- would silently override the follow that arrived staged for the
  // new trace: the exact landing this surface exists for (review finding).
  useEffect(() => {
    setLocalFollow(null);
  }, [trace?.id]);
  const locateJoin = useCallback((entry) => {
    const key = joinFollowNeedleKey(entry);
    if (!key) return;
    setLocalFollow((cur) => (cur?.key === key
      ? { needles: null, key: null, seq: (cur?.seq || 0) + 1 }
      : { needles: key.split("\n"), key, seq: (cur?.seq || 0) + 1 }));
  }, []);
  const clearFollow = useCallback(() => {
    setLocalFollow((cur) => ({ needles: null, key: null, seq: (cur?.seq || 0) + 1 }));
  }, []);
  // The strings to land on: a reader's local gesture (locate or clear) wins
  // over the cross-trace follow that opened this view -- the newer gesture is
  // the current question. Located on the same lane-hinted inputs the census
  // derives its needles from, so a followed string lights exactly the cells
  // a census pin could claim.
  const followStrings = localFollow ? localFollow.needles : followNeedles;
  const followHits = useMemo(() => {
    if (!followStrings?.length || !turns?.length || collapseGroups) return null;
    const hits = locateNeedleStrings(turnsToJoinInputs(turns, model), followStrings);
    return hits.length ? hits : null;
  }, [followStrings, turns, model, collapseGroups]);
  useEffect(() => {
    if (!followHits?.length) return;
    centerOn(Math.min(...followHits.map((h) => h.turns[0])));
  }, [followHits, localFollow?.seq, centerOn]);

  // The turn range carried in from another reading (lib/traceTurnFocus.js).
  // Selecting the range's first turn opens the dock on it; the band that marks
  // the columns is drawn by the scene (selband prim in laneScene.js), because
  // at Fit the axis has no overflow to scroll and a selection alone paints
  // nothing on the plane.
  //
  // Declared AFTER the follow landing above on purpose: both write scroll
  // position, and the follow effect re-fires on centerOn's identity (i.e. on
  // every cellW change). Being last means the reader's carried position is the
  // current question in any shared commit — the same rule followStrings states
  // for itself. It must also stay a useEffect, not a layout effect: the
  // zoom-anchor useLayoutEffect applies pendingScrollRef and would overwrite
  // a scroll issued before it.
  const focusArrivalRef = useRef(-1);  // -1 so the cold mount counts as an arrival
  useEffect(() => {
    if (!turnCount) return;
    if (focusArrivalRef.current === turnFocusArrival) return;
    // No carried turn: consume the arrival anyway. Otherwise the first
    // selection made HERE would come back through onTurnFocus and re-enter as
    // a landing, and the landing's `laneId: null` would wipe the lane the
    // reader just clicked.
    if (!Number.isInteger(turnFocus)) { focusArrivalRef.current = turnFocusArrival; return; }
    // A finding carried in alongside the turn owns the landing -- it is the
    // more specific claim about why the reader is here, and the turn param is
    // written by their own later clicking. Consume the arrival rather than
    // ordering the two effects: this one is ready on the frame the axis is
    // measured, the finding landing below has to wait for the findings read,
    // so whichever runs last would otherwise decide.
    if (findingFocus || graphClaimFocus) { focusArrivalRef.current = turnFocusArrival; return; }
    const el = scrollElRef.current;
    // centerOn is a no-op before the scroller is measured, and cellW is still
    // the pre-fit estimate — so don't consume the arrival yet. Listing cellW /
    // view.width below makes this a self-retrying landing that fires once, on
    // the first frame where the axis is real.
    if (!el || el.clientWidth <= railW) return;
    focusArrivalRef.current = turnFocusArrival;
    const turn = Math.max(0, Math.min(turnFocus, turnCount - 1));
    // setSel directly rather than through pickTurn: pickTurn's first branch
    // spreads `...cur`, which preserves insetOf — an outer turn index applied
    // to a spliced child's axis. Same shape pickBeat produces.
    setSel({ laneId: null, turn, tab: "turn" });
    centerOn(turn);
  }, [turnFocus, turnFocusArrival, turnCount, cellW, view.width, centerOn, railW, findingFocus, graphClaimFocus]);

  // A claim carried in from the chat: open the dock on it, at the first turn
  // its evidence sits on. A claim none of whose evidence the walk could place
  // still opens the graph tab, where it is listed with why; there is no turn
  // to travel to. It waits for the overlay, which waits for the turns.
  const graphArrivalRef = useRef(null);
  const graphReadable = chatGraph.on && !chatGraph.empty;
  useEffect(() => {
    if (!graphClaimFocus || !turnCount) return;
    // A graph that names nothing in this trace cannot carry the claim here.
    if (!graphOverlay) {
      if (graphReadable) onGraphClaimLanded?.();
      return;
    }
    const el = scrollElRef.current;
    if (!el || el.clientWidth <= railW) return;
    const arrival = `${graphClaimFocus}:${turnFocusArrival}`;
    if (graphArrivalRef.current === arrival) return;
    graphArrivalRef.current = arrival;
    // Spent either way: a claim this trace does not carry is a stale link, and
    // leaving it would keep the turn landing standing aside for nothing.
    onGraphClaimLanded?.();
    const claim = graphOverlay.claims.find((c) => c.id === graphClaimFocus);
    if (!claim) return;
    // A claim with a pin opens its card, the way a link to a comment does; one
    // with nothing on the axis opens the dock's list, where it is shown with why.
    const anchor = badgeAnchors.find((a) => a.claims.includes(claim.id));
    if (anchor) {
      centerOn(anchor.turn);
      setOpenPin({ id: anchor.id, claims: anchor.claims });
      setRevealPin(anchor.id);
      return;
    }
    pickGraph(0, false, 0, claim.id);
  }, [graphClaimFocus, graphOverlay, graphReadable, badgeAnchors, turnFocusArrival, turnCount, cellW, view.width, railW, centerOn, pickGraph, onGraphClaimLanded]);

  // A finding carried in by a notification: open the dock ON it, the landing a
  // reader clicking "Open in Origin" asked for. It shares the turn landing's
  // arrival counter -- every door into this reading bumps that one -- and the
  // turn landing above stands aside for it.
  //
  // It waits for the findings read rather than consuming the arrival on the
  // first frame: placement is client-side (quotes matched against the loaded
  // turns), so before the report lands there is nothing to aim at.
  //
  // A finding this conversation carries but whose quotes the loaded page does
  // not is behind it: the landing reads earlier pages, one at a time, until the
  // quotes place it or the record runs out.
  const findingArrivalRef = useRef(-1);
  const findingPagesRef = useRef({ pages: 0, askedAt: null });
  useEffect(() => {
    if (!findingFocus || !turnCount) return;
    if (findingArrivalRef.current === turnFocusArrival) return;
    if (!findingsReport) return;
    const el = scrollElRef.current;
    // Same self-retrying guard the turn landing uses: centerOn is a no-op
    // before the scroller is measured.
    if (!el || el.clientWidth <= railW) return;
    const incident = findIncidentById(findingsReport, findingFocus);
    const at = incident?.turns || [];
    if (!at.length && incident && onLoadEarlier) {
      // A page on its way may carry the quotes: wait for it. The page asked
      // for is on its way until the loaded count moves, which also covers the
      // frames before the record reports it is loading.
      const asked = findingPagesRef.current;
      if (record?.session && (record.loading || record.pending || asked.askedAt === record.loaded)) return;
      if (landingMayReadEarlier(record, asked.pages)) {
        findingPagesRef.current = { pages: asked.pages + 1, askedAt: record.loaded };
        onLoadEarlier();
        return;
      }
    }
    findingArrivalRef.current = turnFocusArrival;
    // A finding whose quotes the whole readable record does not carry has no
    // position on the axis at all. The strip still lists it, which is the
    // whole of what can be shown, so travelling somewhere arbitrary would be
    // worse.
    if (!at.length) return;
    pickFinding(at[0] - 1, true, at[at.length - 1] - 1);
  }, [findingFocus, turnFocusArrival, turnCount, findingsReport, cellW, view.width, railW, pickFinding, record, onLoadEarlier]);

  // Report the reader's SELECTION back out — picking a cell, a beat, or
  // scrubbing the dock are all deliberate "this turn" acts, and they are what
  // the other readings should land on. Panning is not: it moves the viewport,
  // not the reader's claim about which turn matters.
  const reportedSelRef = useRef(false);
  useEffect(() => {
    if (!onTurnFocus) return;
    // A cell of another session, read from the session graph, is no turn of
    // this trace's: the page's own turn stays where it was.
    if (String(sel?.insetOf || "").startsWith("board:")) return;
    if (Number.isInteger(sel?.turn)) {
      reportedSelRef.current = true;
      onTurnFocus(sel.turn);
      return;
    }
    // Only a selection that was CLEARED deselects. The null before the first
    // pick is "nothing chosen yet", and reporting it would wipe the turn a
    // deep link or another reading carried in before the landing runs.
    if (!reportedSelRef.current) return;
    reportedSelRef.current = false;
    onTurnFocus(null);
  }, [onTurnFocus, sel?.turn, sel?.insetOf]);
  // The run-threads panel is chip-toggled, not selection-driven: a worker
  // trace has no agent lane to dock, and the hop to its orchestrator must not
  // depend on one. Its height is the reader's to set, same as the dock's.
  const [threadsOpen, setThreadsOpen] = useState(false);
  // The session graph in place of the plane, kept in the URL (sg=1).
  // A page that is a canvas (`canvas`: the workspace) holds it on, and has no
  // trace panel header for it to stand aside.
  const [sessionGraphOn, setSessionGraphOn] = useState(() => !!canvas || (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("sg") === "1"));
  useEffect(() => {
    if (!trace || canvas) return undefined;
    setTraceCanvasMode(sessionGraphOn);
    return () => setTraceCanvasMode(false);
  }, [sessionGraphOn, trace, canvas]);
  // On the trace page the session graph is the trace's own workspace, drawn in
  // place of its lanes: the board the workspace overlay opens on, holding what
  // was laid on it there and here alike. A page that is a canvas brings its
  // own (`canvas`).
  const graphSession = !canvas && trace ? trace.sessionId || trace.id : null;
  const graphCanvasId = traceCanvasId(graphSession);
  const [graphAdding, setGraphAdding] = useState(false);
  const [graphCover, setGraphCover] = useState(0);
  const onGraphAdd = useCallback(() => setGraphAdding((a) => !a), []);
  const graphHost = useTraceWorkspaceHost(graphCanvasId, { adding: graphAdding, onAdd: onGraphAdd, cover: graphCover });
  useEffect(() => {
    if (sessionGraphOn && graphSession) keepTraceCanvas({ id: graphSession, title: trace?.summary || "" });
  }, [sessionGraphOn, graphSession]); // eslint-disable-line react-hooks/exhaustive-deps
  const boardHost = canvas || graphHost;
  // The overlay holds the board the page tells the sidebar and the chat
  // about while it is up; the trace page's own copy stands aside meanwhile.
  const overlayUp = !!useTraceCanvasOpen();
  const setSessionGraph = (on) => {
    setSessionGraphOn(on);
    if (!on) setGraphAdding(false);
    // A cell of another session, read from the session graph, has no place on
    // this trace's own plane.
    if (!on && String(sel?.insetOf || "").startsWith("board:")) setSel(null);
    try {
      const url = new URL(window.location.href);
      if (on) url.searchParams.set("sg", "1"); else url.searchParams.delete("sg");
      window.history.replaceState(window.history.state, "", url);
    } catch { /* URL state is a convenience */ }
  };
  const [threadsH, setThreadsH] = useState(240);
  const threadsHRef = useRef(240);
  threadsHRef.current = threadsH;
  const startThreadsDrag = useCallback((e) => {
    // Same drag discipline as the dock divider: suspend text selection for
    // the drag's duration, absolute math from the pointer-down origin.
    e.preventDefault();
    const startY = e.clientY;
    const startH = threadsHRef.current;
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* pointer capture is an enhancement, not a requirement */
    }
    const move = (ev) => setThreadsH(Math.max(96, Math.min(640, Math.round(startH + ev.clientY - startY))));
    const up = () => {
      document.body.style.userSelect = prevSelect;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, []);
  // The grid ⇄ dock split is the reader's to set: dragging the divider pins the
  // dock height; until then it takes up to 46% of the column. The pinned value
  // is shared at module scope rather than held per instance (the same idiom as
  // SplitPage's detail-panel width): the dock outlives any one mount now that
  // a chat can sit in it, and a height the reader dragged should not reset
  // when they open the next trace.
  const [dockH, setDockH] = useState(sharedDockHeight);
  // What the chat's tools read. A thunk over a ref rather than a value, so a
  // lane selected or a reading finished after the agent was built is still
  // visible to its next tool call. It hands over the model this view ALREADY
  // built -- which carries the extractor lanes and the shell door's
  // grammar-derived items both -- so the chat issues no read of its own.
  // Filled in below, once the axis's turn base is known.
  const chatSourceRef = useRef(null);
  const chatModel = useCallback(() => chatSourceRef.current, []);
  const chatShape = useMemo(
    () => ({ turnCount, laneCount: model.lanes.length, hasReading: !!reading.report }),
    [turnCount, model.lanes.length, reading.report],
  );
  const startDockDrag = useCallback((e) => {
    const root = rootRef.current;
    if (!root) return;
    // Text selection starts on MOUSEDOWN, so preventing the pointer event
    // alone still lets a drag sweep-select the dock's content; suspend
    // selection for the drag's duration instead.
    e.preventDefault();
    const rect = root.getBoundingClientRect();
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* pointer capture is an enhancement, not a requirement */
    }
    // rAF-throttled pointermove: the height fans out to
    // every occupant of the slot, so a raw per-pixel setState would re-render
    // all of them per pixel of drag.
    let raf = 0;
    let clientY = 0;
    const apply = () => {
      raf = 0;
      const next = Math.max(DOCK_MIN_H, Math.min(rect.height - DOCK_PLANE_RESERVE, Math.round(rect.bottom - clientY - 5)));
      sharedDockHeight = next;
      setDockH(next);
    };
    const move = (ev) => {
      // A drag whose release was missed (let go outside the window) ends at
      // the next move with no button held, rather than the dock following
      // the pointer from then on.
      if (!(ev.buttons & 1)) {
        up();
        return;
      }
      clientY = ev.clientY;
      if (!raf) raf = window.requestAnimationFrame(apply);
    };
    const up = () => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
      document.body.style.userSelect = prevSelect;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }, []);
  const toggle = useCallback((id) => {
    setClosed((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // The ribbon draws exactly when the projection placed something:
  // buildTopicAxis returns null for a session with no cluster assignments, an
  // undatable axis, or a journey that lands entirely off the axis. It rides
  // inside the ruler, so nothing below has to move for it.
  const topicOpen = Boolean(topicAxis);

  // Cell pins are for ARTIFACT lanes only: the strip's turn marks already
  // cover the conversation, and pinning every mention onto the prompt/reply
  // rows drowned them (found live). A pin also needs a cell to sit on — a
  // needle can occur at a turn where its lane drew nothing (a read the lane
  // does not chart), and a floating bubble reads as a misplaced one. Lanes
  // folded into a group header keep their pins (the header aggregates).
  const laneCellsById = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      if (row.type === "lane" && row.lane?.cells) map.set(row.lane.id, row.lane.cells);
    }
    return map;
  }, [rows]);
  const joinCellMarks = useMemo(() => {
    if (contentJoins.status !== "done") return [];
    return filterJoinCellMarks(buildJoinCellMarks(joinContext.needles, joinRows), laneCellsById);
  }, [contentJoins.status, joinContext, joinRows, laneCellsById]);
  // Follow rings obey the same gate as the census pins: artifact lanes only,
  // and only where the lane actually charted a cell -- a ring around nothing
  // reads as highlighting something that does not exist (found live).
  const followCells = useMemo(() => {
    if (!followHits) return [];
    const seen = new Map();
    for (const h of followHits) {
      for (const c of h.cells || []) {
        const key = `${c.lane}@${c.turn}`;
        if (!seen.has(key)) seen.set(key, { lane: c.lane, turn: c.turn });
      }
    }
    return filterJoinCellMarks([...seen.values()], laneCellsById);
  }, [followHits, laneCellsById]);
  // The pins a lane row actually shows: the lane's marks re-folded at the
  // chip's legibility floor, exactly like the strip — a catch-all lane
  // (Bash) collected thirty-nine pins whose session sets never matched
  // run-merge's strict equality, and a row of overlapping dots reads as
  // noise, not evidence (found live). Distinct sessions and directions
  // aggregate across the fold; the pin sits at the fold's first charted
  // turn, which is what the click hands the dock.
  const joinCellPins = useMemo(() => {
    if (!joinCellMarks.length) return [];
    const byLane = new Map();
    for (const m of joinCellMarks) {
      if (!byLane.has(m.lane)) byLane.set(m.lane, []);
      byLane.get(m.lane).push(m);
    }
    const out = [];
    for (const [lane, ms] of byLane) {
      for (const sp of foldJoinSpans(ms, cellW, JOIN_MARK_FOLD_PX)) out.push({ ...sp, lane });
    }
    return out;
  }, [joinCellMarks, cellW]);

  // Captured requests as marks on the axis. Every turn of the record says
  // which capture shipped it (sourceEventId), so a request sits at the last
  // message it shipped -- exact, no clock. The rows are the calls the thread
  // wiring already resolves (the session's for a whole conversation, the run's
  // for one bounded at a request), so this costs no extra read; a request
  // whose messages are not on the axis draws nothing.
  const threadRows = useThreadRows(threads);
  const requestsByTurn = useMemo(() => {
    if (!threadRows.rows?.length) return null;
    const lastTurn = new Map();
    (turns || []).forEach((t, i) => {
      const id = String(t?.sourceEventId || "").toLowerCase();
      if (id) lastTurn.set(id, i);
    });
    const out = new Map();
    for (const row of threadRows.rows) {
      const turn = lastTurn.get(String(row?.traceId || "").toLowerCase());
      if (turn == null) continue;
      const cur = out.get(turn);
      out.set(turn, cur ? { ...cur, rows: [...cur.rows, row] } : { turn, rows: [row] });
    }
    return out;
  }, [threadRows.rows, turns]);
  const requestsOpen = !!requestsByTurn && requestsByTurn.size > 0;
  // The captured requests ride the Tokens strip when there is one to carry
  // them -- a request is where the context was sent, which is where the token
  // line steps -- and claim a row of their own only when there is not.
  const requestsInline = useMemo(() => requestsOpen && densityShown(density), [requestsOpen, density]);
  const requestMarks = useMemo(() => (requestsOpen
    ? [...requestsByTurn.values()].sort((a, b) => a.turn - b.turn).map((m) => ({
      turn: m.turn, weight: "clear",
      label: m.rows[0].summary || m.rows[0].model || "captured request",
    }))
    : null), [requestsOpen, requestsByTurn]);
  const requestsLine = useMemo(() => {
    if (!requestsOpen) return "";
    const total = threadRows.rows.length;
    const parts = [`${total} captured request${total === 1 ? "" : "s"}`];
    if (requestMarks.length < total) parts.push(`${requestMarks.length} on the loaded axis`);
    if (threadRows.truncated) parts.push("newest missing past the fetch limit");
    return parts.join(" \u00b7 ");
  }, [requestsOpen, threadRows, requestMarks]);

  // A session axis's record, as the host reads it in pages: how much of the
  // conversation is on the axis, and the one control that extends it. Both
  // ride the ruler's rail head -- the axis is the record, so the record's
  // account belongs on the axis's own row, not a line above the plane.
  const axisRecord = useMemo(() => {
    if (!record?.session) return null;
    const shown = Number(record.loaded) || 0;
    const total = record.total != null && Number.isFinite(Number(record.total)) ? Number(record.total) : null;
    return {
      shown, total,
      remaining: total != null && total > shown ? total - shown : null,
      pending: !!record.pending, loading: !!record.loading, rejected: !!record.rejected,
      exhausted: !!record.exhausted, reason: record.reason ? String(record.reason) : null,
    };
  }, [record]);
  // Messages before the loaded page: what every printed turn number adds.
  // Zero while the record's total is not known yet as well as when the whole
  // record is on the axis: page-relative is the only number there is until the
  // total lands, and the labels catch up in the same render it does. The
  // Context review ledger prints through the same rule.
  const turnBase = recordTurnBase(record);
  // The chat speaks the turn numbers the axis prints, so it carries the base.
  chatSourceRef.current = { turns, lanes: model, reading, turnBase };
  // The reader chat's notes: pinned by its answers at the turns they point at
  // (trace_annotate), held with its thread for the session, placed on the
  // axis loaded now.
  const noteTraceId = trace?.id ?? null;
  const subscribeNotes = useCallback((cb) => (noteTraceId ? subscribeThreadNotes(noteTraceId, cb) : () => {}), [noteTraceId]);
  const heldNotes = useSyncExternalStore(subscribeNotes, () => (noteTraceId ? readThreadNotes(noteTraceId) : NO_NOTES), () => NO_NOTES);
  const placedNotes = useMemo(() => placeNotes(heldNotes, {
    turnBase,
    turnCount,
    hasMark: (lane, t) => (model?.laneById?.get(lane)?.cells?.get?.(t) || []).length > 0,
  }), [heldNotes, turnBase, turnCount, model]);
  const noteAnchorList = useMemo(() => noteAnchors(placedNotes), [placedNotes]);
  const noteMarks = useMemo(() => noteStripMarks(placedNotes), [placedNotes]);
  const noteById = useMemo(() => new Map(placedNotes.map((n) => [n.id, n])), [placedNotes]);
  const axisControl = useMemo(() => {
    const r = axisRecord;
    if (!r) return null;
    if (r.pending || r.loading) return { label: "loading", busy: true, help: r.pending ? "Reading the conversation's newest page." : "Reading the page before the oldest turn shown." };
    if (!onLoadEarlier) return null;
    if (r.rejected) {
      return {
        label: r.shown ? "retry earlier" : "retry", onClick: onLoadEarlier,
        help: `${r.shown ? "The page before the oldest turn shown" : "The conversation"} could not be loaded${r.reason ? ` (${r.reason})` : ""}. Read it again.`,
      };
    }
    if (r.exhausted) return null;
    return {
      label: r.remaining != null ? `${formatCompact(r.remaining)} earlier` : "earlier", onClick: onLoadEarlier,
      help: r.remaining != null
        ? `Read the page before the oldest turn shown: ${r.remaining.toLocaleString()} of the record's ${r.total.toLocaleString()} messages are not on the axis yet.`
        : "Read the page before the oldest turn shown.",
    };
  }, [axisRecord, onLoadEarlier]);

  // Which lane the pointer is over, for calling out the same file behind the
  // other mount (relatedPaths below).
  const [hoverLaneId, setHoverLaneId] = useState(null);
  const onHoverLane = useCallback((id) => setHoverLaneId(id), []);
  // A step of a derivation the reader is pointing at: the artifact it produced,
  // and the turn the command ran. Held here rather than in the dock because the
  // rows it marks live in the rail.
  const [derivMark, setDerivMark] = useState(null);
  const onHighlight = useCallback((m) => setDerivMark(m), []);
  const relatedPaths = useMemo(() => {
    const out = new Set();
    for (const id of [sel?.laneId, hoverLaneId]) {
      const l = id ? model.laneById.get(id) : null;
      for (const p of l?.sameFileAs || []) out.add(p);
    }
    for (const p of derivMark?.paths || []) out.add(p);
    // Never point at the row the reader already has open. Every step now marks
    // the cell its command ran in, and for a chain opened from that command's
    // own row that cell is on THIS row - a pointer to it would compete for the
    // same edge as the twin's and send the reader where they already are. The
    // cell mark reads from derivMark directly and is unaffected.
    const selPath = sel?.laneId ? model.laneById.get(sel.laneId)?.path : null;
    if (selPath) out.delete(selPath);
    return out;
  }, [sel?.laneId, hoverLaneId, derivMark, model]);
  // A derivation step is somewhere to go only if its artifact is a row. Most are
  // not: the chain establishes links through conventions the write grammar
  // deliberately does not claim, so it knows about files the rail never saw.

  // What the sticky headers occupy: the axis band, which carries the ruler,
  // the topic ground and the token skyline together.
  const stickyH = RULER_H;
  // Bring a row into the band without disturbing the selection: the reader is
  // pointing at it from the dock, not asking to read it instead.
  const revealRow = useCallback((rowTop) => {
    const el = scrollEl;
    if (!el) return;
    el.scrollTop = Math.max(0, rowTop - (el.clientHeight + stickyH) / 2 + ROW_H / 2);
  }, [scrollEl, stickyH]);

  // ── Influence ─────────────────────────────────────────────────────────────
  // The provenance pass is pairwise over the trace's ops and can cost a few
  // hundred ms on a long session, so it runs AFTER the grid has painted rather
  // than in a render memo: the timeline is the primary reading and must not
  // wait on an overlay that draws nothing until something is focused.
  // The influence overlay is always on: it draws only for a hovered or
  // selected mark, so there is nothing to switch off at rest, and the toggle
  // that used to sit on the toolbar was one more control to explain.
  const [influence, setInfluence] = useState(null);
  useEffect(() => {
    setInfluence(null);
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      try {
        setInfluence(buildTraceInfluence(model));
      } catch (err) {
        console.warn("[trace-lanes] influence projection threw", err);
        setInfluence(null);
      }
    };
    const idle = typeof window.requestIdleCallback === "function";
    const id = idle ? window.requestIdleCallback(run, { timeout: 1200 }) : window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      if (idle) window.cancelIdleCallback?.(id);
      else window.clearTimeout(id);
    };
  }, [turns, model]);

  // Which mark the pointer is over, for the transient reading. Selection is
  // the persistent one; hover wins while it lasts so the reader can sweep the
  // plane and see each mark's neighbourhood without losing their place.
  const [hoverCell, setHoverCell] = useState(null);
  // The review mark under the pointer, as a turn range: the hover fan's anchor.
  const [hoverFinding, setHoverFinding] = useState(null);
  // The strip mark the pointer is on, when its cells sit on rows: the plane
  // points at them when they are off screen, and a click brings them in.
  const [hoverStripMark, setHoverStripMark] = useState(null);
  // `insetOf` names the spliced child a hovered cell sits in: its lane and turn
  // belong to THAT conversation, so its lines come from the child's own graph.
  const onHoverCell = useCallback((laneId, turn, insetOf = null) => {
    setHoverCell(laneId == null ? null : { laneId, turn, insetOf });
  }, []);
  // A folded head-row mark hovers as its whole GROUP: the reading follows
  // the fold instead of disappearing with it.
  const onHoverGroup = useCallback((laneIds, turn, insetOf = null) => {
    setHoverCell(laneIds == null ? null : { laneIds, turn, insetOf });
  }, []);
  // A zoom re-buckets the marks, so the hovered one is unmounted rather than
  // left — no mouseleave fires, and the overlay would keep drawing a
  // neighbourhood around a mark that is no longer under the pointer.
  useEffect(() => setHoverCell(null), [cellW]);
  // The topic stretch the pointer is inside — the only thing the ribbon draws
  // on the plane, and only while pointed at. Held here rather than in the
  // ribbon because the guides render in the plane's gridline layer, not in the
  // ruler, and it clears on a zoom for the same reason the hovered mark does.
  // Everything the ruler needs to draw the ribbon, in one object so the ruler's
  // memo has a single topic prop to compare.
  const topicRibbon = useMemo(() => (topicAxis ? {
    axis: topicAxis,
    // Per turn, for the hue the band's own marks take over each stretch.
    topicOf: topicAxis.topicOf,
    buckets: topicBuckets(topicAxis.topicOf, cellW, TOPIC_SEG_MIN_PX, topicHueMap),
    hues: topicHueMap,
    estimatedBefore: topicClock.estimatedBefore,
    dated: topicClock.dated,
  } : null), [topicAxis, topicHueMap, topicClock, cellW]);
  const [hotTopic, setHotTopic] = useState(null);
  const onHoverTopic = useCallback((turn) => {
    setHotTopic(turn == null ? null : topicRunList.find((r) => r.from <= turn && turn <= r.to) ?? null);
  }, [topicRunList]);
  useEffect(() => setHotTopic(null), [cellW, topicRunList]);

  // What the overlay is drawn around: the hovered mark, else the selection —
  // a selected mark focuses that subject at that turn, a selected TURN (no
  // lane) focuses the turn itself and shows everything crossing it.
  // A cell inside a spliced child is focused in THAT child's plane (see
  // insetFocus), never in this one: its lane and turn are another
  // conversation's.
  const focus = useMemo(() => {
    const hovered = hoverCell && !hoverCell.insetOf ? hoverCell : null;
    const picked = sel && !sel.insetOf ? sel : null;
    const cell = hovered || (picked?.laneId && picked.tab === "subject" ? { laneId: picked.laneId, turn: picked.turn } : null);
    if (cell) return { laneId: cell.laneId ?? null, laneIds: cell.laneIds ?? null, ...markTurnRange(cell.turn, cellW) };
    if (picked != null) return { laneId: null, ...markTurnRange(picked.turn, cellW) };
    return null;
  }, [hoverCell, sel, cellW]);
  const insetFocus = useMemo(() => {
    if (hoverCell?.insetOf) return hoverCell;
    return sel?.insetOf && sel.laneId && sel.tab === "subject" ? { laneId: sel.laneId, turn: sel.turn, insetOf: sel.insetOf } : null;
  }, [hoverCell, sel]);
  const focusLinks = useMemo(() => {
    if (!influence || !focus) return { links: [], dropped: 0 };
    const { incoming, outgoing } = linksAt(influence, focus);
    return capLinks([...incoming, ...outgoing]);
  }, [influence, focus]);
  // A claim of the chat's graph drawn as the path its evidence takes through
  // the cells: the claim the graph dock leads with, or the claim row under the
  // pointer while one is hovered. With the dock on a turn and no claim named,
  // every claim resting on that turn.
  const [hoverClaim, setHoverClaim] = useState(null);
  // A claim row that unmounts under the pointer never says the pointer left.
  useEffect(() => { setHoverClaim(null); }, [sel?.tab, sel?.turn, openPin]);
  const graphLinks = useMemo(() => {
    if (!graphOverlay) return [];
    const docked = sel?.tab === "graph";
    const id = hoverClaim || (docked ? sel.graphClaim : null);
    if (id) return claimCellLinks(graphOverlay, id);
    if (openPin?.claims?.length) return openPin.claims.flatMap((c) => claimCellLinks(graphOverlay, c)).slice(0, GRAPH_LINKS_CAP);
    if (!docked || !Number.isInteger(sel?.turn)) return [];
    const { claims } = graphAtTurn(graphOverlay, sel.turn, Math.max(sel.turn, sel.graphThrough ?? sel.turn));
    return claims.flatMap((c) => claimCellLinks(graphOverlay, c.id)).slice(0, GRAPH_LINKS_CAP);
  }, [graphOverlay, hoverClaim, openPin, sel?.tab, sel?.graphClaim, sel?.turn, sel?.graphThrough]);
  // The cells the open card's claims rest on, ringed; the claim under the
  // pointer narrows them to its own.
  const graphRings = useMemo(() => {
    if (!graphOverlay || !openPin) return [];
    const ids = new Set(hoverClaim ? [hoverClaim] : openPin.claims);
    const out = [];
    for (const c of graphOverlay.claims) {
      if (!ids.has(c.id)) continue;
      for (const cell of c.cells) if (!out.some((x) => x.lane === cell.lane && x.turn === cell.turn)) out.push(cell);
    }
    return out;
  }, [graphOverlay, openPin, hoverClaim]);

  const promptTurns = [...model.beats.entries()].filter(([, b]) => b.prompt).map(([t]) => t).sort((a, b) => a - b);
  const selLane = sel?.laneId ? model.laneById.get(sel.laneId) : null;
  // --- the spliced children, as sub-scenes ---------------------------------
  //
  // A child's axis is FIT to its box, because the box is the only frame it ever
  // gets. The width comes from the scene's own helper so the plane and the child
  // agree on it; deriving it here independently left the child first narrower
  // than its box, then wider than the clip that cuts it.
  const insetAxisW = insetAxisWidth(view.width, width, railW);
  // Each child's lanes, built once per state the child reaches (landed, then
  // its shell reads added) rather than on every zoom and pan of its box.
  const insetModelCache = useRef(new WeakMap());
  const insetModels = useMemo(() => {
    const out = new Map();
    for (const [id, st] of children) {
      if (st.status !== "ready" || !st.sub?.turns?.length) continue;
      let built = insetModelCache.current.get(st);
      if (!built) {
        const subTurns = st.sub.turns;
        const subModel = buildTraceLanes(subTurns, st.shellItems?.length ? { extraItems: st.shellItems } : {});
        const subWeights = buildLaneWeights(subTurns, subModel);
        built = {
          turns: subTurns, model: subModel, weights: subWeights,
          rowWeights: subtreeWeights(subModel, subWeights), density: buildTurnDensity(subTurns, {}),
        };
        insetModelCache.current.set(st, built);
      }
      out.set(id, built);
    }
    return out;
  }, [children]);
  // A child's own provenance graph, for the lines drawn inside its box. The
  // same pass the parent runs, and for the same reason after paint: the box
  // must not wait on an overlay that draws nothing until a cell is focused.
  const [insetInfluence, setInsetInfluence] = useState(() => new WeakMap());
  useEffect(() => {
    const pending = [...insetModels.values()].filter((m) => !insetInfluence.has(m.model));
    if (!pending.length) return undefined;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const next = new WeakMap();
      for (const m of insetModels.values()) {
        if (insetInfluence.has(m.model)) { next.set(m.model, insetInfluence.get(m.model)); continue; }
        try {
          next.set(m.model, buildTraceInfluence(m.model));
        } catch (err) {
          console.warn("[trace-lanes] inset influence projection threw", err);
          next.set(m.model, null);
        }
      }
      setInsetInfluence(next);
    };
    const idle = typeof window.requestIdleCallback === "function";
    const handle = idle ? window.requestIdleCallback(run, { timeout: 1200 }) : window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      if (idle) window.cancelIdleCallback?.(handle);
      else window.clearTimeout(handle);
    };
  }, [insetModels, insetInfluence]);
  const insetScenes = useMemo(() => {
    const out = new Map();
    for (const [id, st] of children) {
      const built = insetModels.get(id);
      if (!built) {
        // Every open lane gets a box, so the fold visibly does something the
        // moment it is opened rather than when the fetch happens to land.
        out.set(id, { status: st.status === "loading" ? "loading" : "empty", note: insetNote(st) });
        continue;
      }
      const { turns: subTurns, model: subModel, weights: subWeights, rowWeights: subRowWeights, density: subDensity } = built;
      const fold = insetClosed.get(id) ?? defaultInsetFold(subModel);
      const subCellW = insetZoom.get(id)
        ?? Math.max(CELL_MIN, insetAxisW / Math.max(1, subTurns.length));
      // The focused cell's links, when the focus is in this child.
      const subInfluence = insetInfluence.get(subModel) || null;
      const subFocus = insetFocus?.insetOf === id
        ? { laneId: insetFocus.laneId ?? null, laneIds: insetFocus.laneIds ?? null, ...markTurnRange(insetFocus.turn, subCellW) }
        : null;
      let subLinks = { links: [], dropped: 0 };
      if (subInfluence && subFocus) {
        const { incoming, outgoing } = linksAt(subInfluence, subFocus);
        subLinks = capLinks([...incoming, ...outgoing]);
      }
      out.set(id, { status: "ready", fold, model: subModel, turns: subTurns, scene: buildLaneScene({
        influence: subLinks.links,
        influenceAnchor: subFocus,
        influenceDropped: subLinks.dropped,
        callRings: subFocus ? callCellsAt(subModel, subFocus) : [],
        rows: flattenRows(subModel, fold).map((r) => (
          r.type === "head" ? { ...r, weight: subRowWeights.get(r.id) || null } : r
        )),
        cellW: subCellW,
        width: Math.max(insetAxisW, Math.round(subCellW * subTurns.length)),
        turnCount: subTurns.length,
        weights: subWeights,
        // A grandchild's toggle is only honest on the same terms as its
        // parent's. Today an inset cannot exist without a loader, so this is
        // belt and braces -- but the rule belongs where the scene is built,
        // not in a chain of reasoning about how the inset got here.
        canSplice,
        railW,
        columns: subModel.columns,
        beats: subModel.beats,
        density: subDensity,
        // The child's own figures on its own ruler head, as the root axis has.
        rulerDetail: `${subModel.lanes.length} lanes \u00b7 ${subModel.columns.length} of ${subTurns.length} turns`,
        viewportW: insetAxisW + railW,
        meta: {
          traceId: st.sub.traceId || null,
          stats: `${subModel.coverage.claimed} of ${subModel.coverage.toolUses} tool calls in lanes \u00b7 ${subModel.lanes.length} lanes \u00b7 ${subModel.columns.length} of ${subTurns.length} turns active`,
          zoom: {
            cellW: subCellW,
            fitCellW: Math.max(CELL_MIN, insetAxisW / Math.max(1, subTurns.length)),
            fitted: !insetZoom.has(id),
            panX: insetPan.get(id) || 0,
          },
        },
      }) });
    }
    return out;
  }, [children, insetModels, insetInfluence, insetFocus, insetAxisW, insetClosed, insetZoom, insetPan, canSplice, railW]);

  // A cell picked on a canvas's board reads in the dock: one of this trace's
  // as any pick here does; one on another session's card from that card's
  // trace, as its own lanes view would read it (`boardPick`, held under
  // `board:<session>`). What the board can keep of it rides along, for the
  // dock to offer.
  const [boardScenes, setBoardScenes] = useState(() => new Map());
  const [boardKeep, setBoardKeep] = useState(null);
  const [boardLay, setBoardLay] = useState(null);
  const [boardPick, setBoardPick] = useState(null);
  const boardSel = String(sel?.insetOf || "").startsWith("board:");
  // An annotation opened on the board brings the dock to its cell, even one
  // picked already (the dock may have gone back to the chat since).
  const showDockTabRef = useRef(null);
  // The board a pick came from, to light where the dock steps to on it: the
  // selection it belongs to (`insetOf`, null for this trace's own card).
  const boardFollow = useRef(null);
  const onBoardPick = useCallback((p) => {
    const { key = null, laneId, turn, record = null, keep = null, tab = null } = p;
    const onCard = !!(key && record?.model && record?.turns);
    if (onCard) {
      const id = `board:${key}`;
      setBoardScenes((cur) => (cur.get(id)?.turns === record.turns ? cur : new Map(cur).set(id, { status: "ready", model: record.model, turns: record.turns, endpointId: record.endpointId || null })));
      setBoardPick(p);
      pick(laneId, turn, false, id, tab);
    } else pick(laneId, turn, false, null, tab);
    boardFollow.current = p.follow ? { follow: p.follow, insetOf: onCard ? `board:${key}` : null } : null;
    if (tab) showDockTabRef.current?.("sel");
    setBoardKeep(() => keep);
    setBoardLay(() => p.lay || null);
  }, [pick]);
  useEffect(() => {
    const f = boardFollow.current;
    if (!f || sel?.turn == null || (sel.insetOf || null) !== f.insetOf) return;
    f.follow({ laneId: sel.laneId ?? null, turn: sel.turn });
  }, [sel?.laneId, sel?.turn, sel?.insetOf]); // eslint-disable-line react-hooks/exhaustive-deps

  // A pick inside a spliced child reads THAT conversation: its turns, its
  // beats, its lane. One dock either way -- the child never gets its own.
  const docked = useMemo(() => {
    const st = sel?.insetOf ? insetScenes.get(sel.insetOf) || boardScenes.get(sel.insetOf) : null;
    if (st?.status === "ready" && st.model) {
      return {
        turns: st.turns,
        // An inset scene is a DIFFERENT conversation, so its dock must be
        // stamped from its own turns; the outer scene's stamps are positional
        // and would date these turns from someone else's clock.
        stamps: buildTurnStamps(st.turns),
        turnCount: st.turns.length,
        beats: st.model.beats,
        narration: st.model.narration,
        lane: st.model.laneById.get(sel.laneId) || null,
        sameCall: (laneId, t) => sameCallLanes(st.model, laneId, t),
      };
    }
    return {
      turns, stamps: turnStamps, turnCount, beats: model.beats, narration: model.narration, lane: selLane,
      sameCall: (laneId, t) => sameCallLanes(model, laneId, t),
    };
  }, [sel?.insetOf, sel?.laneId, insetScenes, boardScenes, turns, turnStamps, turnCount, model, selLane]);
  // What the dock reads: this trace from what this view has of it (nothing
  // read again); a card on its board from that card's trace, as its own lanes
  // view would read it; a spliced child from its record alone.
  const ownGiven = useMemo(() => ({
    reading, model, reports: pipelineReports, findingsReport: findings.report || null, reviewProfile, graphOverlay, contentJoins,
  }), [reading, model, pipelineReports, findings.report, reviewProfile, graphOverlay, contentJoins]);
  const ownDock = useTraceDockData({ trace, turns, model, traceTimestamp, focalSpan: joinFocalSpan, given: ownGiven });
  const boardTrace = useMemo(() => (boardPick?.session ? { id: boardPick.session, sessionId: boardPick.session, endpointId: boardPick.record?.endpointId || null } : null), [boardPick]);
  const boardSpan = useMemo(() => spanOfRecord(boardPick?.record), [boardPick]);
  const boardDock = useTraceDockData({
    trace: boardTrace, turns: boardPick?.record?.turns || null, model: boardPick?.record?.model || null, shellItems: boardPick?.record?.shellItems || null,
    traceTimestamp: boardPick?.record?.firstMs ?? null, focalSpan: boardSpan, graphKey, enabled: boardSel,
  });
  const childDock = useMemo(() => {
    const st = sel?.insetOf && !boardSel ? insetScenes.get(sel.insetOf) : null;
    return st?.status === "ready" && st.model ? recordDockData(st.turns, st.model) : null;
  }, [sel?.insetOf, boardSel, insetScenes]);
  // A hand-off's shared text lit on the board's cards: this trace's on its
  // own card, where the board stands in for its lanes, and a card's on that
  // card.
  const handoffLight = useMemo(() => createHandoffLight(), []);
  const anchorHandoff = useLitHandoff(handoffLight, null, ownDock);
  const boardHandoff = useLitHandoff(handoffLight, boardPick?.key ?? null, boardDock);
  // A hand-off's other session opened on the board, under the card the cell
  // read is on: this trace's own (from the cell selected here) or a card's.
  const boardLanding = useMemo(() => createBoardLanding(), []);
  const selRef = useRef(sel);
  selRef.current = sel;
  const anchorLay = useMemo(() => handoffLayOf((req) => boardLanding.land({
    ...req, from: { key: null, lane: selRef.current?.laneId ?? null, turn: selRef.current?.turn ?? 0 },
  })), [boardLanding]);
  const boardExtra = useMemo(() => ({ ...boardHandoff, onLayJoin: handoffLayOf(boardPick?.land) }), [boardHandoff, boardPick?.land]);
  // The turns a docked cell is numbered from: this trace's record's, a card's
  // own record's, a spliced child's from its start.
  const dockBase = !sel?.insetOf ? turnBase : boardSel ? boardPick?.base || 0 : 0;

  // The telemetry band's geometry, shaped for the scene. Bars bucket exactly as
  // the token strip's do, so the two read at the same grain when zoomed out.
  const systemBand = useMemo(() => {
    if (sys.status !== "ready" || sys.empty) return null;
    const span = cellW >= DENSITY_BAR_MIN_PX ? 1 : Math.ceil(DENSITY_BAR_MIN_PX / cellW);
    const raw = [];
    const totals = { all: 0 };
    let max = 0;
    for (let start = 0; start < turnCount; start += span) {
      const end = Math.min(turnCount - 1, start + span - 1);
      const counts = {};
      let total = 0;
      for (let t = start; t <= end; t += 1) {
        const b = sys.perTurn.get(t);
        if (!b) continue;
        for (const k of SYSTEM_STREAM_KINDS) {
          const n = b[k.key] || 0;
          if (!n) continue;
          counts[k.key] = (counts[k.key] || 0) + n;
          if (counts[k.key] > max) max = counts[k.key];
          totals[k.key] = (totals[k.key] || 0) + n;
          totals.all += n;
        }
        total += b.total;
      }
      if (total) raw.push({ start, end, counts });
    }
    const events = (n) => (n ? `${formatCompact(n)} event${n === 1 ? "" : "s"}` : "");
    return {
      label: "System",
      digest: totals.all ? formatCompact(totals.all) : "",
      open: sysOpen,
      pinned: sysPinned,
      source: sys.source ?? null,
      hosted: !!sys.hosted,
      truncated: !!sys.truncated,
      kinds: SYSTEM_STREAM_KINDS.filter((k) => sys.kindsPresent?.includes(k.key)).map((k) => ({
        key: k.key,
        label: k.label,
        digest: events(totals[k.key]),
        buckets: raw.filter((b) => b.counts[k.key]).map((b) => ({
          start: b.start,
          end: b.end,
          count: b.counts[k.key],
          weight: bucketWeight(b.counts[k.key], max),
          open: !!(systemSel && systemSel.kind === k.key && systemSel.start === b.start),
        })),
      })),
    };
  }, [sys, cellW, turnCount, sysOpen, sysPinned, systemSel]);

  // --- the scene -------------------------------------------------------------
  //
  // The on-demand scans are one shape on the axis: glyphs whose fill
  // weight is the finding's confidence, over an optional band of located
  // traffic. They differ in what they found, not in how they draw, so the scene
  // takes them through one input rather than three near-copies.
  // The stretches a scan actually covered, 0-based on the axis, or null when it
  // covered everything it was handed -- a bracket around the whole axis carries
  // no information, so it is not drawn.
  const handoffCoverage = (report) => {
    const cov = report?.coverage || null;
    if (!cov || cov.scannedTurns >= cov.totalTurns) return null;
    return (cov.ranges || []).map(([a, b]) => [a - 1, b - 1]);
  };

  // The kin of the findings marked over a turn range: their own incidents,
  // the incidents merely related to those, and the findings themselves. One
  // lookup serves the fan and the tooltip so the two never disagree.
  const incidentKinAt = useCallback((from, through) => {
    if (!findingIncidents) return null;
    const here = findingsInRange(findingsAtTurn, from, through);
    const own = new Set(here.map((inc) => findingIncidents.byFinding.get(inc.id)).filter((g) => g != null));
    if (!own.size) return null;
    const soft = new Set();
    for (const r of findingIncidents.related) {
      if (own.has(r.a) && !own.has(r.b)) soft.add(r.b);
      if (own.has(r.b) && !own.has(r.a)) soft.add(r.a);
    }
    const rank = { danger: 2, warn: 1, calm: 0 };
    const incidents = [...own].map((g) => findingIncidents.incidents[g]);
    const related = [...soft].map((g) => findingIncidents.incidents[g]);
    // The arcs and the ticks are about the conduct that RECURS, so they take
    // its tone: a one-off finding folded into the same mark must not paint a
    // low-severity habit red.
    const toned = incidents.some((i) => i.count > 1) ? incidents.filter((i) => i.count > 1) : incidents;
    const tone = toned.map((i) => i.tone).sort((a, b) => (rank[b] ?? 0) - (rank[a] ?? 0))[0];
    return { from, through, here, incidents, related, tone };
  }, [findingIncidents, findingsAtTurn]);
  const fanFor = useCallback((from, through, hover) => {
    const kin = incidentKinAt(from, through);
    if (!kin) return null;
    const seen = new Set();
    const targets = [];
    const add = (turn, isSoft) => {
      if ((turn >= from && turn <= through) || seen.has(turn)) return;
      seen.add(turn);
      targets.push({ turn, soft: isSoft });
    };
    for (const i of kin.incidents) for (const t of i.turns) add(t, false);
    for (const i of kin.related) for (const t of i.turns) add(t, true);
    if (!targets.length) return null;
    return { strip: reportsOpen ? "reports" : "findings", turn: from, tone: kin.tone, hover, targets };
  }, [incidentKinAt, reportsOpen]);
  // The fan: from the selected finding while one is docked, else from the
  // mark under the pointer, lighter. A hover never adds a second fan beside a
  // selected one: two fans from two anchors is where the hairball starts.
  const findingFan = useMemo(() => {
    if (sel?.tab === "findings" && Number.isInteger(sel?.turn)) {
      return fanFor(sel.turn, Math.max(sel.turn, sel.findingThrough ?? sel.turn), false);
    }
    if (hoverFinding) return fanFor(hoverFinding.turn, hoverFinding.through, true);
    return null;
  }, [fanFor, sel?.tab, sel?.turn, sel?.findingThrough, hoverFinding]);

  const scanStrips = useMemo(() => {
    const out = [];
    if (requestsOpen && !requestsInline) {
      out.push({
        key: "requests", label: "Requests", token: "ok", busy: false,
        detail: requestsLine,
        marks: requestMarks, spans: [], coverage: null, retry: false,
      });
    }
    if (chatGraph.on) {
      out.push({
        key: "graph", label: "Graph", token: "graph", busy: false,
        detail: graphOverlay
          ? graphStripSummary(graphOverlay)
          : chatGraph.empty
            ? "the chat's knowledge graph is empty in this browser"
            : "the chat's knowledge graph names nothing in this trace",
        marks: graphMarks,
        spans: [],
        coverage: null,
        retry: false,
      });
    }
    if (heldNotes.length) {
      out.push({
        key: "notes", label: "Notes", token: "graph", busy: false,
        detail: noteStripSummary(placedNotes, heldNotes),
        marks: noteMarks,
        spans: [],
        coverage: null,
        retry: false,
      });
    }
    if (reportsOpen || findingsOpen) {
      // The pipeline's review is ONE band. Its reports are chapters, not marks:
      // each is the judge's account of one reviewed window, and they lie end to
      // end along the axis. Its findings are marks inside those chapters, with
      // the same severity tones used throughout the review UI. The row is named
      // for what it holds: the reports when there are
      // any, the findings alone otherwise.
      out.push({
        key: reportsOpen ? "reports" : "findings",
        label: reportsOpen ? "Reports" : "Findings",
        token: findingsOpen ? "amber" : "info", busy: false,
        detail: [
          reportsOpen ? reportsSummaryLine(reportsTimeline) : "",
          findingsOpen ? findingsSummaryLine(findingsReport) : "",
        ].filter(Boolean).join(" \u00b7 "),
        marks: findingsOpen
          ? reviewMarks(findingsReport, turnCount).map((m) => ({
            turn: m.turn,
            weight: m.tone === "danger" ? "alarm" : m.tone,
            label: m.incidents?.[0]?.title || "review finding",
          }))
          : [],
        spans: [],
        segments: reportsOpen ? reportSegments(reportsTimeline, turnCount) : [],
        // A habit's reach: one bar per incident that recurred, first to last.
        incidents: findingIncidents
          ? findingIncidents.incidents.filter((i) => i.count > 1 && i.through < turnCount)
            .map((i) => ({ from: i.from, through: i.through, count: i.count, tone: i.tone, label: i.title, index: i.index }))
          : [],
        // Coverage is per review window and the projection carries findings
        // only, so the strip makes no claim about which stretches were read.
        coverage: null,
        retry: false,
      });
    }
    if (alertTurns?.length) {
      out.push({
        key: "alert",
        label: "Alert",
        token: "red",
        busy: false,
        detail: alertTurns.length === 1 ? "fired on this turn" : `fired on ${alertTurns.length} turns`,
        marks: alertTurns.map((turn) => ({ turn, weight: "alarm", label: "Alert fired here" })),
        spans: [],
        coverage: null,
        retry: false,
      });
    }
    if (handoffOpen) {
      out.push({
        key: "handoff", label: "Hand-offs", token: "info", busy: handoffs.status === "running",
        detail: handoffs.status === "running"
          ? (handoffs.progress?.total > 1 ? `scanning\u2026 ${handoffs.progress.done} of ${handoffs.progress.total} windows` : "scanning every turn\u2026")
          : handoffs.status === "error" ? (handoffs.error?.message || "the scan returned no report")
            : handoffSummaryLine(handoffs.report),
        marks: handoffs.report
          ? handoffMarks(handoffs.report, turnCount).map((m) => ({
            turn: m.turn, weight: m.confidence, label: m.candidates?.[0]?.title || "hand-off",
          }))
          : [],
        spans: handoffs.report
          ? handoffTrafficMarks(handoffs.report, turnCount).map((m) => ({ turn: m.turn }))
          : [],
        coverage: handoffCoverage(handoffs.report),
        retry: handoffs.status === "error",
      });
    }
    if (readingOpen) {
      // A live run draws from its PARTIAL report, which is the settled shape:
      // verdicts land on the axis as the judge writes them, through the same
      // readingMarks the finished run uses.
      const running = reading.status === "running";
      const rep = reading.report || (running ? reading.liveReport : null);
      out.push({
        key: "reading", label: "Reading", token: "ok", busy: running,
        detail: readingSummaryLine({
          status: reading.status, report: reading.report, error: reading.error,
          progress: reading.progress, delta: reading.delta, fresh: reading.fresh, live: reading.live,
        }),
        // One kind of thing, which is all a scan strip's mark says: the
        // drafters' reading needed correcting here. Verified additions and
        // disputed claims are both that, so neither takes the weight channel —
        // that channel means "how sure", and spending it on "which kind" makes
        // the quieter half read as a rendering artefact rather than a finding.
        // The count carries the size; the tooltip carries the split.
        marks: rep
          ? readingMarks(rep, turnCount).map((m) => ({
            turn: m.turn,
            weight: "clear",
            label: m.disputed ? `${m.verified} verified, ${m.disputed} disputed` : `${m.verified} verified`,
          }))
          : [],
        spans: [],
        // What has been READ so far, as the coverage every scan strip already
        // speaks. A run fills it in as each judge finishes, so progress shows
        // WHERE it happened without the reading wearing a wait its siblings do
        // not have. Reuse is stated in the dock, in words, rather than as a
        // second band competing with the marks.
        coverage: readingCoverage(rep),
        retry: reading.status === "error",
      });
    }
    return out.length ? out : null;
  }, [alertTurns, requestsOpen, requestsInline, requestMarks, requestsLine, reportsOpen, reportsTimeline, findingsOpen, findingsReport, handoffOpen, handoffs, readingOpen, reading, turnCount,
      chatGraph.on, chatGraph.empty, graphOverlay, graphMarks, heldNotes, placedNotes, noteMarks]);

  // Windowed strip building, for axes long enough that building them whole
  // costs more than rebuilding them occasionally. Below the threshold the whole
  // axis is cheap and a window would only add rebuilds.
  const [turnWindow, setTurnWindow] = useState(null);
  const onTurnWindow = useCallback((w) => setTurnWindow(w), []);

  // Under the board (the workspace, or this reading's session graph) the
  // plane is not drawn, so its scene is an empty one: built whole, it was
  // among the costliest things the board's opening waited on, for nothing
  // shown.
  const boardOn = sessionGraphOn && !!trace;
  const scene = useMemo(() => {
    if (boardOn) return buildLaneScene({ rows: [], cellW, width: 0, turnCount: 0, railW });
    const cellsById = foldAwareCellsById(rows);
    return buildLaneScene({
      rows,
      cellW,
      width,
      turnCount,
      railW,
      weights,
      columns: model.columns,
      beats: model.beats,
      density,
      rulerDetail: `${model.lanes.length} lanes \u00b7 ${model.columns.length} of ${turnCount} turns`,
      rulerControls: !!axisControl,
      rulerBusy: !!axisControl?.busy,
      requestMarks: requestsInline ? requestMarks : null,
      scanStrips,
      joinStripMarks: joinsOpen ? joinMarks : null,
      joinStripSummary: joinsOpen ? joinsSummaryLine(joinLens) : null,
      joinStripBusy: joinsOpen && contentJoins.status === "loading",
      joinPins: filterJoinCellMarks(joinCellPins, cellsById),
      joinRings: filterJoinCellMarks(followCells, cellsById),
      findingPins: filterJoinCellMarks(findingPins, cellsById),
      graphPins: filterJoinCellMarks(graphPins, cellsById),
      graphBadges: badgeAnchors,
      noteBadges: noteAnchorList,
      graphRings: filterJoinCellMarks(graphRings, cellsById),
      callRings: filterJoinCellMarks(callCellsAt(model, focus), cellsById),
      findingFan,
      joinHues,
      observerIds: joinObserverIds,
      influence: graphLinks.length ? [...focusLinks.links, ...graphLinks] : focusLinks.links,
      influenceAnchor: focus,
      influenceDropped: focusLinks.dropped,
      promptTurns,
      sel,
      turnFocus,
      hoverCell,
      crossLane: hoverLaneId || hoverCell?.laneId || null,
      canSplice,
      relatedPaths,
      derivMark,
      topics: topicRibbon,
      hotTopic,
      system: systemBand,
      insetOf: (lane) => insetScenes.get(lane.id) || null,
      viewportW: view.width,
      turnWindow: turnCount > SCENE_WINDOW_MIN_TURNS ? turnWindow : null,
    });
  }, [rows, cellW, width, turnCount, weights, model, density, level, requestsInline, requestMarks, requestsLine, axisControl, scanStrips, joinsOpen,
      joinMarks, contentJoins, joinLens, joinCellPins, followCells, findingPins, graphPins, graphLinks, badgeAnchors, noteAnchorList, graphRings, findingFan, joinHues, joinObserverIds, focusLinks,
      focus, promptTurns, sel, turnFocus, hoverCell, hoverLaneId, relatedPaths, derivMark, topicRibbon, hotTopic,
      systemBand, insetScenes, view.width, turnWindow, canSplice, railW, boardOn]);

  // The comment pins, where the scene put them, merged where they crowd. A pin
  // takes the tone of the strongest claim it holds.
  const graphClaimById = useMemo(() => new Map((graphOverlay?.claims || []).map((c) => [c.id, c])), [graphOverlay]);
  // The cells a strip mark stands for, on the rows below the strips
  // (stripMarkGuide.js).
  const stripTargets = useCallback((mark) => stripMarkTargets(mark, {
    badgeAnchors, claimById: graphClaimById, noteAnchors: noteAnchorList, incidents: findingsReport?.incidents || [], joinPins: joinCellPins,
  }), [badgeAnchors, graphClaimById, noteAnchorList, findingsReport, joinCellPins]);
  const planeGuide = useMemo(() => {
    const targets = stripTargets(hoverStripMark);
    return targets.length ? targets : null;
  }, [stripTargets, hoverStripMark]);
  // A strip mark's click brings the nearest of its cells into the band when
  // none is on screen, on top of what the click opens.
  const revealStripTargets = useCallback((mark) => {
    const el = scrollEl;
    if (!el) return;
    const rows = guideTargets(scene.rows, scene.tops, stripTargets(mark));
    const top = guideReveal(rows, {
      scrollTop: el.scrollTop,
      viewHeight: el.clientHeight,
      headerH: scene.headerH,
      bottomInset: pinnedBandInset(scene.systemMeta, { scrollY: el.scrollTop, viewH: el.clientHeight }),
    });
    if (top == null) return;
    if (typeof el.scrollTo === "function") el.scrollTo({ top, behavior: "smooth" });
    else el.scrollTop = top;
  }, [scrollEl, scene, stripTargets]);
  const graphAnchorByKey = useMemo(() => new Map((graphOverlay?.anchors || []).map((a) => [a.key, a])), [graphOverlay]);
  const commentPins = useMemo(() => {
    if (!badgeAnchors.length) return [];
    const byId = new Map(badgeAnchors.map((a) => [a.id, a]));
    const points = (scene.badges || [])
      .map((b) => ({ ...b, claims: byId.get(b.id)?.claims || [], turn: byId.get(b.id)?.turn ?? null }))
      .filter((p) => p.claims.length);
    return clusterBadges(points).map((p) => {
      const first = graphClaimById.get(p.claims[0]);
      const statement = String(first?.statement || "");
      return {
        ...p,
        tone: p.claims.some((id) => graphClaimById.get(id)?.status === "supported") ? "supported" : "insufficient",
        preview: `${statement.length > 160 ? `${statement.slice(0, 159)}\u2026` : statement}${p.claims.length > 1 ? ` (+${p.claims.length - 1} more)` : ""}`,
      };
    });
  }, [scene.badges, badgeAnchors, graphClaimById]);
  // The open card is followed by the claim it opened on, not by its pin's id:
  // a pin is named for its cell, and a cell's lane can be re-homed as the lanes
  // model learns more (the shell reading moves a call onto the lane of what it
  // built), and zooming re-clusters pins.
  const pinHoldsOpen = useCallback(
    (pin) => !!openPin && openPin.claims.some((id) => pin.claims.includes(id)),
    [openPin],
  );
  const openCluster = useMemo(() => commentPins.find(pinHoldsOpen) || null, [commentPins, pinHoldsOpen]);
  // The reader chat's notes as comment pins, the same pieces the claims use,
  // with a card of their own: one card is open at a time, whichever kind.
  const notePins = useMemo(() => {
    if (!noteAnchorList.length) return [];
    const byId = new Map(noteAnchorList.map((a) => [a.id, a]));
    const points = (scene.noteBadges || [])
      .map((b) => ({ ...b, claims: byId.get(b.id)?.claims || [], turn: byId.get(b.id)?.turn ?? null }))
      .filter((p) => p.claims.length);
    return clusterBadges(points).map((p) => {
      const text = String(noteById.get(p.claims[0])?.text || "");
      return { ...p, kind: "note", tone: "supported", preview: `${text}${p.claims.length > 1 ? ` (+${p.claims.length - 1} more)` : ""}` };
    });
  }, [scene.noteBadges, noteAnchorList, noteById]);
  const [openNote, setOpenNote] = useState(null); // { id, notes }
  useEffect(() => { setOpenNote(null); }, [trace?.id]);
  const noteHoldsOpen = useCallback(
    (pin) => !!openNote && openNote.notes.some((id) => pin.claims.includes(id)),
    [openNote],
  );
  const openNoteCluster = useMemo(() => notePins.find(noteHoldsOpen) || null, [notePins, noteHoldsOpen]);
  const closeNote = useCallback(() => setOpenNote(null), []);
  const openNoteById = useCallback((id) => {
    const pin = notePins.find((p) => p.id === id);
    if (!pin) return;
    if (openNote && openNote.notes.some((n) => pin.claims.includes(n))) {
      setOpenNote(null);
      return;
    }
    closePin();
    setOpenNote({ id: pin.id, notes: pin.claims });
  }, [notePins, openNote, closePin]);
  // A follow-up typed on a note's card goes to the reader chat, and its answer
  // comes back to the card through the reply store.
  const noteReplyKey = readerReplyKey(noteTraceId);
  const subscribeNoteReplies = useCallback((cb) => graphReplies.subscribe(noteReplyKey, cb), [noteReplyKey]);
  const noteReplyList = useSyncExternalStore(subscribeNoteReplies, () => graphReplies.list(noteReplyKey), () => NO_REPLIES);
  const openNoteReplies = useMemo(() => {
    const ids = new Set(openNoteCluster?.claims || []);
    return ids.size ? noteReplyList.filter((r) => (r.claimIds || []).some((id) => ids.has(id))) : NO_REPLIES;
  }, [noteReplyList, openNoteCluster]);
  const askFromNote = useCallback((words) => {
    if (!openNoteCluster || !noteTraceId) return;
    const noteIds = openNoteCluster.claims;
    const text = noteReplyText({
      text: words,
      notes: noteIds.map((id) => noteById.get(id)).filter(Boolean),
      turnLabel: Number.isInteger(openNoteCluster.turn) ? turnBase + openNoteCluster.turn : null,
    });
    const sent = askTrace(noteTraceId, { text, words, noteIds });
    if (!sent?.replyId) {
      graphReplies.upsert(noteReplyKey, { id: nextReplyId(), text: words, claimIds: noteIds, at: Date.now(), state: "missing" });
    }
  }, [openNoteCluster, noteTraceId, noteById, turnBase, noteReplyKey]);
  const rowNotePins = useMemo(() => notePins.filter((p) => !p.strip), [notePins]);
  const stripNotePins = useMemo(() => notePins.filter((p) => p.strip), [notePins]);
  // The replies typed on this graph's cards and what the chat said back; a
  // card shows the ones asked about its own claims.
  const subscribeReplies = useCallback((cb) => (graphKey ? graphReplies.subscribe(graphKey, cb) : () => {}), [graphKey]);
  const graphReplyList = useSyncExternalStore(subscribeReplies, () => (graphKey ? graphReplies.list(graphKey) : NO_REPLIES), () => NO_REPLIES);
  const openReplies = useMemo(() => {
    const ids = new Set(openCluster?.claims || []);
    return ids.size ? graphReplyList.filter((r) => (r.claimIds || []).some((id) => ids.has(id))) : NO_REPLIES;
  }, [graphReplyList, openCluster]);
  const replyFromCard = useCallback((words) => {
    if (!openCluster) return;
    const claimIds = openCluster.claims;
    const text = claimReplyText({
      text: words,
      claims: claimIds.map((id) => graphClaimById.get(id)).filter(Boolean),
      traceId: trace?.sessionId || trace?.id || null,
      turnLabel: Number.isInteger(openCluster.turn) ? turnBase + openCluster.turn : null,
    });
    const sent = onGraphReply?.({ text, words, claimIds });
    // No chat took it (none is mounted to): the card says so rather than
    // showing a question that went nowhere as sent.
    if (!sent?.replyId && graphKey) {
      graphReplies.upsert(graphKey, { id: nextReplyId(), text: words, claimIds, at: Date.now(), state: "missing" });
    }
  }, [openCluster, graphClaimById, trace, turnBase, onGraphReply, graphKey]);
  const pinEls = useRef(new Map());
  const bindPin = useCallback((id, el) => {
    if (el) pinEls.current.set(id, el);
    else pinEls.current.delete(id);
  }, []);
  const openPinById = useCallback((id) => {
    const pin = commentPins.find((p) => p.id === id);
    if (!pin) return;
    const cur = openPinRef.current;
    if (cur && cur.claims.some((c) => pin.claims.includes(c))) {
      markPinSeen(cur);
      setOpenPin(null);
      return;
    }
    markPinSeen(cur);
    setOpenNote(null);
    setOpenPin({ id: pin.id, claims: pin.claims });
  }, [commentPins, markPinSeen]);
  const rowPins = useMemo(() => commentPins.filter((p) => !p.strip), [commentPins]);
  // Bring a pin a link opened into the rows' view: the landing centres the axis
  // on its turn, and this scrolls its row into sight when it is off screen.
  const [revealPin, setRevealPin] = useState(null);
  useEffect(() => {
    if (!revealPin || !scrollEl) return;
    const b = (scene.badges || []).find((x) => x.id === revealPin);
    setRevealPin(null);
    if (!b || b.strip) return;
    const room = scrollEl.clientHeight - scene.headerH;
    const top = b.y - scene.headerH;
    if (top < scrollEl.scrollTop + 32 || top > scrollEl.scrollTop + room - 32) scrollEl.scrollTop = Math.max(0, top - room / 2);
  }, [revealPin, scrollEl, scene]);
  const stripPins = useMemo(() => commentPins.filter((p) => p.strip), [commentPins]);

  // In fill mode the dock is permanent. The reading panel -- the judge's
  // account and the conversation about the trace, one subject in one panel --
  // is its default tab and cannot be closed, so a question is always one
  // keystroke away. A picked cell opens its own pane as a closeable tab. The
  // dock can be minimized to its tabs, never closed. A stable dock also keeps
  // the plane from changing height every time a mark is picked or put down.
  // A canvas keeps no dock of its own: in a workspace (the home's panes, or
  // one over the page) it lends the workspace's dock its tabs and draws their
  // panes into it (dockBridge.js) while it is the board in front; a canvas
  // with no such dock opens one only on a pick, to read it, and keeps the
  // column otherwise.
  const permanentDock = fill && !!trace && (!canvas || canvas.dock === "bridge");
  const bridged = permanentDock && !!canvas;
  const dockFront = bridged && !!canvas?.dockFront;
  const dockScope = useDockScope();
  const dockOwner = useMemo(() => ({ board: "lanes", scope: dockScope }), [dockScope]);
  const { slot: dockSlot } = useDockBridge();
  const boardBar = boardHost?.bar;
  const [dockMin, setDockMin] = useState(() => {
    try { return localStorage.getItem(DOCK_MIN_KEY) === "1"; } catch { return false; }
  });
  const setDockMinimized = useCallback((next) => {
    setDockMin(next);
    try { localStorage.setItem(DOCK_MIN_KEY, next ? "1" : "0"); } catch { /* a per-browser convenience */ }
  }, []);
  // The dock's Walkthrough tab, where a board is up: an investigator walks
  // the reader through the board's trace (lib/traceWalkthrough.js), its
  // moments pinned on the trace's card. A moment in the tab and its pin point
  // at each other: the tab lights the pin it is pointed at and opens the one
  // clicked (`n` tells one click from the next), and the board says which
  // pins its pointer is on.
  const walkSession = canvas && sessionGraphOn && trace ? String(trace.sessionId || trace.id || "").toLowerCase() || null : null;
  // A workspace holding more than its trace is walked through whole, as one
  // with no anchor is: every trace it holds together, its trace first
  // (TraceWorkspaceHost's walkTraces).
  const walkTraces = walkSession && canvas?.workspaceId && (canvas.walkTraces?.length || 0) > 1 ? canvas.walkTraces : null;
  const walkWs = walkSession && canvas?.workspaceId ? workspaceWalkKey(canvas.workspaceId) : null;
  const walkNext = walkTraces ? walkWs : walkSession;
  // The tab keeps the walkthrough the reader has (the trace's own, or the
  // workspace's), its moments and the questions under it, as traces are laid
  // on or taken off; walking through again walks what the workspace holds
  // then (walkNext).
  useWalkthrough(walkSession);
  useWalkthrough(walkWs);
  const walkKey = shownWalkKey({ own: walkSession, ws: walkWs, next: walkNext });
  // Where a dock tab's questions pin the notes their answers point at when the
  // board is drawn: its pins, each kept by the conversation that pinned it and
  // named by the question it answers (on the lanes, the reading's own notes).
  const boardNotesFor = useCallback((key) => boardNoteSink(key, walkSession), [walkSession]);
  const shownWalk = useWalkthrough(walkKey);
  const walkRunning = shownWalk.status === "running";
  // The board stands the moments of the walkthrough the dock holds, and none
  // a walkthrough no longer kept left on its cards.
  const boardWalkKey = walkKey && !(shownWalk.account || shownWalk.notes.length) ? null : walkthroughChatKey(walkKey || "");
  const [notePoint, setNotePoint] = useState(null);
  const [noteOpen, setNoteOpen] = useState(null);
  const [pinsPointed, setPinsPointed] = useState(null);
  const openNoteOnBoard = useCallback((id) => setNoteOpen((cur) => ({ id, n: (cur?.n || 0) + 1 })), []);
  const walkTurnLabel = useCallback((messageId, session = null) => {
    if (session && String(session).toLowerCase() !== walkSession) return null;
    const want = String(messageId || "").toLowerCase();
    const at = want ? (turns || []).findIndex((t) => String(t?.messageId || "").toLowerCase() === want) : -1;
    return at >= 0 ? `T${turnBase + at}` : null;
  }, [turns, turnBase, walkSession]);
  // Which pane stands in front: the reading panel, the walkthrough, or the
  // picked cell's; the dock rests on the reading.
  const restTab = "reading";
  const restTabRef = useRef(restTab);
  restTabRef.current = restTab;
  const [front, setFrontState] = useState(restTab);
  // The pane in front as last set, ahead of the render that draws it: the
  // tabs lent in the commit a pane is asked for name that pane, not the one
  // drawn before it, whichever effect runs first.
  const frontRef = useRef(restTab);
  const setFront = useCallback((next) => {
    frontRef.current = typeof next === "function" ? next(frontRef.current) : next;
    setFrontState(frontRef.current);
  }, []);
  // The workspace's chat is its dock's, not the board's: asked for, the
  // board shows its reading.
  const activeOf = (f) => (f === "sel" ? (sel != null ? "sel" : restTab) : f === "chat" ? "reading" : f);
  const activeDockTab = activeOf(front);
  // The dock's Investigate tab, where a board is up: the cases that touch the
  // sessions on it (CaseInbox), the board's own case first where it was
  // opened for one. It reads only once opened, and stays read after.
  const [investigateSeen, setInvestigateSeen] = useState(false);
  if (!investigateSeen && walkSession && activeDockTab === "investigate") setInvestigateSeen(true);
  const boardIdsKey = walkSession ? [...new Set([walkSession, ...(canvas?.walkTraces || []).map((t) => t.id)])].join(",") : "";
  const boardIds = useMemo(() => (boardIdsKey ? boardIdsKey.split(",") : []), [boardIdsKey]);
  const investigateRange = useMemo(() => toApiRange(canvas?.range || "1Q"), [canvas?.range]);
  // A note opens its pin on the board; a session comes into view.
  const onInvestigateFocus = useCallback(({ session, noteId = null }) => {
    if (noteId) openNoteOnBoard(noteId);
    else if (session) requestWorkspaceFocus(session);
  }, [openNoteOnBoard]);
  const layOn = boardHost?.onLayer;
  const onInvestigateLayOn = useCallback((session) => layOn?.([{ id: session, title: "" }]), [layOn]);
  // Anything that asks for the dock's body -- a tab, a pick, the reading
  // strip -- brings it back from minimized.
  const showDockTab = useCallback((id) => {
    setFront(id);
    setDockMinimized(false);
    if (bridged) askDock(dockOwner, id === "chat" ? "reading" : id);
  }, [setFront, setDockMinimized, bridged, dockOwner]);
  showDockTabRef.current = showDockTab;
  const selKey = sel
    ? [sel.insetOf, sel.laneId, sel.turn, sel.tab, sel.reportIndex, sel.joinThrough, sel.findingThrough].join("|")
    : null;
  useEffect(() => {
    if (selKey != null) showDockTab("sel");
    else setFront((f) => (f === "sel" ? restTabRef.current : f));
  }, [selKey, showDockTab, setFront]);
  useEffect(() => {
    setFront(restTabRef.current);
  }, [traceKey, setFront]);
  const permanentDockRef = useRef(permanentDock);
  permanentDockRef.current = permanentDock;
  // Lent to the home's dock: the reading's tab and the picked cell's, with
  // the one in front; picked or closed there as they are here.
  const selDockLabel = sel == null ? ""
    : systemSubject ? systemSubject.label
      : docked.lane ? `T${dockBase + sel.turn} ${docked.lane.label}` : `Turn ${dockBase + sel.turn}`;
  const readingRunning = reading.status === "running";
  useEffect(() => {
    if (!dockFront) return;
    lendDock(dockOwner, {
      tabs: [
        { id: "reading", label: "Reading", dot: readingRunning ? "bg-[color:var(--eyebrow)]" : null },
        ...(walkSession ? [{ id: "walkthrough", label: "Walkthrough", dot: walkRunning ? "bg-[color:var(--eyebrow)]" : null }] : []),
        ...(walkSession ? [{ id: "investigate", label: "Investigate" }] : []),
        ...(sel != null ? [{ id: "sel", label: selDockLabel, closable: true, picked: true }] : []),
      ],
      active: activeOf(frontRef.current),
      onSelect: showDockTab,
      onClose: (id) => { if (id === "sel") { setSel(null); setSysPick(null); } },
    });
  });
  useEffect(() => (dockFront ? () => takeDockBack(dockOwner) : undefined), [dockFront, dockOwner]);

  const scanControls = useMemo(() => {
    if (!scanStrips?.length) return null;
    const runOf = { handoff: handoffs, reading };
    const btn = "cursor-pointer p-0 text-[9px] uppercase leading-none tracking-[0.07em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]";
    const place = (strip, i, control) => {
      const button = (
        <button type="button" data-scan-control={strip.key} className={btn} onClick={control.onClick}>
          {control.label}
        </button>
      );
      return (
        <div
          key={strip.key}
          data-scan-controls={strip.key}
          className="pointer-events-auto absolute flex items-center gap-[6px]"
          style={{ top: scene.strips.scans + i * SCAN_H + (SCAN_H - 12) / 2, left: railW - STRIP_CTRL_W, height: 12 }}
        >
          {control.help
            ? <HoverTooltip wide placement="bottom" label={control.help}>{button}</HoverTooltip>
            : button}
        </div>
      );
    };
    return scanStrips.map((strip, i) => {
      if (strip.key === "graph") {
        return onGraphClose
          ? place(strip, i, { label: "close", onClick: onGraphClose, help: "Take the chat's knowledge graph off the trace views. The chat keeps it." })
          : null;
      }
      if (strip.key === "notes") {
        return noteTraceId
          ? place(strip, i, { label: "clear", onClick: () => clearThreadNotes(noteTraceId), help: "Take the reader chat's notes off the lanes. Its answers stay in the conversation." })
          : null;
      }
      const run = runOf[strip.key];
      if (!run) return null;
      // Calls a previous run left unjudged. They were never banked, so running
      // again picks up exactly those and nothing else.
      const unjudged = run.report?.judgeFailures?.calls || 0;
      // ONE verb, whichever one is true right now. "again" and "re-read" sat
      // side by side saying nearly the same thing, and the cheaper of the two
      // did NOTHING at all on a finished trace whose calls are all banked — a
      // control whose honest outcome is "nothing happened" reads as broken,
      // the same lesson the ledger-priced re-run taught twice.
      //
      // The order is what the reader would do next. Unjudged calls first,
      // because a benchmark against a partial read measures the wrong thing:
      // the re-read carries the last read's additions in as claims, so the
      // baseline has to be complete before comparing to it means anything.
      const control = run.status === "running"
        ? { label: "stop", onClick: run.stop, help: "Stop the run. Calls already read stay read." }
        : strip.retry
          ? { label: "retry", onClick: run.run, help: "Run this scan again from the top." }
          : !run.report
            ? { label: "run", onClick: run.run, help: null }
            : unjudged
              ? {
                label: "finish",
                onClick: run.run,
                help: `Read the ${unjudged} call${unjudged === 1 ? "" : "s"} the last run left unjudged. Everything already read is reused from the local ledger and costs nothing.`,
              }
              : strip.key === "reading" && run.rerun
                ? {
                  label: "read again",
                  onClick: run.rerun,
                  help: "Read the whole trace again with a fresh judge: every banked verdict is ignored and re-earned, and the last read's additions go in as claims for it to confirm or refute. Full cost — this is the pass that shows whether the reading is getting better.",
                }
                : { label: "run again", onClick: run.run, help: null };
      return place(strip, i, control);
    });
  }, [scanStrips, scene.strips.scans, handoffs, reading, railW, onGraphClose, noteTraceId]);

  // The record's paging control, pinned over the ruler's rail head the way
  // the scan strips' run controls are pinned over theirs.
  const axisControlEl = useMemo(() => {
    if (!axisControl) return null;
    const cls = "cursor-pointer p-0 text-[9px] uppercase leading-none tracking-[0.07em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] disabled:cursor-default";
    const inner = axisControl.busy
      ? <span data-axis-control-busy="" tabIndex={0} className="text-[9px] uppercase leading-none tracking-[0.07em] text-[color:var(--muted-soft)]">{axisControl.label}</span>
      : <button type="button" data-load-earlier="" className={cls} onClick={axisControl.onClick}>{axisControl.label}</button>;
    return (
      <div
        key="axis"
        data-axis-control=""
        className="pointer-events-auto absolute flex items-center gap-[6px]"
        style={{ top: scene.strips.ruler + (RULER_H - 12) / 2, left: railW - STRIP_CTRL_W, height: 12 }}
      >
        <HoverTooltip wide placement="bottom" label={axisControl.help}>{inner}</HoverTooltip>
      </div>
    );
  }, [axisControl, scene.strips.ruler, railW]);

  // --- the plane's handlers -------------------------------------------------
  //
  // A hit is a scene position, not a DOM event: the plane resolves what was
  // under the pointer and hands back what it means, so everything below reads
  // as the same vocabulary the DOM rows used to raise.
  const setPlaneScroller = useCallback((el) => {
    // Browser scroll anchoring reacts to a large insertion (an expanded
    // sub-agent inset) by adjusting the scroll offset -- on this plane that
    // reads as the timeline jumping sideways. The reader's scroll position is
    // theirs; nothing here may move it.
    if (el) el.style.overflowAnchor = "none";
    setScrollEl(el);
  }, []);

  const onPlanePick = useCallback((hit) => {
    if (!hit) return;
    switch (hit.type) {
      case "insetopen":
        // The panel's own navigation, whichever door it arrived through:
        // ContextPanel passes it as a prop AND inside the threads wiring.
        if (hit.traceId) (onOpenTrace || threads?.onOpenTrace)?.(hit.traceId);
        return;
      case "insetzoom": {
        if (hit.zoom === "fit") {
          setInsetPan((cur) => { const n = new Map(cur); n.delete(hit.laneId); return n; });
        }
        setInsetZoom((cur) => {
          const next = new Map(cur);
          if (hit.zoom === "fit") next.delete(hit.laneId);
          else {
            const z = insetScenes.get(hit.laneId)?.scene?.meta?.zoom;
            const at = z?.cellW ?? CELL_1X;
            // A child's ceiling is relative to ITS OWN fit, not the parent's:
            // a thirty-turn thread fits at ~37px/turn, already above the
            // parent's ceiling, so clamping there made "zoom in" zoom out.
            const ceiling = Math.max(CELL_MAX, (z?.fitCellW ?? CELL_1X) * 3);
            next.set(hit.laneId, Math.max(CELL_MIN, Math.min(ceiling, hit.zoom === "in" ? at * 1.6 : at / 1.6)));
          }
          return next;
        });
        return;
      }
      case "systemhead": setSysOpen((v) => !v); return;
      case "systempin": toggleSysPin(); return;
      case "systembucket":
        pickSystem(systemSel && systemSel.kind === hit.kind && systemSel.start === hit.start
          ? null
          : { kind: hit.kind, start: hit.start, end: hit.end });
        return;
      case "topic": pickTurn(hit.start); return;
      // An edge chip is a navigation, not a reading: it says where the nearest
      // activity is and takes the reader there.
      case "edgechip": goTo(hit.laneId, hit.turn); return;
      case "relatedchip": revealRow(hit.rowTop); return;
      case "beat": pickBeat(hit.turn); return;
      case "strip": pickTurn(hit.turn); return;
      // A scan mark and a run of located traffic both name a turn to read.
      case "scanmark": case "scantraffic":
        if (hit.strip === "requests") {
          // A request mark opens the capture itself -- the audit view of what
          // that call was sent -- through the panel's own navigation.
          const id = requestsByTurn?.get(hit.turn)?.rows?.[0]?.traceId;
          if (id) (onOpenTrace || threads?.onOpenTrace)?.(id);
          else pickTurn(hit.turn);
          return;
        }
        // A finding mark on the pipeline's band -- named for its reports when
        // there are any -- opens the dock on the finding.
        if (hit.strip === "findings" || hit.strip === "reports") pickFinding(hit.turn, false, hit.through ?? hit.turn);
        else if (hit.strip === "graph") pickGraph(hit.turn, false, hit.through ?? hit.turn);
        else pickTurn(hit.turn);
        revealStripTargets(stripMarkOf(hit));
        return;
      // A chapter names a report AND the turn under the pointer.
      case "scanseg": pickReport(hit.turn, hit.index); return;
      case "fpin": pickFinding(hit.turn); return;
      case "gpin": pickGraph(hit.turn); return;
      case "pin": case "ring": pickJoin(hit.turn, hit.lane ?? null); return;
      case "jspan":
        pickJoin(hit.turn, hit.lane ?? null);
        revealStripTargets(stripMarkOf(hit));
        return;
      case "striphead": {
        // The graph's row opens the dock on the graph at its first placed turn,
        // or on its list alone when nothing could be placed.
        if (hit.strip === "graph") {
          const turnsWithClaims = (graphOverlay?.claims || []).flatMap((c) => c.turns);
          const first = turnsWithClaims.length ? Math.min(...turnsWithClaims) : null;
          pickGraph(first ?? 0, first != null);
          return;
        }
        // A closed reading reopens from its own row -- the report is already
        // cached, so this is a view change and never a run.
        const r = readingRef.current;
        if (hit.strip !== "reading" || !r?.report) return;
        if (permanentDockRef.current) showDockTab("reading");
        else if (!r.dockOpen) r.openDock();
        return;
      }
      // An incident's bar docks every occurrence at once: the run is the
      // selection, so the dock lists them all and the fan draws from its start.
      case "scanspan": pickFinding(hit.from, false, hit.through); return;
      case "insethead": case "inset": return;
      // A rail name opens the lane at its LAST mark: the name is how a reader
      // says "take me to this subject", and turn 0 is rarely where its story is.
      //
      // A name inside a spliced child names a lane in THAT conversation, so the
      // turn is looked up in the child's model and the pick carries the child
      // it was made in. Reading it out of the parent's model finds nothing,
      // opens turn 0 of the wrong conversation, and travels the parent's axis
      // to get there.
      case "rail": {
        const owner = hit.insetOf ? insetScenes.get(hit.insetOf)?.model : model;
        const turns = owner?.laneById?.get(hit.laneId)?.turns || [];
        const at = turns.length ? turns[turns.length - 1] : 0;
        // Travel is the PARENT axis moving, which is not the child's to move:
        // a child pans inside its own box.
        pick(hit.laneId, at, !hit.insetOf, hit.insetOf || null);
        return;
      }
      default: pick(hit.laneId, hit.turn ?? 0, false, hit.insetOf || null);
    }
  }, [showDockTab, insetScenes, pick, goTo, pickTurn, pickBeat, pickJoin, pickFinding, pickGraph, graphOverlay, pickSystem, systemSel, revealRow,
      onOpenTrace, threads, model, requestsByTurn, revealStripTargets]);

  const onPlaneToggle = useCallback((hit) => {
    if (!hit) return;
    // A fold inside a spliced child applies to THAT plane's own state: one
    // shared set would fold every open thread at once.
    if (hit.insetOf) {
      setInsetClosed((cur) => {
        const next = new Map(cur);
        // Seeded from the fold the child is CURRENTLY drawn with, which is the
        // default fold until the reader has touched it. Starting from an empty
        // set inverted the whole child on the first click: every group folded
        // by default sprang open and only the clicked one stayed shut.
        const own = new Set(next.get(hit.insetOf) ?? insetScenes.get(hit.insetOf)?.fold ?? []);
        if (own.has(hit.rowId)) own.delete(hit.rowId); else own.add(hit.rowId);
        next.set(hit.insetOf, own);
        return next;
      });
      return;
    }
    if (hit.expandable) { toggleAgent(hit.laneId); return; }
    if (hit.rowId) toggle(hit.rowId);
  }, [toggle, toggleAgent, insetScenes]);

  const onPlaneHover = useCallback((hit) => {
    const mark = stripMarkOf(hit);
    setHoverStripMark((cur) => (cur?.strip === mark?.strip && cur?.turn === mark?.turn && cur?.through === mark?.through ? cur : mark));
    if (!hit) { onHoverCell(null, null); onHoverLane(null); onHoverTopic(null); setHoverFinding(null); return; }
    if (hit.type === "topic") { onHoverTopic(hit.start); return; }
    onHoverTopic(null);
    // A folded header's mark is about every lane it stands for, which is a
    // different reading from one lane's cell.
    if (hit.laneIds?.length) onHoverGroup(hit.laneIds, hit.turn, hit.insetOf || null);
    else if (hit.type === "cell" || hit.type === "row" || hit.type === "fpin" || hit.type === "gpin") onHoverCell(hit.laneId, hit.turn, hit.insetOf || null);
    else if (hit.type === "rail") onHoverLane(hit.laneId);
    else onHoverCell(null, null);
    const reviewMark = hit.type === "scanmark" && (hit.strip === "findings" || hit.strip === "reports");
    setHoverFinding((cur) => {
      if (!reviewMark) return cur == null ? cur : null;
      return cur && cur.turn === hit.turn && cur.through === hit.through ? cur : { turn: hit.turn, through: hit.through };
    });
  }, [onHoverCell, onHoverGroup, onHoverLane, onHoverTopic]);

  const onPlaneInsetPan = useCallback((laneId, x) => {
    setInsetPan((cur) => (cur.get(laneId) === x ? cur : new Map(cur).set(laneId, x)));
  }, []);

  // The reference clock a join excerpt dates against.
  const joinRefMs = useMemo(
    () => (typeof traceTimestamp === "number" ? traceTimestamp : Date.parse(traceTimestamp ?? "")),
    [traceTimestamp],
  );

  // --- what the plane says on hover -----------------------------------------
  //
  // Every text-heavy reading is the component the DOM plane already used. The
  // canvas owns geometry and hit-testing; the moment it owns wording too, the
  // wording drifts from the surface it was written for.
  const tooltipOf = useCallback((hit) => {
    if (!hit) return null;
    // A hit inside a spliced child reads THAT conversation's own turns, whose
    // page is whole: the outer record's base is not its.
    const ownBase = hit.insetOf ? 0 : turnBase;
    const joinTitle = (h, word) => {
      const span = h.through > h.turn ? `Turns ${ownBase + h.turn}-${ownBase + h.through}` : `Turn ${ownBase + h.turn}`;
      return `${span} \u00b7 ${word} ${h.count} session${h.count === 1 ? "" : "s"}`;
    };
    // A hit inside a spliced child reads THAT conversation, as a pick does
    // (docked): its lane, its turns, its narration. Read out of the parent's
    // model the child's lane is nobody, and the hover fell back to a bare turn
    // number where the parent shows the subject's whole state. The influence
    // projection is the parent's own and does not span the child.
    const owner = hit.insetOf ? insetScenes.get(hit.insetOf) : null;
    const ownModel = hit.insetOf ? (owner?.status === "ready" ? owner.model : null) : model;
    const ownTurns = hit.insetOf ? (owner?.status === "ready" ? owner.turns : null) : turns;
    // Stamped from the child's own turns, as the dock is: the outer scene's
    // stamps are positional on the parent's axis.
    const ownStamps = hit.insetOf ? (ownTurns ? buildTurnStamps(ownTurns) : null) : turnStamps;
    switch (hit.type) {
      case "cell": {
        const lane = ownModel?.laneById?.get(hit.laneId);
        if (!lane) return <span className="text-[color:var(--muted)]">turn {ownBase + hit.turn}</span>;
        return (
          <TurnBaseContext.Provider value={ownBase}>
            <LaneCellCard
              lane={lane}
              turn={hit.turn}
              firstTurn={hit.first}
              turns={ownTurns}
              influence={hit.insetOf ? null : influence}
              laneById={ownModel.laneById}
              narration={ownModel.narration}
              stamps={ownStamps}
            />
          </TurnBaseContext.Provider>
        );
      }
      case "pin":
        return (
          <JoinMarkTooltip
            title={joinTitle(hit, `this cell's content is ${hit.observed ? "observed by" : "shared with"}`)}
            entries={hit.entries || []}
            hues={joinHues}
            refMs={joinRefMs}
            searchWindow={joinSearchWindow}
          />
        );
      case "jspan":
        return (
          <JoinMarkTooltip
            title={joinTitle(hit, `content ${hit.observed ? "observed by" : "shared with"}`)}
            entries={hit.entries || []}
            structure={hit.structure}
            hues={joinHues}
            refMs={joinRefMs}
            searchWindow={joinSearchWindow}
          />
        );
      case "ring":
        return <span>shared hand-off content at turn {ownBase + hit.turn}</span>;
      case "beat":
        return (
          <span className="grid gap-[4px]">
            <span className="flex items-baseline gap-[6px]">
              <span className="font-medium">{hit.label} · T{ownBase + hit.turn}</span>
              {/* A tooltip takes no pointer, so its stamp names the capture
                  without offering to open it. */}
              <StampCaptureContext.Provider value={null}>
                <TraceStamp stamp={ownStamps?.[hit.turn]} className="ml-auto" />
              </StampCaptureContext.Provider>
            </span>
            <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere] text-[color:var(--muted)]">
              {String(hit.text || "").slice(0, 280)}
            </span>
          </span>
        );
      case "topic": {
        // The ribbon's own reading: what the window is ABOUT. Same card the DOM
        // ribbon showed, because the ribbon is the only place on the axis that
        // answers "what was this stretch of work" rather than "what happened".
        const b = hit.bucket;
        const axis = topicRibbon?.axis;
        const nameOf = (id) => (axis ? topicNameOf(axis, id) : "unlabeled topic");
        const hueOf = (id) => topicRibbon?.hues?.get(id) || TOPIC_OTHER_HUE;
        const span = hit.start === hit.end ? `Turn ${ownBase + hit.start}` : `Turns ${ownBase + hit.start}-${ownBase + hit.end}`;
        return (
          <span className="grid gap-[3px]">
            <span className="font-medium">{span}</span>
            {(b?.mix || []).slice(0, 5).map((seg) => (
              <span key={seg.clusterId ?? "other"} className="flex items-baseline gap-[5px]">
                <span aria-hidden="true" className="mt-[3px] h-[7px] w-[7px] shrink-0" style={{ background: hueOf(seg.clusterId) }} />
                <span className="min-w-0 flex-1 truncate">
                  {seg.clusterId != null
                    ? nameOf(seg.clusterId)
                    : seg.others === 1 ? nameOf(seg.ids[0]) : `${seg.others} smaller topics`}
                </span>
                {b.turns > 1 ? <span className="shrink-0 text-[color:var(--muted-soft)]">{`${seg.turns}/${b.turns}`}</span> : null}
              </span>
            ))}
            {(b?.mix?.length || 0) > 5 ? (
              <span className="text-[color:var(--muted-soft)]">{`+${b.mix.length - 5} more topics here`}</span>
            ) : null}
            {b?.unknown ? (
              <span className="text-[color:var(--muted-soft)]">
                {`${b.unknown} turn${b.unknown === 1 ? "" : "s"} with no dated request`}
              </span>
            ) : null}
          </span>
        );
      }
      case "strip": {
        // On the ruler, name the topic the turn sits in. Between the beats the
        // axis is otherwise mute, and "which stretch of work is this" is the
        // question a bare turn number cannot answer.
        const run = hit.strip === "ruler"
          ? topicRunList.find((r) => r.from <= hit.turn && hit.turn <= r.to)
          : null;
        return (
          <span className="grid gap-[2px]">
            <span className="font-medium">
              {hit.strip === "joins" ? "Joins · " : ""}turn {ownBase + hit.turn}
            </span>
            {run ? (
              <span className="flex items-baseline gap-[5px]">
                <span aria-hidden="true" className="mt-[3px] h-[7px] w-[7px] shrink-0" style={{ background: topicHueMap?.get(run.clusterId) || TOPIC_OTHER_HUE }} />
                <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]">
                  {topicAxis ? topicNameOf(topicAxis, run.clusterId) : "unlabeled topic"}
                </span>
              </span>
            ) : null}
          </span>
        );
      }
      case "scanspan":
        return (
          <span className="grid gap-[2px]">
            <span className="font-medium">{hit.label}</span>
            <span className="text-[color:var(--muted)]">
              {hit.count} occurrences {"\u00b7"} turns {ownBase + hit.from}-{ownBase + hit.through} {"\u00b7"} the same conduct, recurring
            </span>
          </span>
        );
      case "scantraffic":
        return (
          <span className="grid gap-[2px]">
            <span>
              {hit.stripLabel} · {hit.through > hit.from ? `turns ${ownBase + hit.from}-${ownBase + hit.through}` : `turn ${ownBase + hit.from}`}
            </span>
            <span className="text-[10px] text-[color:var(--muted)]">channel traffic</span>
          </span>
        );
      case "fpin":
        return (
          <span className="grid gap-[2px]">
            <span>
              Finding {"\u00b7"} turn {ownBase + hit.turn}
              {hit.count > 1 ? ` \u00b7 ${hit.count} findings` : ""}
            </span>
            <span className="text-[10px] text-[color:var(--muted)]">{hit.label}</span>
          </span>
        );
      case "gpin": {
        // Evidence: what the chat concluded from this cell first, then what the
        // cell holds that it cited. The pin's card and the dock have the rest.
        const here = graphOverlay ? graphAtTurn(graphOverlay, hit.turn) : { anchors: [], claims: [] };
        const inCell = here.anchors.filter((a) => a.placement?.cells?.some((c) => c.lane === hit.laneId && c.turn === hit.turn));
        const claims = here.claims.filter((c) => inCell.some((a) => a.claims.includes(c.id)));
        const clipTo = (text, n) => (text.length > n ? `${text.slice(0, n - 1)}\u2026` : text);
        return (
          <span className="grid max-w-[340px] gap-[5px]">
            <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)]">
              Evidence for {claims.length} claim{claims.length === 1 ? "" : "s"} {"\u00b7"} T{ownBase + hit.turn}
            </span>
            {claims.slice(0, 2).map((c) => (
              <span key={c.id} className="whitespace-normal text-[11px] leading-[1.45] [overflow-wrap:anywhere]">
                {clipTo(c.statement, 180)}
              </span>
            ))}
            {claims.length > 2 ? <span className="text-[10px] text-[color:var(--muted)]">+{claims.length - 2} more</span> : null}
            {inCell.slice(0, 2).map((a) => (
              <span key={a.key} className="whitespace-normal font-mono text-[10px] leading-[1.4] text-[color:var(--muted)] [overflow-wrap:anywhere]">
                {clipTo(a.display || a.id, 90)}
              </span>
            ))}
          </span>
        );
      }
      case "scanmark": {
        const kin = !hit.insetOf && (hit.strip === "findings" || hit.strip === "reports") ? incidentKinAt(hit.turn, hit.through) : null;
        return (
          <span className="grid gap-[2px]">
            <span>
              {hit.stripLabel} · {hit.through > hit.turn ? `turns ${ownBase + hit.turn}-${ownBase + hit.through}` : `turn ${ownBase + hit.turn}`}
              {scanMarkCountLine(hit.strip, hit.count)}
            </span>
            <span className="text-[10px] text-[color:var(--muted)]">{hit.label}</span>
            {kin ? <IncidentKin kin={kin} turnCount={turnCount} base={ownBase} /> : null}
          </span>
        );
      }
      case "scanseg":
        // The chapter's number and stretch, then the judge's account of it in
        // full: the summary IS the content, so the hover does not truncate it.
        return (
          <span className="grid max-w-[360px] gap-[3px]">
            <span>
              {hit.stripLabel} {"·"} {hit.index} {"·"} {hit.through > hit.from ? `turns ${ownBase + hit.from}-${ownBase + hit.through}` : `turn ${ownBase + hit.from}`}
            </span>
            <span className="whitespace-normal text-[10px] leading-[1.45] text-[color:var(--muted)] [overflow-wrap:anywhere]">{hit.label}</span>
          </span>
        );
      case "systembucket":
        return (
          <span className="grid gap-[3px]">
            <span className="font-medium">
              {hit.start === hit.end ? `Turn ${ownBase + hit.start}` : `Turns ${ownBase + hit.start}-${ownBase + hit.end}`}
            </span>
            <span className="text-[color:var(--muted)]">{`${hit.count} event${hit.count === 1 ? "" : "s"}`}</span>
          </span>
        );
      case "edgechip":
        return (
          <span className="grid gap-[2px]">
            <span>Nearest activity at turn {ownBase + hit.turn}</span>
            <span className="font-mono text-[10px] text-[color:var(--muted)]">{hit.label}</span>
          </span>
        );
      case "rail": {
        // The full path leads: a truncated label is exactly what a reader hovers
        // the rail to resolve.
        const lane = ownModel?.laneById?.get(hit.laneId);
        // A row's own accumulation, which the rail states for a lane and for a
        // folded header alike -- the folded one carrying its whole subtree's.
        // The weights here are the parent's; a child's rail states its figure
        // on the row and names the lane in the hover.
        const w = hit.insetOf ? null : (weights?.byLane?.get(hit.laneId) || rowWeights.get(hit.rowId) || null);
        return (
          <span className="grid min-w-0 gap-[2px]">
            <span className="min-w-0 font-mono text-[11px] [overflow-wrap:anywhere] text-[color:var(--ink)]">
              {lane?.path || lane?.label || hit.label || hit.laneId}
            </span>
            {/* The row's accumulation in words. The rail states it as a compact
                numeral, the way the scope tree states activity; the words live
                here, where there is room to read them. */}
            {lane?.digest ? <span className="text-[10px] text-[color:var(--muted)]">{lane.digest}</span> : null}
            {!lane && hit.count != null ? (
              <span className="text-[10px] text-[color:var(--muted)]">{`${formatCompact(hit.count)} lane${hit.count === 1 ? "" : "s"}`}</span>
            ) : null}
            {w ? <span className="text-[10px] text-[color:var(--muted)]">{weightLine(w, weights)}</span> : null}
          </span>
        );
      }
      case "insetopen": return <span>open the spawned thread&apos;s full trace</span>;
      case "insethead": return <span className="text-[color:var(--muted)]">the spliced thread&apos;s own timeline</span>;
      case "striphead":
        // The joins strip's header resolves to the session inventory — the
        // rail summary names the counts, the hover names the counterparts.
        if (hit.strip === "joins") return <JoinsInventoryTooltip lens={joinLens} hues={joinHues} />;
        // The legend lives where the marks are: the axis band's head explains
        // the mark vocabulary and, when the band carries the token skyline,
        // what its bars, line and request marks say.
        if (hit.strip === "ruler") {
          return (
            <AxisLegend
              facts={axisFacts}
              weights={!!weights}
              tokens={density ? { detail: densityLabelLine(density, level), requests: requestsInline ? requestsLine : "" } : null}
            />
          );
        }
        if (hit.strip === "graph") {
          return (
            <span className="grid max-w-[340px] gap-[3px]">
              <span className="whitespace-normal">
                {graphOverlay ? graphOverlaySummary(graphOverlay) : chatGraph.empty ? "the chat's knowledge graph is empty in this browser" : "the chat's knowledge graph names nothing in this trace"}
              </span>
              <span className="whitespace-normal text-[10px] leading-[1.45] text-[color:var(--muted)]">
                The chat&apos;s knowledge graph over this trace: each pin is a claim it made, each dot the evidence a claim rests on, each mark here a turn claims rest on. Click to list every claim.
              </span>
            </span>
          );
        }
        if (hit.strip === "requests") {
          return (
            <span className="grid gap-[2px]">
              <span>{requestsLine || "captured requests"}</span>
              <span className="text-[10px] text-[color:var(--muted)]">one mark per captured request, at the last message it shipped; click opens the request</span>
            </span>
          );
        }
        // The band's summary is longer than the rail, so the hover carries the
        // whole line for each of the two things it holds: the reports (count,
        // how many with findings, the worst rungs) and the findings (count, the
        // worst rungs, what the loaded turns could not place).
        if ((hit.strip === "reports" || hit.strip === "findings") && (reportsOpen || findingsOpen)) {
          return (
            <span className="grid gap-[2px]">
              {reportsOpen ? <span>{reportsSummaryLine(reportsTimeline)}</span> : null}
              {findingsOpen ? <span>{findingsSummaryLine(findingsReport)}</span> : null}
              <span className="text-[10px] text-[color:var(--muted)]">
                {reportsOpen ? "trace review reports, one per reviewed window, placed by the messages they reviewed" : ""}
                {reportsOpen && findingsOpen ? "; " : ""}
                {findingsOpen ? "findings located by their quoted evidence" : ""}
              </span>
            </span>
          );
        }
        return null;
      case "row": {
        // Between the marks. A bare turn number orients nobody -- the reader is
        // over a row they cannot name, at a turn nothing claimed -- so this says
        // WHICH subject and where its nearest work sits either side.
        const lane = model.laneById?.get(hit.laneId);
        const near = lane?.turns?.length ? nearestTurns(lane.turns, hit.turn, hit.turn) : null;
        const before = near?.left != null ? `T${ownBase + near.left}` : null;
        const after = near?.right != null ? `T${ownBase + near.right}` : null;
        return (
          <span className="grid min-w-0 gap-[2px]">
            <span className="min-w-0 font-mono text-[11px] [overflow-wrap:anywhere] text-[color:var(--ink)]">
              {lane?.path || lane?.label || hit.laneId}
            </span>
            <span className="text-[color:var(--muted)]">
              {`turn ${ownBase + hit.turn} \u00b7 nothing here`}
              {before || after
                ? ` \u00b7 nearest ${[before && `${before} before`, after && `${after} after`].filter(Boolean).join(", ")}`
                : " \u00b7 no activity in this lane"}
            </span>
            <span className="text-[10px] text-[color:var(--muted-soft)]">click to read this turn in the dock</span>
          </span>
        );
      }
      default: return null;
    }
  }, [model, turns, turnStamps, influence, insetScenes, joinHues, joinLens, joinRefMs, joinSearchWindow,
      topicRibbon, topicRunList, topicHueMap, topicAxis, weights, rowWeights, incidentKinAt, turnCount, graphOverlay]);

  if (!model.columns.length) {
    // Fill mode holds the frame even with nothing to draw, so switching to a
    // lane-less trace doesn't collapse the panel and re-expand on the next.
    return (
      <div
        ref={rootRef}
        className={`py-[8px] ${META_CLASS} text-[color:var(--muted)] ${fill ? "min-h-0" : ""}`}
        style={fill && fillH ? { height: fillH } : undefined}
      >
        No classifiable operations found. The trace contains no recognized prompts, file work, research, queries, or publishes.
      </div>
    );
  }



  const zoomBtn = "grid h-[16px] min-w-[16px] place-items-center rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[3px] font-mono text-[10px] leading-none text-[color:var(--muted)] cursor-pointer hover:text-[color:var(--ink)]";
  // Same box as zoomBtn -- one height, one radius, one border -- so the run
  // controls and the zoom stack read as one row of controls rather than two.
  const actionBtn = "inline-flex h-[16px] shrink-0 items-center rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] text-[10px] leading-none text-[color:var(--muted)] cursor-pointer hover:border-[color:var(--ink)] hover:text-[color:var(--ink)] disabled:cursor-default disabled:opacity-50";

  // What the axis holds, in one line per fact, shown on the Lanes head's hover
  // (the rail head carries the lanes and turns figures itself). Three
  // different units meet here, and each defines itself: tool calls (tool_use
  // blocks in THIS conversation), turns (this conversation's messages, the
  // x-axis), and API calls (every model round-trip in the agentic turn --
  // sub-agents and retries included, which have their own conversations and
  // never appear as turns here). The thread list's token/cost figures are
  // measured at the API-call grain, which is why calls can far outnumber both
  // other counts; the Tokens strip names its own spend level separately.
  const axisFacts = [
    axisRecord && axisRecord.total != null ? {
      key: "record",
      text: axisRecord.remaining != null
        ? `${axisRecord.shown.toLocaleString()} of ${axisRecord.total.toLocaleString()} messages on the axis`
        : `${axisRecord.shown.toLocaleString()} messages, the whole record`,
      note: "The conversation record, read newest first in pages. The control on this row reads the page before the oldest turn shown.",
    } : null,
    (() => {
      const habits = (findingIncidents?.incidents || []).filter((i) => i.count > 1);
      if (!habits.length) return null;
      const recurring = habits.reduce((n, i) => n + i.count, 0);
      return {
        key: "incidents",
        text: `${recurring} findings recur as ${habits.length} incident${habits.length === 1 ? "" : "s"}`,
        note: "The same conduct raised on more than one turn, told by what the findings quote. A bar under the review marks spans each incident from its first occurrence to its last. Hover or select an occurrence and arcs reach the others; a dashed arc reaches conduct that is only related.",
      };
    })(),
    {
      key: "tools",
      text: `${model.coverage.claimed} of ${model.coverage.toolUses} tool calls in lanes`,
      note: "Tool invocations (tool_use blocks) inside this conversation's turns; the count in lanes is how many an extractor claimed for a subject.",
    },
    {
      key: "turns",
      text: `${model.lanes.length} lanes \u00b7 ${model.columns.length} of ${turnCount} turns active`,
      note: "Turns are this conversation's messages (prompts, replies, and tool results) and the timeline's x-axis. An active turn touched at least one lane.",
    },
    topicOpen ? {
      key: "topics",
      text: `${topicRanked.length} topic${topicRanked.length === 1 ? "" : "s"} colour the axis`,
      note: "The token bars on the axis band take the hue of the topic each stretch of the conversation was about. Hover the band for the topic under the pointer; the legend on the toolbar names them all.",
    } : null,
    callsTotal != null && callsTotal > 1 ? {
      key: "calls",
      text: `${formatCompact(callsTotal)} API calls in this run`,
      note: "Every model round-trip in this run: the main thread, its sub-agents, and retries from one user prompt. A run is narrower than the session and distinct from the conversation turns on the axis. The thread list's token and cost figures are measured at this grain.",
    } : null,
    requestsOpen ? {
      key: "requests",
      text: requestsLine,
      note: requestsInline
        ? "One mark per captured request on the axis band, at the last message it shipped. Click a mark to open the request."
        : "One mark per captured request on the Requests row, at the last message it shipped. Click a mark to open the request.",
    } : null,
    // Which half of the final exchange this axis actually holds. The captured
    // case is visible as the last turn; the others would otherwise be
    // indistinguishable from "the session just ended".
    responseStatus ? {
      key: "response", status: responseStatus,
      text: `\u23ce ${RESPONSE_TEXT[responseStatus] || RESPONSE_TEXT.unavailable}`,
      note: RESPONSE_NOTE[responseStatus] || RESPONSE_NOTE.unavailable,
    } : null,
    // The host's caveats about how the axis was built (a code-cell grammar
    // that did not load, say): they change what the lanes could see, so they
    // belong with the counts rather than on a row of their own.
    ...(axisNotes || []).filter(Boolean).map((text, i) => ({ key: `note-${i}`, text, caveat: true })),
  ].filter(Boolean);
  // The toolbar's first row holds only what asks for the reader's attention
  // right now: a content follow in progress, with its clear control. Everything
  // else about the axis lives on the axis's own rows, and the row is not drawn
  // when there is nothing to say.
  const facts = [
    followStrings?.length ? (
      <span key="follow" data-join-follow-note="" className="flex items-center gap-[5px] text-[10px] leading-none" style={{ color: JOIN_COLOR }}>
        <span aria-hidden="true" className="h-[9px] w-[9px] shrink-0 rounded-full" style={{ border: `1.5px solid ${JOIN_COLOR}` }} />
        {followCells.length
          ? followCells.length === 1
            ? "1 cell carries the followed content"
            : `${followCells.length} cells carry the followed content`
          : followHits?.length
            ? "the followed content appears only in conversation turns"
            : "the followed content was not found in this view"}
        <button
          type="button"
          data-join-follow-clear=""
          onClick={clearFollow}
          className="cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]"
        >
          clear
        </button>
      </span>
    ) : null,
  ].filter(Boolean);

  return (
    <TurnBaseContext.Provider value={turnBase}>
    <div
      ref={rootRef}
      className={fill ? "flex min-h-0 min-w-0 flex-col gap-[6px]" : "grid min-w-0 content-start gap-[6px]"}
      style={fill && fillH ? { height: fillH } : undefined}
    >
      {/* Two rows over the plane. The first is the axis's account -- its facts,
          each separated from the next by a dot. The second is the controls:
          what can be run on the trace on the left, what is drawn and how far
          in on the right. Facts and controls used to share one wrapping line,
          which split the controls across two rows at most widths. */}
      {facts.length && !(sessionGraphOn && trace) ? (
        <div className={`flex min-w-0 flex-wrap items-center gap-x-[6px] gap-y-[3px] ${META_CLASS} text-[color:var(--muted)]`} data-lanes-facts="">
          {facts.map((fact, i) => (
            <Fragment key={i}>{i > 0 ? <Dot /> : null}{fact}</Fragment>
          ))}
        </div>
      ) : null}
      <div className={`flex min-w-0 flex-wrap items-center justify-between gap-x-[10px] gap-y-[3px] ${sessionGraphOn && trace ? "hidden" : ""}`} data-lanes-controls="">
        <span className="flex items-center gap-[4px]">
          {trace ? (
            <>
          {/* The scan states its price and stops offering itself once its strip
              is up. Its report is cached per trace and model, so re-running
              buys no new answer. */}
          {!handoffOpen ? (
            <HoverTooltip
              wide
              placement="bottom"
              label={
                handoffs.ready
                  ? "Scan every turn for possible communication or hand-off with another agent — sub-agent spawns, message files, channels another session may read or write. One agent pass per transcript window; each candidate carries search strings for finding the other agent's session."
                  : "Configure a chat model to run the scan."
              }
            >
              <button
                type="button"
                data-lanes-handoff-run=""
                onClick={handoffs.run}
                disabled={!handoffs.ready}
                className={actionBtn}
              >
                Find agent hand-offs
              </button>
            </HoverTooltip>
          ) : null}
          {/* The referee over the timeline's OWN automated reading. Same price
              discipline as its siblings: cached per trace, model, and claims
              fingerprint, and once its strip is up the button stands down. */}
          {!permanentDock && !reading.dockOpen ? (
            <HoverTooltip
              wide
              placement="bottom"
              label={
                reading.canAsk
                  ? "Open the reading panel for this trace. Asking costs nothing: the agent answers from the lanes, turns and verdicts already on this screen. From inside, a judging agent can audit the automated reading call by call - claims accepted or rejected against the raw bytes, missed work returned as quote-anchored additions, verdicts kept locally so sibling requests of this session reuse them."
                  : "Configure a chat model to read or ask about this trace."
              }
            >
              <button
                type="button"
                data-lanes-reading-open=""
                onClick={reading.openDock}
                disabled={!reading.canAsk}
                className={actionBtn}
              >
                Read this trace
              </button>
            </HoverTooltip>
          ) : null}
            </>
          ) : null}
          {trace ? (
            <button type="button" data-session-graph-toggle="" onClick={() => setSessionGraph(!sessionGraphOn)} className={actionBtn}>
              {sessionGraphOn ? "Lanes" : "Session graph"}
            </button>
          ) : null}
          {/* The run's calls, one control among the others: with a thread
              list to show it opens the threads panel; without one it states
              the figure. Its definition is on the Lanes head's hover. */}
          {callsTotal != null && callsTotal > 1 ? (
            <HoverTooltip
              wide
              placement="bottom"
              label="Every model round-trip in this run: the main thread, its sub-agents, and retries from one user prompt. A run is narrower than the session and distinct from the conversation turns on the axis. Click to list the run's traces and hop between its threads."
            >
              {threads ? (
                <button
                  type="button"
                  data-lanes-calls-total=""
                  onClick={() => setThreadsOpen((v) => !v)}
                  aria-expanded={threadsOpen}
                  className={actionBtn}
                >
                  {formatCompact(callsTotal)} API calls in this run
                </button>
              ) : (
                <span tabIndex={0} className={`cursor-help ${META_CLASS} text-[color:var(--muted)]`} data-lanes-calls-total="">
                  {formatCompact(callsTotal)} API calls in this run
                </span>
              )}
            </HoverTooltip>
          ) : null}
        </span>
        <span className="flex items-center gap-[8px]">
        <span className="flex items-center gap-[8px]">
          {topicOpen ? (
            <TopicLegend
              axis={topicAxis}
              ranked={topicRanked}
              hues={topicHueMap}
              turnCount={turnCount}
              dated={topicClock.dated}
              truncated={journeyState.truncated}
              estimatedBefore={topicClock.estimatedBefore}
            />
          ) : null}
        </span>
        <span className={`flex items-center gap-[4px] ${sessionGraphOn && trace ? "hidden" : ""}`}>
          <HoverTooltip placement="bottom" label="Zoom out. Command-scroll over the timeline zooms at the pointer.">
            <button type="button" className={zoomBtn} onClick={() => zoomAt(1 / 1.5)} aria-label="Zoom out">
              −
            </button>
          </HoverTooltip>
          <HoverTooltip placement="bottom" label="Zoom in. Command-scroll over the timeline zooms at the pointer.">
            <button type="button" className={zoomBtn} onClick={() => zoomAt(1.5)} aria-label="Zoom in">
              +
            </button>
          </HoverTooltip>
          <button
            type="button"
            className={zoomBtn}
            onClick={() => {
              // Fit RE-ENTERS fit mode: the axis follows the container again
              // until the next hand zoom, instead of freezing at this width.
              userZoomedRef.current = false;
              setCellW(fitRef.current);
            }}
            aria-label="Zoom to fit"
          >
            Fit
          </button>
          <button type="button" className={zoomBtn} onClick={() => zoomTo(CELL_1X)} aria-label="Zoom to one turn per column">
            1:1
          </button>
        </span>
        </span>
      </div>
      {threadsOpen && threads ? <TurnThreadsPanel threads={threads} rates={threadRates} currentTurnKeys={currentTurnKeys} currentText={currentStableText} height={threadsH} onResizeStart={startThreadsDrag} /> : null}


      {/* VOID-FIT in fill mode, on both axes: the frame is the panel, whatever
          the trace holds. A box that hugged its content moved whenever the
          content changed — a lane-light trace left the dock floating
          mid-panel, and opening or closing a turn reading shifted the grid
          under the reader mid-scrub. Claiming the column (flex-1) and the
          width instead pins the dock to the bottom edge and the marks to a
          fixed origin: only the dock's own box resizes. min-h-0 keeps flex
          able to shrink it, so a tall dock still wins space and the overflow
          goes to the viewport. Embedded (non-fill) use stays content-sized —
          there is no column there to fill. A spliced inset (collapseGroups)
          must never cap its own height either: nested scrollbars are exactly
          what the content-sized inset row exists to avoid - the child grows
          and the page scrolls. */}
      {/* The plane. Everything that costs per lane per turn is drawn here --
          rows, rail, marks, spans, the strip stack, the flow lines, the
          telemetry band -- because reconciling it as elements is what bounded
          the surface: ~1,300 of them per zoom step, and a plane meant to hold
          several sessions at once cannot pay per turn per lane. The scene is
          the queryable surface that replaces the data-* geometry anchors. */}
      {sessionGraphOn && trace ? (
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <AnchorDockContext.Provider value={ownDock}>
        <SessionCanvasView
          trace={trace}
          turns={turns}
          model={model}
          joinRows={joinRows}
          joinStatus={contentJoins.status}
          needles={joinContext.needles}
          joinHues={joinHues}
          turnsToJoinInputs={turnsToJoinInputs}
          kg={chatGraph.graph?.nodes?.size ? { graph: chatGraph.graph, version: chatGraph.version, overlay: chatGraph.overlay } : null}
          onCellPick={permanentDock ? onBoardPick : null}
          cellDock={BoardCellDock}
          light={handoffLight}
          landing={boardLanding}
          onCardSelect={canvas?.onCardSelect ?? null}
          initialFocus={canvas?.focus ?? null}
          onSelection={canvas?.onSelection ?? null}
          onClose={canvas ? null : () => setSessionGraph(false)}
          publish={canvas ? canvas.publish !== false : !overlayUp}
          sessionGraph={!canvas}
          layered={boardHost.layered}
          onLayer={boardHost.onLayer}
          onUnlayer={boardHost.onUnlayer}
          onAnchor={boardHost.onAnchor ?? null}
          boardBar={boardBar}
          focalWork={shellWork.status === "running"
            ? { calls: shellWork.calls, done: (shellWork.result?.counts?.claimed || 0) + (shellWork.result?.counts?.unclaimed || 0) }
            : null}
          readings={boardHost.readings}
          onReadings={boardHost.onReadings}
          hidden={boardHost.hidden}
          onHidden={boardHost.onHidden}
          notes={boardHost.notes}
          onNotes={boardHost.onNotes ?? null}
          sizes={boardHost.sizes}
          onSizes={boardHost.onSizes}
          order={boardHost.order}
          onOrder={boardHost.onOrder}
          padMachines={boardHost.machines ?? null}
          padSearches={boardHost.searches ?? null}
          padNested={boardHost.nested ?? null}
          onEnter={boardHost.onEnter ?? null}
          onExit={boardHost.onExit ?? null}
          flash={boardHost.flash ?? null}
          range={boardHost.range ?? null}
          workspaceId={boardHost.workspaceId ?? null}
          nestInset={boardHost.inset ?? null}
          titles={boardHost.titles ?? null}
          pinnedFindings={boardHost.pinnedFindings ?? null}
          looseNotes={boardHost.looseNotes ?? null}
          onLooseNote={boardHost.onLooseNote ?? null}
          onThingOff={boardHost.onThingOff ?? null}
          onThingBack={boardHost.onThingBack ?? null}
          onBranch={boardHost.onBranch ?? null}
          turnBase={turnBase}
          focalRecord={record}
          onFocalEarlier={onLoadEarlier}
          focalFindings={findings.report || null}
          pointNote={walkSession ? notePoint : null}
          openNote={walkSession ? noteOpen : null}
          onPinHover={walkSession ? setPinsPointed : null}
          walkthroughKey={boardWalkKey}
          walkKey={walkKey}
          nestedHomes={boardHost.nestedHomes ?? null}
        />
        </AnchorDockContext.Provider>
        {canvas ? null : (
          <WorkspaceAddSheet open={graphAdding} onClose={() => setGraphAdding(false)} onCover={setGraphCover} canvasId={graphCanvasId} title={trace.summary || ""} />
        )}
        </div>
      ) : (
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      <LanesCanvasPlane
        scene={scene}
        // The sizing contract the DOM plane had, minus the part that cannot
        // survive: a drawn plane has no content width to hug, so `w-fit` is
        // meaningless here. Never exceeding the panel still matters, and so
        // does claiming the column when it is filling one.
        className={`min-w-[280px] max-w-full rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] ${
          fill ? "w-full min-h-[140px] flex-1" : "h-[52vh] w-full"
        }`}
        onScroller={setPlaneScroller}
        onPick={onPlanePick}
        onToggleRow={onPlaneToggle}
        onHover={onPlaneHover}
        guide={planeGuide}
        onInsetPan={onPlaneInsetPan}
        onTurnWindow={turnCount > SCENE_WINDOW_MIN_TURNS ? onTurnWindow : null}
        tooltipOf={tooltipOf}
        pinnedChildren={<>{axisControlEl}{scanControls}</>}
        floatingChildren={rowPins.length || rowNotePins.length ? (
          <>
            <GraphPinLayer pins={rowPins} isOpen={pinHoldsOpen} isUnseen={isUnseen} onOpen={openPinById} bindPin={bindPin} />
            <GraphPinLayer pins={rowNotePins} isOpen={noteHoldsOpen} onOpen={openNoteById} bindPin={bindPin} />
          </>
        ) : null}
        floatingPinnedChildren={stripPins.length || stripNotePins.length ? (
          <>
            <GraphPinLayer pins={stripPins} isOpen={pinHoldsOpen} isUnseen={isUnseen} onOpen={openPinById} bindPin={bindPin} />
            <GraphPinLayer pins={stripNotePins} isOpen={noteHoldsOpen} onOpen={openNoteById} bindPin={bindPin} />
          </>
        ) : null}
      />
      {openCluster && graphOverlay ? (
        <GraphCommentCard
          key={openPin.claims[0]}
          pin={openCluster}
          claims={openCluster.claims.map((id) => graphClaimById.get(id)).filter(Boolean)}
          anchors={graphAnchorByKey}
          base={turnBase}
          getPinEl={() => pinEls.current.get(openCluster.id) || null}
          scroller={scrollEl}
          isUnseen={isUnseen}
          replies={openReplies}
          onClose={closePin}
          onReply={onGraphReply ? replyFromCard : null}
          onOpenChat={onOpenGraphChat}
          onOpenList={() => {
            const t = Number.isInteger(openCluster.turn) ? openCluster.turn : 0;
            closePin();
            pickGraph(t, true, t, openCluster.claims[0] ?? null);
          }}
          onPickTurn={(t) => centerOn(t)}
          onHoverClaim={setHoverClaim}
        />
      ) : null}
      {openNoteCluster ? (
        <GraphCommentCard
          key={`note:${openNote.notes[0]}`}
          pin={openNoteCluster}
          claims={NO_CLAIMS}
          notes={openNoteCluster.claims.map((id) => noteById.get(id)).filter(Boolean)}
          anchors={NO_ANCHORS}
          base={turnBase}
          getPinEl={() => pinEls.current.get(openNoteCluster.id) || null}
          scroller={scrollEl}
          replies={openNoteReplies}
          onClose={closeNote}
          onReply={askFromNote}
          onOpenChat={() => showDockTab("reading")}
          onPickTurn={(t) => centerOn(t)}
        />
      ) : null}
      {/* The seam between the names and the axis, draggable: the rail is the
          one width a reader wants to set for themselves (long paths, short
          ids). A hairline shows where the grip is only while the pointer is
          on it or the drag is on; at rest the seam is the rail's own edge. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the rail"
        aria-valuenow={railW}
        aria-valuemin={RAIL_W_MIN}
        aria-valuemax={RAIL_W_MAX}
        data-rail-resize=""
        onPointerDown={startRailDrag}
        className="group absolute bottom-0 top-0 z-[6] w-[7px] -translate-x-1/2 cursor-col-resize select-none"
        style={{ left: railW, touchAction: "none" }}
      >
        <span aria-hidden="true" className="absolute inset-y-0 left-[3px] w-px bg-transparent group-hover:bg-[color:var(--line-strong)] group-active:bg-[color:var(--line-strong)]" />
      </div>
      </div>
      )}

      {(() => {
        // One dock, whichever card the cell is on: this trace's own moves
        // the plane with it, and has what only this view of it has.
        const own = !sel?.insetOf;
        const dock = sel == null ? null : (
          <CellDock
            data={boardSel ? boardDock : own ? ownDock : childDock || ownDock}
            at={sel}
            onAt={(move) => setSel((cur) => ({ ...move(cur), insetOf: cur?.insetOf ?? null }))}
            base={dockBase}
            onClose={() => {
              setSel(null);
              setSysPick(null);
            }}
            keepAction={boardKeep ? () => { boardKeep(); setBoardKeep(null); } : null}
            layAction={boardLay}
            sessionTitle={boardSel ? boardPick?.title || null : trace?.summary || null}
            onOpenTrace={onOpenTrace}
            onScopeNavigate={onScopeNavigate}
            onOpenGraphChat={onOpenGraphChat}
            onHoverClaim={setHoverClaim}
            moves={own ? {
              onScrubLane: (t) => selLane && goTo(selLane.id, t),
              onSelectLane: (laneId) => goTo(laneId, sel?.turn ?? 0),
              onSelectCell: (laneId, t) => pick(laneId, t),
              onNavTurn: (t) => {
                setSel((cur) => ({ ...cur, turn: t, tab: "turn" }));
                centerOn(t);
              },
              onPickGraph: (t, claimId) => pickGraph(t, true, t, claimId ?? null),
            } : null}
            extra={own ? {
              system: systemSubject,
              onScrubSystem: (t) => pickSystem({ kind: sysPick.kind, start: t, end: t }),
              threads,
              onLocateJoin: sessionGraphOn ? anchorHandoff.onLocateJoin : locateJoin,
              followKey: sessionGraphOn ? anchorHandoff.followKey : localFollow?.key || null,
              onLayJoin: sessionGraphOn ? anchorLay : null,
              onHighlight,
            } : boardSel ? boardExtra : null}
            fill={fill}
            height={fill && !bridged ? dockH : null}
            bare={permanentDock}
          />
        );
        // A minimized dock has no height to trade; otherwise the divider is
        // always there, however few rows the trace has.
        const divider = fill && !(permanentDock && dockMin) ? (
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize detail dock"
            data-dock-divider=""
            onPointerDown={startDockDrag}
            onMouseDown={(e) => e.preventDefault()}
            // The 12px grip spans the gutter (the row's 6px gaps less
            // its margins) and a pixel of each frame.
            className="group -my-[7px] flex h-[12px] flex-none cursor-row-resize select-none items-center justify-center"
            style={{ touchAction: "none" }}
          >
            {/* The app's column knob, the drawer's seam's too: shown pointed
                at or dragged (the capture keeps it pointed at), not at rest,
                where a gutter this narrow holds it too close to the frames. */}
            <span className="h-[3px] w-[48px] rounded-full bg-[color:var(--line-strong)] opacity-0 transition-opacity duration-[180ms] group-hover:opacity-100" />
          </div>
        ) : null;
        // The reading is one more detail expansion: same dock shell, same
        // slot, above whatever the reader has selected. It opens on the run
        // and STAYS once the run lands -- the reading is the answer, and a
        // dock that vanished when it finished took the list of what was
        // found with it. Closing it is the reader's move.
        // It takes the slot's resizable height when it IS the slot; with a
        // selection dock open too, the reader's divider still sizes that one
        // and the reading sits above it at its own height rather than the two
        // of them each claiming 46% of the column.
        //
        // Rows come from the RUN while it runs and from the settled REPORT
        // once it lands — the same row shape either way, so the finished dock
        // shows the authoritative answer rather than the live state the run
        // happened to end on, and cannot drift from the strip beside it.
        const readingBusy = reading.status === "running";
        // The conversation rides INSIDE the reading panel rather than beside
        // it. Two docks in one slot each took their own share of the column
        // and squeezed the plane between them, and worse, they split one
        // subject in half: the account of what the agent did is the thing a
        // reader wants to ask about, and the claims it kept and disputed are
        // the material of the question.
        // Keyed by trace: one conversation per trace, remounted when the view
        // switches traces under the permanent dock, so a thread is only ever
        // saved under the trace it was about.
        const chat = trace ? (
          <TraceChat
            key={trace.id ?? "none"}
            traceId={trace.id ?? null}
            chatTab="reading"
            getModel={chatModel}
            shape={chatShape}
            enabled={reading.chatStatus === "ready"}
            unavailable={reading.chatStatus === "none" ? "Configure a chat model to ask about this trace." : null}
            shown={!permanentDock || (activeDockTab === "reading" && !dockMin)}
            onNotes={walkSession ? boardNotesFor(`reading:${walkSession}`) : null}
            workspace={canvas?.workspaceId || null}
          />
        ) : null;
        // The questions about the reading sit under it, in a workspace's dock
        // too, beside the workspace's own chat.
        const unreadable = readingOfferNote({ ready: reading.ready, hasCalls: reading.hasCalls, asks: !!chat });
        const readingProps = {
          rows: readingBusy
            ? readingRows(reading.liveReport, (reading.live?.judged || []).map((j) => j.id))
            : readingRows(reading.report),
          callsById: reading.callsById,
          summary: reading.summary,
          totals: readingBusy
            ? reading.live?.totals
            : (reading.report ? readingDockTotals(reading.report) : null),
          batches: reading.live?.batches || [],
          busy: readingBusy,
          progress: reading.progress,
          onStop: reading.stop,
          onRead: readHere,
          unreadable,
        };
        const walkPane = walkKey ? (
          <Suspense fallback={null}>
            <WalkthroughDock
              key={walkKey}
              session={walkKey}
              next={walkNext}
              title={trace?.summary || ""}
              sessions={walkTraces}
              workspaceChat={canvas?.walkChat ?? null}
              board={canvas?.workspaceId ?? null}
              onShow={() => showDockTab("walkthrough")}
              onFocusSession={walkTraces ? requestWorkspaceFocus : null}
              turnLabel={walkTurnLabel}
              pointed={pinsPointed}
              onPoint={setNotePoint}
              onOpen={openNoteOnBoard}
              chatFor={(told) => (isWorkspaceWalk(walkKey) ? (
                // Across every trace on the workspace, as one with no anchor
                // asks about its walkthrough.
                <TraceChat
                  key={told.key}
                  threadKey={told.key}
                  chatTab="walkthrough"
                  getModel={NO_TRACE_MODEL}
                  toolsFor={(pin) => workspaceWalkTools({ sessions: told.sessions || [], window: told.window || null, pin, claims: told.claims || [], readChat: told.readChat || null, shown: told.seed?.length ? told.shown : null })}
                  suffix={workspaceWalkSuffix({ ...told, continued: !!told.seed?.length })}
                  seed={told.seed}
                  enabled={reading.chatStatus === "ready"}
                  unavailable={reading.chatStatus === "none" ? "Configure a chat model to ask about this walkthrough." : null}
                  shown={activeDockTab === "walkthrough" && !dockMin}
                  onNotes={(notes) => notes.flatMap((n) => boardNoteSink(told.key, n.session)([n]))}
                  placeholder="Ask about this walkthrough"
                  workspace={canvas?.workspaceId || null}
                />
              ) : (
                <TraceChat
                  key={told.key}
                  traceId={trace?.id ?? null}
                  threadKey={told.key}
                  chatTab="walkthrough"
                  walkthrough={told}
                  seed={told.seed}
                  // The investigator's reads, which the conversation it carries
                  // on made, and its look into the chat behind the claims.
                  moreTools={() => [
                    ...createCaseReader({ sessions: [{ id: walkSession, ref: "S1" }], window: told.window }).tools,
                    ...(told.claims?.length ? [claimOriginTool({ claims: told.claims, readChat: told.readChat })] : []),
                  ]}
                  getModel={chatModel}
                  shape={chatShape}
                  enabled={reading.chatStatus === "ready"}
                  unavailable={reading.chatStatus === "none" ? "Configure a chat model to ask about this walkthrough." : null}
                  shown={activeDockTab === "walkthrough" && !dockMin}
                  onNotes={boardNotesFor(told.key)}
                  placeholder="Ask about this walkthrough"
                  workspace={canvas?.workspaceId || null}
                />
              ))}
            />
          </Suspense>
        ) : null;
        const investigatePane = walkSession && investigateSeen ? (
          <Suspense fallback={null}>
            <div data-dock-investigate="" className="min-h-0 min-w-0 flex-1 overflow-y-auto">
              <CaseInbox
                mode="dock"
                steer={`session:${boardIds.join(",")}`}
                range={investigateRange}
                focusCaseId={boardCaseId(canvas?.workspaceId)}
                onBoard={boardIds}
                onFocus={onInvestigateFocus}
                onLayOn={layOn ? onInvestigateLayOn : null}
                pointed={pinsPointed}
                onPoint={setNotePoint}
              />
            </div>
          </Suspense>
        ) : null;
        if (bridged) {
          if (!dockFront || !dockSlot) return null;
          return createPortal(
            <>
              <div data-dock-reading-pane="" className={activeDockTab === "reading" ? "flex min-h-0 min-w-0 flex-1 flex-col" : "hidden"}>
                <ReadingDock key="reading-dock" {...readingProps} fill bare chat={chat} />
              </div>
              {walkPane ? (
                <div data-dock-walkthrough-pane="" className={activeDockTab === "walkthrough" ? "flex min-h-0 min-w-0 flex-1 flex-col" : "hidden"}>
                  {walkPane}
                </div>
              ) : null}
              {investigatePane ? (
                <div data-dock-investigate-pane="" className={activeDockTab === "investigate" ? "flex min-h-0 min-w-0 flex-1 flex-col" : "hidden"}>
                  {investigatePane}
                </div>
              ) : null}
              {activeDockTab === "sel" ? dock : null}
            </>,
            dockSlot,
          );
        }
        if (permanentDock) {
          const tb = dockBase;
          const selLabel = sel == null
            ? ""
            : systemSubject
              ? systemSubject.label
              : docked.lane
                ? `T${tb + sel.turn} ${docked.lane.label}`
                : `Turn ${tb + sel.turn}`;
          const tabs = [
            {
              id: "reading", label: "Reading", active: activeDockTab === "reading",
              dot: readingBusy ? "bg-[color:var(--eyebrow)]" : null,
              onSelect: () => showDockTab("reading"),
            },
            ...(walkSession ? [{
              id: "walkthrough", label: "Walkthrough", active: activeDockTab === "walkthrough",
              dot: walkRunning ? "bg-[color:var(--eyebrow)]" : null,
              onSelect: () => showDockTab("walkthrough"),
            }] : []),
            ...(walkSession ? [{
              id: "investigate", label: "Investigate", active: activeDockTab === "investigate",
              onSelect: () => showDockTab("investigate"),
            }] : []),
            ...(sel != null
              ? [{
                id: "sel", label: selLabel, active: activeDockTab === "sel",
                onSelect: () => showDockTab("sel"),
                onClose: () => { setSel(null); setSysPick(null); },
              }]
              : []),
          ];
          // Its arrow points the way the dock folds: down, to its tabs.
          const foldArrow = dockMin ? "M3 7l3-3 3 3" : "M3 5l3 3 3-3";
          const minimize = (
            <HoverTooltip placement="top" label={dockMin ? "Show the dock" : "Minimize the dock to its tabs"}>
              <button
                type="button"
                data-dock-minimize=""
                aria-label={dockMin ? "Restore the dock" : "Minimize the dock"}
                aria-expanded={!dockMin}
                onClick={() => setDockMinimized(!dockMin)}
                className="flex h-full w-[26px] cursor-pointer items-center justify-center text-[color:var(--muted)] hover:text-[color:var(--ink)]"
              >
                <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d={foldArrow} />
                </svg>
              </button>
            </HoverTooltip>
          );
          return (
            <>
              {divider}
              <div
                data-lanes-dock-shell=""
                data-dock-pane={activeDockTab}
                data-dock-minimized={dockMin ? "" : undefined}
                className="flex min-h-0 min-w-0 flex-none flex-col rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]"
                // One size whatever the tab, so switching never moves the
                // plane: the reader's dragged height, else the default share.
                style={dockMin ? undefined : { height: dockH ?? `${Math.round((sessionGraphOn && trace ? SESSION_DOCK_SHARE : DOCK_SHARE) * 100)}%` }}
              >
                {/* The Lanes view's own control scale: one 24px band, 11px
                    labels, the active tab on the control group's green ground.
                    Hairlines between tabs and none around them: the dock's
                    frame is the only outline, so no edge is drawn twice: the
                    row's own rule under the tabs exists only while there is a
                    body beneath it, since minimized it would sit on the
                    frame's bottom edge. */}
                <div
                  role="tablist"
                  aria-label="Detail dock"
                  className={`flex h-[24px] min-w-0 flex-none items-stretch ${dockMin ? "" : "border-b border-[color:var(--line-soft)]"}`}
                >
                  {tabs.map((t) => (
                    <div
                      key={t.id}
                      data-dock-tab={t.id}
                      className={`flex min-w-0 max-w-[320px] items-stretch border-r border-[color:var(--line-soft)] ${
                        t.active ? "[background-image:linear-gradient(var(--green-soft),var(--green-soft))]" : ""
                      }`}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={t.active}
                        onClick={t.onSelect}
                        className={`flex min-w-0 items-center gap-[6px] pl-[9px] text-[11px] ${t.onClose ? "pr-[3px]" : "pr-[9px]"} ${
                          t.active ? "text-[color:var(--ink)]" : "cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                        }`}
                      >
                        {t.dot ? <span aria-hidden="true" className={`h-[6px] w-[6px] flex-none rounded-full ${t.dot}`} /> : null}
                        {/* truncate clips to the line box, so the box has to be tall
                            enough to hold descenders. */}
                        <span className="min-w-0 truncate leading-[16px]">{t.label}</span>
                      </button>
                      {t.onClose ? (
                        <button
                          type="button"
                          aria-label={`Close ${t.label}`}
                          onClick={t.onClose}
                          className="flex w-[20px] flex-none cursor-pointer items-center justify-center text-[12px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                  ))}
                  <span className="min-w-0 flex-1" />
                  {minimize}
                </div>
                {/* Kept mounted behind a cell's pane and while minimized: a
                    question the agent is still answering must not be torn
                    down because the reader looked at a cell. */}
                <div
                  data-dock-reading-pane=""
                  className={activeDockTab === "reading" && !dockMin ? "flex min-h-0 min-w-0 flex-1 flex-col" : "hidden"}
                >
                  <ReadingDock key="reading-dock" {...readingProps} fill bare chat={chat} />
                </div>
                {walkPane ? (
                  <div
                    data-dock-walkthrough-pane=""
                    className={activeDockTab === "walkthrough" && !dockMin ? "flex min-h-0 min-w-0 flex-1 flex-col" : "hidden"}
                  >
                    {walkPane}
                  </div>
                ) : null}
                {investigatePane ? (
                  <div
                    data-dock-investigate-pane=""
                    className={activeDockTab === "investigate" && !dockMin ? "flex min-h-0 min-w-0 flex-1 flex-col" : "hidden"}
                  >
                    {investigatePane}
                  </div>
                ) : null}
                {activeDockTab === "sel" && !dockMin ? dock : null}
              </div>
            </>
          );
        }
        const readingDock = reading.dockOpen && (readingBusy || reading.report || chat)
          ? (
            <ReadingDock
              key="reading-dock"
              {...readingProps}
              onClose={reading.closeDock}
              fill={!dock && fill}
              height={!dock && fill ? dockH : null}
              chat={chat}
            />
          )
          : null;
        if (readingDock || dock) {
          return (
            <>
              {divider}
              {readingDock}
              {dock}
            </>
          );
        }
        // No dock open: the plane keeps the whole column. The usage hint that
        // used to sit here cost a line of the timeline on every trace to say
        // what one click teaches.
        return null;
      })()}
    </div>
    </TurnBaseContext.Provider>
  );
}
